// build/runtime.ts
var BASE_PATH = "https://api.x.flatfile.com/v1".replace(/\/+$/, "");
var Configuration = class {
  constructor(configuration = {}) {
    this.configuration = configuration;
  }
  set config(configuration) {
    this.configuration = configuration;
  }
  get basePath() {
    return this.configuration.basePath != null ? this.configuration.basePath : BASE_PATH;
  }
  get fetchApi() {
    return this.configuration.fetchApi;
  }
  get middleware() {
    return this.configuration.middleware || [];
  }
  get queryParamsStringify() {
    return this.configuration.queryParamsStringify || querystring;
  }
  get username() {
    return this.configuration.username;
  }
  get password() {
    return this.configuration.password;
  }
  get apiKey() {
    const apiKey = this.configuration.apiKey;
    if (apiKey) {
      return typeof apiKey === "function" ? apiKey : () => apiKey;
    }
    return void 0;
  }
  get accessToken() {
    const accessToken = this.configuration.accessToken;
    if (accessToken) {
      return typeof accessToken === "function" ? accessToken : async () => accessToken;
    }
    return void 0;
  }
  get headers() {
    return this.configuration.headers;
  }
  get credentials() {
    return this.configuration.credentials;
  }
};
var DefaultConfig = new Configuration();
var BaseAPI = class {
  constructor(configuration = DefaultConfig) {
    this.configuration = configuration;
    this.middleware = configuration.middleware;
  }
  middleware;
  withMiddleware(...middlewares) {
    const next = this.clone();
    next.middleware = next.middleware.concat(...middlewares);
    return next;
  }
  withPreMiddleware(...preMiddlewares) {
    const middlewares = preMiddlewares.map((pre) => ({ pre }));
    return this.withMiddleware(...middlewares);
  }
  withPostMiddleware(...postMiddlewares) {
    const middlewares = postMiddlewares.map((post) => ({ post }));
    return this.withMiddleware(...middlewares);
  }
  async request(context, initOverrides) {
    const { url, init } = await this.createFetchParams(context, initOverrides);
    const response = await this.fetchApi(url, init);
    if (response && (response.status >= 200 && response.status < 300)) {
      return response;
    }
    throw new ResponseError(response, "Response returned an error code");
  }
  async createFetchParams(context, initOverrides) {
    let url = this.configuration.basePath + context.path;
    if (context.query !== void 0 && Object.keys(context.query).length !== 0) {
      url += "?" + this.configuration.queryParamsStringify(context.query);
    }
    const headers = Object.assign({}, this.configuration.headers, context.headers);
    Object.keys(headers).forEach((key) => headers[key] === void 0 ? delete headers[key] : {});
    const initOverrideFn = typeof initOverrides === "function" ? initOverrides : async () => initOverrides;
    const initParams = {
      method: context.method,
      headers,
      body: context.body,
      credentials: this.configuration.credentials
    };
    const overridedInit = {
      ...initParams,
      ...await initOverrideFn({
        init: initParams,
        context
      })
    };
    const init = {
      ...overridedInit,
      body: isFormData(overridedInit.body) || overridedInit.body instanceof URLSearchParams || isBlob(overridedInit.body) ? overridedInit.body : JSON.stringify(overridedInit.body)
    };
    return { url, init };
  }
  fetchApi = async (url, init) => {
    let fetchParams = { url, init };
    for (const middleware of this.middleware) {
      if (middleware.pre) {
        fetchParams = await middleware.pre({
          fetch: this.fetchApi,
          ...fetchParams
        }) || fetchParams;
      }
    }
    let response = void 0;
    try {
      response = await (this.configuration.fetchApi || fetch)(fetchParams.url, fetchParams.init);
    } catch (e) {
      for (const middleware of this.middleware) {
        if (middleware.onError) {
          response = await middleware.onError({
            fetch: this.fetchApi,
            url: fetchParams.url,
            init: fetchParams.init,
            error: e,
            response: response ? response.clone() : void 0
          }) || response;
        }
      }
      if (response === void 0) {
        if (e instanceof Error) {
          throw new FetchError(e, "The request failed and the interceptors did not return an alternative response");
        } else {
          throw e;
        }
      }
    }
    for (const middleware of this.middleware) {
      if (middleware.post) {
        response = await middleware.post({
          fetch: this.fetchApi,
          url: fetchParams.url,
          init: fetchParams.init,
          response: response.clone()
        }) || response;
      }
    }
    return response;
  };
  /**
   * Create a shallow clone of `this` by constructing a new instance
   * and then shallow cloning data members.
   */
  clone() {
    const constructor = this.constructor;
    const next = new constructor(this.configuration);
    next.middleware = this.middleware.slice();
    return next;
  }
};
function isBlob(value) {
  return typeof Blob !== "undefined" && value instanceof Blob;
}
function isFormData(value) {
  return typeof FormData !== "undefined" && value instanceof FormData;
}
var ResponseError = class extends Error {
  constructor(response, msg) {
    super(msg);
    this.response = response;
  }
  name = "ResponseError";
};
var FetchError = class extends Error {
  constructor(cause, msg) {
    super(msg);
    this.cause = cause;
  }
  name = "FetchError";
};
var RequiredError = class extends Error {
  constructor(field, msg) {
    super(msg);
    this.field = field;
  }
  name = "RequiredError";
};
var COLLECTION_FORMATS = {
  csv: ",",
  ssv: " ",
  tsv: "	",
  pipes: "|"
};
function exists(json, key) {
  const value = json[key];
  return value !== null && value !== void 0;
}
function querystring(params, prefix = "") {
  return Object.keys(params).map((key) => querystringSingleKey(key, params[key], prefix)).filter((part) => part.length > 0).join("&");
}
function querystringSingleKey(key, value, keyPrefix = "") {
  const fullKey = keyPrefix + (keyPrefix.length ? `[${key}]` : key);
  if (value instanceof Array) {
    const multiValue = value.map((singleValue) => encodeURIComponent(String(singleValue))).join(`&${encodeURIComponent(fullKey)}=`);
    return `${encodeURIComponent(fullKey)}=${multiValue}`;
  }
  if (value instanceof Set) {
    const valueAsArray = Array.from(value);
    return querystringSingleKey(key, valueAsArray, keyPrefix);
  }
  if (value instanceof Date) {
    return `${encodeURIComponent(fullKey)}=${encodeURIComponent(value.toISOString())}`;
  }
  if (value instanceof Object) {
    return querystring(value, fullKey);
  }
  return `${encodeURIComponent(fullKey)}=${encodeURIComponent(String(value))}`;
}
function mapValues(data, fn) {
  return Object.keys(data).reduce(
    (acc, key) => ({ ...acc, [key]: fn(data[key]) }),
    {}
  );
}
function canConsumeForm(consumes) {
  for (const consume of consumes) {
    if ("multipart/form-data" === consume.contentType) {
      return true;
    }
  }
  return false;
}
var JSONApiResponse = class {
  constructor(raw, transformer = (jsonValue) => jsonValue) {
    this.raw = raw;
    this.transformer = transformer;
  }
  async value() {
    return this.transformer(await this.raw.json());
  }
};
var VoidApiResponse = class {
  constructor(raw) {
    this.raw = raw;
  }
  async value() {
    return void 0;
  }
};
var BlobApiResponse = class {
  constructor(raw) {
    this.raw = raw;
  }
  async value() {
    return await this.raw.blob();
  }
};
var TextApiResponse = class {
  constructor(raw) {
    this.raw = raw;
  }
  async value() {
    return await this.raw.text();
  }
};

// build/models/AccessToken.ts
function instanceOfAccessToken(value) {
  let isInstance = true;
  isInstance = isInstance && "accessToken" in value;
  isInstance = isInstance && "expiresIn" in value;
  isInstance = isInstance && "expires" in value;
  return isInstance;
}
function AccessTokenFromJSON(json) {
  return AccessTokenFromJSONTyped(json, false);
}
function AccessTokenFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "accessToken": json["accessToken"],
    "expiresIn": json["expiresIn"],
    "expires": json["expires"],
    "email": !exists(json, "email") ? void 0 : json["email"],
    "userId": !exists(json, "userId") ? void 0 : json["userId"],
    "accountId": !exists(json, "accountId") ? void 0 : json["accountId"]
  };
}
function AccessTokenToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "accessToken": value.accessToken,
    "expiresIn": value.expiresIn,
    "expires": value.expires,
    "email": value.email,
    "userId": value.userId,
    "accountId": value.accountId
  };
}

// build/models/AccessTokenResponse.ts
function instanceOfAccessTokenResponse(value) {
  let isInstance = true;
  return isInstance;
}
function AccessTokenResponseFromJSON(json) {
  return AccessTokenResponseFromJSONTyped(json, false);
}
function AccessTokenResponseFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "data": !exists(json, "data") ? void 0 : AccessTokenFromJSON(json["data"])
  };
}
function AccessTokenResponseToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "data": AccessTokenToJSON(value.data)
  };
}

// build/models/Account.ts
function instanceOfAccount(value) {
  let isInstance = true;
  isInstance = isInstance && "id" in value;
  isInstance = isInstance && "name" in value;
  isInstance = isInstance && "createdAt" in value;
  return isInstance;
}
function AccountFromJSON(json) {
  return AccountFromJSONTyped(json, false);
}
function AccountFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "id": json["id"],
    "name": json["name"],
    "createdAt": new Date(json["createdAt"]),
    "subdomain": !exists(json, "subdomain") ? void 0 : json["subdomain"],
    "vanityDomainDashboard": !exists(json, "vanityDomainDashboard") ? void 0 : json["vanityDomainDashboard"],
    "vanityDomainSpaces": !exists(json, "vanityDomainSpaces") ? void 0 : json["vanityDomainSpaces"],
    "embeddedDomainWhitelist": !exists(json, "embeddedDomainWhitelist") ? void 0 : json["embeddedDomainWhitelist"],
    "customFromEmail": !exists(json, "customFromEmail") ? void 0 : json["customFromEmail"],
    "stripeCustomerId": !exists(json, "stripeCustomerId") ? void 0 : json["stripeCustomerId"],
    "defaultAppId": !exists(json, "defaultAppId") ? void 0 : json["defaultAppId"],
    "dashboard": !exists(json, "dashboard") ? void 0 : json["dashboard"]
  };
}
function AccountToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "id": value.id,
    "name": value.name,
    "createdAt": value.createdAt.toISOString(),
    "subdomain": value.subdomain,
    "vanityDomainDashboard": value.vanityDomainDashboard,
    "vanityDomainSpaces": value.vanityDomainSpaces,
    "embeddedDomainWhitelist": value.embeddedDomainWhitelist,
    "customFromEmail": value.customFromEmail,
    "stripeCustomerId": value.stripeCustomerId,
    "defaultAppId": value.defaultAppId,
    "dashboard": value.dashboard
  };
}

// build/models/AccountResponse.ts
function instanceOfAccountResponse(value) {
  let isInstance = true;
  return isInstance;
}
function AccountResponseFromJSON(json) {
  return AccountResponseFromJSONTyped(json, false);
}
function AccountResponseFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "data": !exists(json, "data") ? void 0 : AccountFromJSON(json["data"])
  };
}
function AccountResponseToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "data": AccountToJSON(value.data)
  };
}

// build/models/HasAllValidConstraint.ts
var HasAllValidConstraintTypeEnum = {
  HasAllValid: "hasAllValid"
};
function instanceOfHasAllValidConstraint(value) {
  let isInstance = true;
  isInstance = isInstance && "type" in value;
  return isInstance;
}
function HasAllValidConstraintFromJSON(json) {
  return HasAllValidConstraintFromJSONTyped(json, false);
}
function HasAllValidConstraintFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "type": json["type"]
  };
}
function HasAllValidConstraintToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "type": value.type
  };
}

// build/models/HasDataConstraint.ts
var HasDataConstraintTypeEnum = {
  HasData: "hasData"
};
function instanceOfHasDataConstraint(value) {
  let isInstance = true;
  isInstance = isInstance && "type" in value;
  return isInstance;
}
function HasDataConstraintFromJSON(json) {
  return HasDataConstraintFromJSONTyped(json, false);
}
function HasDataConstraintFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "type": json["type"]
  };
}
function HasDataConstraintToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "type": value.type
  };
}

// build/models/HasSelectionConstraint.ts
var HasSelectionConstraintTypeEnum = {
  HasSelection: "hasSelection"
};
function instanceOfHasSelectionConstraint(value) {
  let isInstance = true;
  isInstance = isInstance && "type" in value;
  return isInstance;
}
function HasSelectionConstraintFromJSON(json) {
  return HasSelectionConstraintFromJSONTyped(json, false);
}
function HasSelectionConstraintFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "type": json["type"]
  };
}
function HasSelectionConstraintToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "type": value.type
  };
}

// build/models/ActionConstraint.ts
function ActionConstraintFromJSON(json) {
  return ActionConstraintFromJSONTyped(json, false);
}
function ActionConstraintFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  switch (json["type"]) {
    case "hasAllValid":
      return { ...HasAllValidConstraintFromJSONTyped(json, true), type: "hasAllValid" };
    case "hasData":
      return { ...HasDataConstraintFromJSONTyped(json, true), type: "hasData" };
    case "hasSelection":
      return { ...HasSelectionConstraintFromJSONTyped(json, true), type: "hasSelection" };
    default:
      throw new Error(`No variant of ActionConstraint exists with 'type=${json["type"]}'`);
  }
}
function ActionConstraintToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  switch (value["type"]) {
    case "hasAllValid":
      return HasAllValidConstraintToJSON(value);
    case "hasData":
      return HasDataConstraintToJSON(value);
    case "hasSelection":
      return HasSelectionConstraintToJSON(value);
    default:
      throw new Error(`No variant of ActionConstraint exists with 'type=${value["type"]}'`);
  }
}

// build/models/RequiredConstraint.ts
var RequiredConstraintTypeEnum = {
  Required: "required"
};
function instanceOfRequiredConstraint(value) {
  let isInstance = true;
  isInstance = isInstance && "type" in value;
  return isInstance;
}
function RequiredConstraintFromJSON(json) {
  return RequiredConstraintFromJSONTyped(json, false);
}
function RequiredConstraintFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "type": json["type"]
  };
}
function RequiredConstraintToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "type": value.type
  };
}

// build/models/InputConstraint.ts
function InputConstraintFromJSON(json) {
  return InputConstraintFromJSONTyped(json, false);
}
function InputConstraintFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  switch (json["type"]) {
    case "required":
      return { ...RequiredConstraintFromJSONTyped(json, true), type: "required" };
    default:
      throw new Error(`No variant of InputConstraint exists with 'type=${json["type"]}'`);
  }
}
function InputConstraintToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  switch (value["type"]) {
    case "required":
      return RequiredConstraintToJSON(value);
    default:
      throw new Error(`No variant of InputConstraint exists with 'type=${value["type"]}'`);
  }
}

// build/models/EnumPropertyOptionValue.ts
function instanceOfEnumPropertyOptionValue(value) {
  let isInstance = true;
  return isInstance;
}
function EnumPropertyOptionValueFromJSON(json) {
  return EnumPropertyOptionValueFromJSONTyped(json, false);
}
function EnumPropertyOptionValueFromJSONTyped(json, ignoreDiscriminator) {
  return json;
}
function EnumPropertyOptionValueToJSON(value) {
  return value;
}

// build/models/EnumPropertyOption.ts
function instanceOfEnumPropertyOption(value) {
  let isInstance = true;
  isInstance = isInstance && "value" in value;
  return isInstance;
}
function EnumPropertyOptionFromJSON(json) {
  return EnumPropertyOptionFromJSONTyped(json, false);
}
function EnumPropertyOptionFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "value": EnumPropertyOptionValueFromJSON(json["value"]),
    "label": !exists(json, "label") ? void 0 : json["label"],
    "description": !exists(json, "description") ? void 0 : json["description"],
    "color": !exists(json, "color") ? void 0 : json["color"],
    "icon": !exists(json, "icon") ? void 0 : json["icon"],
    "meta": !exists(json, "meta") ? void 0 : json["meta"],
    "alternativeNames": !exists(json, "alternativeNames") ? void 0 : json["alternativeNames"]
  };
}
function EnumPropertyOptionToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "value": EnumPropertyOptionValueToJSON(value.value),
    "label": value.label,
    "description": value.description,
    "color": value.color,
    "icon": value.icon,
    "meta": value.meta,
    "alternativeNames": value.alternativeNames
  };
}

// build/models/InputFieldConfig.ts
function instanceOfInputFieldConfig(value) {
  let isInstance = true;
  isInstance = isInstance && "options" in value;
  return isInstance;
}
function InputFieldConfigFromJSON(json) {
  return InputFieldConfigFromJSONTyped(json, false);
}
function InputFieldConfigFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "options": json["options"].map(EnumPropertyOptionFromJSON)
  };
}
function InputFieldConfigToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "options": value.options.map(EnumPropertyOptionToJSON)
  };
}

// build/models/InputFieldDefaultValue.ts
function instanceOfInputFieldDefaultValue(value) {
  let isInstance = true;
  return isInstance;
}
function InputFieldDefaultValueFromJSON(json) {
  return InputFieldDefaultValueFromJSONTyped(json, false);
}
function InputFieldDefaultValueFromJSONTyped(json, ignoreDiscriminator) {
  return json;
}
function InputFieldDefaultValueToJSON(value) {
  return value;
}

// build/models/InputField.ts
var InputFieldTypeEnum = {
  String: "string",
  Textarea: "textarea",
  Number: "number",
  Boolean: "boolean",
  Enum: "enum"
};
function instanceOfInputField(value) {
  let isInstance = true;
  isInstance = isInstance && "key" in value;
  isInstance = isInstance && "label" in value;
  isInstance = isInstance && "type" in value;
  return isInstance;
}
function InputFieldFromJSON(json) {
  return InputFieldFromJSONTyped(json, false);
}
function InputFieldFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "key": json["key"],
    "label": json["label"],
    "description": !exists(json, "description") ? void 0 : json["description"],
    "type": json["type"],
    "defaultValue": !exists(json, "defaultValue") ? void 0 : InputFieldDefaultValueFromJSON(json["defaultValue"]),
    "config": !exists(json, "config") ? void 0 : InputFieldConfigFromJSON(json["config"]),
    "constraints": !exists(json, "constraints") ? void 0 : json["constraints"].map(InputConstraintFromJSON)
  };
}
function InputFieldToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "key": value.key,
    "label": value.label,
    "description": value.description,
    "type": value.type,
    "defaultValue": InputFieldDefaultValueToJSON(value.defaultValue),
    "config": InputFieldConfigToJSON(value.config),
    "constraints": value.constraints === void 0 ? void 0 : value.constraints.map(InputConstraintToJSON)
  };
}

// build/models/ActionInputForm.ts
var ActionInputFormTypeEnum = {
  Simple: "simple"
};
function instanceOfActionInputForm(value) {
  let isInstance = true;
  isInstance = isInstance && "type" in value;
  isInstance = isInstance && "fields" in value;
  return isInstance;
}
function ActionInputFormFromJSON(json) {
  return ActionInputFormFromJSONTyped(json, false);
}
function ActionInputFormFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "type": json["type"],
    "fields": json["fields"].map(InputFieldFromJSON)
  };
}
function ActionInputFormToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "type": value.type,
    "fields": value.fields.map(InputFieldToJSON)
  };
}

// build/models/ActionMessage.ts
var ActionMessageTypeEnum = {
  Error: "error",
  Info: "info"
};
function instanceOfActionMessage(value) {
  let isInstance = true;
  isInstance = isInstance && "type" in value;
  isInstance = isInstance && "content" in value;
  return isInstance;
}
function ActionMessageFromJSON(json) {
  return ActionMessageFromJSONTyped(json, false);
}
function ActionMessageFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "type": json["type"],
    "content": json["content"]
  };
}
function ActionMessageToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "type": value.type,
    "content": value.content
  };
}

// build/models/Action.ts
var ActionScheduleEnum = {
  Weekly: "weekly",
  Daily: "daily",
  Hourly: "hourly"
};
var ActionModeEnum = {
  Foreground: "foreground",
  Background: "background",
  ToolbarBlocking: "toolbarBlocking"
};
function instanceOfAction(value) {
  let isInstance = true;
  isInstance = isInstance && "label" in value;
  return isInstance;
}
function ActionFromJSON(json) {
  return ActionFromJSONTyped(json, false);
}
function ActionFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "slug": !exists(json, "slug") ? void 0 : json["slug"],
    "label": json["label"],
    "description": !exists(json, "description") ? void 0 : json["description"],
    "tooltip": !exists(json, "tooltip") ? void 0 : json["tooltip"],
    "schedule": !exists(json, "schedule") ? void 0 : json["schedule"],
    "operation": !exists(json, "operation") ? void 0 : json["operation"],
    "mode": !exists(json, "mode") ? void 0 : json["mode"],
    "primary": !exists(json, "primary") ? void 0 : json["primary"],
    "confirm": !exists(json, "confirm") ? void 0 : json["confirm"],
    "requireAllValid": !exists(json, "requireAllValid") ? void 0 : json["requireAllValid"],
    "requireSelection": !exists(json, "requireSelection") ? void 0 : json["requireSelection"],
    "icon": !exists(json, "icon") ? void 0 : json["icon"],
    "messages": !exists(json, "messages") ? void 0 : json["messages"].map(ActionMessageFromJSON),
    "inputForm": !exists(json, "inputForm") ? void 0 : ActionInputFormFromJSON(json["inputForm"]),
    "constraints": !exists(json, "constraints") ? void 0 : json["constraints"].map(ActionConstraintFromJSON)
  };
}
function ActionToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "slug": value.slug,
    "label": value.label,
    "description": value.description,
    "tooltip": value.tooltip,
    "schedule": value.schedule,
    "operation": value.operation,
    "mode": value.mode,
    "primary": value.primary,
    "confirm": value.confirm,
    "requireAllValid": value.requireAllValid,
    "requireSelection": value.requireSelection,
    "icon": value.icon,
    "messages": value.messages === void 0 ? void 0 : value.messages.map(ActionMessageToJSON),
    "inputForm": ActionInputFormToJSON(value.inputForm),
    "constraints": value.constraints === void 0 ? void 0 : value.constraints.map(ActionConstraintToJSON)
  };
}

// build/models/ActivityContextSlugs.ts
function instanceOfActivityContextSlugs(value) {
  let isInstance = true;
  return isInstance;
}
function ActivityContextSlugsFromJSON(json) {
  return ActivityContextSlugsFromJSONTyped(json, false);
}
function ActivityContextSlugsFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "space": !exists(json, "space") ? void 0 : json["space"],
    "workbook": !exists(json, "workbook") ? void 0 : json["workbook"],
    "sheet": !exists(json, "sheet") ? void 0 : json["sheet"]
  };
}
function ActivityContextSlugsToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "space": value.space,
    "workbook": value.workbook,
    "sheet": value.sheet
  };
}

// build/models/ActorId.ts
function instanceOfActorId(value) {
  let isInstance = true;
  return isInstance;
}
function ActorIdFromJSON(json) {
  return ActorIdFromJSONTyped(json, false);
}
function ActorIdFromJSONTyped(json, ignoreDiscriminator) {
  return json;
}
function ActorIdToJSON(value) {
  return value;
}

// build/models/ActivityContext.ts
function instanceOfActivityContext(value) {
  let isInstance = true;
  isInstance = isInstance && "accountId" in value;
  isInstance = isInstance && "environmentId" in value;
  return isInstance;
}
function ActivityContextFromJSON(json) {
  return ActivityContextFromJSONTyped(json, false);
}
function ActivityContextFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "namespaces": !exists(json, "namespaces") ? void 0 : json["namespaces"],
    "slugs": !exists(json, "slugs") ? void 0 : ActivityContextSlugsFromJSON(json["slugs"]),
    "actionName": !exists(json, "actionName") ? void 0 : json["actionName"],
    "accountId": json["accountId"],
    "environmentId": json["environmentId"],
    "spaceId": !exists(json, "spaceId") ? void 0 : json["spaceId"],
    "workbookId": !exists(json, "workbookId") ? void 0 : json["workbookId"],
    "sheetId": !exists(json, "sheetId") ? void 0 : json["sheetId"],
    "sheetSlug": !exists(json, "sheetSlug") ? void 0 : json["sheetSlug"],
    "snapshotId": !exists(json, "snapshotId") ? void 0 : json["snapshotId"],
    "versionId": !exists(json, "versionId") ? void 0 : json["versionId"],
    "commitId": !exists(json, "commitId") ? void 0 : json["commitId"],
    "jobId": !exists(json, "jobId") ? void 0 : json["jobId"],
    "programId": !exists(json, "programId") ? void 0 : json["programId"],
    "fileId": !exists(json, "fileId") ? void 0 : json["fileId"],
    "documentId": !exists(json, "documentId") ? void 0 : json["documentId"],
    "precedingEventId": !exists(json, "precedingEventId") ? void 0 : json["precedingEventId"],
    "actorId": !exists(json, "actorId") ? void 0 : ActorIdFromJSON(json["actorId"])
  };
}
function ActivityContextToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "namespaces": value.namespaces,
    "slugs": ActivityContextSlugsToJSON(value.slugs),
    "actionName": value.actionName,
    "accountId": value.accountId,
    "environmentId": value.environmentId,
    "spaceId": value.spaceId,
    "workbookId": value.workbookId,
    "sheetId": value.sheetId,
    "sheetSlug": value.sheetSlug,
    "snapshotId": value.snapshotId,
    "versionId": value.versionId,
    "commitId": value.commitId,
    "jobId": value.jobId,
    "programId": value.programId,
    "fileId": value.fileId,
    "documentId": value.documentId,
    "precedingEventId": value.precedingEventId,
    "actorId": ActorIdToJSON(value.actorId)
  };
}

// build/models/EventTopic.ts
var EventTopic = {
  Agentcreated: "agent:created",
  Agentupdated: "agent:updated",
  Agentdeleted: "agent:deleted",
  Spacecreated: "space:created",
  Spaceupdated: "space:updated",
  Spacedeleted: "space:deleted",
  Spacearchived: "space:archived",
  Spaceexpired: "space:expired",
  SpaceguestAdded: "space:guestAdded",
  SpaceguestRemoved: "space:guestRemoved",
  Documentcreated: "document:created",
  Documentupdated: "document:updated",
  Documentdeleted: "document:deleted",
  Workbookcreated: "workbook:created",
  Workbookupdated: "workbook:updated",
  Workbookdeleted: "workbook:deleted",
  Workbookexpired: "workbook:expired",
  Sheetcreated: "sheet:created",
  Sheetupdated: "sheet:updated",
  Sheetdeleted: "sheet:deleted",
  SheetcountsUpdated: "sheet:counts-updated",
  Snapshotcreated: "snapshot:created",
  Recordscreated: "records:created",
  Recordsupdated: "records:updated",
  Recordsdeleted: "records:deleted",
  Filecreated: "file:created",
  Fileupdated: "file:updated",
  Filedeleted: "file:deleted",
  Fileexpired: "file:expired",
  Jobcreated: "job:created",
  Jobupdated: "job:updated",
  Jobdeleted: "job:deleted",
  Jobready: "job:ready",
  Jobscheduled: "job:scheduled",
  Jobcompleted: "job:completed",
  Jobfailed: "job:failed",
  JoboutcomeAcknowledged: "job:outcome-acknowledged",
  JobpartsCompleted: "job:parts-completed",
  Programcreated: "program:created",
  Programupdated: "program:updated",
  Commitcreated: "commit:created",
  Commitupdated: "commit:updated",
  Commitcompleted: "commit:completed",
  Layercreated: "layer:created",
  Secretcreated: "secret:created",
  Secretupdated: "secret:updated",
  Secretdeleted: "secret:deleted",
  Cron5Minutes: "cron:5-minutes",
  Cronhourly: "cron:hourly",
  Crondaily: "cron:daily",
  Cronweekly: "cron:weekly",
  Environmentcreated: "environment:created",
  Environmentupdated: "environment:updated",
  Environmentdeleted: "environment:deleted"
};
function EventTopicFromJSON(json) {
  return EventTopicFromJSONTyped(json, false);
}
function EventTopicFromJSONTyped(json, ignoreDiscriminator) {
  return json;
}
function EventTopicToJSON(value) {
  return value;
}

// build/models/Activity.ts
function instanceOfActivity(value) {
  let isInstance = true;
  return isInstance;
}
function ActivityFromJSON(json) {
  return ActivityFromJSONTyped(json, false);
}
function ActivityFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "activity": !exists(json, "activity") ? void 0 : EventTopicFromJSON(json["activity"]),
    "resourceId": !exists(json, "resourceId") ? void 0 : json["resourceId"],
    "resourceType": !exists(json, "resourceType") ? void 0 : json["resourceType"],
    "resourceSlug": !exists(json, "resourceSlug") ? void 0 : json["resourceSlug"],
    "resourceName": !exists(json, "resourceName") ? void 0 : json["resourceName"],
    "actorId": !exists(json, "actorId") ? void 0 : json["actorId"],
    "actorType": !exists(json, "actorType") ? void 0 : json["actorType"],
    "actorName": !exists(json, "actorName") ? void 0 : json["actorName"],
    "context": !exists(json, "context") ? void 0 : ActivityContextFromJSON(json["context"]),
    "payload": !exists(json, "payload") ? void 0 : json["payload"],
    "activityTime": !exists(json, "activityTime") ? void 0 : new Date(json["activityTime"])
  };
}
function ActivityToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "activity": EventTopicToJSON(value.activity),
    "resourceId": value.resourceId,
    "resourceType": value.resourceType,
    "resourceSlug": value.resourceSlug,
    "resourceName": value.resourceName,
    "actorId": value.actorId,
    "actorType": value.actorType,
    "actorName": value.actorName,
    "context": ActivityContextToJSON(value.context),
    "payload": value.payload,
    "activityTime": value.activityTime === void 0 ? void 0 : value.activityTime.toISOString()
  };
}

// build/models/Pagination.ts
function instanceOfPagination(value) {
  let isInstance = true;
  isInstance = isInstance && "currentPage" in value;
  isInstance = isInstance && "pageCount" in value;
  isInstance = isInstance && "totalCount" in value;
  return isInstance;
}
function PaginationFromJSON(json) {
  return PaginationFromJSONTyped(json, false);
}
function PaginationFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "currentPage": json["currentPage"],
    "pageCount": json["pageCount"],
    "totalCount": json["totalCount"]
  };
}
function PaginationToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "currentPage": value.currentPage,
    "pageCount": value.pageCount,
    "totalCount": value.totalCount
  };
}

// build/models/ActivitiesResponse.ts
function instanceOfActivitiesResponse(value) {
  let isInstance = true;
  return isInstance;
}
function ActivitiesResponseFromJSON(json) {
  return ActivitiesResponseFromJSONTyped(json, false);
}
function ActivitiesResponseFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "data": !exists(json, "data") ? void 0 : json["data"].map(ActivityFromJSON),
    "pagination": !exists(json, "pagination") ? void 0 : PaginationFromJSON(json["pagination"])
  };
}
function ActivitiesResponseToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "data": value.data === void 0 ? void 0 : value.data.map(ActivityToJSON),
    "pagination": PaginationToJSON(value.pagination)
  };
}

// build/models/ResourceId.ts
function instanceOfResourceId(value) {
  let isInstance = true;
  return isInstance;
}
function ResourceIdFromJSON(json) {
  return ResourceIdFromJSONTyped(json, false);
}
function ResourceIdFromJSONTyped(json, ignoreDiscriminator) {
  return json;
}
function ResourceIdToJSON(value) {
  return value;
}

// build/models/ActorResource.ts
function instanceOfActorResource(value) {
  let isInstance = true;
  isInstance = isInstance && "actorId" in value;
  isInstance = isInstance && "resourceId" in value;
  return isInstance;
}
function ActorResourceFromJSON(json) {
  return ActorResourceFromJSONTyped(json, false);
}
function ActorResourceFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "actorId": ActorIdFromJSON(json["actorId"]),
    "resourceId": ResourceIdFromJSON(json["resourceId"])
  };
}
function ActorResourceToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "actorId": ActorIdToJSON(value.actorId),
    "resourceId": ResourceIdToJSON(value.resourceId)
  };
}

// build/models/ActorRoleResponse.ts
function instanceOfActorRoleResponse(value) {
  let isInstance = true;
  isInstance = isInstance && "id" in value;
  isInstance = isInstance && "roleId" in value;
  isInstance = isInstance && "actorId" in value;
  isInstance = isInstance && "resourceId" in value;
  isInstance = isInstance && "createdAt" in value;
  isInstance = isInstance && "updatedAt" in value;
  return isInstance;
}
function ActorRoleResponseFromJSON(json) {
  return ActorRoleResponseFromJSONTyped(json, false);
}
function ActorRoleResponseFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "id": json["id"],
    "roleId": json["roleId"],
    "actorId": ActorIdFromJSON(json["actorId"]),
    "resourceId": ResourceIdFromJSON(json["resourceId"]),
    "createdAt": new Date(json["createdAt"]),
    "updatedAt": new Date(json["updatedAt"])
  };
}
function ActorRoleResponseToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "id": value.id,
    "roleId": value.roleId,
    "actorId": ActorIdToJSON(value.actorId),
    "resourceId": ResourceIdToJSON(value.resourceId),
    "createdAt": value.createdAt.toISOString(),
    "updatedAt": value.updatedAt.toISOString()
  };
}

// build/models/Agent.ts
var AgentCompilerEnum = {
  Js: "js"
};
function instanceOfAgent(value) {
  let isInstance = true;
  isInstance = isInstance && "id" in value;
  return isInstance;
}
function AgentFromJSON(json) {
  return AgentFromJSONTyped(json, false);
}
function AgentFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "id": json["id"],
    "createdAt": !exists(json, "createdAt") ? void 0 : json["createdAt"],
    "deletedAt": !exists(json, "deletedAt") ? void 0 : json["deletedAt"],
    "updatedAt": !exists(json, "updatedAt") ? void 0 : json["updatedAt"],
    "accountId": !exists(json, "accountId") ? void 0 : json["accountId"],
    "environmentId": !exists(json, "environmentId") ? void 0 : json["environmentId"],
    "topics": !exists(json, "topics") ? void 0 : json["topics"].map(EventTopicFromJSON),
    "compiler": !exists(json, "compiler") ? void 0 : json["compiler"],
    "source": !exists(json, "source") ? void 0 : json["source"],
    "sourceMap": !exists(json, "sourceMap") ? void 0 : json["sourceMap"],
    "slug": !exists(json, "slug") ? void 0 : json["slug"]
  };
}
function AgentToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "id": value.id,
    "createdAt": value.createdAt,
    "deletedAt": value.deletedAt,
    "updatedAt": value.updatedAt,
    "accountId": value.accountId,
    "environmentId": value.environmentId,
    "topics": value.topics === void 0 ? void 0 : value.topics.map(EventTopicToJSON),
    "compiler": value.compiler,
    "source": value.source,
    "sourceMap": value.sourceMap,
    "slug": value.slug
  };
}

// build/models/AgentConfig.ts
var AgentConfigCompilerEnum = {
  Js: "js"
};
function instanceOfAgentConfig(value) {
  let isInstance = true;
  return isInstance;
}
function AgentConfigFromJSON(json) {
  return AgentConfigFromJSONTyped(json, false);
}
function AgentConfigFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "topics": !exists(json, "topics") ? void 0 : json["topics"].map(EventTopicFromJSON),
    "compiler": !exists(json, "compiler") ? void 0 : json["compiler"],
    "source": !exists(json, "source") ? void 0 : json["source"],
    "sourceMap": !exists(json, "sourceMap") ? void 0 : json["sourceMap"],
    "slug": !exists(json, "slug") ? void 0 : json["slug"]
  };
}
function AgentConfigToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "topics": value.topics === void 0 ? void 0 : value.topics.map(EventTopicToJSON),
    "compiler": value.compiler,
    "source": value.source,
    "sourceMap": value.sourceMap,
    "slug": value.slug
  };
}

// build/models/AgentLog.ts
function instanceOfAgentLog(value) {
  let isInstance = true;
  isInstance = isInstance && "eventId" in value;
  isInstance = isInstance && "success" in value;
  isInstance = isInstance && "createdAt" in value;
  isInstance = isInstance && "completedAt" in value;
  return isInstance;
}
function AgentLogFromJSON(json) {
  return AgentLogFromJSONTyped(json, false);
}
function AgentLogFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "eventId": json["eventId"],
    "success": json["success"],
    "createdAt": json["createdAt"],
    "completedAt": json["completedAt"],
    "log": !exists(json, "log") ? void 0 : json["log"]
  };
}
function AgentLogToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "eventId": value.eventId,
    "success": value.success,
    "createdAt": value.createdAt,
    "completedAt": value.completedAt,
    "log": value.log
  };
}

// build/models/DetailedAgentLog.ts
function instanceOfDetailedAgentLog(value) {
  let isInstance = true;
  isInstance = isInstance && "eventId" in value;
  isInstance = isInstance && "success" in value;
  isInstance = isInstance && "createdAt" in value;
  isInstance = isInstance && "completedAt" in value;
  return isInstance;
}
function DetailedAgentLogFromJSON(json) {
  return DetailedAgentLogFromJSONTyped(json, false);
}
function DetailedAgentLogFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "eventId": json["eventId"],
    "success": json["success"],
    "createdAt": json["createdAt"],
    "completedAt": json["completedAt"],
    "duration": !exists(json, "duration") ? void 0 : json["duration"],
    "topic": !exists(json, "topic") ? void 0 : json["topic"],
    "context": !exists(json, "context") ? void 0 : json["context"],
    "log": !exists(json, "log") ? void 0 : json["log"]
  };
}
function DetailedAgentLogToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "eventId": value.eventId,
    "success": value.success,
    "createdAt": value.createdAt,
    "completedAt": value.completedAt,
    "duration": value.duration,
    "topic": value.topic,
    "context": value.context,
    "log": value.log
  };
}

// build/models/AgentLogResponse.ts
function instanceOfAgentLogResponse(value) {
  let isInstance = true;
  return isInstance;
}
function AgentLogResponseFromJSON(json) {
  return AgentLogResponseFromJSONTyped(json, false);
}
function AgentLogResponseFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "data": !exists(json, "data") ? void 0 : DetailedAgentLogFromJSON(json["data"])
  };
}
function AgentLogResponseToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "data": DetailedAgentLogToJSON(value.data)
  };
}

// build/models/AgentLogsResponse.ts
function instanceOfAgentLogsResponse(value) {
  let isInstance = true;
  return isInstance;
}
function AgentLogsResponseFromJSON(json) {
  return AgentLogsResponseFromJSONTyped(json, false);
}
function AgentLogsResponseFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "pagination": !exists(json, "pagination") ? void 0 : PaginationFromJSON(json["pagination"]),
    "data": !exists(json, "data") ? void 0 : json["data"].map(AgentLogFromJSON)
  };
}
function AgentLogsResponseToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "pagination": PaginationToJSON(value.pagination),
    "data": value.data === void 0 ? void 0 : value.data.map(AgentLogToJSON)
  };
}

// build/models/AgentLogsResponse1.ts
function instanceOfAgentLogsResponse1(value) {
  let isInstance = true;
  return isInstance;
}
function AgentLogsResponse1FromJSON(json) {
  return AgentLogsResponse1FromJSONTyped(json, false);
}
function AgentLogsResponse1FromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "pagination": !exists(json, "pagination") ? void 0 : PaginationFromJSON(json["pagination"]),
    "data": !exists(json, "data") ? void 0 : json["data"].map(DetailedAgentLogFromJSON)
  };
}
function AgentLogsResponse1ToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "pagination": PaginationToJSON(value.pagination),
    "data": value.data === void 0 ? void 0 : value.data.map(DetailedAgentLogToJSON)
  };
}

// build/models/AgentResponse.ts
function instanceOfAgentResponse(value) {
  let isInstance = true;
  return isInstance;
}
function AgentResponseFromJSON(json) {
  return AgentResponseFromJSONTyped(json, false);
}
function AgentResponseFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "data": !exists(json, "data") ? void 0 : AgentFromJSON(json["data"])
  };
}
function AgentResponseToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "data": AgentToJSON(value.data)
  };
}

// build/models/AgentStats.ts
function instanceOfAgentStats(value) {
  let isInstance = true;
  return isInstance;
}
function AgentStatsFromJSON(json) {
  return AgentStatsFromJSONTyped(json, false);
}
function AgentStatsFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "start": !exists(json, "start") ? void 0 : new Date(json["start"]),
    "end": !exists(json, "end") ? void 0 : new Date(json["end"]),
    "executions": !exists(json, "executions") ? void 0 : json["executions"],
    "succeeded": !exists(json, "succeeded") ? void 0 : json["succeeded"],
    "failed": !exists(json, "failed") ? void 0 : json["failed"]
  };
}
function AgentStatsToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "start": value.start === void 0 ? void 0 : value.start.toISOString(),
    "end": value.end === void 0 ? void 0 : value.end.toISOString(),
    "executions": value.executions,
    "succeeded": value.succeeded,
    "failed": value.failed
  };
}

// build/models/AgentsResponse.ts
function instanceOfAgentsResponse(value) {
  let isInstance = true;
  return isInstance;
}
function AgentsResponseFromJSON(json) {
  return AgentsResponseFromJSONTyped(json, false);
}
function AgentsResponseFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "data": !exists(json, "data") ? void 0 : json["data"].map(AgentFromJSON)
  };
}
function AgentsResponseToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "data": value.data === void 0 ? void 0 : value.data.map(AgentToJSON)
  };
}

// build/models/ApiCredentials.ts
function instanceOfApiCredentials(value) {
  let isInstance = true;
  isInstance = isInstance && "clientId" in value;
  isInstance = isInstance && "secret" in value;
  return isInstance;
}
function ApiCredentialsFromJSON(json) {
  return ApiCredentialsFromJSONTyped(json, false);
}
function ApiCredentialsFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "clientId": json["clientId"],
    "secret": json["secret"]
  };
}
function ApiCredentialsToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "clientId": value.clientId,
    "secret": value.secret
  };
}

// build/models/ApiKeyOperationsInner.ts
function instanceOfApiKeyOperationsInner(value) {
  let isInstance = true;
  isInstance = isInstance && "path" in value;
  isInstance = isInstance && "method" in value;
  return isInstance;
}
function ApiKeyOperationsInnerFromJSON(json) {
  return ApiKeyOperationsInnerFromJSONTyped(json, false);
}
function ApiKeyOperationsInnerFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "path": json["path"],
    "method": json["method"]
  };
}
function ApiKeyOperationsInnerToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "path": value.path,
    "method": value.method
  };
}

// build/models/ApiKey.ts
var ApiKeyTypeEnum = {
  Publishable: "PUBLISHABLE",
  Secret: "SECRET"
};
function instanceOfApiKey(value) {
  let isInstance = true;
  isInstance = isInstance && "id" in value;
  isInstance = isInstance && "type" in value;
  isInstance = isInstance && "operations" in value;
  isInstance = isInstance && "createdAt" in value;
  return isInstance;
}
function ApiKeyFromJSON(json) {
  return ApiKeyFromJSONTyped(json, false);
}
function ApiKeyFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "id": json["id"],
    "rawKey": !exists(json, "rawKey") ? void 0 : json["rawKey"],
    "type": json["type"],
    "environmentId": !exists(json, "environmentId") ? void 0 : json["environmentId"],
    "accountId": !exists(json, "accountId") ? void 0 : json["accountId"],
    "operations": json["operations"].map(ApiKeyOperationsInnerFromJSON),
    "createdAt": new Date(json["createdAt"]),
    "updatedAt": !exists(json, "updatedAt") ? void 0 : new Date(json["updatedAt"]),
    "deletedAt": !exists(json, "deletedAt") ? void 0 : new Date(json["deletedAt"])
  };
}
function ApiKeyToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "id": value.id,
    "rawKey": value.rawKey,
    "type": value.type,
    "environmentId": value.environmentId,
    "accountId": value.accountId,
    "operations": value.operations.map(ApiKeyOperationsInnerToJSON),
    "createdAt": value.createdAt.toISOString(),
    "updatedAt": value.updatedAt === void 0 ? void 0 : value.updatedAt.toISOString(),
    "deletedAt": value.deletedAt === void 0 ? void 0 : value.deletedAt.toISOString()
  };
}

// build/models/ApiKeysResponse.ts
function instanceOfApiKeysResponse(value) {
  let isInstance = true;
  return isInstance;
}
function ApiKeysResponseFromJSON(json) {
  return ApiKeysResponseFromJSONTyped(json, false);
}
function ApiKeysResponseFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "data": !exists(json, "data") ? void 0 : json["data"].map(ApiKeyFromJSON)
  };
}
function ApiKeysResponseToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "data": value.data === void 0 ? void 0 : value.data.map(ApiKeyToJSON)
  };
}

// build/models/ApiToken.ts
function instanceOfApiToken(value) {
  let isInstance = true;
  isInstance = isInstance && "clientId" in value;
  isInstance = isInstance && "description" in value;
  isInstance = isInstance && "metadata" in value;
  isInstance = isInstance && "createdAt" in value;
  return isInstance;
}
function ApiTokenFromJSON(json) {
  return ApiTokenFromJSONTyped(json, false);
}
function ApiTokenFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "clientId": json["clientId"],
    "description": json["description"],
    "metadata": json["metadata"],
    "createdAt": json["createdAt"],
    "secret": !exists(json, "secret") ? void 0 : json["secret"]
  };
}
function ApiTokenToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "clientId": value.clientId,
    "description": value.description,
    "metadata": value.metadata,
    "createdAt": value.createdAt,
    "secret": value.secret
  };
}

// build/models/ApiTokenResponse.ts
function instanceOfApiTokenResponse(value) {
  let isInstance = true;
  return isInstance;
}
function ApiTokenResponseFromJSON(json) {
  return ApiTokenResponseFromJSONTyped(json, false);
}
function ApiTokenResponseFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "data": !exists(json, "data") ? void 0 : ApiTokenFromJSON(json["data"])
  };
}
function ApiTokenResponseToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "data": ApiTokenToJSON(value.data)
  };
}

// build/models/ApiTokensResponse.ts
function instanceOfApiTokensResponse(value) {
  let isInstance = true;
  return isInstance;
}
function ApiTokensResponseFromJSON(json) {
  return ApiTokensResponseFromJSONTyped(json, false);
}
function ApiTokensResponseFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "pagination": !exists(json, "pagination") ? void 0 : PaginationFromJSON(json["pagination"]),
    "data": !exists(json, "data") ? void 0 : json["data"].map(ApiTokenFromJSON)
  };
}
function ApiTokensResponseToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "pagination": PaginationToJSON(value.pagination),
    "data": value.data === void 0 ? void 0 : value.data.map(ApiTokenToJSON)
  };
}

// build/models/AppVersionResponse.ts
function instanceOfAppVersionResponse(value) {
  let isInstance = true;
  return isInstance;
}
function AppVersionResponseFromJSON(json) {
  return AppVersionResponseFromJSONTyped(json, false);
}
function AppVersionResponseFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "commit": !exists(json, "commit") ? void 0 : json["commit"],
    "version": !exists(json, "version") ? void 0 : json["version"]
  };
}
function AppVersionResponseToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "commit": value.commit,
    "version": value.version
  };
}

// build/models/ArrayableProperty.ts
function instanceOfArrayableProperty(value) {
  let isInstance = true;
  return isInstance;
}
function ArrayablePropertyFromJSON(json) {
  return ArrayablePropertyFromJSONTyped(json, false);
}
function ArrayablePropertyFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "isArray": !exists(json, "isArray") ? void 0 : json["isArray"]
  };
}
function ArrayablePropertyToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "isArray": value.isArray
  };
}

// build/models/AssignActorRoleRequest.ts
function instanceOfAssignActorRoleRequest(value) {
  let isInstance = true;
  isInstance = isInstance && "roleId" in value;
  isInstance = isInstance && "resourceId" in value;
  return isInstance;
}
function AssignActorRoleRequestFromJSON(json) {
  return AssignActorRoleRequestFromJSONTyped(json, false);
}
function AssignActorRoleRequestFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "roleId": json["roleId"],
    "resourceId": ResourceIdFromJSON(json["resourceId"])
  };
}
function AssignActorRoleRequestToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "roleId": value.roleId,
    "resourceId": ResourceIdToJSON(value.resourceId)
  };
}

// build/models/AssignRoleResponse.ts
function instanceOfAssignRoleResponse(value) {
  let isInstance = true;
  isInstance = isInstance && "data" in value;
  return isInstance;
}
function AssignRoleResponseFromJSON(json) {
  return AssignRoleResponseFromJSONTyped(json, false);
}
function AssignRoleResponseFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "data": ActorRoleResponseFromJSON(json["data"])
  };
}
function AssignRoleResponseToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "data": ActorRoleResponseToJSON(value.data)
  };
}

// build/models/AuditActivity.ts
var AuditActivity = {
  Agentcreated: "agent:created",
  Agentupdated: "agent:updated",
  Agentdeleted: "agent:deleted",
  Spacecreated: "space:created",
  Spaceupdated: "space:updated",
  Spacedeleted: "space:deleted",
  Spacearchived: "space:archived",
  Spaceexpired: "space:expired",
  SpaceguestAdded: "space:guestAdded",
  SpaceguestRemoved: "space:guestRemoved",
  Documentcreated: "document:created",
  Documentupdated: "document:updated",
  Documentdeleted: "document:deleted",
  Workbookcreated: "workbook:created",
  Workbookupdated: "workbook:updated",
  Workbookdeleted: "workbook:deleted",
  Workbookexpired: "workbook:expired",
  Sheetcreated: "sheet:created",
  Sheetupdated: "sheet:updated",
  Sheetdeleted: "sheet:deleted",
  Snapshotcreated: "snapshot:created",
  Filecreated: "file:created",
  Filedeleted: "file:deleted",
  Fileexpired: "file:expired",
  Jobcreated: "job:created",
  Jobdeleted: "job:deleted",
  Jobready: "job:ready",
  Jobscheduled: "job:scheduled",
  Jobcompleted: "job:completed",
  Jobfailed: "job:failed",
  JoboutcomeAcknowledged: "job:outcome-acknowledged",
  JobpartsCompleted: "job:parts-completed",
  Programcreated: "program:created",
  Programupdated: "program:updated",
  Commitcreated: "commit:created",
  Commitupdated: "commit:updated",
  Commitcompleted: "commit:completed",
  Layercreated: "layer:created",
  Secretcreated: "secret:created",
  Secretupdated: "secret:updated",
  Secretdeleted: "secret:deleted"
};
function AuditActivityFromJSON(json) {
  return AuditActivityFromJSONTyped(json, false);
}
function AuditActivityFromJSONTyped(json, ignoreDiscriminator) {
  return json;
}
function AuditActivityToJSON(value) {
  return value;
}

// build/models/BaseActionConstraint.ts
var BaseActionConstraintTypeEnum = {
  HasAllValid: "hasAllValid",
  HasSelection: "hasSelection",
  HasData: "hasData"
};
function instanceOfBaseActionConstraint(value) {
  let isInstance = true;
  isInstance = isInstance && "type" in value;
  return isInstance;
}
function BaseActionConstraintFromJSON(json) {
  return BaseActionConstraintFromJSONTyped(json, false);
}
function BaseActionConstraintFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "type": json["type"]
  };
}
function BaseActionConstraintToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "type": value.type
  };
}

// build/models/BaseConstraint.ts
var BaseConstraintTypeEnum = {
  Required: "required",
  Unique: "unique",
  Computed: "computed",
  External: "external"
};
function instanceOfBaseConstraint(value) {
  let isInstance = true;
  isInstance = isInstance && "type" in value;
  return isInstance;
}
function BaseConstraintFromJSON(json) {
  return BaseConstraintFromJSONTyped(json, false);
}
function BaseConstraintFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "type": json["type"]
  };
}
function BaseConstraintToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "type": value.type
  };
}

// build/models/BasePropertyAppearance.ts
var BasePropertyAppearanceSizeEnum = {
  Xs: "xs",
  S: "s",
  M: "m",
  L: "l",
  Xl: "xl"
};
function instanceOfBasePropertyAppearance(value) {
  let isInstance = true;
  return isInstance;
}
function BasePropertyAppearanceFromJSON(json) {
  return BasePropertyAppearanceFromJSONTyped(json, false);
}
function BasePropertyAppearanceFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "size": !exists(json, "size") ? void 0 : json["size"]
  };
}
function BasePropertyAppearanceToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "size": value.size
  };
}

// build/models/ComputedConstraint.ts
var ComputedConstraintTypeEnum = {
  Computed: "computed"
};
function instanceOfComputedConstraint(value) {
  let isInstance = true;
  isInstance = isInstance && "type" in value;
  return isInstance;
}
function ComputedConstraintFromJSON(json) {
  return ComputedConstraintFromJSONTyped(json, false);
}
function ComputedConstraintFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "type": json["type"]
  };
}
function ComputedConstraintToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "type": value.type
  };
}

// build/models/ExternalConstraint.ts
var ExternalConstraintTypeEnum = {
  External: "external"
};
function instanceOfExternalConstraint(value) {
  let isInstance = true;
  isInstance = isInstance && "type" in value;
  isInstance = isInstance && "validator" in value;
  return isInstance;
}
function ExternalConstraintFromJSON(json) {
  return ExternalConstraintFromJSONTyped(json, false);
}
function ExternalConstraintFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "type": json["type"],
    "validator": json["validator"],
    "config": !exists(json, "config") ? void 0 : json["config"]
  };
}
function ExternalConstraintToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "type": value.type,
    "validator": value.validator,
    "config": value.config
  };
}

// build/models/UniqueConstraintAllOfConfig.ts
function instanceOfUniqueConstraintAllOfConfig(value) {
  let isInstance = true;
  return isInstance;
}
function UniqueConstraintAllOfConfigFromJSON(json) {
  return UniqueConstraintAllOfConfigFromJSONTyped(json, false);
}
function UniqueConstraintAllOfConfigFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "caseSensitive": !exists(json, "caseSensitive") ? void 0 : json["caseSensitive"],
    "ignoreEmpty": !exists(json, "ignoreEmpty") ? void 0 : json["ignoreEmpty"]
  };
}
function UniqueConstraintAllOfConfigToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "caseSensitive": value.caseSensitive,
    "ignoreEmpty": value.ignoreEmpty
  };
}

// build/models/UniqueConstraint.ts
var UniqueConstraintTypeEnum = {
  Unique: "unique"
};
function instanceOfUniqueConstraint(value) {
  let isInstance = true;
  isInstance = isInstance && "type" in value;
  return isInstance;
}
function UniqueConstraintFromJSON(json) {
  return UniqueConstraintFromJSONTyped(json, false);
}
function UniqueConstraintFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "type": json["type"],
    "config": !exists(json, "config") ? void 0 : UniqueConstraintAllOfConfigFromJSON(json["config"])
  };
}
function UniqueConstraintToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "type": value.type,
    "config": UniqueConstraintAllOfConfigToJSON(value.config)
  };
}

// build/models/Constraint.ts
function ConstraintFromJSON(json) {
  return ConstraintFromJSONTyped(json, false);
}
function ConstraintFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  switch (json["type"]) {
    case "computed":
      return { ...ComputedConstraintFromJSONTyped(json, true), type: "computed" };
    case "external":
      return { ...ExternalConstraintFromJSONTyped(json, true), type: "external" };
    case "required":
      return { ...RequiredConstraintFromJSONTyped(json, true), type: "required" };
    case "unique":
      return { ...UniqueConstraintFromJSONTyped(json, true), type: "unique" };
    default:
      throw new Error(`No variant of Constraint exists with 'type=${json["type"]}'`);
  }
}
function ConstraintToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  switch (value["type"]) {
    case "computed":
      return ComputedConstraintToJSON(value);
    case "external":
      return ExternalConstraintToJSON(value);
    case "required":
      return RequiredConstraintToJSON(value);
    case "unique":
      return UniqueConstraintToJSON(value);
    default:
      throw new Error(`No variant of Constraint exists with 'type=${value["type"]}'`);
  }
}

// build/models/BaseProperty.ts
var BasePropertyTypeEnum = {
  String: "string",
  StringList: "string-list",
  Number: "number",
  Boolean: "boolean",
  Date: "date",
  Enum: "enum",
  EnumList: "enum-list",
  Reference: "reference",
  ReferenceList: "reference-list"
};
function instanceOfBaseProperty(value) {
  let isInstance = true;
  isInstance = isInstance && "key" in value;
  isInstance = isInstance && "type" in value;
  return isInstance;
}
function BasePropertyFromJSON(json) {
  return BasePropertyFromJSONTyped(json, false);
}
function BasePropertyFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "key": json["key"],
    "type": json["type"],
    "label": !exists(json, "label") ? void 0 : json["label"],
    "description": !exists(json, "description") ? void 0 : json["description"],
    "constraints": !exists(json, "constraints") ? void 0 : json["constraints"].map(ConstraintFromJSON),
    "readonly": !exists(json, "readonly") ? void 0 : json["readonly"],
    "metadata": !exists(json, "metadata") ? void 0 : json["metadata"],
    "actions": !exists(json, "actions") ? void 0 : json["actions"].map(ActionFromJSON),
    "appearance": !exists(json, "appearance") ? void 0 : BasePropertyAppearanceFromJSON(json["appearance"]),
    "treatments": !exists(json, "treatments") ? void 0 : json["treatments"],
    "alternativeNames": !exists(json, "alternativeNames") ? void 0 : json["alternativeNames"]
  };
}
function BasePropertyToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "key": value.key,
    "type": value.type,
    "label": value.label,
    "description": value.description,
    "constraints": value.constraints === void 0 ? void 0 : value.constraints.map(ConstraintToJSON),
    "readonly": value.readonly,
    "metadata": value.metadata,
    "actions": value.actions === void 0 ? void 0 : value.actions.map(ActionToJSON),
    "appearance": BasePropertyAppearanceToJSON(value.appearance),
    "treatments": value.treatments,
    "alternativeNames": value.alternativeNames
  };
}

// build/models/BaseSideBarConfig.ts
var BaseSideBarConfigTypeEnum = {
  Workbook: "workbook",
  Document: "document",
  Link: "link"
};
function instanceOfBaseSideBarConfig(value) {
  let isInstance = true;
  return isInstance;
}
function BaseSideBarConfigFromJSON(json) {
  return BaseSideBarConfigFromJSONTyped(json, false);
}
function BaseSideBarConfigFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "type": !exists(json, "type") ? void 0 : json["type"]
  };
}
function BaseSideBarConfigToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "type": value.type
  };
}

// build/models/BillingLimits.ts
function instanceOfBillingLimits(value) {
  let isInstance = true;
  isInstance = isInstance && "fromDate" in value;
  isInstance = isInstance && "toDate" in value;
  isInstance = isInstance && "fileUploadIsLockedUntilPayment" in value;
  isInstance = isInstance && "sheetIsLockedUntilPayment" in value;
  return isInstance;
}
function BillingLimitsFromJSON(json) {
  return BillingLimitsFromJSONTyped(json, false);
}
function BillingLimitsFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "fromDate": new Date(json["fromDate"]),
    "toDate": new Date(json["toDate"]),
    "fileUploadIsLockedUntilPayment": json["fileUploadIsLockedUntilPayment"],
    "sheetIsLockedUntilPayment": json["sheetIsLockedUntilPayment"]
  };
}
function BillingLimitsToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "fromDate": value.fromDate.toISOString(),
    "toDate": value.toDate.toISOString(),
    "fileUploadIsLockedUntilPayment": value.fileUploadIsLockedUntilPayment,
    "sheetIsLockedUntilPayment": value.sheetIsLockedUntilPayment
  };
}

// build/models/BillingLimitsResponse.ts
function instanceOfBillingLimitsResponse(value) {
  let isInstance = true;
  return isInstance;
}
function BillingLimitsResponseFromJSON(json) {
  return BillingLimitsResponseFromJSONTyped(json, false);
}
function BillingLimitsResponseFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "data": !exists(json, "data") ? void 0 : BillingLimitsFromJSON(json["data"])
  };
}
function BillingLimitsResponseToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "data": BillingLimitsToJSON(value.data)
  };
}

// build/models/BillingPlanWithUsage.ts
function instanceOfBillingPlanWithUsage(value) {
  let isInstance = true;
  isInstance = isInstance && "accountId" in value;
  isInstance = isInstance && "plan" in value;
  isInstance = isInstance && "pdvs" in value;
  isInstance = isInstance && "files" in value;
  isInstance = isInstance && "fromDate" in value;
  isInstance = isInstance && "toDate" in value;
  isInstance = isInstance && "freeFileLimit" in value;
  isInstance = isInstance && "freePdvLimit" in value;
  return isInstance;
}
function BillingPlanWithUsageFromJSON(json) {
  return BillingPlanWithUsageFromJSONTyped(json, false);
}
function BillingPlanWithUsageFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "accountId": json["accountId"],
    "plan": json["plan"],
    "pdvs": json["pdvs"],
    "files": json["files"],
    "fromDate": new Date(json["fromDate"]),
    "toDate": new Date(json["toDate"]),
    "freeFileLimit": json["freeFileLimit"],
    "freePdvLimit": json["freePdvLimit"]
  };
}
function BillingPlanWithUsageToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "accountId": value.accountId,
    "plan": value.plan,
    "pdvs": value.pdvs,
    "files": value.files,
    "fromDate": value.fromDate.toISOString(),
    "toDate": value.toDate.toISOString(),
    "freeFileLimit": value.freeFileLimit,
    "freePdvLimit": value.freePdvLimit
  };
}

// build/models/BillingPlanWithUsageResponse.ts
function instanceOfBillingPlanWithUsageResponse(value) {
  let isInstance = true;
  return isInstance;
}
function BillingPlanWithUsageResponseFromJSON(json) {
  return BillingPlanWithUsageResponseFromJSONTyped(json, false);
}
function BillingPlanWithUsageResponseFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "data": !exists(json, "data") ? void 0 : BillingPlanWithUsageFromJSON(json["data"])
  };
}
function BillingPlanWithUsageResponseToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "data": BillingPlanWithUsageToJSON(value.data)
  };
}

// build/models/StripeMetadata.ts
function instanceOfStripeMetadata(value) {
  let isInstance = true;
  return isInstance;
}
function StripeMetadataFromJSON(json) {
  return StripeMetadataFromJSONTyped(json, false);
}
function StripeMetadataFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "canSelfServe": !exists(json, "canSelfServe") ? void 0 : json["canSelfServe"],
    "commitment": !exists(json, "commitment") ? void 0 : json["commitment"],
    "isDefaultSelection": !exists(json, "isDefaultSelection") ? void 0 : json["isDefaultSelection"],
    "interval": !exists(json, "interval") ? void 0 : json["interval"],
    "planType": !exists(json, "planType") ? void 0 : json["planType"],
    "platform": !exists(json, "platform") ? void 0 : json["platform"],
    "prepayId": !exists(json, "prepayId") ? void 0 : json["prepayId"],
    "proOnly": !exists(json, "proOnly") ? void 0 : json["proOnly"],
    "productType": !exists(json, "productType") ? void 0 : json["productType"],
    "unitLabel": !exists(json, "unitLabel") ? void 0 : json["unitLabel"],
    "usageType": !exists(json, "usageType") ? void 0 : json["usageType"]
  };
}
function StripeMetadataToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "canSelfServe": value.canSelfServe,
    "commitment": value.commitment,
    "isDefaultSelection": value.isDefaultSelection,
    "interval": value.interval,
    "planType": value.planType,
    "platform": value.platform,
    "prepayId": value.prepayId,
    "proOnly": value.proOnly,
    "productType": value.productType,
    "unitLabel": value.unitLabel,
    "usageType": value.usageType
  };
}

// build/models/StripePriceTier.ts
function instanceOfStripePriceTier(value) {
  let isInstance = true;
  isInstance = isInstance && "unitAmount" in value;
  isInstance = isInstance && "upTo" in value;
  return isInstance;
}
function StripePriceTierFromJSON(json) {
  return StripePriceTierFromJSONTyped(json, false);
}
function StripePriceTierFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "unitAmount": json["unitAmount"],
    "upTo": json["upTo"]
  };
}
function StripePriceTierToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "unitAmount": value.unitAmount,
    "upTo": value.upTo
  };
}

// build/models/StripePrice.ts
function instanceOfStripePrice(value) {
  let isInstance = true;
  isInstance = isInstance && "id" in value;
  return isInstance;
}
function StripePriceFromJSON(json) {
  return StripePriceFromJSONTyped(json, false);
}
function StripePriceFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "id": json["id"],
    "unitAmount": !exists(json, "unitAmount") ? void 0 : json["unitAmount"],
    "tiers": !exists(json, "tiers") ? void 0 : json["tiers"].map(StripePriceTierFromJSON)
  };
}
function StripePriceToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "id": value.id,
    "unitAmount": value.unitAmount,
    "tiers": value.tiers === void 0 ? void 0 : value.tiers.map(StripePriceTierToJSON)
  };
}

// build/models/Product.ts
function instanceOfProduct(value) {
  let isInstance = true;
  isInstance = isInstance && "id" in value;
  isInstance = isInstance && "defaultPrice" in value;
  isInstance = isInstance && "description" in value;
  isInstance = isInstance && "metadata" in value;
  isInstance = isInstance && "name" in value;
  return isInstance;
}
function ProductFromJSON(json) {
  return ProductFromJSONTyped(json, false);
}
function ProductFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "id": json["id"],
    "defaultPrice": StripePriceFromJSON(json["defaultPrice"]),
    "description": json["description"],
    "metadata": StripeMetadataFromJSON(json["metadata"]),
    "name": json["name"]
  };
}
function ProductToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "id": value.id,
    "defaultPrice": StripePriceToJSON(value.defaultPrice),
    "description": value.description,
    "metadata": StripeMetadataToJSON(value.metadata),
    "name": value.name
  };
}

// build/models/BillingSubscriptionItem.ts
function instanceOfBillingSubscriptionItem(value) {
  let isInstance = true;
  isInstance = isInstance && "price" in value;
  isInstance = isInstance && "product" in value;
  isInstance = isInstance && "quantity" in value;
  return isInstance;
}
function BillingSubscriptionItemFromJSON(json) {
  return BillingSubscriptionItemFromJSONTyped(json, false);
}
function BillingSubscriptionItemFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    ...json,
    "price": StripePriceFromJSON(json["price"]),
    "product": ProductFromJSON(json["product"]),
    "quantity": json["quantity"]
  };
}
function BillingSubscriptionItemToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    ...value,
    "price": StripePriceToJSON(value.price),
    "product": ProductToJSON(value.product),
    "quantity": value.quantity
  };
}

// build/models/BillingSubscription.ts
function instanceOfBillingSubscription(value) {
  let isInstance = true;
  isInstance = isInstance && "id" in value;
  isInstance = isInstance && "stripeSubscriptionId" in value;
  isInstance = isInstance && "status" in value;
  isInstance = isInstance && "accountId" in value;
  isInstance = isInstance && "items" in value;
  return isInstance;
}
function BillingSubscriptionFromJSON(json) {
  return BillingSubscriptionFromJSONTyped(json, false);
}
function BillingSubscriptionFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    ...json,
    "id": json["id"],
    "stripeSubscriptionId": json["stripeSubscriptionId"],
    "status": json["status"],
    "accountId": json["accountId"],
    "items": json["items"].map(BillingSubscriptionItemFromJSON)
  };
}
function BillingSubscriptionToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    ...value,
    "id": value.id,
    "stripeSubscriptionId": value.stripeSubscriptionId,
    "status": value.status,
    "accountId": value.accountId,
    "items": value.items.map(BillingSubscriptionItemToJSON)
  };
}

// build/models/BillingSubscriptionResponse.ts
function instanceOfBillingSubscriptionResponse(value) {
  let isInstance = true;
  return isInstance;
}
function BillingSubscriptionResponseFromJSON(json) {
  return BillingSubscriptionResponseFromJSONTyped(json, false);
}
function BillingSubscriptionResponseFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "data": !exists(json, "data") ? void 0 : BillingSubscriptionFromJSON(json["data"])
  };
}
function BillingSubscriptionResponseToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "data": BillingSubscriptionToJSON(value.data)
  };
}

// build/models/BillingSubscriptionsResponse.ts
function instanceOfBillingSubscriptionsResponse(value) {
  let isInstance = true;
  return isInstance;
}
function BillingSubscriptionsResponseFromJSON(json) {
  return BillingSubscriptionsResponseFromJSONTyped(json, false);
}
function BillingSubscriptionsResponseFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "data": !exists(json, "data") ? void 0 : json["data"].map(BillingSubscriptionFromJSON)
  };
}
function BillingSubscriptionsResponseToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "data": value.data === void 0 ? void 0 : value.data.map(BillingSubscriptionToJSON)
  };
}

// build/models/BooleanPropertyAllOfConfig.ts
function instanceOfBooleanPropertyAllOfConfig(value) {
  let isInstance = true;
  return isInstance;
}
function BooleanPropertyAllOfConfigFromJSON(json) {
  return BooleanPropertyAllOfConfigFromJSONTyped(json, false);
}
function BooleanPropertyAllOfConfigFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "allowIndeterminate": !exists(json, "allowIndeterminate") ? void 0 : json["allowIndeterminate"]
  };
}
function BooleanPropertyAllOfConfigToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "allowIndeterminate": value.allowIndeterminate
  };
}

// build/models/BooleanProperty.ts
var BooleanPropertyTypeEnum = {
  Boolean: "boolean"
};
function instanceOfBooleanProperty(value) {
  let isInstance = true;
  isInstance = isInstance && "key" in value;
  isInstance = isInstance && "type" in value;
  return isInstance;
}
function BooleanPropertyFromJSON(json) {
  return BooleanPropertyFromJSONTyped(json, false);
}
function BooleanPropertyFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "key": json["key"],
    "type": json["type"],
    "label": !exists(json, "label") ? void 0 : json["label"],
    "description": !exists(json, "description") ? void 0 : json["description"],
    "constraints": !exists(json, "constraints") ? void 0 : json["constraints"].map(ConstraintFromJSON),
    "readonly": !exists(json, "readonly") ? void 0 : json["readonly"],
    "metadata": !exists(json, "metadata") ? void 0 : json["metadata"],
    "actions": !exists(json, "actions") ? void 0 : json["actions"].map(ActionFromJSON),
    "appearance": !exists(json, "appearance") ? void 0 : BasePropertyAppearanceFromJSON(json["appearance"]),
    "treatments": !exists(json, "treatments") ? void 0 : json["treatments"],
    "alternativeNames": !exists(json, "alternativeNames") ? void 0 : json["alternativeNames"],
    "config": !exists(json, "config") ? void 0 : BooleanPropertyAllOfConfigFromJSON(json["config"])
  };
}
function BooleanPropertyToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "key": value.key,
    "type": value.type,
    "label": value.label,
    "description": value.description,
    "constraints": value.constraints === void 0 ? void 0 : value.constraints.map(ConstraintToJSON),
    "readonly": value.readonly,
    "metadata": value.metadata,
    "actions": value.actions === void 0 ? void 0 : value.actions.map(ActionToJSON),
    "appearance": BasePropertyAppearanceToJSON(value.appearance),
    "treatments": value.treatments,
    "alternativeNames": value.alternativeNames,
    "config": BooleanPropertyAllOfConfigToJSON(value.config)
  };
}

// build/models/DateProperty.ts
var DatePropertyTypeEnum = {
  Date: "date"
};
function instanceOfDateProperty(value) {
  let isInstance = true;
  isInstance = isInstance && "key" in value;
  isInstance = isInstance && "type" in value;
  return isInstance;
}
function DatePropertyFromJSON(json) {
  return DatePropertyFromJSONTyped(json, false);
}
function DatePropertyFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "key": json["key"],
    "type": json["type"],
    "label": !exists(json, "label") ? void 0 : json["label"],
    "description": !exists(json, "description") ? void 0 : json["description"],
    "constraints": !exists(json, "constraints") ? void 0 : json["constraints"].map(ConstraintFromJSON),
    "readonly": !exists(json, "readonly") ? void 0 : json["readonly"],
    "metadata": !exists(json, "metadata") ? void 0 : json["metadata"],
    "actions": !exists(json, "actions") ? void 0 : json["actions"].map(ActionFromJSON),
    "appearance": !exists(json, "appearance") ? void 0 : BasePropertyAppearanceFromJSON(json["appearance"]),
    "treatments": !exists(json, "treatments") ? void 0 : json["treatments"],
    "alternativeNames": !exists(json, "alternativeNames") ? void 0 : json["alternativeNames"]
  };
}
function DatePropertyToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "key": value.key,
    "type": value.type,
    "label": value.label,
    "description": value.description,
    "constraints": value.constraints === void 0 ? void 0 : value.constraints.map(ConstraintToJSON),
    "readonly": value.readonly,
    "metadata": value.metadata,
    "actions": value.actions === void 0 ? void 0 : value.actions.map(ActionToJSON),
    "appearance": BasePropertyAppearanceToJSON(value.appearance),
    "treatments": value.treatments,
    "alternativeNames": value.alternativeNames
  };
}

// build/models/EnumPropertyAllOfConfig.ts
function instanceOfEnumPropertyAllOfConfig(value) {
  let isInstance = true;
  isInstance = isInstance && "options" in value;
  return isInstance;
}
function EnumPropertyAllOfConfigFromJSON(json) {
  return EnumPropertyAllOfConfigFromJSONTyped(json, false);
}
function EnumPropertyAllOfConfigFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "allowCustom": !exists(json, "allowCustom") ? void 0 : json["allowCustom"],
    "options": json["options"].map(EnumPropertyOptionFromJSON)
  };
}
function EnumPropertyAllOfConfigToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "allowCustom": value.allowCustom,
    "options": value.options.map(EnumPropertyOptionToJSON)
  };
}

// build/models/EnumListProperty.ts
var EnumListPropertyTypeEnum = {
  EnumList: "enum-list"
};
function instanceOfEnumListProperty(value) {
  let isInstance = true;
  isInstance = isInstance && "key" in value;
  isInstance = isInstance && "type" in value;
  isInstance = isInstance && "config" in value;
  return isInstance;
}
function EnumListPropertyFromJSON(json) {
  return EnumListPropertyFromJSONTyped(json, false);
}
function EnumListPropertyFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "key": json["key"],
    "type": json["type"],
    "label": !exists(json, "label") ? void 0 : json["label"],
    "description": !exists(json, "description") ? void 0 : json["description"],
    "constraints": !exists(json, "constraints") ? void 0 : json["constraints"].map(ConstraintFromJSON),
    "readonly": !exists(json, "readonly") ? void 0 : json["readonly"],
    "metadata": !exists(json, "metadata") ? void 0 : json["metadata"],
    "actions": !exists(json, "actions") ? void 0 : json["actions"].map(ActionFromJSON),
    "appearance": !exists(json, "appearance") ? void 0 : BasePropertyAppearanceFromJSON(json["appearance"]),
    "treatments": !exists(json, "treatments") ? void 0 : json["treatments"],
    "alternativeNames": !exists(json, "alternativeNames") ? void 0 : json["alternativeNames"],
    "config": EnumPropertyAllOfConfigFromJSON(json["config"])
  };
}
function EnumListPropertyToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "key": value.key,
    "type": value.type,
    "label": value.label,
    "description": value.description,
    "constraints": value.constraints === void 0 ? void 0 : value.constraints.map(ConstraintToJSON),
    "readonly": value.readonly,
    "metadata": value.metadata,
    "actions": value.actions === void 0 ? void 0 : value.actions.map(ActionToJSON),
    "appearance": BasePropertyAppearanceToJSON(value.appearance),
    "treatments": value.treatments,
    "alternativeNames": value.alternativeNames,
    "config": EnumPropertyAllOfConfigToJSON(value.config)
  };
}

// build/models/EnumProperty.ts
var EnumPropertyTypeEnum = {
  Enum: "enum"
};
function instanceOfEnumProperty(value) {
  let isInstance = true;
  isInstance = isInstance && "key" in value;
  isInstance = isInstance && "type" in value;
  isInstance = isInstance && "config" in value;
  return isInstance;
}
function EnumPropertyFromJSON(json) {
  return EnumPropertyFromJSONTyped(json, false);
}
function EnumPropertyFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "key": json["key"],
    "type": json["type"],
    "label": !exists(json, "label") ? void 0 : json["label"],
    "description": !exists(json, "description") ? void 0 : json["description"],
    "constraints": !exists(json, "constraints") ? void 0 : json["constraints"].map(ConstraintFromJSON),
    "readonly": !exists(json, "readonly") ? void 0 : json["readonly"],
    "metadata": !exists(json, "metadata") ? void 0 : json["metadata"],
    "actions": !exists(json, "actions") ? void 0 : json["actions"].map(ActionFromJSON),
    "appearance": !exists(json, "appearance") ? void 0 : BasePropertyAppearanceFromJSON(json["appearance"]),
    "treatments": !exists(json, "treatments") ? void 0 : json["treatments"],
    "alternativeNames": !exists(json, "alternativeNames") ? void 0 : json["alternativeNames"],
    "isArray": !exists(json, "isArray") ? void 0 : json["isArray"],
    "config": EnumPropertyAllOfConfigFromJSON(json["config"])
  };
}
function EnumPropertyToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "key": value.key,
    "type": value.type,
    "label": value.label,
    "description": value.description,
    "constraints": value.constraints === void 0 ? void 0 : value.constraints.map(ConstraintToJSON),
    "readonly": value.readonly,
    "metadata": value.metadata,
    "actions": value.actions === void 0 ? void 0 : value.actions.map(ActionToJSON),
    "appearance": BasePropertyAppearanceToJSON(value.appearance),
    "treatments": value.treatments,
    "alternativeNames": value.alternativeNames,
    "isArray": value.isArray,
    "config": EnumPropertyAllOfConfigToJSON(value.config)
  };
}

// build/models/NumberPropertyAllOfConfig.ts
function instanceOfNumberPropertyAllOfConfig(value) {
  let isInstance = true;
  return isInstance;
}
function NumberPropertyAllOfConfigFromJSON(json) {
  return NumberPropertyAllOfConfigFromJSONTyped(json, false);
}
function NumberPropertyAllOfConfigFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "decimalPlaces": !exists(json, "decimalPlaces") ? void 0 : json["decimalPlaces"]
  };
}
function NumberPropertyAllOfConfigToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "decimalPlaces": value.decimalPlaces
  };
}

// build/models/NumberProperty.ts
var NumberPropertyTypeEnum = {
  Number: "number"
};
function instanceOfNumberProperty(value) {
  let isInstance = true;
  isInstance = isInstance && "key" in value;
  isInstance = isInstance && "type" in value;
  return isInstance;
}
function NumberPropertyFromJSON(json) {
  return NumberPropertyFromJSONTyped(json, false);
}
function NumberPropertyFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "key": json["key"],
    "type": json["type"],
    "label": !exists(json, "label") ? void 0 : json["label"],
    "description": !exists(json, "description") ? void 0 : json["description"],
    "constraints": !exists(json, "constraints") ? void 0 : json["constraints"].map(ConstraintFromJSON),
    "readonly": !exists(json, "readonly") ? void 0 : json["readonly"],
    "metadata": !exists(json, "metadata") ? void 0 : json["metadata"],
    "actions": !exists(json, "actions") ? void 0 : json["actions"].map(ActionFromJSON),
    "appearance": !exists(json, "appearance") ? void 0 : BasePropertyAppearanceFromJSON(json["appearance"]),
    "treatments": !exists(json, "treatments") ? void 0 : json["treatments"],
    "alternativeNames": !exists(json, "alternativeNames") ? void 0 : json["alternativeNames"],
    "isArray": !exists(json, "isArray") ? void 0 : json["isArray"],
    "config": !exists(json, "config") ? void 0 : NumberPropertyAllOfConfigFromJSON(json["config"])
  };
}
function NumberPropertyToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "key": value.key,
    "type": value.type,
    "label": value.label,
    "description": value.description,
    "constraints": value.constraints === void 0 ? void 0 : value.constraints.map(ConstraintToJSON),
    "readonly": value.readonly,
    "metadata": value.metadata,
    "actions": value.actions === void 0 ? void 0 : value.actions.map(ActionToJSON),
    "appearance": BasePropertyAppearanceToJSON(value.appearance),
    "treatments": value.treatments,
    "alternativeNames": value.alternativeNames,
    "isArray": value.isArray,
    "config": NumberPropertyAllOfConfigToJSON(value.config)
  };
}

// build/models/ReferenceListPropertyAllOfConfig.ts
function instanceOfReferenceListPropertyAllOfConfig(value) {
  let isInstance = true;
  isInstance = isInstance && "ref" in value;
  isInstance = isInstance && "key" in value;
  return isInstance;
}
function ReferenceListPropertyAllOfConfigFromJSON(json) {
  return ReferenceListPropertyAllOfConfigFromJSONTyped(json, false);
}
function ReferenceListPropertyAllOfConfigFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "ref": json["ref"],
    "key": json["key"]
  };
}
function ReferenceListPropertyAllOfConfigToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "ref": value.ref,
    "key": value.key
  };
}

// build/models/ReferenceListProperty.ts
var ReferenceListPropertyTypeEnum = {
  ReferenceList: "reference-list"
};
function instanceOfReferenceListProperty(value) {
  let isInstance = true;
  isInstance = isInstance && "key" in value;
  isInstance = isInstance && "type" in value;
  isInstance = isInstance && "config" in value;
  return isInstance;
}
function ReferenceListPropertyFromJSON(json) {
  return ReferenceListPropertyFromJSONTyped(json, false);
}
function ReferenceListPropertyFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "key": json["key"],
    "type": json["type"],
    "label": !exists(json, "label") ? void 0 : json["label"],
    "description": !exists(json, "description") ? void 0 : json["description"],
    "constraints": !exists(json, "constraints") ? void 0 : json["constraints"].map(ConstraintFromJSON),
    "readonly": !exists(json, "readonly") ? void 0 : json["readonly"],
    "metadata": !exists(json, "metadata") ? void 0 : json["metadata"],
    "actions": !exists(json, "actions") ? void 0 : json["actions"].map(ActionFromJSON),
    "appearance": !exists(json, "appearance") ? void 0 : BasePropertyAppearanceFromJSON(json["appearance"]),
    "treatments": !exists(json, "treatments") ? void 0 : json["treatments"],
    "alternativeNames": !exists(json, "alternativeNames") ? void 0 : json["alternativeNames"],
    "isArray": !exists(json, "isArray") ? void 0 : json["isArray"],
    "config": ReferenceListPropertyAllOfConfigFromJSON(json["config"])
  };
}
function ReferenceListPropertyToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "key": value.key,
    "type": value.type,
    "label": value.label,
    "description": value.description,
    "constraints": value.constraints === void 0 ? void 0 : value.constraints.map(ConstraintToJSON),
    "readonly": value.readonly,
    "metadata": value.metadata,
    "actions": value.actions === void 0 ? void 0 : value.actions.map(ActionToJSON),
    "appearance": BasePropertyAppearanceToJSON(value.appearance),
    "treatments": value.treatments,
    "alternativeNames": value.alternativeNames,
    "isArray": value.isArray,
    "config": ReferenceListPropertyAllOfConfigToJSON(value.config)
  };
}

// build/models/ReferencePropertyAllOfConfig.ts
var ReferencePropertyAllOfConfigRelationshipEnum = {
  One: "has-one",
  Many: "has-many"
};
function instanceOfReferencePropertyAllOfConfig(value) {
  let isInstance = true;
  isInstance = isInstance && "ref" in value;
  isInstance = isInstance && "key" in value;
  return isInstance;
}
function ReferencePropertyAllOfConfigFromJSON(json) {
  return ReferencePropertyAllOfConfigFromJSONTyped(json, false);
}
function ReferencePropertyAllOfConfigFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "ref": json["ref"],
    "key": json["key"],
    "relationship": !exists(json, "relationship") ? void 0 : json["relationship"]
  };
}
function ReferencePropertyAllOfConfigToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "ref": value.ref,
    "key": value.key,
    "relationship": value.relationship
  };
}

// build/models/ReferenceProperty.ts
var ReferencePropertyTypeEnum = {
  Reference: "reference"
};
function instanceOfReferenceProperty(value) {
  let isInstance = true;
  isInstance = isInstance && "key" in value;
  isInstance = isInstance && "type" in value;
  isInstance = isInstance && "config" in value;
  return isInstance;
}
function ReferencePropertyFromJSON(json) {
  return ReferencePropertyFromJSONTyped(json, false);
}
function ReferencePropertyFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "key": json["key"],
    "type": json["type"],
    "label": !exists(json, "label") ? void 0 : json["label"],
    "description": !exists(json, "description") ? void 0 : json["description"],
    "constraints": !exists(json, "constraints") ? void 0 : json["constraints"].map(ConstraintFromJSON),
    "readonly": !exists(json, "readonly") ? void 0 : json["readonly"],
    "metadata": !exists(json, "metadata") ? void 0 : json["metadata"],
    "actions": !exists(json, "actions") ? void 0 : json["actions"].map(ActionFromJSON),
    "appearance": !exists(json, "appearance") ? void 0 : BasePropertyAppearanceFromJSON(json["appearance"]),
    "treatments": !exists(json, "treatments") ? void 0 : json["treatments"],
    "alternativeNames": !exists(json, "alternativeNames") ? void 0 : json["alternativeNames"],
    "isArray": !exists(json, "isArray") ? void 0 : json["isArray"],
    "config": ReferencePropertyAllOfConfigFromJSON(json["config"])
  };
}
function ReferencePropertyToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "key": value.key,
    "type": value.type,
    "label": value.label,
    "description": value.description,
    "constraints": value.constraints === void 0 ? void 0 : value.constraints.map(ConstraintToJSON),
    "readonly": value.readonly,
    "metadata": value.metadata,
    "actions": value.actions === void 0 ? void 0 : value.actions.map(ActionToJSON),
    "appearance": BasePropertyAppearanceToJSON(value.appearance),
    "treatments": value.treatments,
    "alternativeNames": value.alternativeNames,
    "isArray": value.isArray,
    "config": ReferencePropertyAllOfConfigToJSON(value.config)
  };
}

// build/models/StringListProperty.ts
var StringListPropertyTypeEnum = {
  StringList: "string-list"
};
function instanceOfStringListProperty(value) {
  let isInstance = true;
  isInstance = isInstance && "key" in value;
  isInstance = isInstance && "type" in value;
  return isInstance;
}
function StringListPropertyFromJSON(json) {
  return StringListPropertyFromJSONTyped(json, false);
}
function StringListPropertyFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "key": json["key"],
    "type": json["type"],
    "label": !exists(json, "label") ? void 0 : json["label"],
    "description": !exists(json, "description") ? void 0 : json["description"],
    "constraints": !exists(json, "constraints") ? void 0 : json["constraints"].map(ConstraintFromJSON),
    "readonly": !exists(json, "readonly") ? void 0 : json["readonly"],
    "metadata": !exists(json, "metadata") ? void 0 : json["metadata"],
    "actions": !exists(json, "actions") ? void 0 : json["actions"].map(ActionFromJSON),
    "appearance": !exists(json, "appearance") ? void 0 : BasePropertyAppearanceFromJSON(json["appearance"]),
    "treatments": !exists(json, "treatments") ? void 0 : json["treatments"],
    "alternativeNames": !exists(json, "alternativeNames") ? void 0 : json["alternativeNames"]
  };
}
function StringListPropertyToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "key": value.key,
    "type": value.type,
    "label": value.label,
    "description": value.description,
    "constraints": value.constraints === void 0 ? void 0 : value.constraints.map(ConstraintToJSON),
    "readonly": value.readonly,
    "metadata": value.metadata,
    "actions": value.actions === void 0 ? void 0 : value.actions.map(ActionToJSON),
    "appearance": BasePropertyAppearanceToJSON(value.appearance),
    "treatments": value.treatments,
    "alternativeNames": value.alternativeNames
  };
}

// build/models/StringProperty.ts
var StringPropertyTypeEnum = {
  String: "string"
};
function instanceOfStringProperty(value) {
  let isInstance = true;
  isInstance = isInstance && "key" in value;
  isInstance = isInstance && "type" in value;
  return isInstance;
}
function StringPropertyFromJSON(json) {
  return StringPropertyFromJSONTyped(json, false);
}
function StringPropertyFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "key": json["key"],
    "type": json["type"],
    "label": !exists(json, "label") ? void 0 : json["label"],
    "description": !exists(json, "description") ? void 0 : json["description"],
    "constraints": !exists(json, "constraints") ? void 0 : json["constraints"].map(ConstraintFromJSON),
    "readonly": !exists(json, "readonly") ? void 0 : json["readonly"],
    "metadata": !exists(json, "metadata") ? void 0 : json["metadata"],
    "actions": !exists(json, "actions") ? void 0 : json["actions"].map(ActionFromJSON),
    "appearance": !exists(json, "appearance") ? void 0 : BasePropertyAppearanceFromJSON(json["appearance"]),
    "treatments": !exists(json, "treatments") ? void 0 : json["treatments"],
    "alternativeNames": !exists(json, "alternativeNames") ? void 0 : json["alternativeNames"]
  };
}
function StringPropertyToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "key": value.key,
    "type": value.type,
    "label": value.label,
    "description": value.description,
    "constraints": value.constraints === void 0 ? void 0 : value.constraints.map(ConstraintToJSON),
    "readonly": value.readonly,
    "metadata": value.metadata,
    "actions": value.actions === void 0 ? void 0 : value.actions.map(ActionToJSON),
    "appearance": BasePropertyAppearanceToJSON(value.appearance),
    "treatments": value.treatments,
    "alternativeNames": value.alternativeNames
  };
}

// build/models/Property.ts
function PropertyFromJSON(json) {
  return PropertyFromJSONTyped(json, false);
}
function PropertyFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  switch (json["type"]) {
    case "boolean":
      return { ...BooleanPropertyFromJSONTyped(json, true), type: "boolean" };
    case "date":
      return { ...DatePropertyFromJSONTyped(json, true), type: "date" };
    case "enum":
      return { ...EnumPropertyFromJSONTyped(json, true), type: "enum" };
    case "enum-list":
      return { ...EnumListPropertyFromJSONTyped(json, true), type: "enum-list" };
    case "number":
      return { ...NumberPropertyFromJSONTyped(json, true), type: "number" };
    case "reference":
      return { ...ReferencePropertyFromJSONTyped(json, true), type: "reference" };
    case "reference-list":
      return { ...ReferenceListPropertyFromJSONTyped(json, true), type: "reference-list" };
    case "string":
      return { ...StringPropertyFromJSONTyped(json, true), type: "string" };
    case "string-list":
      return { ...StringListPropertyFromJSONTyped(json, true), type: "string-list" };
    default:
      throw new Error(`No variant of Property exists with 'type=${json["type"]}'`);
  }
}
function PropertyToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  switch (value["type"]) {
    case "boolean":
      return BooleanPropertyToJSON(value);
    case "date":
      return DatePropertyToJSON(value);
    case "enum":
      return EnumPropertyToJSON(value);
    case "enum-list":
      return EnumListPropertyToJSON(value);
    case "number":
      return NumberPropertyToJSON(value);
    case "reference":
      return ReferencePropertyToJSON(value);
    case "reference-list":
      return ReferenceListPropertyToJSON(value);
    case "string":
      return StringPropertyToJSON(value);
    case "string-list":
      return StringListPropertyToJSON(value);
    default:
      throw new Error(`No variant of Property exists with 'type=${value["type"]}'`);
  }
}

// build/models/SheetAccess.ts
var SheetAccess = {
  Star: "*",
  Add: "add",
  Edit: "edit",
  Delete: "delete",
  Import: "import"
};
function SheetAccessFromJSON(json) {
  return SheetAccessFromJSONTyped(json, false);
}
function SheetAccessFromJSONTyped(json, ignoreDiscriminator) {
  return json;
}
function SheetAccessToJSON(value) {
  return value;
}

// build/models/CompositeUniqueConstraint.ts
var CompositeUniqueConstraintTypeEnum = {
  Unique: "unique"
};
var CompositeUniqueConstraintStrategyEnum = {
  Hash: "hash",
  Concat: "concat"
};
function instanceOfCompositeUniqueConstraint(value) {
  let isInstance = true;
  isInstance = isInstance && "type" in value;
  isInstance = isInstance && "name" in value;
  isInstance = isInstance && "fields" in value;
  return isInstance;
}
function CompositeUniqueConstraintFromJSON(json) {
  return CompositeUniqueConstraintFromJSONTyped(json, false);
}
function CompositeUniqueConstraintFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "type": json["type"],
    "name": json["name"],
    "fields": json["fields"],
    "requiredFields": !exists(json, "requiredFields") ? void 0 : json["requiredFields"],
    "strategy": !exists(json, "strategy") ? void 0 : json["strategy"]
  };
}
function CompositeUniqueConstraintToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "type": value.type,
    "name": value.name,
    "fields": value.fields,
    "requiredFields": value.requiredFields,
    "strategy": value.strategy
  };
}

// build/models/ExternalSheetConstraint.ts
var ExternalSheetConstraintTypeEnum = {
  External: "external"
};
function instanceOfExternalSheetConstraint(value) {
  let isInstance = true;
  isInstance = isInstance && "type" in value;
  return isInstance;
}
function ExternalSheetConstraintFromJSON(json) {
  return ExternalSheetConstraintFromJSONTyped(json, false);
}
function ExternalSheetConstraintFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "type": json["type"],
    "fields": !exists(json, "fields") ? void 0 : json["fields"],
    "config": !exists(json, "config") ? void 0 : json["config"],
    "validator": !exists(json, "validator") ? void 0 : json["validator"]
  };
}
function ExternalSheetConstraintToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "type": value.type,
    "fields": value.fields,
    "config": value.config,
    "validator": value.validator
  };
}

// build/models/SheetConstraint.ts
function SheetConstraintFromJSON(json) {
  return SheetConstraintFromJSONTyped(json, false);
}
function SheetConstraintFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  switch (json["type"]) {
    case "external":
      return { ...ExternalSheetConstraintFromJSONTyped(json, true), type: "external" };
    case "unique":
      return { ...CompositeUniqueConstraintFromJSONTyped(json, true), type: "unique" };
    default:
      throw new Error(`No variant of SheetConstraint exists with 'type=${json["type"]}'`);
  }
}
function SheetConstraintToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  switch (value["type"]) {
    case "external":
      return ExternalSheetConstraintToJSON(value);
    case "unique":
      return CompositeUniqueConstraintToJSON(value);
    default:
      throw new Error(`No variant of SheetConstraint exists with 'type=${value["type"]}'`);
  }
}

// build/models/SheetConfig.ts
function instanceOfSheetConfig(value) {
  let isInstance = true;
  isInstance = isInstance && "name" in value;
  isInstance = isInstance && "fields" in value;
  return isInstance;
}
function SheetConfigFromJSON(json) {
  return SheetConfigFromJSONTyped(json, false);
}
function SheetConfigFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "name": json["name"],
    "description": !exists(json, "description") ? void 0 : json["description"],
    "slug": !exists(json, "slug") ? void 0 : json["slug"],
    "readonly": !exists(json, "readonly") ? void 0 : json["readonly"],
    "allowAdditionalFields": !exists(json, "allowAdditionalFields") ? void 0 : json["allowAdditionalFields"],
    "mappingConfidenceThreshold": !exists(json, "mappingConfidenceThreshold") ? void 0 : json["mappingConfidenceThreshold"],
    "access": !exists(json, "access") ? void 0 : json["access"].map(SheetAccessFromJSON),
    "fields": json["fields"].map(PropertyFromJSON),
    "actions": !exists(json, "actions") ? void 0 : json["actions"].map(ActionFromJSON),
    "metadata": !exists(json, "metadata") ? void 0 : json["metadata"],
    "constraints": !exists(json, "constraints") ? void 0 : json["constraints"].map(SheetConstraintFromJSON)
  };
}
function SheetConfigToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "name": value.name,
    "description": value.description,
    "slug": value.slug,
    "readonly": value.readonly,
    "allowAdditionalFields": value.allowAdditionalFields,
    "mappingConfidenceThreshold": value.mappingConfidenceThreshold,
    "access": value.access === void 0 ? void 0 : value.access.map(SheetAccessToJSON),
    "fields": value.fields.map(PropertyToJSON),
    "actions": value.actions === void 0 ? void 0 : value.actions.map(ActionToJSON),
    "metadata": value.metadata,
    "constraints": value.constraints === void 0 ? void 0 : value.constraints.map(SheetConstraintToJSON)
  };
}

// build/models/Blueprint.ts
function instanceOfBlueprint(value) {
  let isInstance = true;
  isInstance = isInstance && "slug" in value;
  isInstance = isInstance && "name" in value;
  isInstance = isInstance && "sheets" in value;
  return isInstance;
}
function BlueprintFromJSON(json) {
  return BlueprintFromJSONTyped(json, false);
}
function BlueprintFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "slug": json["slug"],
    "name": json["name"],
    "primary": !exists(json, "primary") ? void 0 : json["primary"],
    "sheets": json["sheets"].map(SheetConfigFromJSON)
  };
}
function BlueprintToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "slug": value.slug,
    "name": value.name,
    "primary": value.primary,
    "sheets": value.sheets.map(SheetConfigToJSON)
  };
}

// build/models/BooleanPropertyAllOf.ts
var BooleanPropertyAllOfTypeEnum = {
  Boolean: "boolean"
};
function instanceOfBooleanPropertyAllOf(value) {
  let isInstance = true;
  return isInstance;
}
function BooleanPropertyAllOfFromJSON(json) {
  return BooleanPropertyAllOfFromJSONTyped(json, false);
}
function BooleanPropertyAllOfFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "type": !exists(json, "type") ? void 0 : json["type"],
    "config": !exists(json, "config") ? void 0 : BooleanPropertyAllOfConfigFromJSON(json["config"])
  };
}
function BooleanPropertyAllOfToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "type": value.type,
    "config": BooleanPropertyAllOfConfigToJSON(value.config)
  };
}

// build/models/ValidationMessage.ts
var ValidationMessageTypeEnum = {
  Error: "error",
  Warn: "warn",
  Info: "info"
};
var ValidationMessageSourceEnum = {
  RequiredConstraint: "required-constraint",
  UniqueConstraint: "unique-constraint",
  CustomLogic: "custom-logic",
  Unlinked: "unlinked",
  InvalidOption: "invalid-option",
  IsArtifact: "is-artifact"
};
function instanceOfValidationMessage(value) {
  let isInstance = true;
  return isInstance;
}
function ValidationMessageFromJSON(json) {
  return ValidationMessageFromJSONTyped(json, false);
}
function ValidationMessageFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "field": !exists(json, "field") ? void 0 : json["field"],
    "type": !exists(json, "type") ? void 0 : json["type"],
    "source": !exists(json, "source") ? void 0 : json["source"],
    "path": !exists(json, "path") ? void 0 : json["path"],
    "message": !exists(json, "message") ? void 0 : json["message"]
  };
}
function ValidationMessageToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "field": value.field,
    "type": value.type,
    "source": value.source,
    "path": value.path,
    "message": value.message
  };
}

// build/models/Value.ts
function instanceOfValue(value) {
  let isInstance = true;
  return isInstance;
}
function ValueFromJSON(json) {
  return ValueFromJSONTyped(json, false);
}
function ValueFromJSONTyped(json, ignoreDiscriminator) {
  return json;
}
function ValueToJSON(value) {
  return value;
}

// build/models/CellValue.ts
function instanceOfCellValue(value) {
  let isInstance = true;
  isInstance = isInstance && "value" in value;
  return isInstance;
}
function CellValueFromJSON(json) {
  return CellValueFromJSONTyped(json, false);
}
function CellValueFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "value": ValueFromJSON(json["value"]),
    "valid": !exists(json, "valid") ? void 0 : json["valid"],
    "messages": !exists(json, "messages") ? void 0 : json["messages"].map(ValidationMessageFromJSON),
    "layer": !exists(json, "layer") ? void 0 : json["layer"],
    "updatedAt": !exists(json, "updatedAt") ? void 0 : json["updatedAt"] === null ? null : new Date(json["updatedAt"]),
    "metadata": !exists(json, "metadata") ? void 0 : json["metadata"]
  };
}
function CellValueToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "value": ValueToJSON(value.value),
    "valid": value.valid,
    "messages": value.messages === void 0 ? void 0 : value.messages.map(ValidationMessageToJSON),
    "layer": value.layer,
    "updatedAt": value.updatedAt === void 0 ? void 0 : value.updatedAt === null ? null : value.updatedAt.toISOString(),
    "metadata": value.metadata
  };
}

// build/models/FieldRecordCounts.ts
function instanceOfFieldRecordCounts(value) {
  let isInstance = true;
  return isInstance;
}
function FieldRecordCountsFromJSON(json) {
  return FieldRecordCountsFromJSONTyped(json, false);
}
function FieldRecordCountsFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "total": !exists(json, "total") ? void 0 : json["total"],
    "valid": !exists(json, "valid") ? void 0 : json["valid"],
    "error": !exists(json, "error") ? void 0 : json["error"],
    "empty": !exists(json, "empty") ? void 0 : json["empty"]
  };
}
function FieldRecordCountsToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "total": value.total,
    "valid": value.valid,
    "error": value.error,
    "empty": value.empty
  };
}

// build/models/RecordCounts.ts
function instanceOfRecordCounts(value) {
  let isInstance = true;
  isInstance = isInstance && "total" in value;
  isInstance = isInstance && "valid" in value;
  isInstance = isInstance && "error" in value;
  return isInstance;
}
function RecordCountsFromJSON(json) {
  return RecordCountsFromJSONTyped(json, false);
}
function RecordCountsFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "total": json["total"],
    "valid": json["valid"],
    "error": json["error"],
    "errorsByField": !exists(json, "errorsByField") ? void 0 : json["errorsByField"],
    "byField": !exists(json, "byField") ? void 0 : mapValues(json["byField"], FieldRecordCountsFromJSON)
  };
}
function RecordCountsToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "total": value.total,
    "valid": value.valid,
    "error": value.error,
    "errorsByField": value.errorsByField,
    "byField": value.byField === void 0 ? void 0 : mapValues(value.byField, FieldRecordCountsToJSON)
  };
}

// build/models/CellValueWithCounts.ts
function instanceOfCellValueWithCounts(value) {
  let isInstance = true;
  isInstance = isInstance && "value" in value;
  return isInstance;
}
function CellValueWithCountsFromJSON(json) {
  return CellValueWithCountsFromJSONTyped(json, false);
}
function CellValueWithCountsFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "value": ValueFromJSON(json["value"]),
    "valid": !exists(json, "valid") ? void 0 : json["valid"],
    "messages": !exists(json, "messages") ? void 0 : json["messages"].map(ValidationMessageFromJSON),
    "layer": !exists(json, "layer") ? void 0 : json["layer"],
    "updatedAt": !exists(json, "updatedAt") ? void 0 : json["updatedAt"] === null ? null : new Date(json["updatedAt"]),
    "metadata": !exists(json, "metadata") ? void 0 : json["metadata"],
    "counts": !exists(json, "counts") ? void 0 : RecordCountsFromJSON(json["counts"])
  };
}
function CellValueWithCountsToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "value": ValueToJSON(value.value),
    "valid": value.valid,
    "messages": value.messages === void 0 ? void 0 : value.messages.map(ValidationMessageToJSON),
    "layer": value.layer,
    "updatedAt": value.updatedAt === void 0 ? void 0 : value.updatedAt === null ? null : value.updatedAt.toISOString(),
    "metadata": value.metadata,
    "counts": RecordCountsToJSON(value.counts)
  };
}

// build/models/CellValueWithCountsAllOf.ts
function instanceOfCellValueWithCountsAllOf(value) {
  let isInstance = true;
  return isInstance;
}
function CellValueWithCountsAllOfFromJSON(json) {
  return CellValueWithCountsAllOfFromJSONTyped(json, false);
}
function CellValueWithCountsAllOfFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "counts": !exists(json, "counts") ? void 0 : RecordCountsFromJSON(json["counts"])
  };
}
function CellValueWithCountsAllOfToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "counts": RecordCountsToJSON(value.counts)
  };
}

// build/models/Records.ts
function instanceOfRecords(value) {
  let isInstance = true;
  return isInstance;
}
function RecordsFromJSON(json) {
  return RecordsFromJSONTyped(json, false);
}
function RecordsFromJSONTyped(json, ignoreDiscriminator) {
  return json;
}
function RecordsToJSON(value) {
  return value;
}

// build/models/CellValueWithLinks.ts
function instanceOfCellValueWithLinks(value) {
  let isInstance = true;
  isInstance = isInstance && "value" in value;
  return isInstance;
}
function CellValueWithLinksFromJSON(json) {
  return CellValueWithLinksFromJSONTyped(json, false);
}
function CellValueWithLinksFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "links": !exists(json, "links") ? void 0 : RecordsFromJSON(json["links"]),
    "value": ValueFromJSON(json["value"]),
    "valid": !exists(json, "valid") ? void 0 : json["valid"],
    "messages": !exists(json, "messages") ? void 0 : json["messages"].map(ValidationMessageFromJSON),
    "layer": !exists(json, "layer") ? void 0 : json["layer"],
    "updatedAt": !exists(json, "updatedAt") ? void 0 : json["updatedAt"] === null ? null : new Date(json["updatedAt"]),
    "metadata": !exists(json, "metadata") ? void 0 : json["metadata"]
  };
}
function CellValueWithLinksToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "links": RecordsToJSON(value.links),
    "value": ValueToJSON(value.value),
    "valid": value.valid,
    "messages": value.messages === void 0 ? void 0 : value.messages.map(ValidationMessageToJSON),
    "layer": value.layer,
    "updatedAt": value.updatedAt === void 0 ? void 0 : value.updatedAt === null ? null : value.updatedAt.toISOString(),
    "metadata": value.metadata
  };
}

// build/models/CellsResponseData.ts
function instanceOfCellsResponseData(value) {
  let isInstance = true;
  return isInstance;
}
function CellsResponseDataFromJSON(json) {
  return CellsResponseDataFromJSONTyped(json, false);
}
function CellsResponseDataFromJSONTyped(json, ignoreDiscriminator) {
  return json;
}
function CellsResponseDataToJSON(value) {
  return value;
}

// build/models/CellsResponse.ts
function instanceOfCellsResponse(value) {
  let isInstance = true;
  return isInstance;
}
function CellsResponseFromJSON(json) {
  return CellsResponseFromJSONTyped(json, false);
}
function CellsResponseFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "data": !exists(json, "data") ? void 0 : CellsResponseDataFromJSON(json["data"])
  };
}
function CellsResponseToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "data": CellsResponseDataToJSON(value.data)
  };
}

// build/models/CheckoutSession.ts
function instanceOfCheckoutSession(value) {
  let isInstance = true;
  return isInstance;
}
function CheckoutSessionFromJSON(json) {
  return CheckoutSessionFromJSONTyped(json, false);
}
function CheckoutSessionFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    ...json,
    "id": !exists(json, "id") ? void 0 : json["id"],
    "object": !exists(json, "object") ? void 0 : json["object"]
  };
}
function CheckoutSessionToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    ...value,
    "id": value.id,
    "object": value.object
  };
}

// build/models/LineItem.ts
function instanceOfLineItem(value) {
  let isInstance = true;
  return isInstance;
}
function LineItemFromJSON(json) {
  return LineItemFromJSONTyped(json, false);
}
function LineItemFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "price": !exists(json, "price") ? void 0 : json["price"],
    "quantity": !exists(json, "quantity") ? void 0 : json["quantity"]
  };
}
function LineItemToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "price": value.price,
    "quantity": value.quantity
  };
}

// build/models/CheckoutSessionBody.ts
function instanceOfCheckoutSessionBody(value) {
  let isInstance = true;
  isInstance = isInstance && "lineItems" in value;
  isInstance = isInstance && "successUrl" in value;
  return isInstance;
}
function CheckoutSessionBodyFromJSON(json) {
  return CheckoutSessionBodyFromJSONTyped(json, false);
}
function CheckoutSessionBodyFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "lineItems": json["lineItems"].map(LineItemFromJSON),
    "successUrl": json["successUrl"]
  };
}
function CheckoutSessionBodyToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "lineItems": value.lineItems.map(LineItemToJSON),
    "successUrl": value.successUrl
  };
}

// build/models/JobSubjectType.ts
var JobSubjectType = {
  Resource: "resource",
  Collection: "collection"
};
function JobSubjectTypeFromJSON(json) {
  return JobSubjectTypeFromJSONTyped(json, false);
}
function JobSubjectTypeFromJSONTyped(json, ignoreDiscriminator) {
  return json;
}
function JobSubjectTypeToJSON(value) {
  return value;
}

// build/models/CollectionJobSubject.ts
function instanceOfCollectionJobSubject(value) {
  let isInstance = true;
  isInstance = isInstance && "resource" in value;
  isInstance = isInstance && "type" in value;
  return isInstance;
}
function CollectionJobSubjectFromJSON(json) {
  return CollectionJobSubjectFromJSONTyped(json, false);
}
function CollectionJobSubjectFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "resource": json["resource"],
    "type": JobSubjectTypeFromJSON(json["type"]),
    "query": !exists(json, "query") ? void 0 : json["query"],
    "params": !exists(json, "params") ? void 0 : json["params"]
  };
}
function CollectionJobSubjectToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "resource": value.resource,
    "type": JobSubjectTypeToJSON(value.type),
    "query": value.query,
    "params": value.params
  };
}

// build/models/Commit.ts
function instanceOfCommit(value) {
  let isInstance = true;
  isInstance = isInstance && "id" in value;
  isInstance = isInstance && "sheetId" in value;
  isInstance = isInstance && "createdBy" in value;
  isInstance = isInstance && "createdAt" in value;
  return isInstance;
}
function CommitFromJSON(json) {
  return CommitFromJSONTyped(json, false);
}
function CommitFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "id": json["id"],
    "sheetId": json["sheetId"],
    "createdBy": json["createdBy"],
    "completedBy": !exists(json, "completedBy") ? void 0 : json["completedBy"],
    "createdAt": new Date(json["createdAt"]),
    "completedAt": !exists(json, "completedAt") ? void 0 : new Date(json["completedAt"])
  };
}
function CommitToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "id": value.id,
    "sheetId": value.sheetId,
    "createdBy": value.createdBy,
    "completedBy": value.completedBy,
    "createdAt": value.createdAt.toISOString(),
    "completedAt": value.completedAt === void 0 ? void 0 : value.completedAt.toISOString()
  };
}

// build/models/CompositeUniqueConstraintAllOf.ts
var CompositeUniqueConstraintAllOfTypeEnum = {
  Unique: "unique"
};
var CompositeUniqueConstraintAllOfStrategyEnum = {
  Hash: "hash",
  Concat: "concat"
};
function instanceOfCompositeUniqueConstraintAllOf(value) {
  let isInstance = true;
  isInstance = isInstance && "type" in value;
  isInstance = isInstance && "name" in value;
  isInstance = isInstance && "fields" in value;
  return isInstance;
}
function CompositeUniqueConstraintAllOfFromJSON(json) {
  return CompositeUniqueConstraintAllOfFromJSONTyped(json, false);
}
function CompositeUniqueConstraintAllOfFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "type": json["type"],
    "name": json["name"],
    "fields": json["fields"],
    "requiredFields": !exists(json, "requiredFields") ? void 0 : json["requiredFields"],
    "strategy": !exists(json, "strategy") ? void 0 : json["strategy"]
  };
}
function CompositeUniqueConstraintAllOfToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "type": value.type,
    "name": value.name,
    "fields": value.fields,
    "requiredFields": value.requiredFields,
    "strategy": value.strategy
  };
}

// build/models/ComputedConstraintAllOf.ts
var ComputedConstraintAllOfTypeEnum = {
  Computed: "computed"
};
function instanceOfComputedConstraintAllOf(value) {
  let isInstance = true;
  return isInstance;
}
function ComputedConstraintAllOfFromJSON(json) {
  return ComputedConstraintAllOfFromJSONTyped(json, false);
}
function ComputedConstraintAllOfFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "type": !exists(json, "type") ? void 0 : json["type"]
  };
}
function ComputedConstraintAllOfToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "type": value.type
  };
}

// build/models/ConnectionLink.ts
function instanceOfConnectionLink(value) {
  let isInstance = true;
  return isInstance;
}
function ConnectionLinkFromJSON(json) {
  return ConnectionLinkFromJSONTyped(json, false);
}
function ConnectionLinkFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    ...json,
    "token": !exists(json, "token") ? void 0 : json["token"]
  };
}
function ConnectionLinkToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    ...value,
    "token": value.token
  };
}

// build/models/ConnectionLinkBody.ts
function instanceOfConnectionLinkBody(value) {
  let isInstance = true;
  isInstance = isInstance && "endUserOriginId" in value;
  isInstance = isInstance && "endUserOrganizationName" in value;
  isInstance = isInstance && "endUserEmailAddress" in value;
  isInstance = isInstance && "categories" in value;
  return isInstance;
}
function ConnectionLinkBodyFromJSON(json) {
  return ConnectionLinkBodyFromJSONTyped(json, false);
}
function ConnectionLinkBodyFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "endUserOriginId": json["endUserOriginId"],
    "endUserOrganizationName": json["endUserOrganizationName"],
    "endUserEmailAddress": json["endUserEmailAddress"],
    "categories": json["categories"],
    "integration": !exists(json, "integration") ? void 0 : json["integration"],
    "linkExpiryMins": !exists(json, "linkExpiryMins") ? void 0 : json["linkExpiryMins"],
    "shouldCreateMagicLinkUrl": !exists(json, "shouldCreateMagicLinkUrl") ? void 0 : json["shouldCreateMagicLinkUrl"]
  };
}
function ConnectionLinkBodyToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "endUserOriginId": value.endUserOriginId,
    "endUserOrganizationName": value.endUserOrganizationName,
    "endUserEmailAddress": value.endUserEmailAddress,
    "categories": value.categories,
    "integration": value.integration,
    "linkExpiryMins": value.linkExpiryMins,
    "shouldCreateMagicLinkUrl": value.shouldCreateMagicLinkUrl
  };
}

// build/models/UserCredentials.ts
function instanceOfUserCredentials(value) {
  let isInstance = true;
  isInstance = isInstance && "email" in value;
  isInstance = isInstance && "password" in value;
  return isInstance;
}
function UserCredentialsFromJSON(json) {
  return UserCredentialsFromJSONTyped(json, false);
}
function UserCredentialsFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "email": json["email"],
    "password": json["password"]
  };
}
function UserCredentialsToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "email": value.email,
    "password": value.password
  };
}

// build/models/CreateAccessTokenRequest.ts
function CreateAccessTokenRequestFromJSON(json) {
  return CreateAccessTokenRequestFromJSONTyped(json, false);
}
function CreateAccessTokenRequestFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return { ...ApiCredentialsFromJSONTyped(json, true), ...UserCredentialsFromJSONTyped(json, true) };
}
function CreateAccessTokenRequestToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  if (instanceOfApiCredentials(value)) {
    return ApiCredentialsToJSON(value);
  }
  if (instanceOfUserCredentials(value)) {
    return UserCredentialsToJSON(value);
  }
  return {};
}

// build/models/CreateCheckoutSessionResponse.ts
function instanceOfCreateCheckoutSessionResponse(value) {
  let isInstance = true;
  return isInstance;
}
function CreateCheckoutSessionResponseFromJSON(json) {
  return CreateCheckoutSessionResponseFromJSONTyped(json, false);
}
function CreateCheckoutSessionResponseFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "data": !exists(json, "data") ? void 0 : CheckoutSessionFromJSON(json["data"])
  };
}
function CreateCheckoutSessionResponseToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "data": CheckoutSessionToJSON(value.data)
  };
}

// build/models/CreateCommitIdRequest.ts
function instanceOfCreateCommitIdRequest(value) {
  let isInstance = true;
  return isInstance;
}
function CreateCommitIdRequestFromJSON(json) {
  return CreateCommitIdRequestFromJSONTyped(json, false);
}
function CreateCommitIdRequestFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "sheetId": !exists(json, "sheetId") ? void 0 : json["sheetId"],
    "parentCommitId": !exists(json, "parentCommitId") ? void 0 : json["parentCommitId"]
  };
}
function CreateCommitIdRequestToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "sheetId": value.sheetId,
    "parentCommitId": value.parentCommitId
  };
}

// build/models/CreateConnectionLinkResponse.ts
function instanceOfCreateConnectionLinkResponse(value) {
  let isInstance = true;
  return isInstance;
}
function CreateConnectionLinkResponseFromJSON(json) {
  return CreateConnectionLinkResponseFromJSONTyped(json, false);
}
function CreateConnectionLinkResponseFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "data": !exists(json, "data") ? void 0 : ConnectionLinkFromJSON(json["data"])
  };
}
function CreateConnectionLinkResponseToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "data": ConnectionLinkToJSON(value.data)
  };
}

// build/models/GuestAuthenticationEnum.ts
var GuestAuthenticationEnum = {
  SharedLink: "shared_link",
  MagicLink: "magic_link"
};
function GuestAuthenticationEnumFromJSON(json) {
  return GuestAuthenticationEnumFromJSONTyped(json, false);
}
function GuestAuthenticationEnumFromJSONTyped(json, ignoreDiscriminator) {
  return json;
}
function GuestAuthenticationEnumToJSON(value) {
  return value;
}

// build/models/CreateEnvironmentRequest.ts
var CreateEnvironmentRequestDataRetentionPolicyEnum = {
  LastActivity: "lastActivity",
  SinceCreated: "sinceCreated"
};
function instanceOfCreateEnvironmentRequest(value) {
  let isInstance = true;
  isInstance = isInstance && "name" in value;
  isInstance = isInstance && "isProd" in value;
  return isInstance;
}
function CreateEnvironmentRequestFromJSON(json) {
  return CreateEnvironmentRequestFromJSONTyped(json, false);
}
function CreateEnvironmentRequestFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "name": json["name"],
    "isProd": json["isProd"],
    "guestAuthentication": !exists(json, "guestAuthentication") ? void 0 : json["guestAuthentication"].map(GuestAuthenticationEnumFromJSON),
    "metadata": !exists(json, "metadata") ? void 0 : json["metadata"],
    "translationsPath": !exists(json, "translationsPath") ? void 0 : json["translationsPath"],
    "namespaces": !exists(json, "namespaces") ? void 0 : json["namespaces"],
    "languageOverride": !exists(json, "languageOverride") ? void 0 : json["languageOverride"],
    "dataRetentionPolicy": !exists(json, "dataRetentionPolicy") ? void 0 : json["dataRetentionPolicy"]
  };
}
function CreateEnvironmentRequestToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "name": value.name,
    "isProd": value.isProd,
    "guestAuthentication": value.guestAuthentication === void 0 ? void 0 : value.guestAuthentication.map(GuestAuthenticationEnumToJSON),
    "metadata": value.metadata,
    "translationsPath": value.translationsPath,
    "namespaces": value.namespaces,
    "languageOverride": value.languageOverride,
    "dataRetentionPolicy": value.dataRetentionPolicy
  };
}

// build/models/CreateMappingRequest.ts
function instanceOfCreateMappingRequest(value) {
  let isInstance = true;
  isInstance = isInstance && "source" in value;
  isInstance = isInstance && "destination" in value;
  return isInstance;
}
function CreateMappingRequestFromJSON(json) {
  return CreateMappingRequestFromJSONTyped(json, false);
}
function CreateMappingRequestFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "source": SheetConfigFromJSON(json["source"]),
    "destination": SheetConfigFromJSON(json["destination"])
  };
}
function CreateMappingRequestToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "source": SheetConfigToJSON(value.source),
    "destination": SheetConfigToJSON(value.destination)
  };
}

// build/models/CreateVersionIdRequest.ts
function instanceOfCreateVersionIdRequest(value) {
  let isInstance = true;
  return isInstance;
}
function CreateVersionIdRequestFromJSON(json) {
  return CreateVersionIdRequestFromJSONTyped(json, false);
}
function CreateVersionIdRequestFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "sheetId": !exists(json, "sheetId") ? void 0 : json["sheetId"],
    "parentVersionId": !exists(json, "parentVersionId") ? void 0 : json["parentVersionId"]
  };
}
function CreateVersionIdRequestToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "sheetId": value.sheetId,
    "parentVersionId": value.parentVersionId
  };
}

// build/models/Filter.ts
var Filter = {
  Valid: "valid",
  Error: "error",
  All: "all",
  None: "none"
};
function FilterFromJSON(json) {
  return FilterFromJSONTyped(json, false);
}
function FilterFromJSONTyped(json, ignoreDiscriminator) {
  return json;
}
function FilterToJSON(value) {
  return value;
}

// build/models/SortDirection.ts
var SortDirection = {
  Asc: "asc",
  Desc: "desc"
};
function SortDirectionFromJSON(json) {
  return SortDirectionFromJSONTyped(json, false);
}
function SortDirectionFromJSONTyped(json, ignoreDiscriminator) {
  return json;
}
function SortDirectionToJSON(value) {
  return value;
}

// build/models/ViewConfig.ts
function instanceOfViewConfig(value) {
  let isInstance = true;
  return isInstance;
}
function ViewConfigFromJSON(json) {
  return ViewConfigFromJSONTyped(json, false);
}
function ViewConfigFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "versionId": !exists(json, "versionId") ? void 0 : json["versionId"],
    "commitId": !exists(json, "commitId") ? void 0 : json["commitId"],
    "sinceVersionId": !exists(json, "sinceVersionId") ? void 0 : json["sinceVersionId"],
    "sinceCommitId": !exists(json, "sinceCommitId") ? void 0 : json["sinceCommitId"],
    "sortField": !exists(json, "sortField") ? void 0 : json["sortField"],
    "sortDirection": !exists(json, "sortDirection") ? void 0 : SortDirectionFromJSON(json["sortDirection"]),
    "filter": !exists(json, "filter") ? void 0 : FilterFromJSON(json["filter"]),
    "filterField": !exists(json, "filterField") ? void 0 : json["filterField"],
    "searchValue": !exists(json, "searchValue") ? void 0 : json["searchValue"],
    "searchField": !exists(json, "searchField") ? void 0 : json["searchField"],
    "ids": !exists(json, "ids") ? void 0 : json["ids"],
    "pageSize": !exists(json, "pageSize") ? void 0 : json["pageSize"],
    "pageNumber": !exists(json, "pageNumber") ? void 0 : json["pageNumber"],
    "includeLinks": !exists(json, "includeLinks") ? void 0 : json["includeLinks"],
    "includeMessages": !exists(json, "includeMessages") ? void 0 : json["includeMessages"],
    "_for": !exists(json, "for") ? void 0 : json["for"],
    "q": !exists(json, "q") ? void 0 : json["q"],
    "includeLength": !exists(json, "includeLength") ? void 0 : json["includeLength"],
    "includeCounts": !exists(json, "includeCounts") ? void 0 : json["includeCounts"]
  };
}
function ViewConfigToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "versionId": value.versionId,
    "commitId": value.commitId,
    "sinceVersionId": value.sinceVersionId,
    "sinceCommitId": value.sinceCommitId,
    "sortField": value.sortField,
    "sortDirection": SortDirectionToJSON(value.sortDirection),
    "filter": FilterToJSON(value.filter),
    "filterField": value.filterField,
    "searchValue": value.searchValue,
    "searchField": value.searchField,
    "ids": value.ids,
    "pageSize": value.pageSize,
    "pageNumber": value.pageNumber,
    "includeLinks": value.includeLinks,
    "includeMessages": value.includeMessages,
    "for": value._for,
    "q": value.q,
    "includeLength": value.includeLength,
    "includeCounts": value.includeCounts
  };
}

// build/models/CreateViewRequest.ts
function instanceOfCreateViewRequest(value) {
  let isInstance = true;
  isInstance = isInstance && "sheetId" in value;
  isInstance = isInstance && "config" in value;
  isInstance = isInstance && "name" in value;
  return isInstance;
}
function CreateViewRequestFromJSON(json) {
  return CreateViewRequestFromJSONTyped(json, false);
}
function CreateViewRequestFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "sheetId": json["sheetId"],
    "config": ViewConfigFromJSON(json["config"]),
    "name": json["name"]
  };
}
function CreateViewRequestToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "sheetId": value.sheetId,
    "config": ViewConfigToJSON(value.config),
    "name": value.name
  };
}

// build/models/DBUser.ts
function instanceOfDBUser(value) {
  let isInstance = true;
  isInstance = isInstance && "username" in value;
  isInstance = isInstance && "password" in value;
  return isInstance;
}
function DBUserFromJSON(json) {
  return DBUserFromJSONTyped(json, false);
}
function DBUserFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "username": json["username"],
    "password": json["password"]
  };
}
function DBUserToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "username": value.username,
    "password": value.password
  };
}

// build/models/DataRetentionPolicyEnum.ts
var DataRetentionPolicyEnum = {
  LastActivity: "lastActivity",
  SinceCreated: "sinceCreated"
};
function DataRetentionPolicyEnumFromJSON(json) {
  return DataRetentionPolicyEnumFromJSONTyped(json, false);
}
function DataRetentionPolicyEnumFromJSONTyped(json, ignoreDiscriminator) {
  return json;
}
function DataRetentionPolicyEnumToJSON(value) {
  return value;
}

// build/models/DataRetentionPolicy.ts
function instanceOfDataRetentionPolicy(value) {
  let isInstance = true;
  isInstance = isInstance && "id" in value;
  isInstance = isInstance && "createdAt" in value;
  isInstance = isInstance && "updatedAt" in value;
  isInstance = isInstance && "type" in value;
  isInstance = isInstance && "period" in value;
  isInstance = isInstance && "environmentId" in value;
  return isInstance;
}
function DataRetentionPolicyFromJSON(json) {
  return DataRetentionPolicyFromJSONTyped(json, false);
}
function DataRetentionPolicyFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "id": json["id"],
    "createdAt": new Date(json["createdAt"]),
    "updatedAt": new Date(json["updatedAt"]),
    "type": DataRetentionPolicyEnumFromJSON(json["type"]),
    "period": json["period"],
    "environmentId": json["environmentId"]
  };
}
function DataRetentionPolicyToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "id": value.id,
    "createdAt": value.createdAt.toISOString(),
    "updatedAt": value.updatedAt.toISOString(),
    "type": DataRetentionPolicyEnumToJSON(value.type),
    "period": value.period,
    "environmentId": value.environmentId
  };
}

// build/models/DataRetentionPolicyConfig.ts
function instanceOfDataRetentionPolicyConfig(value) {
  let isInstance = true;
  isInstance = isInstance && "type" in value;
  isInstance = isInstance && "period" in value;
  isInstance = isInstance && "environmentId" in value;
  return isInstance;
}
function DataRetentionPolicyConfigFromJSON(json) {
  return DataRetentionPolicyConfigFromJSONTyped(json, false);
}
function DataRetentionPolicyConfigFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "type": DataRetentionPolicyEnumFromJSON(json["type"]),
    "period": json["period"],
    "environmentId": json["environmentId"]
  };
}
function DataRetentionPolicyConfigToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "type": DataRetentionPolicyEnumToJSON(value.type),
    "period": value.period,
    "environmentId": value.environmentId
  };
}

// build/models/DataRetentionPolicyConfigUpdate.ts
function instanceOfDataRetentionPolicyConfigUpdate(value) {
  let isInstance = true;
  return isInstance;
}
function DataRetentionPolicyConfigUpdateFromJSON(json) {
  return DataRetentionPolicyConfigUpdateFromJSONTyped(json, false);
}
function DataRetentionPolicyConfigUpdateFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "type": !exists(json, "type") ? void 0 : DataRetentionPolicyEnumFromJSON(json["type"]),
    "period": !exists(json, "period") ? void 0 : json["period"],
    "environmentId": !exists(json, "environmentId") ? void 0 : json["environmentId"]
  };
}
function DataRetentionPolicyConfigUpdateToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "type": DataRetentionPolicyEnumToJSON(value.type),
    "period": value.period,
    "environmentId": value.environmentId
  };
}

// build/models/DataRetentionPolicyResponse.ts
function instanceOfDataRetentionPolicyResponse(value) {
  let isInstance = true;
  return isInstance;
}
function DataRetentionPolicyResponseFromJSON(json) {
  return DataRetentionPolicyResponseFromJSONTyped(json, false);
}
function DataRetentionPolicyResponseFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "data": !exists(json, "data") ? void 0 : DataRetentionPolicyFromJSON(json["data"])
  };
}
function DataRetentionPolicyResponseToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "data": DataRetentionPolicyToJSON(value.data)
  };
}

// build/models/DatePropertyAllOf.ts
var DatePropertyAllOfTypeEnum = {
  Date: "date"
};
function instanceOfDatePropertyAllOf(value) {
  let isInstance = true;
  return isInstance;
}
function DatePropertyAllOfFromJSON(json) {
  return DatePropertyAllOfFromJSONTyped(json, false);
}
function DatePropertyAllOfFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "type": !exists(json, "type") ? void 0 : json["type"]
  };
}
function DatePropertyAllOfToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "type": value.type
  };
}

// build/models/DeleteRecordsJobConfig.ts
function instanceOfDeleteRecordsJobConfig(value) {
  let isInstance = true;
  isInstance = isInstance && "sheet" in value;
  return isInstance;
}
function DeleteRecordsJobConfigFromJSON(json) {
  return DeleteRecordsJobConfigFromJSONTyped(json, false);
}
function DeleteRecordsJobConfigFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "filter": !exists(json, "filter") ? void 0 : FilterFromJSON(json["filter"]),
    "filterField": !exists(json, "filterField") ? void 0 : json["filterField"],
    "searchValue": !exists(json, "searchValue") ? void 0 : json["searchValue"],
    "searchField": !exists(json, "searchField") ? void 0 : json["searchField"],
    "q": !exists(json, "q") ? void 0 : json["q"],
    "sheet": json["sheet"],
    "exceptions": !exists(json, "exceptions") ? void 0 : json["exceptions"]
  };
}
function DeleteRecordsJobConfigToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "filter": FilterToJSON(value.filter),
    "filterField": value.filterField,
    "searchValue": value.searchValue,
    "searchField": value.searchField,
    "q": value.q,
    "sheet": value.sheet,
    "exceptions": value.exceptions
  };
}

// build/models/DeleteRulesRequest.ts
function instanceOfDeleteRulesRequest(value) {
  let isInstance = true;
  return isInstance;
}
function DeleteRulesRequestFromJSON(json) {
  return DeleteRulesRequestFromJSONTyped(json, false);
}
function DeleteRulesRequestFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "ruleIds": !exists(json, "ruleIds") ? void 0 : json["ruleIds"]
  };
}
function DeleteRulesRequestToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "ruleIds": value.ruleIds
  };
}

// build/models/FieldConfig.ts
function FieldConfigFromJSON(json) {
  return FieldConfigFromJSONTyped(json, false);
}
function FieldConfigFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  switch (json["type"]) {
    case "boolean":
      return { ...BooleanPropertyFromJSONTyped(json, true), type: "boolean" };
    case "date":
      return { ...DatePropertyFromJSONTyped(json, true), type: "date" };
    case "enum":
      return { ...EnumPropertyFromJSONTyped(json, true), type: "enum" };
    case "enum-list":
      return { ...EnumListPropertyFromJSONTyped(json, true), type: "enum-list" };
    case "number":
      return { ...NumberPropertyFromJSONTyped(json, true), type: "number" };
    case "reference":
      return { ...ReferencePropertyFromJSONTyped(json, true), type: "reference" };
    case "reference-list":
      return { ...ReferenceListPropertyFromJSONTyped(json, true), type: "reference-list" };
    case "string":
      return { ...StringPropertyFromJSONTyped(json, true), type: "string" };
    case "string-list":
      return { ...StringListPropertyFromJSONTyped(json, true), type: "string-list" };
    default:
      throw new Error(`No variant of FieldConfig exists with 'type=${json["type"]}'`);
  }
}
function FieldConfigToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  switch (value["type"]) {
    case "boolean":
      return BooleanPropertyToJSON(value);
    case "date":
      return DatePropertyToJSON(value);
    case "enum":
      return EnumPropertyToJSON(value);
    case "enum-list":
      return EnumListPropertyToJSON(value);
    case "number":
      return NumberPropertyToJSON(value);
    case "reference":
      return ReferencePropertyToJSON(value);
    case "reference-list":
      return ReferenceListPropertyToJSON(value);
    case "string":
      return StringPropertyToJSON(value);
    case "string-list":
      return StringListPropertyToJSON(value);
    default:
      throw new Error(`No variant of FieldConfig exists with 'type=${value["type"]}'`);
  }
}

// build/models/DestinationField.ts
function instanceOfDestinationField(value) {
  let isInstance = true;
  isInstance = isInstance && "destinationField" in value;
  return isInstance;
}
function DestinationFieldFromJSON(json) {
  return DestinationFieldFromJSONTyped(json, false);
}
function DestinationFieldFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "destinationField": FieldConfigFromJSON(json["destinationField"]),
    "preview": !exists(json, "preview") ? void 0 : json["preview"]
  };
}
function DestinationFieldToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "destinationField": FieldConfigToJSON(value.destinationField),
    "preview": value.preview
  };
}

// build/models/DiffData.ts
function instanceOfDiffData(value) {
  let isInstance = true;
  return isInstance;
}
function DiffDataFromJSON(json) {
  return DiffDataFromJSONTyped(json, false);
}
function DiffDataFromJSONTyped(json, ignoreDiscriminator) {
  return json;
}
function DiffDataToJSON(value) {
  return value;
}

// build/models/DiffRecord.ts
function instanceOfDiffRecord(value) {
  let isInstance = true;
  isInstance = isInstance && "values" in value;
  isInstance = isInstance && "id" in value;
  return isInstance;
}
function DiffRecordFromJSON(json) {
  return DiffRecordFromJSONTyped(json, false);
}
function DiffRecordFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "values": DiffDataFromJSON(json["values"]),
    "id": json["id"],
    "versionId": !exists(json, "versionId") ? void 0 : json["versionId"],
    "commitId": !exists(json, "commitId") ? void 0 : json["commitId"],
    "valid": !exists(json, "valid") ? void 0 : json["valid"],
    "messages": !exists(json, "messages") ? void 0 : json["messages"].map(ValidationMessageFromJSON),
    "metadata": !exists(json, "metadata") ? void 0 : json["metadata"],
    "config": !exists(json, "config") ? void 0 : json["config"]
  };
}
function DiffRecordToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "values": DiffDataToJSON(value.values),
    "id": value.id,
    "versionId": value.versionId,
    "commitId": value.commitId,
    "valid": value.valid,
    "messages": value.messages === void 0 ? void 0 : value.messages.map(ValidationMessageToJSON),
    "metadata": value.metadata,
    "config": value.config
  };
}

// build/models/DiffRecordsResponseData.ts
function instanceOfDiffRecordsResponseData(value) {
  let isInstance = true;
  return isInstance;
}
function DiffRecordsResponseDataFromJSON(json) {
  return DiffRecordsResponseDataFromJSONTyped(json, false);
}
function DiffRecordsResponseDataFromJSONTyped(json, ignoreDiscriminator) {
  return json;
}
function DiffRecordsResponseDataToJSON(value) {
  return value;
}

// build/models/DiffRecordsResponse.ts
function instanceOfDiffRecordsResponse(value) {
  let isInstance = true;
  return isInstance;
}
function DiffRecordsResponseFromJSON(json) {
  return DiffRecordsResponseFromJSONTyped(json, false);
}
function DiffRecordsResponseFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "data": !exists(json, "data") ? void 0 : DiffRecordsResponseDataFromJSON(json["data"])
  };
}
function DiffRecordsResponseToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "data": DiffRecordsResponseDataToJSON(value.data)
  };
}

// build/models/DiffValue.ts
function instanceOfDiffValue(value) {
  let isInstance = true;
  isInstance = isInstance && "value" in value;
  return isInstance;
}
function DiffValueFromJSON(json) {
  return DiffValueFromJSONTyped(json, false);
}
function DiffValueFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "snapshotValue": !exists(json, "snapshotValue") ? void 0 : ValueFromJSON(json["snapshotValue"]),
    "value": ValueFromJSON(json["value"]),
    "valid": !exists(json, "valid") ? void 0 : json["valid"],
    "messages": !exists(json, "messages") ? void 0 : json["messages"].map(ValidationMessageFromJSON),
    "layer": !exists(json, "layer") ? void 0 : json["layer"],
    "updatedAt": !exists(json, "updatedAt") ? void 0 : json["updatedAt"] === null ? null : new Date(json["updatedAt"]),
    "metadata": !exists(json, "metadata") ? void 0 : json["metadata"]
  };
}
function DiffValueToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "snapshotValue": ValueToJSON(value.snapshotValue),
    "value": ValueToJSON(value.value),
    "valid": value.valid,
    "messages": value.messages === void 0 ? void 0 : value.messages.map(ValidationMessageToJSON),
    "layer": value.layer,
    "updatedAt": value.updatedAt === void 0 ? void 0 : value.updatedAt === null ? null : value.updatedAt.toISOString(),
    "metadata": value.metadata
  };
}

// build/models/Document.ts
function instanceOfDocument(value) {
  let isInstance = true;
  isInstance = isInstance && "id" in value;
  isInstance = isInstance && "createdAt" in value;
  isInstance = isInstance && "updatedAt" in value;
  isInstance = isInstance && "title" in value;
  isInstance = isInstance && "body" in value;
  return isInstance;
}
function DocumentFromJSON(json) {
  return DocumentFromJSONTyped(json, false);
}
function DocumentFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "id": json["id"],
    "spaceId": !exists(json, "spaceId") ? void 0 : json["spaceId"],
    "environmentId": !exists(json, "environmentId") ? void 0 : json["environmentId"],
    "createdAt": new Date(json["createdAt"]),
    "updatedAt": new Date(json["updatedAt"]),
    "title": json["title"],
    "body": json["body"],
    "actions": !exists(json, "actions") ? void 0 : json["actions"].map(ActionFromJSON),
    "treatments": !exists(json, "treatments") ? void 0 : json["treatments"]
  };
}
function DocumentToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "id": value.id,
    "spaceId": value.spaceId,
    "environmentId": value.environmentId,
    "createdAt": value.createdAt.toISOString(),
    "updatedAt": value.updatedAt.toISOString(),
    "title": value.title,
    "body": value.body,
    "actions": value.actions === void 0 ? void 0 : value.actions.map(ActionToJSON),
    "treatments": value.treatments
  };
}

// build/models/DocumentConfig.ts
function instanceOfDocumentConfig(value) {
  let isInstance = true;
  isInstance = isInstance && "title" in value;
  isInstance = isInstance && "body" in value;
  return isInstance;
}
function DocumentConfigFromJSON(json) {
  return DocumentConfigFromJSONTyped(json, false);
}
function DocumentConfigFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "title": json["title"],
    "body": json["body"],
    "actions": !exists(json, "actions") ? void 0 : json["actions"].map(ActionFromJSON),
    "treatments": !exists(json, "treatments") ? void 0 : json["treatments"]
  };
}
function DocumentConfigToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "title": value.title,
    "body": value.body,
    "actions": value.actions === void 0 ? void 0 : value.actions.map(ActionToJSON),
    "treatments": value.treatments
  };
}

// build/models/DocumentResponse.ts
function instanceOfDocumentResponse(value) {
  let isInstance = true;
  return isInstance;
}
function DocumentResponseFromJSON(json) {
  return DocumentResponseFromJSONTyped(json, false);
}
function DocumentResponseFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "data": !exists(json, "data") ? void 0 : DocumentFromJSON(json["data"])
  };
}
function DocumentResponseToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "data": DocumentToJSON(value.data)
  };
}

// build/models/DocumentSidebarConfig.ts
var DocumentSidebarConfigTypeEnum = {
  Workbook: "workbook",
  Document: "document",
  Link: "link"
};
function instanceOfDocumentSidebarConfig(value) {
  let isInstance = true;
  isInstance = isInstance && "documentId" in value;
  return isInstance;
}
function DocumentSidebarConfigFromJSON(json) {
  return DocumentSidebarConfigFromJSONTyped(json, false);
}
function DocumentSidebarConfigFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "type": !exists(json, "type") ? void 0 : json["type"],
    "documentId": json["documentId"]
  };
}
function DocumentSidebarConfigToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "type": value.type,
    "documentId": value.documentId
  };
}

// build/models/DocumentSidebarConfigAllOf.ts
function instanceOfDocumentSidebarConfigAllOf(value) {
  let isInstance = true;
  isInstance = isInstance && "documentId" in value;
  return isInstance;
}
function DocumentSidebarConfigAllOfFromJSON(json) {
  return DocumentSidebarConfigAllOfFromJSONTyped(json, false);
}
function DocumentSidebarConfigAllOfFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "documentId": json["documentId"]
  };
}
function DocumentSidebarConfigAllOfToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "documentId": value.documentId
  };
}

// build/models/DocumentsResponse.ts
function instanceOfDocumentsResponse(value) {
  let isInstance = true;
  return isInstance;
}
function DocumentsResponseFromJSON(json) {
  return DocumentsResponseFromJSONTyped(json, false);
}
function DocumentsResponseFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "data": !exists(json, "data") ? void 0 : json["data"].map(DocumentFromJSON)
  };
}
function DocumentsResponseToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "data": value.data === void 0 ? void 0 : value.data.map(DocumentToJSON)
  };
}

// build/models/MappingCertainty.ts
var MappingCertainty = {
  Absolute: "absolute",
  Strong: "strong",
  Moderate: "moderate",
  Weak: "weak"
};
function MappingCertaintyFromJSON(json) {
  return MappingCertaintyFromJSONTyped(json, false);
}
function MappingCertaintyFromJSONTyped(json, ignoreDiscriminator) {
  return json;
}
function MappingCertaintyToJSON(value) {
  return value;
}

// build/models/EdgeMetadata.ts
function instanceOfEdgeMetadata(value) {
  let isInstance = true;
  return isInstance;
}
function EdgeMetadataFromJSON(json) {
  return EdgeMetadataFromJSONTyped(json, false);
}
function EdgeMetadataFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "certainty": !exists(json, "certainty") ? void 0 : MappingCertaintyFromJSON(json["certainty"]),
    "confidence": !exists(json, "confidence") ? void 0 : json["confidence"],
    "source": !exists(json, "source") ? void 0 : json["source"]
  };
}
function EdgeMetadataToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "certainty": MappingCertaintyToJSON(value.certainty),
    "confidence": value.confidence,
    "source": value.source
  };
}

// build/models/EnumMappingField.ts
function instanceOfEnumMappingField(value) {
  let isInstance = true;
  return isInstance;
}
function EnumMappingFieldFromJSON(json) {
  return EnumMappingFieldFromJSONTyped(json, false);
}
function EnumMappingFieldFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "sourceValue": !exists(json, "sourceValue") ? void 0 : ValueFromJSON(json["sourceValue"]),
    "destinationValue": !exists(json, "destinationValue") ? void 0 : ValueFromJSON(json["destinationValue"])
  };
}
function EnumMappingFieldToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "sourceValue": ValueToJSON(value.sourceValue),
    "destinationValue": ValueToJSON(value.destinationValue)
  };
}

// build/models/EnumDetails.ts
function instanceOfEnumDetails(value) {
  let isInstance = true;
  isInstance = isInstance && "mapping" in value;
  isInstance = isInstance && "unusedSourceValues" in value;
  isInstance = isInstance && "unusedDestinationValues" in value;
  return isInstance;
}
function EnumDetailsFromJSON(json) {
  return EnumDetailsFromJSONTyped(json, false);
}
function EnumDetailsFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "mapping": json["mapping"].map(EnumMappingFieldFromJSON),
    "unusedSourceValues": json["unusedSourceValues"].map(ValueFromJSON),
    "unusedDestinationValues": json["unusedDestinationValues"].map(ValueFromJSON)
  };
}
function EnumDetailsToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "mapping": value.mapping.map(EnumMappingFieldToJSON),
    "unusedSourceValues": value.unusedSourceValues.map(ValueToJSON),
    "unusedDestinationValues": value.unusedDestinationValues.map(ValueToJSON)
  };
}

// build/models/Edge.ts
function instanceOfEdge(value) {
  let isInstance = true;
  isInstance = isInstance && "sourceField" in value;
  isInstance = isInstance && "destinationField" in value;
  return isInstance;
}
function EdgeFromJSON(json) {
  return EdgeFromJSONTyped(json, false);
}
function EdgeFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "sourceField": FieldConfigFromJSON(json["sourceField"]),
    "destinationField": FieldConfigFromJSON(json["destinationField"]),
    "preview": !exists(json, "preview") ? void 0 : json["preview"],
    "enumDetails": !exists(json, "enumDetails") ? void 0 : EnumDetailsFromJSON(json["enumDetails"]),
    "metadata": !exists(json, "metadata") ? void 0 : EdgeMetadataFromJSON(json["metadata"])
  };
}
function EdgeToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "sourceField": FieldConfigToJSON(value.sourceField),
    "destinationField": FieldConfigToJSON(value.destinationField),
    "preview": value.preview,
    "enumDetails": EnumDetailsToJSON(value.enumDetails),
    "metadata": EdgeMetadataToJSON(value.metadata)
  };
}

// build/models/Entitlement.ts
function instanceOfEntitlement(value) {
  let isInstance = true;
  isInstance = isInstance && "key" in value;
  return isInstance;
}
function EntitlementFromJSON(json) {
  return EntitlementFromJSONTyped(json, false);
}
function EntitlementFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "key": json["key"],
    "metadata": !exists(json, "metadata") ? void 0 : json["metadata"]
  };
}
function EntitlementToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "key": value.key,
    "metadata": value.metadata
  };
}

// build/models/EntitlementsResponse.ts
function instanceOfEntitlementsResponse(value) {
  let isInstance = true;
  return isInstance;
}
function EntitlementsResponseFromJSON(json) {
  return EntitlementsResponseFromJSONTyped(json, false);
}
function EntitlementsResponseFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "data": !exists(json, "data") ? void 0 : json["data"].map(EntitlementFromJSON)
  };
}
function EntitlementsResponseToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "data": value.data === void 0 ? void 0 : value.data.map(EntitlementToJSON)
  };
}

// build/models/EnumListPropertyAllOf.ts
var EnumListPropertyAllOfTypeEnum = {
  EnumList: "enum-list"
};
function instanceOfEnumListPropertyAllOf(value) {
  let isInstance = true;
  isInstance = isInstance && "config" in value;
  return isInstance;
}
function EnumListPropertyAllOfFromJSON(json) {
  return EnumListPropertyAllOfFromJSONTyped(json, false);
}
function EnumListPropertyAllOfFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "type": !exists(json, "type") ? void 0 : json["type"],
    "config": EnumPropertyAllOfConfigFromJSON(json["config"])
  };
}
function EnumListPropertyAllOfToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "type": value.type,
    "config": EnumPropertyAllOfConfigToJSON(value.config)
  };
}

// build/models/EnumPropertyAllOf.ts
var EnumPropertyAllOfTypeEnum = {
  Enum: "enum"
};
function instanceOfEnumPropertyAllOf(value) {
  let isInstance = true;
  isInstance = isInstance && "config" in value;
  return isInstance;
}
function EnumPropertyAllOfFromJSON(json) {
  return EnumPropertyAllOfFromJSONTyped(json, false);
}
function EnumPropertyAllOfFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "type": !exists(json, "type") ? void 0 : json["type"],
    "config": EnumPropertyAllOfConfigFromJSON(json["config"])
  };
}
function EnumPropertyAllOfToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "type": value.type,
    "config": EnumPropertyAllOfConfigToJSON(value.config)
  };
}

// build/models/Environment.ts
var EnvironmentDataRetentionPolicyEnum = {
  LastActivity: "lastActivity",
  SinceCreated: "sinceCreated"
};
function instanceOfEnvironment(value) {
  let isInstance = true;
  isInstance = isInstance && "id" in value;
  isInstance = isInstance && "accountId" in value;
  isInstance = isInstance && "features" in value;
  isInstance = isInstance && "name" in value;
  isInstance = isInstance && "isProd" in value;
  isInstance = isInstance && "guestAuthentication" in value;
  return isInstance;
}
function EnvironmentFromJSON(json) {
  return EnvironmentFromJSONTyped(json, false);
}
function EnvironmentFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "id": json["id"],
    "accountId": json["accountId"],
    "features": json["features"],
    "name": json["name"],
    "isProd": json["isProd"],
    "guestAuthentication": json["guestAuthentication"].map(GuestAuthenticationEnumFromJSON),
    "metadata": !exists(json, "metadata") ? void 0 : json["metadata"],
    "translationsPath": !exists(json, "translationsPath") ? void 0 : json["translationsPath"],
    "namespaces": !exists(json, "namespaces") ? void 0 : json["namespaces"],
    "languageOverride": !exists(json, "languageOverride") ? void 0 : json["languageOverride"],
    "dataRetentionPolicy": !exists(json, "dataRetentionPolicy") ? void 0 : json["dataRetentionPolicy"]
  };
}
function EnvironmentToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "id": value.id,
    "accountId": value.accountId,
    "features": value.features,
    "name": value.name,
    "isProd": value.isProd,
    "guestAuthentication": value.guestAuthentication.map(GuestAuthenticationEnumToJSON),
    "metadata": value.metadata,
    "translationsPath": value.translationsPath,
    "namespaces": value.namespaces,
    "languageOverride": value.languageOverride,
    "dataRetentionPolicy": value.dataRetentionPolicy
  };
}

// build/models/EnvironmentConfig.ts
var EnvironmentConfigDataRetentionPolicyEnum = {
  LastActivity: "lastActivity",
  SinceCreated: "sinceCreated"
};
function instanceOfEnvironmentConfig(value) {
  let isInstance = true;
  return isInstance;
}
function EnvironmentConfigFromJSON(json) {
  return EnvironmentConfigFromJSONTyped(json, false);
}
function EnvironmentConfigFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "name": !exists(json, "name") ? void 0 : json["name"],
    "isProd": !exists(json, "isProd") ? void 0 : json["isProd"],
    "guestAuthentication": !exists(json, "guestAuthentication") ? void 0 : json["guestAuthentication"].map(GuestAuthenticationEnumFromJSON),
    "metadata": !exists(json, "metadata") ? void 0 : json["metadata"],
    "translationsPath": !exists(json, "translationsPath") ? void 0 : json["translationsPath"],
    "namespaces": !exists(json, "namespaces") ? void 0 : json["namespaces"],
    "languageOverride": !exists(json, "languageOverride") ? void 0 : json["languageOverride"],
    "dataRetentionPolicy": !exists(json, "dataRetentionPolicy") ? void 0 : json["dataRetentionPolicy"]
  };
}
function EnvironmentConfigToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "name": value.name,
    "isProd": value.isProd,
    "guestAuthentication": value.guestAuthentication === void 0 ? void 0 : value.guestAuthentication.map(GuestAuthenticationEnumToJSON),
    "metadata": value.metadata,
    "translationsPath": value.translationsPath,
    "namespaces": value.namespaces,
    "languageOverride": value.languageOverride,
    "dataRetentionPolicy": value.dataRetentionPolicy
  };
}

// build/models/EnvironmentResponse.ts
function instanceOfEnvironmentResponse(value) {
  let isInstance = true;
  return isInstance;
}
function EnvironmentResponseFromJSON(json) {
  return EnvironmentResponseFromJSONTyped(json, false);
}
function EnvironmentResponseFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "data": !exists(json, "data") ? void 0 : EnvironmentFromJSON(json["data"])
  };
}
function EnvironmentResponseToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "data": EnvironmentToJSON(value.data)
  };
}

// build/models/EnvironmentStatsResponseData.ts
function instanceOfEnvironmentStatsResponseData(value) {
  let isInstance = true;
  return isInstance;
}
function EnvironmentStatsResponseDataFromJSON(json) {
  return EnvironmentStatsResponseDataFromJSONTyped(json, false);
}
function EnvironmentStatsResponseDataFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "agentExecutions": !exists(json, "agentExecutions") ? void 0 : AgentStatsFromJSON(json["agentExecutions"])
  };
}
function EnvironmentStatsResponseDataToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "agentExecutions": AgentStatsToJSON(value.agentExecutions)
  };
}

// build/models/EnvironmentStatsResponse.ts
function instanceOfEnvironmentStatsResponse(value) {
  let isInstance = true;
  return isInstance;
}
function EnvironmentStatsResponseFromJSON(json) {
  return EnvironmentStatsResponseFromJSONTyped(json, false);
}
function EnvironmentStatsResponseFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "data": !exists(json, "data") ? void 0 : EnvironmentStatsResponseDataFromJSON(json["data"])
  };
}
function EnvironmentStatsResponseToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "data": EnvironmentStatsResponseDataToJSON(value.data)
  };
}

// build/models/EnvironmentsResponse.ts
function instanceOfEnvironmentsResponse(value) {
  let isInstance = true;
  return isInstance;
}
function EnvironmentsResponseFromJSON(json) {
  return EnvironmentsResponseFromJSONTyped(json, false);
}
function EnvironmentsResponseFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "data": !exists(json, "data") ? void 0 : json["data"].map(EnvironmentFromJSON),
    "pagination": !exists(json, "pagination") ? void 0 : PaginationFromJSON(json["pagination"])
  };
}
function EnvironmentsResponseToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "data": value.data === void 0 ? void 0 : value.data.map(EnvironmentToJSON),
    "pagination": PaginationToJSON(value.pagination)
  };
}

// build/models/ErrorResponseErrorsInner.ts
function instanceOfErrorResponseErrorsInner(value) {
  let isInstance = true;
  isInstance = isInstance && "message" in value;
  return isInstance;
}
function ErrorResponseErrorsInnerFromJSON(json) {
  return ErrorResponseErrorsInnerFromJSONTyped(json, false);
}
function ErrorResponseErrorsInnerFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "key": !exists(json, "key") ? void 0 : json["key"],
    "message": json["message"]
  };
}
function ErrorResponseErrorsInnerToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "key": value.key,
    "message": value.message
  };
}

// build/models/ErrorResponse.ts
function instanceOfErrorResponse(value) {
  let isInstance = true;
  return isInstance;
}
function ErrorResponseFromJSON(json) {
  return ErrorResponseFromJSONTyped(json, false);
}
function ErrorResponseFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "errors": !exists(json, "errors") ? void 0 : json["errors"].map(ErrorResponseErrorsInnerFromJSON)
  };
}
function ErrorResponseToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "errors": value.errors === void 0 ? void 0 : value.errors.map(ErrorResponseErrorsInnerToJSON)
  };
}

// build/models/EventConfigAttributesProgress.ts
function instanceOfEventConfigAttributesProgress(value) {
  let isInstance = true;
  return isInstance;
}
function EventConfigAttributesProgressFromJSON(json) {
  return EventConfigAttributesProgressFromJSONTyped(json, false);
}
function EventConfigAttributesProgressFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "current": !exists(json, "current") ? void 0 : json["current"],
    "total": !exists(json, "total") ? void 0 : json["total"],
    "percent": !exists(json, "percent") ? void 0 : json["percent"]
  };
}
function EventConfigAttributesProgressToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "current": value.current,
    "total": value.total,
    "percent": value.percent
  };
}

// build/models/EventConfigAttributes.ts
function instanceOfEventConfigAttributes(value) {
  let isInstance = true;
  return isInstance;
}
function EventConfigAttributesFromJSON(json) {
  return EventConfigAttributesFromJSONTyped(json, false);
}
function EventConfigAttributesFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "targetUpdatedAt": !exists(json, "targetUpdatedAt") ? void 0 : new Date(json["targetUpdatedAt"]),
    "progress": !exists(json, "progress") ? void 0 : EventConfigAttributesProgressFromJSON(json["progress"])
  };
}
function EventConfigAttributesToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "targetUpdatedAt": value.targetUpdatedAt === void 0 ? void 0 : value.targetUpdatedAt.toISOString(),
    "progress": EventConfigAttributesProgressToJSON(value.progress)
  };
}

// build/models/EventConfigOrigin.ts
function instanceOfEventConfigOrigin(value) {
  let isInstance = true;
  return isInstance;
}
function EventConfigOriginFromJSON(json) {
  return EventConfigOriginFromJSONTyped(json, false);
}
function EventConfigOriginFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "id": !exists(json, "id") ? void 0 : json["id"],
    "slug": !exists(json, "slug") ? void 0 : json["slug"]
  };
}
function EventConfigOriginToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "id": value.id,
    "slug": value.slug
  };
}

// build/models/Event.ts
var EventDomainEnum = {
  File: "file",
  Space: "space",
  Workbook: "workbook",
  Job: "job",
  Document: "document",
  Sheet: "sheet",
  Program: "program",
  Secret: "secret",
  Cron: "cron",
  Environment: "environment"
};
function instanceOfEvent(value) {
  let isInstance = true;
  isInstance = isInstance && "id" in value;
  isInstance = isInstance && "createdAt" in value;
  isInstance = isInstance && "domain" in value;
  isInstance = isInstance && "topic" in value;
  isInstance = isInstance && "context" in value;
  isInstance = isInstance && "payload" in value;
  return isInstance;
}
function EventFromJSON(json) {
  return EventFromJSONTyped(json, false);
}
function EventFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "id": json["id"],
    "createdAt": new Date(json["createdAt"]),
    "domain": json["domain"],
    "topic": EventTopicFromJSON(json["topic"]),
    "namespaces": !exists(json, "namespaces") ? void 0 : json["namespaces"],
    "context": ActivityContextFromJSON(json["context"]),
    "attributes": !exists(json, "attributes") ? void 0 : EventConfigAttributesFromJSON(json["attributes"]),
    "payload": json["payload"],
    "callbackUrl": !exists(json, "callbackUrl") ? void 0 : json["callbackUrl"],
    "dataUrl": !exists(json, "dataUrl") ? void 0 : json["dataUrl"],
    "deletedAt": !exists(json, "deletedAt") ? void 0 : new Date(json["deletedAt"]),
    "acknowledgedAt": !exists(json, "acknowledgedAt") ? void 0 : json["acknowledgedAt"] === null ? null : new Date(json["acknowledgedAt"]),
    "acknowledgedBy": !exists(json, "acknowledgedBy") ? void 0 : json["acknowledgedBy"],
    "target": !exists(json, "target") ? void 0 : json["target"],
    "origin": !exists(json, "origin") ? void 0 : EventConfigOriginFromJSON(json["origin"])
  };
}
function EventToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "id": value.id,
    "createdAt": value.createdAt.toISOString(),
    "domain": value.domain,
    "topic": EventTopicToJSON(value.topic),
    "namespaces": value.namespaces,
    "context": ActivityContextToJSON(value.context),
    "attributes": EventConfigAttributesToJSON(value.attributes),
    "payload": value.payload,
    "callbackUrl": value.callbackUrl,
    "dataUrl": value.dataUrl,
    "deletedAt": value.deletedAt === void 0 ? void 0 : value.deletedAt.toISOString(),
    "acknowledgedAt": value.acknowledgedAt === void 0 ? void 0 : value.acknowledgedAt === null ? null : value.acknowledgedAt.toISOString(),
    "acknowledgedBy": value.acknowledgedBy,
    "target": value.target,
    "origin": EventConfigOriginToJSON(value.origin)
  };
}

// build/models/EventConfig.ts
var EventConfigDomainEnum = {
  File: "file",
  Space: "space",
  Workbook: "workbook",
  Job: "job",
  Document: "document",
  Sheet: "sheet",
  Program: "program",
  Secret: "secret",
  Cron: "cron",
  Environment: "environment"
};
function instanceOfEventConfig(value) {
  let isInstance = true;
  isInstance = isInstance && "domain" in value;
  isInstance = isInstance && "topic" in value;
  isInstance = isInstance && "context" in value;
  isInstance = isInstance && "payload" in value;
  return isInstance;
}
function EventConfigFromJSON(json) {
  return EventConfigFromJSONTyped(json, false);
}
function EventConfigFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "domain": json["domain"],
    "topic": EventTopicFromJSON(json["topic"]),
    "namespaces": !exists(json, "namespaces") ? void 0 : json["namespaces"],
    "context": ActivityContextFromJSON(json["context"]),
    "attributes": !exists(json, "attributes") ? void 0 : EventConfigAttributesFromJSON(json["attributes"]),
    "payload": json["payload"],
    "callbackUrl": !exists(json, "callbackUrl") ? void 0 : json["callbackUrl"],
    "dataUrl": !exists(json, "dataUrl") ? void 0 : json["dataUrl"],
    "createdAt": !exists(json, "createdAt") ? void 0 : new Date(json["createdAt"]),
    "deletedAt": !exists(json, "deletedAt") ? void 0 : new Date(json["deletedAt"]),
    "acknowledgedAt": !exists(json, "acknowledgedAt") ? void 0 : json["acknowledgedAt"] === null ? null : new Date(json["acknowledgedAt"]),
    "acknowledgedBy": !exists(json, "acknowledgedBy") ? void 0 : json["acknowledgedBy"],
    "target": !exists(json, "target") ? void 0 : json["target"],
    "origin": !exists(json, "origin") ? void 0 : EventConfigOriginFromJSON(json["origin"])
  };
}
function EventConfigToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "domain": value.domain,
    "topic": EventTopicToJSON(value.topic),
    "namespaces": value.namespaces,
    "context": ActivityContextToJSON(value.context),
    "attributes": EventConfigAttributesToJSON(value.attributes),
    "payload": value.payload,
    "callbackUrl": value.callbackUrl,
    "dataUrl": value.dataUrl,
    "createdAt": value.createdAt === void 0 ? void 0 : value.createdAt.toISOString(),
    "deletedAt": value.deletedAt === void 0 ? void 0 : value.deletedAt.toISOString(),
    "acknowledgedAt": value.acknowledgedAt === void 0 ? void 0 : value.acknowledgedAt === null ? null : value.acknowledgedAt.toISOString(),
    "acknowledgedBy": value.acknowledgedBy,
    "target": value.target,
    "origin": EventConfigOriginToJSON(value.origin)
  };
}

// build/models/EventResponse.ts
function instanceOfEventResponse(value) {
  let isInstance = true;
  return isInstance;
}
function EventResponseFromJSON(json) {
  return EventResponseFromJSONTyped(json, false);
}
function EventResponseFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "data": !exists(json, "data") ? void 0 : EventFromJSON(json["data"])
  };
}
function EventResponseToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "data": EventToJSON(value.data)
  };
}

// build/models/EventToken.ts
function instanceOfEventToken(value) {
  let isInstance = true;
  return isInstance;
}
function EventTokenFromJSON(json) {
  return EventTokenFromJSONTyped(json, false);
}
function EventTokenFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "accountId": !exists(json, "accountId") ? void 0 : json["accountId"],
    "subscribeKey": !exists(json, "subscribeKey") ? void 0 : json["subscribeKey"],
    "ttl": !exists(json, "ttl") ? void 0 : json["ttl"],
    "token": !exists(json, "token") ? void 0 : json["token"]
  };
}
function EventTokenToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "accountId": value.accountId,
    "subscribeKey": value.subscribeKey,
    "ttl": value.ttl,
    "token": value.token
  };
}

// build/models/EventTokenResponse.ts
function instanceOfEventTokenResponse(value) {
  let isInstance = true;
  return isInstance;
}
function EventTokenResponseFromJSON(json) {
  return EventTokenResponseFromJSONTyped(json, false);
}
function EventTokenResponseFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "data": !exists(json, "data") ? void 0 : EventTokenFromJSON(json["data"])
  };
}
function EventTokenResponseToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "data": EventTokenToJSON(value.data)
  };
}

// build/models/EventsResponse.ts
function instanceOfEventsResponse(value) {
  let isInstance = true;
  return isInstance;
}
function EventsResponseFromJSON(json) {
  return EventsResponseFromJSONTyped(json, false);
}
function EventsResponseFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "data": !exists(json, "data") ? void 0 : json["data"].map(EventFromJSON)
  };
}
function EventsResponseToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "data": value.data === void 0 ? void 0 : value.data.map(EventToJSON)
  };
}

// build/models/ExchangeInvitationRequest.ts
function instanceOfExchangeInvitationRequest(value) {
  let isInstance = true;
  return isInstance;
}
function ExchangeInvitationRequestFromJSON(json) {
  return ExchangeInvitationRequestFromJSONTyped(json, false);
}
function ExchangeInvitationRequestFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "token": !exists(json, "token") ? void 0 : json["token"],
    "email": !exists(json, "email") ? void 0 : json["email"],
    "spaceId": !exists(json, "spaceId") ? void 0 : json["spaceId"]
  };
}
function ExchangeInvitationRequestToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "token": value.token,
    "email": value.email,
    "spaceId": value.spaceId
  };
}

// build/models/ExchangeTokenResponseData.ts
function instanceOfExchangeTokenResponseData(value) {
  let isInstance = true;
  isInstance = isInstance && "valid" in value;
  return isInstance;
}
function ExchangeTokenResponseDataFromJSON(json) {
  return ExchangeTokenResponseDataFromJSONTyped(json, false);
}
function ExchangeTokenResponseDataFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "valid": json["valid"],
    "token": !exists(json, "token") ? void 0 : json["token"],
    "sentTo": !exists(json, "sentTo") ? void 0 : json["sentTo"]
  };
}
function ExchangeTokenResponseDataToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "valid": value.valid,
    "token": value.token,
    "sentTo": value.sentTo
  };
}

// build/models/ExchangeTokenResponse.ts
function instanceOfExchangeTokenResponse(value) {
  let isInstance = true;
  return isInstance;
}
function ExchangeTokenResponseFromJSON(json) {
  return ExchangeTokenResponseFromJSONTyped(json, false);
}
function ExchangeTokenResponseFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "data": !exists(json, "data") ? void 0 : ExchangeTokenResponseDataFromJSON(json["data"])
  };
}
function ExchangeTokenResponseToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "data": ExchangeTokenResponseDataToJSON(value.data)
  };
}

// build/models/Execution.ts
function instanceOfExecution(value) {
  let isInstance = true;
  isInstance = isInstance && "eventId" in value;
  isInstance = isInstance && "success" in value;
  isInstance = isInstance && "createdAt" in value;
  isInstance = isInstance && "completedAt" in value;
  return isInstance;
}
function ExecutionFromJSON(json) {
  return ExecutionFromJSONTyped(json, false);
}
function ExecutionFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "eventId": json["eventId"],
    "success": json["success"],
    "createdAt": json["createdAt"],
    "completedAt": json["completedAt"],
    "duration": !exists(json, "duration") ? void 0 : json["duration"],
    "topic": !exists(json, "topic") ? void 0 : json["topic"]
  };
}
function ExecutionToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "eventId": value.eventId,
    "success": value.success,
    "createdAt": value.createdAt,
    "completedAt": value.completedAt,
    "duration": value.duration,
    "topic": value.topic
  };
}

// build/models/ExecutionsResponse.ts
function instanceOfExecutionsResponse(value) {
  let isInstance = true;
  return isInstance;
}
function ExecutionsResponseFromJSON(json) {
  return ExecutionsResponseFromJSONTyped(json, false);
}
function ExecutionsResponseFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "pagination": !exists(json, "pagination") ? void 0 : PaginationFromJSON(json["pagination"]),
    "data": !exists(json, "data") ? void 0 : json["data"].map(ExecutionFromJSON)
  };
}
function ExecutionsResponseToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "pagination": PaginationToJSON(value.pagination),
    "data": value.data === void 0 ? void 0 : value.data.map(ExecutionToJSON)
  };
}

// build/models/ExportOptions.ts
function instanceOfExportOptions(value) {
  let isInstance = true;
  return isInstance;
}
function ExportOptionsFromJSON(json) {
  return ExportOptionsFromJSONTyped(json, false);
}
function ExportOptionsFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "commitId": !exists(json, "commitId") ? void 0 : json["commitId"],
    "versionId": !exists(json, "versionId") ? void 0 : json["versionId"],
    "sortField": !exists(json, "sortField") ? void 0 : json["sortField"],
    "sortDirection": !exists(json, "sortDirection") ? void 0 : SortDirectionFromJSON(json["sortDirection"]),
    "filter": !exists(json, "filter") ? void 0 : FilterFromJSON(json["filter"]),
    "filterField": !exists(json, "filterField") ? void 0 : json["filterField"],
    "searchValue": !exists(json, "searchValue") ? void 0 : json["searchValue"],
    "searchField": !exists(json, "searchField") ? void 0 : json["searchField"],
    "q": !exists(json, "q") ? void 0 : json["q"],
    "ids": !exists(json, "ids") ? void 0 : json["ids"]
  };
}
function ExportOptionsToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "commitId": value.commitId,
    "versionId": value.versionId,
    "sortField": value.sortField,
    "sortDirection": SortDirectionToJSON(value.sortDirection),
    "filter": FilterToJSON(value.filter),
    "filterField": value.filterField,
    "searchValue": value.searchValue,
    "searchField": value.searchField,
    "q": value.q,
    "ids": value.ids
  };
}

// build/models/ExportJobConfig.ts
function instanceOfExportJobConfig(value) {
  let isInstance = true;
  isInstance = isInstance && "options" in value;
  return isInstance;
}
function ExportJobConfigFromJSON(json) {
  return ExportJobConfigFromJSONTyped(json, false);
}
function ExportJobConfigFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "options": ExportOptionsFromJSON(json["options"])
  };
}
function ExportJobConfigToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "options": ExportOptionsToJSON(value.options)
  };
}

// build/models/ExternalConstraintAllOf.ts
var ExternalConstraintAllOfTypeEnum = {
  External: "external"
};
function instanceOfExternalConstraintAllOf(value) {
  let isInstance = true;
  isInstance = isInstance && "type" in value;
  isInstance = isInstance && "validator" in value;
  return isInstance;
}
function ExternalConstraintAllOfFromJSON(json) {
  return ExternalConstraintAllOfFromJSONTyped(json, false);
}
function ExternalConstraintAllOfFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "type": json["type"],
    "validator": json["validator"],
    "config": !exists(json, "config") ? void 0 : json["config"]
  };
}
function ExternalConstraintAllOfToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "type": value.type,
    "validator": value.validator,
    "config": value.config
  };
}

// build/models/ExternalSheetConstraintAllOf.ts
var ExternalSheetConstraintAllOfTypeEnum = {
  External: "external"
};
function instanceOfExternalSheetConstraintAllOf(value) {
  let isInstance = true;
  isInstance = isInstance && "type" in value;
  return isInstance;
}
function ExternalSheetConstraintAllOfFromJSON(json) {
  return ExternalSheetConstraintAllOfFromJSONTyped(json, false);
}
function ExternalSheetConstraintAllOfFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "type": json["type"],
    "fields": !exists(json, "fields") ? void 0 : json["fields"],
    "config": !exists(json, "config") ? void 0 : json["config"],
    "validator": !exists(json, "validator") ? void 0 : json["validator"]
  };
}
function ExternalSheetConstraintAllOfToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "type": value.type,
    "fields": value.fields,
    "config": value.config,
    "validator": value.validator
  };
}

// build/models/FieldConfigResponse.ts
function instanceOfFieldConfigResponse(value) {
  let isInstance = true;
  return isInstance;
}
function FieldConfigResponseFromJSON(json) {
  return FieldConfigResponseFromJSONTyped(json, false);
}
function FieldConfigResponseFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "data": !exists(json, "data") ? void 0 : FieldConfigFromJSON(json["data"])
  };
}
function FieldConfigResponseToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "data": FieldConfigToJSON(value.data)
  };
}

// build/models/FileMode.ts
var FileMode = {
  Import: "import",
  Export: "export"
};
function FileModeFromJSON(json) {
  return FileModeFromJSONTyped(json, false);
}
function FileModeFromJSONTyped(json, ignoreDiscriminator) {
  return json;
}
function FileModeToJSON(value) {
  return value;
}

// build/models/ModelFileStatusEnum.ts
var ModelFileStatusEnum = {
  Partial: "partial",
  Complete: "complete",
  Archived: "archived",
  Purged: "purged",
  Failed: "failed"
};
function ModelFileStatusEnumFromJSON(json) {
  return ModelFileStatusEnumFromJSONTyped(json, false);
}
function ModelFileStatusEnumFromJSONTyped(json, ignoreDiscriminator) {
  return json;
}
function ModelFileStatusEnumToJSON(value) {
  return value;
}

// build/models/FileConfig.ts
function instanceOfFileConfig(value) {
  let isInstance = true;
  return isInstance;
}
function FileConfigFromJSON(json) {
  return FileConfigFromJSONTyped(json, false);
}
function FileConfigFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "workbookId": !exists(json, "workbookId") ? void 0 : json["workbookId"],
    "mode": !exists(json, "mode") ? void 0 : FileModeFromJSON(json["mode"]),
    "status": !exists(json, "status") ? void 0 : ModelFileStatusEnumFromJSON(json["status"]),
    "actions": !exists(json, "actions") ? void 0 : json["actions"].map(ActionFromJSON),
    "name": !exists(json, "name") ? void 0 : json["name"]
  };
}
function FileConfigToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "workbookId": value.workbookId,
    "mode": FileModeToJSON(value.mode),
    "status": ModelFileStatusEnumToJSON(value.status),
    "actions": value.actions === void 0 ? void 0 : value.actions.map(ActionToJSON),
    "name": value.name
  };
}

// build/models/FileJobConfig.ts
var FileJobConfigDriverEnum = {
  Csv: "csv"
};
function instanceOfFileJobConfig(value) {
  let isInstance = true;
  isInstance = isInstance && "driver" in value;
  return isInstance;
}
function FileJobConfigFromJSON(json) {
  return FileJobConfigFromJSONTyped(json, false);
}
function FileJobConfigFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "driver": json["driver"],
    "options": !exists(json, "options") ? void 0 : json["options"],
    "detectedHeaderRow": !exists(json, "detectedHeaderRow") ? void 0 : json["detectedHeaderRow"]
  };
}
function FileJobConfigToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "driver": value.driver,
    "options": value.options,
    "detectedHeaderRow": value.detectedHeaderRow
  };
}

// build/models/FileOrigin.ts
var FileOrigin = {
  Filesystem: "filesystem",
  Googledrive: "googledrive",
  Box: "box",
  Onedrive: "onedrive"
};
function FileOriginFromJSON(json) {
  return FileOriginFromJSONTyped(json, false);
}
function FileOriginFromJSONTyped(json, ignoreDiscriminator) {
  return json;
}
function FileOriginToJSON(value) {
  return value;
}

// build/models/FileResponse.ts
function instanceOfFileResponse(value) {
  let isInstance = true;
  isInstance = isInstance && "data" in value;
  return isInstance;
}
function FileResponseFromJSON(json) {
  return FileResponseFromJSONTyped(json, false);
}
function FileResponseFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "data": json["data"]
  };
}
function FileResponseToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "data": value.data
  };
}

// build/models/FilesResponse.ts
function instanceOfFilesResponse(value) {
  let isInstance = true;
  return isInstance;
}
function FilesResponseFromJSON(json) {
  return FilesResponseFromJSONTyped(json, false);
}
function FilesResponseFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "pagination": !exists(json, "pagination") ? void 0 : PaginationFromJSON(json["pagination"]),
    "data": !exists(json, "data") ? void 0 : json["data"]
  };
}
function FilesResponseToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "pagination": PaginationToJSON(value.pagination),
    "data": value.data
  };
}

// build/models/FindAndReplaceJobConfig.ts
function instanceOfFindAndReplaceJobConfig(value) {
  let isInstance = true;
  isInstance = isInstance && "find" in value;
  isInstance = isInstance && "replace" in value;
  isInstance = isInstance && "fieldKey" in value;
  return isInstance;
}
function FindAndReplaceJobConfigFromJSON(json) {
  return FindAndReplaceJobConfigFromJSONTyped(json, false);
}
function FindAndReplaceJobConfigFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "filter": !exists(json, "filter") ? void 0 : FilterFromJSON(json["filter"]),
    "filterField": !exists(json, "filterField") ? void 0 : json["filterField"],
    "q": !exists(json, "q") ? void 0 : json["q"],
    "searchValue": !exists(json, "searchValue") ? void 0 : json["searchValue"],
    "searchField": !exists(json, "searchField") ? void 0 : json["searchField"],
    "ids": !exists(json, "ids") ? void 0 : json["ids"],
    "find": ValueFromJSON(json["find"]),
    "replace": ValueFromJSON(json["replace"]),
    "fieldKey": json["fieldKey"]
  };
}
function FindAndReplaceJobConfigToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "filter": FilterToJSON(value.filter),
    "filterField": value.filterField,
    "q": value.q,
    "searchValue": value.searchValue,
    "searchField": value.searchField,
    "ids": value.ids,
    "find": ValueToJSON(value.find),
    "replace": ValueToJSON(value.replace),
    "fieldKey": value.fieldKey
  };
}

// build/models/FindAndReplaceRequest.ts
function instanceOfFindAndReplaceRequest(value) {
  let isInstance = true;
  isInstance = isInstance && "find" in value;
  isInstance = isInstance && "replace" in value;
  isInstance = isInstance && "fieldKey" in value;
  return isInstance;
}
function FindAndReplaceRequestFromJSON(json) {
  return FindAndReplaceRequestFromJSONTyped(json, false);
}
function FindAndReplaceRequestFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "find": ValueFromJSON(json["find"]),
    "replace": ValueFromJSON(json["replace"]),
    "fieldKey": json["fieldKey"]
  };
}
function FindAndReplaceRequestToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "find": ValueToJSON(value.find),
    "replace": ValueToJSON(value.replace),
    "fieldKey": value.fieldKey
  };
}

// build/models/FronteggUser.ts
function instanceOfFronteggUser(value) {
  let isInstance = true;
  isInstance = isInstance && "id" in value;
  isInstance = isInstance && "email" in value;
  isInstance = isInstance && "tenantId" in value;
  return isInstance;
}
function FronteggUserFromJSON(json) {
  return FronteggUserFromJSONTyped(json, false);
}
function FronteggUserFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "id": json["id"],
    "email": json["email"],
    "verified": !exists(json, "verified") ? void 0 : json["verified"],
    "name": !exists(json, "name") ? void 0 : json["name"],
    "provider": !exists(json, "provider") ? void 0 : json["provider"],
    "tenantId": json["tenantId"],
    "tenantIds": !exists(json, "tenantIds") ? void 0 : json["tenantIds"],
    "metadata": !exists(json, "metadata") ? void 0 : json["metadata"]
  };
}
function FronteggUserToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "id": value.id,
    "email": value.email,
    "verified": value.verified,
    "name": value.name,
    "provider": value.provider,
    "tenantId": value.tenantId,
    "tenantIds": value.tenantIds,
    "metadata": value.metadata
  };
}

// build/models/FronteggSignup.ts
function instanceOfFronteggSignup(value) {
  let isInstance = true;
  isInstance = isInstance && "user" in value;
  return isInstance;
}
function FronteggSignupFromJSON(json) {
  return FronteggSignupFromJSONTyped(json, false);
}
function FronteggSignupFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "user": FronteggUserFromJSON(json["user"])
  };
}
function FronteggSignupToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "user": FronteggUserToJSON(value.user)
  };
}

// build/models/GenerateMutationConfig.ts
function instanceOfGenerateMutationConfig(value) {
  let isInstance = true;
  isInstance = isInstance && "sheetId" in value;
  isInstance = isInstance && "command" in value;
  return isInstance;
}
function GenerateMutationConfigFromJSON(json) {
  return GenerateMutationConfigFromJSONTyped(json, false);
}
function GenerateMutationConfigFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "sheetId": json["sheetId"],
    "model": !exists(json, "model") ? void 0 : json["model"],
    "modelParams": !exists(json, "modelParams") ? void 0 : json["modelParams"],
    "command": json["command"],
    "templateVariables": !exists(json, "templateVariables") ? void 0 : json["templateVariables"]
  };
}
function GenerateMutationConfigToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "sheetId": value.sheetId,
    "model": value.model,
    "modelParams": value.modelParams,
    "command": value.command,
    "templateVariables": value.templateVariables
  };
}

// build/models/GenerateQueryConfig.ts
function instanceOfGenerateQueryConfig(value) {
  let isInstance = true;
  isInstance = isInstance && "sheetId" in value;
  isInstance = isInstance && "command" in value;
  return isInstance;
}
function GenerateQueryConfigFromJSON(json) {
  return GenerateQueryConfigFromJSONTyped(json, false);
}
function GenerateQueryConfigFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "sheetId": json["sheetId"],
    "model": !exists(json, "model") ? void 0 : json["model"],
    "modelParams": !exists(json, "modelParams") ? void 0 : json["modelParams"],
    "command": json["command"],
    "queryType": !exists(json, "queryType") ? void 0 : json["queryType"],
    "templateVariables": !exists(json, "templateVariables") ? void 0 : json["templateVariables"]
  };
}
function GenerateQueryConfigToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "sheetId": value.sheetId,
    "model": value.model,
    "modelParams": value.modelParams,
    "command": value.command,
    "queryType": value.queryType,
    "templateVariables": value.templateVariables
  };
}

// build/models/GeneratedMutationMetadata.ts
function instanceOfGeneratedMutationMetadata(value) {
  let isInstance = true;
  isInstance = isInstance && "explain" in value;
  return isInstance;
}
function GeneratedMutationMetadataFromJSON(json) {
  return GeneratedMutationMetadataFromJSONTyped(json, false);
}
function GeneratedMutationMetadataFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "explain": json["explain"]
  };
}
function GeneratedMutationMetadataToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "explain": value.explain
  };
}

// build/models/GeneratedMutation.ts
function instanceOfGeneratedMutation(value) {
  let isInstance = true;
  isInstance = isInstance && "mutateRecord" in value;
  isInstance = isInstance && "metadata" in value;
  isInstance = isInstance && "mutationId" in value;
  return isInstance;
}
function GeneratedMutationFromJSON(json) {
  return GeneratedMutationFromJSONTyped(json, false);
}
function GeneratedMutationFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "mutateRecord": json["mutateRecord"],
    "metadata": GeneratedMutationMetadataFromJSON(json["metadata"]),
    "mutationId": json["mutationId"]
  };
}
function GeneratedMutationToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "mutateRecord": value.mutateRecord,
    "metadata": GeneratedMutationMetadataToJSON(value.metadata),
    "mutationId": value.mutationId
  };
}

// build/models/GeneratedMutationResponse.ts
function instanceOfGeneratedMutationResponse(value) {
  let isInstance = true;
  return isInstance;
}
function GeneratedMutationResponseFromJSON(json) {
  return GeneratedMutationResponseFromJSONTyped(json, false);
}
function GeneratedMutationResponseFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "data": !exists(json, "data") ? void 0 : GeneratedMutationFromJSON(json["data"])
  };
}
function GeneratedMutationResponseToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "data": GeneratedMutationToJSON(value.data)
  };
}

// build/models/GeneratedQueryMetadata.ts
function instanceOfGeneratedQueryMetadata(value) {
  let isInstance = true;
  isInstance = isInstance && "explain" in value;
  return isInstance;
}
function GeneratedQueryMetadataFromJSON(json) {
  return GeneratedQueryMetadataFromJSONTyped(json, false);
}
function GeneratedQueryMetadataFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "explain": json["explain"]
  };
}
function GeneratedQueryMetadataToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "explain": value.explain
  };
}

// build/models/GeneratedQuery.ts
function instanceOfGeneratedQuery(value) {
  let isInstance = true;
  isInstance = isInstance && "query" in value;
  isInstance = isInstance && "queryType" in value;
  isInstance = isInstance && "metadata" in value;
  isInstance = isInstance && "queryId" in value;
  return isInstance;
}
function GeneratedQueryFromJSON(json) {
  return GeneratedQueryFromJSONTyped(json, false);
}
function GeneratedQueryFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "query": json["query"],
    "queryType": json["queryType"],
    "metadata": GeneratedQueryMetadataFromJSON(json["metadata"]),
    "queryId": json["queryId"]
  };
}
function GeneratedQueryToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "query": value.query,
    "queryType": value.queryType,
    "metadata": GeneratedQueryMetadataToJSON(value.metadata),
    "queryId": value.queryId
  };
}

// build/models/GeneratedQueryResponse.ts
function instanceOfGeneratedQueryResponse(value) {
  let isInstance = true;
  return isInstance;
}
function GeneratedQueryResponseFromJSON(json) {
  return GeneratedQueryResponseFromJSONTyped(json, false);
}
function GeneratedQueryResponseFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "data": !exists(json, "data") ? void 0 : GeneratedQueryFromJSON(json["data"])
  };
}
function GeneratedQueryResponseToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "data": GeneratedQueryToJSON(value.data)
  };
}

// build/models/GetCommitResponse.ts
function instanceOfGetCommitResponse(value) {
  let isInstance = true;
  return isInstance;
}
function GetCommitResponseFromJSON(json) {
  return GetCommitResponseFromJSONTyped(json, false);
}
function GetCommitResponseFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "data": !exists(json, "data") ? void 0 : CommitFromJSON(json["data"])
  };
}
function GetCommitResponseToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "data": CommitToJSON(value.data)
  };
}

// build/models/GetDatabaseDataTask.ts
function instanceOfGetDatabaseDataTask(value) {
  let isInstance = true;
  return isInstance;
}
function GetDatabaseDataTaskFromJSON(json) {
  return GetDatabaseDataTaskFromJSONTyped(json, false);
}
function GetDatabaseDataTaskFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "status": !exists(json, "status") ? void 0 : json["status"],
    "type": !exists(json, "type") ? void 0 : json["type"],
    "progress": !exists(json, "progress") ? void 0 : json["progress"]
  };
}
function GetDatabaseDataTaskToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "status": value.status,
    "type": value.type,
    "progress": value.progress
  };
}

// build/models/GetDatabaseData.ts
function instanceOfGetDatabaseData(value) {
  let isInstance = true;
  return isInstance;
}
function GetDatabaseDataFromJSON(json) {
  return GetDatabaseDataFromJSONTyped(json, false);
}
function GetDatabaseDataFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "task": !exists(json, "task") ? void 0 : GetDatabaseDataTaskFromJSON(json["task"])
  };
}
function GetDatabaseDataToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "task": GetDatabaseDataTaskToJSON(value.task)
  };
}

// build/models/GetDatabaseResponseResponse.ts
function instanceOfGetDatabaseResponseResponse(value) {
  let isInstance = true;
  return isInstance;
}
function GetDatabaseResponseResponseFromJSON(json) {
  return GetDatabaseResponseResponseFromJSONTyped(json, false);
}
function GetDatabaseResponseResponseFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "data": !exists(json, "data") ? void 0 : GetDatabaseDataFromJSON(json["data"])
  };
}
function GetDatabaseResponseResponseToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "data": GetDatabaseDataToJSON(value.data)
  };
}

// build/models/GetEnumWeightsRequest.ts
function instanceOfGetEnumWeightsRequest(value) {
  let isInstance = true;
  isInstance = isInstance && "sourceField" in value;
  isInstance = isInstance && "sourceValues" in value;
  isInstance = isInstance && "destinationField" in value;
  isInstance = isInstance && "destinationValues" in value;
  return isInstance;
}
function GetEnumWeightsRequestFromJSON(json) {
  return GetEnumWeightsRequestFromJSONTyped(json, false);
}
function GetEnumWeightsRequestFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "sourceField": json["sourceField"],
    "sourceValues": json["sourceValues"],
    "destinationField": json["destinationField"],
    "destinationValues": json["destinationValues"]
  };
}
function GetEnumWeightsRequestToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "sourceField": value.sourceField,
    "sourceValues": value.sourceValues,
    "destinationField": value.destinationField,
    "destinationValues": value.destinationValues
  };
}

// build/models/GetFieldWeightsRequest.ts
function instanceOfGetFieldWeightsRequest(value) {
  let isInstance = true;
  isInstance = isInstance && "sourceFields" in value;
  isInstance = isInstance && "destinationFields" in value;
  return isInstance;
}
function GetFieldWeightsRequestFromJSON(json) {
  return GetFieldWeightsRequestFromJSONTyped(json, false);
}
function GetFieldWeightsRequestFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "sourceFields": json["sourceFields"],
    "destinationFields": json["destinationFields"]
  };
}
function GetFieldWeightsRequestToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "sourceFields": value.sourceFields,
    "destinationFields": value.destinationFields
  };
}

// build/models/RecordsWithLinks.ts
function instanceOfRecordsWithLinks(value) {
  let isInstance = true;
  return isInstance;
}
function RecordsWithLinksFromJSON(json) {
  return RecordsWithLinksFromJSONTyped(json, false);
}
function RecordsWithLinksFromJSONTyped(json, ignoreDiscriminator) {
  return json;
}
function RecordsWithLinksToJSON(value) {
  return value;
}

// build/models/GetRecordsResponseData.ts
function instanceOfGetRecordsResponseData(value) {
  let isInstance = true;
  isInstance = isInstance && "success" in value;
  isInstance = isInstance && "records" in value;
  return isInstance;
}
function GetRecordsResponseDataFromJSON(json) {
  return GetRecordsResponseDataFromJSONTyped(json, false);
}
function GetRecordsResponseDataFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "success": json["success"],
    "records": RecordsWithLinksFromJSON(json["records"]),
    "counts": !exists(json, "counts") ? void 0 : RecordCountsFromJSON(json["counts"]),
    "versionId": !exists(json, "versionId") ? void 0 : json["versionId"],
    "commitId": !exists(json, "commitId") ? void 0 : json["commitId"]
  };
}
function GetRecordsResponseDataToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "success": value.success,
    "records": RecordsWithLinksToJSON(value.records),
    "counts": RecordCountsToJSON(value.counts),
    "versionId": value.versionId,
    "commitId": value.commitId
  };
}

// build/models/GetRecordsResponse.ts
function instanceOfGetRecordsResponse(value) {
  let isInstance = true;
  return isInstance;
}
function GetRecordsResponseFromJSON(json) {
  return GetRecordsResponseFromJSONTyped(json, false);
}
function GetRecordsResponseFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "data": !exists(json, "data") ? void 0 : GetRecordsResponseDataFromJSON(json["data"])
  };
}
function GetRecordsResponseToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "data": GetRecordsResponseDataToJSON(value.data)
  };
}

// build/models/GetRecordsResponseDataAllOf.ts
function instanceOfGetRecordsResponseDataAllOf(value) {
  let isInstance = true;
  isInstance = isInstance && "records" in value;
  return isInstance;
}
function GetRecordsResponseDataAllOfFromJSON(json) {
  return GetRecordsResponseDataAllOfFromJSONTyped(json, false);
}
function GetRecordsResponseDataAllOfFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "records": RecordsWithLinksFromJSON(json["records"]),
    "counts": !exists(json, "counts") ? void 0 : RecordCountsFromJSON(json["counts"]),
    "versionId": !exists(json, "versionId") ? void 0 : json["versionId"],
    "commitId": !exists(json, "commitId") ? void 0 : json["commitId"]
  };
}
function GetRecordsResponseDataAllOfToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "records": RecordsWithLinksToJSON(value.records),
    "counts": RecordCountsToJSON(value.counts),
    "versionId": value.versionId,
    "commitId": value.commitId
  };
}

// build/models/GetSheetCommitsResponse.ts
function instanceOfGetSheetCommitsResponse(value) {
  let isInstance = true;
  return isInstance;
}
function GetSheetCommitsResponseFromJSON(json) {
  return GetSheetCommitsResponseFromJSONTyped(json, false);
}
function GetSheetCommitsResponseFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "data": !exists(json, "data") ? void 0 : json["data"].map(CommitFromJSON)
  };
}
function GetSheetCommitsResponseToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "data": value.data === void 0 ? void 0 : value.data.map(CommitToJSON)
  };
}

// build/models/GetSpaceActivitySortField.ts
var GetSpaceActivitySortField = {
  Activity: "activity",
  CreatedAt: "created_at"
};
function GetSpaceActivitySortFieldFromJSON(json) {
  return GetSpaceActivitySortFieldFromJSONTyped(json, false);
}
function GetSpaceActivitySortFieldFromJSONTyped(json, ignoreDiscriminator) {
  return json;
}
function GetSpaceActivitySortFieldToJSON(value) {
  return value;
}

// build/models/GetSpacesSortField.ts
var GetSpacesSortField = {
  Name: "name",
  WorkbooksCount: "workbooksCount",
  FilesCount: "filesCount",
  EnvironmentId: "environmentId",
  CreatedByUserName: "createdByUserName",
  CreatedAt: "createdAt",
  LastActivityAt: "lastActivityAt"
};
function GetSpacesSortFieldFromJSON(json) {
  return GetSpacesSortFieldFromJSONTyped(json, false);
}
function GetSpacesSortFieldFromJSONTyped(json, ignoreDiscriminator) {
  return json;
}
function GetSpacesSortFieldToJSON(value) {
  return value;
}

// build/models/GetUserResponse.ts
function instanceOfGetUserResponse(value) {
  let isInstance = true;
  return isInstance;
}
function GetUserResponseFromJSON(json) {
  return GetUserResponseFromJSONTyped(json, false);
}
function GetUserResponseFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "data": !exists(json, "data") ? void 0 : DBUserFromJSON(json["data"])
  };
}
function GetUserResponseToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "data": DBUserToJSON(value.data)
  };
}

// build/models/GetWorkbookCommitsResponse.ts
function instanceOfGetWorkbookCommitsResponse(value) {
  let isInstance = true;
  return isInstance;
}
function GetWorkbookCommitsResponseFromJSON(json) {
  return GetWorkbookCommitsResponseFromJSONTyped(json, false);
}
function GetWorkbookCommitsResponseFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "data": !exists(json, "data") ? void 0 : json["data"].map(CommitFromJSON)
  };
}
function GetWorkbookCommitsResponseToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "data": value.data === void 0 ? void 0 : value.data.map(CommitToJSON)
  };
}

// build/models/GuestConfigSpacesInnerWorkbooksInner.ts
function instanceOfGuestConfigSpacesInnerWorkbooksInner(value) {
  let isInstance = true;
  isInstance = isInstance && "id" in value;
  return isInstance;
}
function GuestConfigSpacesInnerWorkbooksInnerFromJSON(json) {
  return GuestConfigSpacesInnerWorkbooksInnerFromJSONTyped(json, false);
}
function GuestConfigSpacesInnerWorkbooksInnerFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "id": json["id"]
  };
}
function GuestConfigSpacesInnerWorkbooksInnerToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "id": value.id
  };
}

// build/models/GuestConfigSpacesInner.ts
function instanceOfGuestConfigSpacesInner(value) {
  let isInstance = true;
  isInstance = isInstance && "id" in value;
  return isInstance;
}
function GuestConfigSpacesInnerFromJSON(json) {
  return GuestConfigSpacesInnerFromJSONTyped(json, false);
}
function GuestConfigSpacesInnerFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "id": json["id"],
    "workbooks": !exists(json, "workbooks") ? void 0 : json["workbooks"].map(GuestConfigSpacesInnerWorkbooksInnerFromJSON),
    "lastAccessed": !exists(json, "lastAccessed") ? void 0 : new Date(json["lastAccessed"])
  };
}
function GuestConfigSpacesInnerToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "id": value.id,
    "workbooks": value.workbooks === void 0 ? void 0 : value.workbooks.map(GuestConfigSpacesInnerWorkbooksInnerToJSON),
    "lastAccessed": value.lastAccessed === void 0 ? void 0 : value.lastAccessed.toISOString()
  };
}

// build/models/Guest.ts
function instanceOfGuest(value) {
  let isInstance = true;
  isInstance = isInstance && "id" in value;
  isInstance = isInstance && "createdAt" in value;
  isInstance = isInstance && "updatedAt" in value;
  isInstance = isInstance && "environmentId" in value;
  isInstance = isInstance && "email" in value;
  isInstance = isInstance && "name" in value;
  isInstance = isInstance && "spaces" in value;
  return isInstance;
}
function GuestFromJSON(json) {
  return GuestFromJSONTyped(json, false);
}
function GuestFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "id": json["id"],
    "createdAt": new Date(json["createdAt"]),
    "updatedAt": new Date(json["updatedAt"]),
    "environmentId": json["environmentId"],
    "email": json["email"],
    "name": json["name"],
    "spaces": json["spaces"].map(GuestConfigSpacesInnerFromJSON)
  };
}
function GuestToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "id": value.id,
    "createdAt": value.createdAt.toISOString(),
    "updatedAt": value.updatedAt.toISOString(),
    "environmentId": value.environmentId,
    "email": value.email,
    "name": value.name,
    "spaces": value.spaces.map(GuestConfigSpacesInnerToJSON)
  };
}

// build/models/GuestConfig.ts
function instanceOfGuestConfig(value) {
  let isInstance = true;
  isInstance = isInstance && "environmentId" in value;
  isInstance = isInstance && "email" in value;
  isInstance = isInstance && "name" in value;
  isInstance = isInstance && "spaces" in value;
  return isInstance;
}
function GuestConfigFromJSON(json) {
  return GuestConfigFromJSONTyped(json, false);
}
function GuestConfigFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "environmentId": json["environmentId"],
    "email": json["email"],
    "name": json["name"],
    "spaces": json["spaces"].map(GuestConfigSpacesInnerFromJSON)
  };
}
function GuestConfigToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "environmentId": value.environmentId,
    "email": value.email,
    "name": value.name,
    "spaces": value.spaces.map(GuestConfigSpacesInnerToJSON)
  };
}

// build/models/GuestConfigUpdate.ts
function instanceOfGuestConfigUpdate(value) {
  let isInstance = true;
  return isInstance;
}
function GuestConfigUpdateFromJSON(json) {
  return GuestConfigUpdateFromJSONTyped(json, false);
}
function GuestConfigUpdateFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "environmentId": !exists(json, "environmentId") ? void 0 : json["environmentId"],
    "email": !exists(json, "email") ? void 0 : json["email"],
    "name": !exists(json, "name") ? void 0 : json["name"],
    "spaces": !exists(json, "spaces") ? void 0 : json["spaces"].map(GuestConfigSpacesInnerFromJSON)
  };
}
function GuestConfigUpdateToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "environmentId": value.environmentId,
    "email": value.email,
    "name": value.name,
    "spaces": value.spaces === void 0 ? void 0 : value.spaces.map(GuestConfigSpacesInnerToJSON)
  };
}

// build/models/GuestResponse.ts
function instanceOfGuestResponse(value) {
  let isInstance = true;
  return isInstance;
}
function GuestResponseFromJSON(json) {
  return GuestResponseFromJSONTyped(json, false);
}
function GuestResponseFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "data": !exists(json, "data") ? void 0 : GuestFromJSON(json["data"])
  };
}
function GuestResponseToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "data": GuestToJSON(value.data)
  };
}

// build/models/GuestsResponse.ts
function instanceOfGuestsResponse(value) {
  let isInstance = true;
  return isInstance;
}
function GuestsResponseFromJSON(json) {
  return GuestsResponseFromJSONTyped(json, false);
}
function GuestsResponseFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "data": !exists(json, "data") ? void 0 : json["data"].map(GuestFromJSON)
  };
}
function GuestsResponseToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "data": value.data === void 0 ? void 0 : value.data.map(GuestToJSON)
  };
}

// build/models/HasAllValidConstraintAllOf.ts
var HasAllValidConstraintAllOfTypeEnum = {
  HasAllValid: "hasAllValid"
};
function instanceOfHasAllValidConstraintAllOf(value) {
  let isInstance = true;
  return isInstance;
}
function HasAllValidConstraintAllOfFromJSON(json) {
  return HasAllValidConstraintAllOfFromJSONTyped(json, false);
}
function HasAllValidConstraintAllOfFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "type": !exists(json, "type") ? void 0 : json["type"]
  };
}
function HasAllValidConstraintAllOfToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "type": value.type
  };
}

// build/models/HasDataConstraintAllOf.ts
var HasDataConstraintAllOfTypeEnum = {
  HasData: "hasData"
};
function instanceOfHasDataConstraintAllOf(value) {
  let isInstance = true;
  return isInstance;
}
function HasDataConstraintAllOfFromJSON(json) {
  return HasDataConstraintAllOfFromJSONTyped(json, false);
}
function HasDataConstraintAllOfFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "type": !exists(json, "type") ? void 0 : json["type"]
  };
}
function HasDataConstraintAllOfToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "type": value.type
  };
}

// build/models/HasSelectionConstraintAllOf.ts
var HasSelectionConstraintAllOfTypeEnum = {
  HasSelection: "hasSelection"
};
function instanceOfHasSelectionConstraintAllOf(value) {
  let isInstance = true;
  return isInstance;
}
function HasSelectionConstraintAllOfFromJSON(json) {
  return HasSelectionConstraintAllOfFromJSONTyped(json, false);
}
function HasSelectionConstraintAllOfFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "type": !exists(json, "type") ? void 0 : json["type"]
  };
}
function HasSelectionConstraintAllOfToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "type": value.type
  };
}

// build/models/HealthResponse.ts
function instanceOfHealthResponse(value) {
  let isInstance = true;
  return isInstance;
}
function HealthResponseFromJSON(json) {
  return HealthResponseFromJSONTyped(json, false);
}
function HealthResponseFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "status": !exists(json, "status") ? void 0 : json["status"]
  };
}
function HealthResponseToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "status": value.status
  };
}

// build/models/InviteGuestsRequestInner.ts
function instanceOfInviteGuestsRequestInner(value) {
  let isInstance = true;
  isInstance = isInstance && "guestId" in value;
  isInstance = isInstance && "spaceId" in value;
  return isInstance;
}
function InviteGuestsRequestInnerFromJSON(json) {
  return InviteGuestsRequestInnerFromJSONTyped(json, false);
}
function InviteGuestsRequestInnerFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "guestId": json["guestId"],
    "spaceId": json["spaceId"],
    "fromName": !exists(json, "fromName") ? void 0 : json["fromName"],
    "message": !exists(json, "message") ? void 0 : json["message"]
  };
}
function InviteGuestsRequestInnerToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "guestId": value.guestId,
    "spaceId": value.spaceId,
    "fromName": value.fromName,
    "message": value.message
  };
}

// build/models/MappingProgramJobConfig.ts
function instanceOfMappingProgramJobConfig(value) {
  let isInstance = true;
  isInstance = isInstance && "sourceSheetId" in value;
  isInstance = isInstance && "destinationSheetId" in value;
  isInstance = isInstance && "mappingRules" in value;
  return isInstance;
}
function MappingProgramJobConfigFromJSON(json) {
  return MappingProgramJobConfigFromJSONTyped(json, false);
}
function MappingProgramJobConfigFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "sourceSheetId": json["sourceSheetId"],
    "destinationSheetId": json["destinationSheetId"],
    "mappingRules": json["mappingRules"]
  };
}
function MappingProgramJobConfigToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "sourceSheetId": value.sourceSheetId,
    "destinationSheetId": value.destinationSheetId,
    "mappingRules": value.mappingRules
  };
}

// build/models/MutateJobConfig.ts
function instanceOfMutateJobConfig(value) {
  let isInstance = true;
  isInstance = isInstance && "sheetId" in value;
  isInstance = isInstance && "mutateRecord" in value;
  return isInstance;
}
function MutateJobConfigFromJSON(json) {
  return MutateJobConfigFromJSONTyped(json, false);
}
function MutateJobConfigFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "sheetId": json["sheetId"],
    "snapshotLabel": !exists(json, "snapshotLabel") ? void 0 : json["snapshotLabel"],
    "snapshotId": !exists(json, "snapshotId") ? void 0 : json["snapshotId"],
    "mutateRecord": json["mutateRecord"],
    "mutationId": !exists(json, "mutationId") ? void 0 : json["mutationId"],
    "filter": !exists(json, "filter") ? void 0 : FilterFromJSON(json["filter"]),
    "filterField": !exists(json, "filterField") ? void 0 : json["filterField"],
    "searchValue": !exists(json, "searchValue") ? void 0 : json["searchValue"],
    "searchField": !exists(json, "searchField") ? void 0 : json["searchField"],
    "q": !exists(json, "q") ? void 0 : json["q"],
    "ids": !exists(json, "ids") ? void 0 : json["ids"]
  };
}
function MutateJobConfigToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "sheetId": value.sheetId,
    "snapshotLabel": value.snapshotLabel,
    "snapshotId": value.snapshotId,
    "mutateRecord": value.mutateRecord,
    "mutationId": value.mutationId,
    "filter": FilterToJSON(value.filter),
    "filterField": value.filterField,
    "searchValue": value.searchValue,
    "searchField": value.searchField,
    "q": value.q,
    "ids": value.ids
  };
}

// build/models/PipelineJobConfig.ts
function instanceOfPipelineJobConfig(value) {
  let isInstance = true;
  isInstance = isInstance && "sourceSheetId" in value;
  isInstance = isInstance && "destinationSheetId" in value;
  return isInstance;
}
function PipelineJobConfigFromJSON(json) {
  return PipelineJobConfigFromJSONTyped(json, false);
}
function PipelineJobConfigFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "sourceSheetId": json["sourceSheetId"],
    "destinationSheetId": json["destinationSheetId"]
  };
}
function PipelineJobConfigToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "sourceSheetId": value.sourceSheetId,
    "destinationSheetId": value.destinationSheetId
  };
}

// build/models/JobConfigConfig.ts
function JobConfigConfigFromJSON(json) {
  return JobConfigConfigFromJSONTyped(json, false);
}
function JobConfigConfigFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return { ...DeleteRecordsJobConfigFromJSONTyped(json, true), ...ExportJobConfigFromJSONTyped(json, true), ...FileJobConfigFromJSONTyped(json, true), ...FindAndReplaceJobConfigFromJSONTyped(json, true), ...MappingProgramJobConfigFromJSONTyped(json, true), ...MutateJobConfigFromJSONTyped(json, true), ...PipelineJobConfigFromJSONTyped(json, true) };
}
function JobConfigConfigToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  if (instanceOfDeleteRecordsJobConfig(value)) {
    return DeleteRecordsJobConfigToJSON(value);
  }
  if (instanceOfExportJobConfig(value)) {
    return ExportJobConfigToJSON(value);
  }
  if (instanceOfFileJobConfig(value)) {
    return FileJobConfigToJSON(value);
  }
  if (instanceOfFindAndReplaceJobConfig(value)) {
    return FindAndReplaceJobConfigToJSON(value);
  }
  if (instanceOfMappingProgramJobConfig(value)) {
    return MappingProgramJobConfigToJSON(value);
  }
  if (instanceOfMutateJobConfig(value)) {
    return MutateJobConfigToJSON(value);
  }
  if (instanceOfPipelineJobConfig(value)) {
    return PipelineJobConfigToJSON(value);
  }
  return {};
}

// build/models/JobOutcomeNextDownload.ts
var JobOutcomeNextDownloadTypeEnum = {
  Download: "download"
};
function instanceOfJobOutcomeNextDownload(value) {
  let isInstance = true;
  isInstance = isInstance && "type" in value;
  isInstance = isInstance && "url" in value;
  return isInstance;
}
function JobOutcomeNextDownloadFromJSON(json) {
  return JobOutcomeNextDownloadFromJSONTyped(json, false);
}
function JobOutcomeNextDownloadFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "type": json["type"],
    "url": json["url"],
    "label": !exists(json, "label") ? void 0 : json["label"],
    "fileName": !exists(json, "fileName") ? void 0 : json["fileName"]
  };
}
function JobOutcomeNextDownloadToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "type": value.type,
    "url": value.url,
    "label": value.label,
    "fileName": value.fileName
  };
}

// build/models/JobOutcomeNextFilesFilesInner.ts
function instanceOfJobOutcomeNextFilesFilesInner(value) {
  let isInstance = true;
  isInstance = isInstance && "fileId" in value;
  return isInstance;
}
function JobOutcomeNextFilesFilesInnerFromJSON(json) {
  return JobOutcomeNextFilesFilesInnerFromJSONTyped(json, false);
}
function JobOutcomeNextFilesFilesInnerFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "fileId": json["fileId"],
    "fileName": !exists(json, "fileName") ? void 0 : json["fileName"]
  };
}
function JobOutcomeNextFilesFilesInnerToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "fileId": value.fileId,
    "fileName": value.fileName
  };
}

// build/models/JobOutcomeNextFiles.ts
var JobOutcomeNextFilesTypeEnum = {
  Files: "files"
};
function instanceOfJobOutcomeNextFiles(value) {
  let isInstance = true;
  isInstance = isInstance && "type" in value;
  return isInstance;
}
function JobOutcomeNextFilesFromJSON(json) {
  return JobOutcomeNextFilesFromJSONTyped(json, false);
}
function JobOutcomeNextFilesFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "type": json["type"],
    "files": !exists(json, "files") ? void 0 : json["files"].map(JobOutcomeNextFilesFilesInnerFromJSON),
    "label": !exists(json, "label") ? void 0 : json["label"]
  };
}
function JobOutcomeNextFilesToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "type": value.type,
    "files": value.files === void 0 ? void 0 : value.files.map(JobOutcomeNextFilesFilesInnerToJSON),
    "label": value.label
  };
}

// build/models/JobOutcomeNextId.ts
var JobOutcomeNextIdTypeEnum = {
  Id: "id"
};
function instanceOfJobOutcomeNextId(value) {
  let isInstance = true;
  isInstance = isInstance && "type" in value;
  isInstance = isInstance && "id" in value;
  return isInstance;
}
function JobOutcomeNextIdFromJSON(json) {
  return JobOutcomeNextIdFromJSONTyped(json, false);
}
function JobOutcomeNextIdFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "type": json["type"],
    "id": json["id"],
    "label": !exists(json, "label") ? void 0 : json["label"],
    "path": !exists(json, "path") ? void 0 : json["path"],
    "query": !exists(json, "query") ? void 0 : json["query"]
  };
}
function JobOutcomeNextIdToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "type": value.type,
    "id": value.id,
    "label": value.label,
    "path": value.path,
    "query": value.query
  };
}

// build/models/JobOutcomeNextRetry.ts
var JobOutcomeNextRetryTypeEnum = {
  Retry: "retry"
};
function instanceOfJobOutcomeNextRetry(value) {
  let isInstance = true;
  isInstance = isInstance && "type" in value;
  return isInstance;
}
function JobOutcomeNextRetryFromJSON(json) {
  return JobOutcomeNextRetryFromJSONTyped(json, false);
}
function JobOutcomeNextRetryFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "type": json["type"],
    "label": !exists(json, "label") ? void 0 : json["label"]
  };
}
function JobOutcomeNextRetryToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "type": value.type,
    "label": value.label
  };
}

// build/models/JobOutcomeNextSnapshot.ts
var JobOutcomeNextSnapshotTypeEnum = {
  Snapshot: "snapshot"
};
function instanceOfJobOutcomeNextSnapshot(value) {
  let isInstance = true;
  isInstance = isInstance && "type" in value;
  isInstance = isInstance && "snapshotId" in value;
  isInstance = isInstance && "sheetId" in value;
  return isInstance;
}
function JobOutcomeNextSnapshotFromJSON(json) {
  return JobOutcomeNextSnapshotFromJSONTyped(json, false);
}
function JobOutcomeNextSnapshotFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "type": json["type"],
    "snapshotId": json["snapshotId"],
    "sheetId": json["sheetId"]
  };
}
function JobOutcomeNextSnapshotToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "type": value.type,
    "snapshotId": value.snapshotId,
    "sheetId": value.sheetId
  };
}

// build/models/JobOutcomeNextUrl.ts
var JobOutcomeNextUrlTypeEnum = {
  Url: "url"
};
function instanceOfJobOutcomeNextUrl(value) {
  let isInstance = true;
  isInstance = isInstance && "type" in value;
  isInstance = isInstance && "url" in value;
  return isInstance;
}
function JobOutcomeNextUrlFromJSON(json) {
  return JobOutcomeNextUrlFromJSONTyped(json, false);
}
function JobOutcomeNextUrlFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "type": json["type"],
    "url": json["url"],
    "label": !exists(json, "label") ? void 0 : json["label"]
  };
}
function JobOutcomeNextUrlToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "type": value.type,
    "url": value.url,
    "label": value.label
  };
}

// build/models/JobOutcomeNextView.ts
var JobOutcomeNextViewTypeEnum = {
  View: "view"
};
function instanceOfJobOutcomeNextView(value) {
  let isInstance = true;
  isInstance = isInstance && "type" in value;
  isInstance = isInstance && "hiddenColumns" in value;
  isInstance = isInstance && "sheetId" in value;
  return isInstance;
}
function JobOutcomeNextViewFromJSON(json) {
  return JobOutcomeNextViewFromJSONTyped(json, false);
}
function JobOutcomeNextViewFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "type": json["type"],
    "label": !exists(json, "label") ? void 0 : json["label"],
    "hiddenColumns": json["hiddenColumns"],
    "sheetId": json["sheetId"]
  };
}
function JobOutcomeNextViewToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "type": value.type,
    "label": value.label,
    "hiddenColumns": value.hiddenColumns,
    "sheetId": value.sheetId
  };
}

// build/models/JobOutcomeNextWait.ts
var JobOutcomeNextWaitTypeEnum = {
  Wait: "wait"
};
function instanceOfJobOutcomeNextWait(value) {
  let isInstance = true;
  isInstance = isInstance && "type" in value;
  return isInstance;
}
function JobOutcomeNextWaitFromJSON(json) {
  return JobOutcomeNextWaitFromJSONTyped(json, false);
}
function JobOutcomeNextWaitFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "type": json["type"]
  };
}
function JobOutcomeNextWaitToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "type": value.type
  };
}

// build/models/JobOutcomeNext.ts
function JobOutcomeNextFromJSON(json) {
  return JobOutcomeNextFromJSONTyped(json, false);
}
function JobOutcomeNextFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return { ...JobOutcomeNextDownloadFromJSONTyped(json, true), ...JobOutcomeNextFilesFromJSONTyped(json, true), ...JobOutcomeNextIdFromJSONTyped(json, true), ...JobOutcomeNextRetryFromJSONTyped(json, true), ...JobOutcomeNextSnapshotFromJSONTyped(json, true), ...JobOutcomeNextUrlFromJSONTyped(json, true), ...JobOutcomeNextViewFromJSONTyped(json, true), ...JobOutcomeNextWaitFromJSONTyped(json, true) };
}
function JobOutcomeNextToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  if (instanceOfJobOutcomeNextDownload(value)) {
    return JobOutcomeNextDownloadToJSON(value);
  }
  if (instanceOfJobOutcomeNextFiles(value)) {
    return JobOutcomeNextFilesToJSON(value);
  }
  if (instanceOfJobOutcomeNextId(value)) {
    return JobOutcomeNextIdToJSON(value);
  }
  if (instanceOfJobOutcomeNextRetry(value)) {
    return JobOutcomeNextRetryToJSON(value);
  }
  if (instanceOfJobOutcomeNextSnapshot(value)) {
    return JobOutcomeNextSnapshotToJSON(value);
  }
  if (instanceOfJobOutcomeNextUrl(value)) {
    return JobOutcomeNextUrlToJSON(value);
  }
  if (instanceOfJobOutcomeNextView(value)) {
    return JobOutcomeNextViewToJSON(value);
  }
  if (instanceOfJobOutcomeNextWait(value)) {
    return JobOutcomeNextWaitToJSON(value);
  }
  return {};
}

// build/models/JobOutcomeTrigger.ts
var JobOutcomeTrigger = {
  Manual: "manual",
  Automatic: "automatic",
  AutomaticSilent: "automatic_silent"
};
function JobOutcomeTriggerFromJSON(json) {
  return JobOutcomeTriggerFromJSONTyped(json, false);
}
function JobOutcomeTriggerFromJSONTyped(json, ignoreDiscriminator) {
  return json;
}
function JobOutcomeTriggerToJSON(value) {
  return value;
}

// build/models/JobOutcome.ts
function instanceOfJobOutcome(value) {
  let isInstance = true;
  return isInstance;
}
function JobOutcomeFromJSON(json) {
  return JobOutcomeFromJSONTyped(json, false);
}
function JobOutcomeFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "heading": !exists(json, "heading") ? void 0 : json["heading"],
    "acknowledge": !exists(json, "acknowledge") ? void 0 : json["acknowledge"],
    "trigger": !exists(json, "trigger") ? void 0 : JobOutcomeTriggerFromJSON(json["trigger"]),
    "message": !exists(json, "message") ? void 0 : json["message"],
    "buttonText": !exists(json, "buttonText") ? void 0 : json["buttonText"],
    "hideDefaultButton": !exists(json, "hideDefaultButton") ? void 0 : json["hideDefaultButton"],
    "next": !exists(json, "next") ? void 0 : JobOutcomeNextFromJSON(json["next"])
  };
}
function JobOutcomeToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "heading": value.heading,
    "acknowledge": value.acknowledge,
    "trigger": JobOutcomeTriggerToJSON(value.trigger),
    "message": value.message,
    "buttonText": value.buttonText,
    "hideDefaultButton": value.hideDefaultButton,
    "next": JobOutcomeNextToJSON(value.next)
  };
}

// build/models/ResourceJobSubject.ts
function instanceOfResourceJobSubject(value) {
  let isInstance = true;
  isInstance = isInstance && "id" in value;
  isInstance = isInstance && "type" in value;
  return isInstance;
}
function ResourceJobSubjectFromJSON(json) {
  return ResourceJobSubjectFromJSONTyped(json, false);
}
function ResourceJobSubjectFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "id": json["id"],
    "type": JobSubjectTypeFromJSON(json["type"])
  };
}
function ResourceJobSubjectToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "id": value.id,
    "type": JobSubjectTypeToJSON(value.type)
  };
}

// build/models/JobSubject.ts
function JobSubjectFromJSON(json) {
  return JobSubjectFromJSONTyped(json, false);
}
function JobSubjectFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  switch (json["type"]) {
    case "collection":
      return { ...CollectionJobSubjectFromJSONTyped(json, true), type: "collection" };
    case "resource":
      return { ...ResourceJobSubjectFromJSONTyped(json, true), type: "resource" };
    default:
      throw new Error(`No variant of JobSubject exists with 'type=${json["type"]}'`);
  }
}
function JobSubjectToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  switch (value["type"]) {
    case "collection":
      return CollectionJobSubjectToJSON(value);
    case "resource":
      return ResourceJobSubjectToJSON(value);
    default:
      throw new Error(`No variant of JobSubject exists with 'type=${value["type"]}'`);
  }
}

// build/models/Job.ts
var JobTypeEnum = {
  File: "file",
  Workbook: "workbook",
  Sheet: "sheet",
  Space: "space",
  Document: "document"
};
var JobTriggerEnum = {
  Manual: "manual",
  Immediate: "immediate"
};
var JobStatusEnum = {
  Created: "created",
  Planning: "planning",
  Scheduled: "scheduled",
  Ready: "ready",
  Executing: "executing",
  Complete: "complete",
  Failed: "failed",
  Canceled: "canceled",
  Waiting: "waiting"
};
var JobModeEnum = {
  Foreground: "foreground",
  Background: "background",
  ToolbarBlocking: "toolbarBlocking"
};
var JobPartExecutionEnum = {
  Sequential: "sequential",
  Parallel: "parallel"
};
function instanceOfJob(value) {
  let isInstance = true;
  isInstance = isInstance && "id" in value;
  isInstance = isInstance && "createdAt" in value;
  isInstance = isInstance && "updatedAt" in value;
  isInstance = isInstance && "type" in value;
  isInstance = isInstance && "operation" in value;
  isInstance = isInstance && "source" in value;
  return isInstance;
}
function JobFromJSON(json) {
  return JobFromJSONTyped(json, false);
}
function JobFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "id": json["id"],
    "createdAt": new Date(json["createdAt"]),
    "updatedAt": new Date(json["updatedAt"]),
    "startedAt": !exists(json, "startedAt") ? void 0 : json["startedAt"] === null ? null : new Date(json["startedAt"]),
    "finishedAt": !exists(json, "finishedAt") ? void 0 : json["finishedAt"] === null ? null : new Date(json["finishedAt"]),
    "estimatedCompletionAt": !exists(json, "estimatedCompletionAt") ? void 0 : json["estimatedCompletionAt"] === null ? null : new Date(json["estimatedCompletionAt"]),
    "outcomeAcknowledgedAt": !exists(json, "outcomeAcknowledgedAt") ? void 0 : json["outcomeAcknowledgedAt"] === null ? null : new Date(json["outcomeAcknowledgedAt"]),
    "type": json["type"],
    "fromAction": !exists(json, "fromAction") ? void 0 : ActionFromJSON(json["fromAction"]),
    "operation": json["operation"],
    "source": json["source"],
    "destination": !exists(json, "destination") ? void 0 : json["destination"],
    "input": !exists(json, "input") ? void 0 : json["input"],
    "subject": !exists(json, "subject") ? void 0 : JobSubjectFromJSON(json["subject"]),
    "outcome": !exists(json, "outcome") ? void 0 : JobOutcomeFromJSON(json["outcome"]),
    "info": !exists(json, "info") ? void 0 : json["info"],
    "managed": !exists(json, "managed") ? void 0 : json["managed"],
    "config": !exists(json, "config") ? void 0 : JobConfigConfigFromJSON(json["config"]),
    "trigger": !exists(json, "trigger") ? void 0 : json["trigger"],
    "status": !exists(json, "status") ? void 0 : json["status"],
    "mode": !exists(json, "mode") ? void 0 : json["mode"],
    "progress": !exists(json, "progress") ? void 0 : json["progress"],
    "parentId": !exists(json, "parentId") ? void 0 : json["parentId"],
    "fileId": !exists(json, "fileId") ? void 0 : json["fileId"],
    "environmentId": !exists(json, "environmentId") ? void 0 : json["environmentId"],
    "part": !exists(json, "part") ? void 0 : json["part"],
    "partData": !exists(json, "partData") ? void 0 : json["partData"],
    "partExecution": !exists(json, "partExecution") ? void 0 : json["partExecution"],
    "predecessorIds": !exists(json, "predecessorIds") ? void 0 : json["predecessorIds"]
  };
}
function JobToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "id": value.id,
    "createdAt": value.createdAt.toISOString(),
    "updatedAt": value.updatedAt.toISOString(),
    "startedAt": value.startedAt === void 0 ? void 0 : value.startedAt === null ? null : value.startedAt.toISOString(),
    "finishedAt": value.finishedAt === void 0 ? void 0 : value.finishedAt === null ? null : value.finishedAt.toISOString(),
    "estimatedCompletionAt": value.estimatedCompletionAt === void 0 ? void 0 : value.estimatedCompletionAt === null ? null : value.estimatedCompletionAt.toISOString(),
    "outcomeAcknowledgedAt": value.outcomeAcknowledgedAt === void 0 ? void 0 : value.outcomeAcknowledgedAt === null ? null : value.outcomeAcknowledgedAt.toISOString(),
    "type": value.type,
    "fromAction": ActionToJSON(value.fromAction),
    "operation": value.operation,
    "source": value.source,
    "destination": value.destination,
    "input": value.input,
    "subject": JobSubjectToJSON(value.subject),
    "outcome": JobOutcomeToJSON(value.outcome),
    "info": value.info,
    "managed": value.managed,
    "config": JobConfigConfigToJSON(value.config),
    "trigger": value.trigger,
    "status": value.status,
    "mode": value.mode,
    "progress": value.progress,
    "parentId": value.parentId,
    "fileId": value.fileId,
    "environmentId": value.environmentId,
    "part": value.part,
    "partData": value.partData,
    "partExecution": value.partExecution,
    "predecessorIds": value.predecessorIds
  };
}

// build/models/JobAckDetails.ts
function instanceOfJobAckDetails(value) {
  let isInstance = true;
  return isInstance;
}
function JobAckDetailsFromJSON(json) {
  return JobAckDetailsFromJSONTyped(json, false);
}
function JobAckDetailsFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "info": !exists(json, "info") ? void 0 : json["info"],
    "progress": !exists(json, "progress") ? void 0 : json["progress"],
    "estimatedCompletionAt": !exists(json, "estimatedCompletionAt") ? void 0 : new Date(json["estimatedCompletionAt"])
  };
}
function JobAckDetailsToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "info": value.info,
    "progress": value.progress,
    "estimatedCompletionAt": value.estimatedCompletionAt === void 0 ? void 0 : value.estimatedCompletionAt.toISOString()
  };
}

// build/models/JobCancelDetails.ts
function instanceOfJobCancelDetails(value) {
  let isInstance = true;
  return isInstance;
}
function JobCancelDetailsFromJSON(json) {
  return JobCancelDetailsFromJSONTyped(json, false);
}
function JobCancelDetailsFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "info": !exists(json, "info") ? void 0 : json["info"]
  };
}
function JobCancelDetailsToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "info": value.info
  };
}

// build/models/JobCompleteDetails.ts
function instanceOfJobCompleteDetails(value) {
  let isInstance = true;
  return isInstance;
}
function JobCompleteDetailsFromJSON(json) {
  return JobCompleteDetailsFromJSONTyped(json, false);
}
function JobCompleteDetailsFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "outcome": !exists(json, "outcome") ? void 0 : JobOutcomeFromJSON(json["outcome"]),
    "info": !exists(json, "info") ? void 0 : json["info"]
  };
}
function JobCompleteDetailsToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "outcome": JobOutcomeToJSON(value.outcome),
    "info": value.info
  };
}

// build/models/JobConfig.ts
var JobConfigTypeEnum = {
  File: "file",
  Workbook: "workbook",
  Sheet: "sheet",
  Space: "space",
  Document: "document"
};
var JobConfigTriggerEnum = {
  Manual: "manual",
  Immediate: "immediate"
};
var JobConfigStatusEnum = {
  Created: "created",
  Planning: "planning",
  Scheduled: "scheduled",
  Ready: "ready",
  Executing: "executing",
  Complete: "complete",
  Failed: "failed",
  Canceled: "canceled",
  Waiting: "waiting"
};
var JobConfigModeEnum = {
  Foreground: "foreground",
  Background: "background",
  ToolbarBlocking: "toolbarBlocking"
};
var JobConfigPartExecutionEnum = {
  Sequential: "sequential",
  Parallel: "parallel"
};
function instanceOfJobConfig(value) {
  let isInstance = true;
  isInstance = isInstance && "type" in value;
  isInstance = isInstance && "operation" in value;
  isInstance = isInstance && "source" in value;
  return isInstance;
}
function JobConfigFromJSON(json) {
  return JobConfigFromJSONTyped(json, false);
}
function JobConfigFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "type": json["type"],
    "fromAction": !exists(json, "fromAction") ? void 0 : ActionFromJSON(json["fromAction"]),
    "operation": json["operation"],
    "source": json["source"],
    "destination": !exists(json, "destination") ? void 0 : json["destination"],
    "input": !exists(json, "input") ? void 0 : json["input"],
    "subject": !exists(json, "subject") ? void 0 : JobSubjectFromJSON(json["subject"]),
    "outcome": !exists(json, "outcome") ? void 0 : JobOutcomeFromJSON(json["outcome"]),
    "info": !exists(json, "info") ? void 0 : json["info"],
    "managed": !exists(json, "managed") ? void 0 : json["managed"],
    "config": !exists(json, "config") ? void 0 : JobConfigConfigFromJSON(json["config"]),
    "trigger": !exists(json, "trigger") ? void 0 : json["trigger"],
    "status": !exists(json, "status") ? void 0 : json["status"],
    "mode": !exists(json, "mode") ? void 0 : json["mode"],
    "progress": !exists(json, "progress") ? void 0 : json["progress"],
    "parentId": !exists(json, "parentId") ? void 0 : json["parentId"],
    "fileId": !exists(json, "fileId") ? void 0 : json["fileId"],
    "environmentId": !exists(json, "environmentId") ? void 0 : json["environmentId"],
    "part": !exists(json, "part") ? void 0 : json["part"],
    "partData": !exists(json, "partData") ? void 0 : json["partData"],
    "partExecution": !exists(json, "partExecution") ? void 0 : json["partExecution"],
    "predecessorIds": !exists(json, "predecessorIds") ? void 0 : json["predecessorIds"]
  };
}
function JobConfigToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "type": value.type,
    "fromAction": ActionToJSON(value.fromAction),
    "operation": value.operation,
    "source": value.source,
    "destination": value.destination,
    "input": value.input,
    "subject": JobSubjectToJSON(value.subject),
    "outcome": JobOutcomeToJSON(value.outcome),
    "info": value.info,
    "managed": value.managed,
    "config": JobConfigConfigToJSON(value.config),
    "trigger": value.trigger,
    "status": value.status,
    "mode": value.mode,
    "progress": value.progress,
    "parentId": value.parentId,
    "fileId": value.fileId,
    "environmentId": value.environmentId,
    "part": value.part,
    "partData": value.partData,
    "partExecution": value.partExecution,
    "predecessorIds": value.predecessorIds
  };
}

// build/models/JobOutcomeNextType.ts
var JobOutcomeNextType = {
  Id: "id",
  Url: "url",
  Download: "download",
  Files: "files",
  Wait: "wait",
  Snapshot: "snapshot",
  Retry: "retry",
  View: "view"
};
function JobOutcomeNextTypeFromJSON(json) {
  return JobOutcomeNextTypeFromJSONTyped(json, false);
}
function JobOutcomeNextTypeFromJSONTyped(json, ignoreDiscriminator) {
  return json;
}
function JobOutcomeNextTypeToJSON(value) {
  return value;
}

// build/models/SourceField.ts
function instanceOfSourceField(value) {
  let isInstance = true;
  isInstance = isInstance && "sourceField" in value;
  return isInstance;
}
function SourceFieldFromJSON(json) {
  return SourceFieldFromJSONTyped(json, false);
}
function SourceFieldFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "sourceField": FieldConfigFromJSON(json["sourceField"]),
    "preview": !exists(json, "preview") ? void 0 : json["preview"]
  };
}
function SourceFieldToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "sourceField": FieldConfigToJSON(value.sourceField),
    "preview": value.preview
  };
}

// build/models/Plan.ts
function instanceOfPlan(value) {
  let isInstance = true;
  isInstance = isInstance && "fieldMapping" in value;
  isInstance = isInstance && "unmappedSourceFields" in value;
  isInstance = isInstance && "unmappedDestinationFields" in value;
  return isInstance;
}
function PlanFromJSON(json) {
  return PlanFromJSONTyped(json, false);
}
function PlanFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "fieldMapping": json["fieldMapping"].map(EdgeFromJSON),
    "unmappedSourceFields": json["unmappedSourceFields"].map(SourceFieldFromJSON),
    "unmappedDestinationFields": json["unmappedDestinationFields"].map(DestinationFieldFromJSON),
    "programId": !exists(json, "programId") ? void 0 : json["programId"]
  };
}
function PlanToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "fieldMapping": value.fieldMapping.map(EdgeToJSON),
    "unmappedSourceFields": value.unmappedSourceFields.map(SourceFieldToJSON),
    "unmappedDestinationFields": value.unmappedDestinationFields.map(DestinationFieldToJSON),
    "programId": value.programId
  };
}

// build/models/JobPlan.ts
function instanceOfJobPlan(value) {
  let isInstance = true;
  isInstance = isInstance && "job" in value;
  isInstance = isInstance && "plan" in value;
  return isInstance;
}
function JobPlanFromJSON(json) {
  return JobPlanFromJSONTyped(json, false);
}
function JobPlanFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "job": JobFromJSON(json["job"]),
    "plan": PlanFromJSON(json["plan"])
  };
}
function JobPlanToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "job": JobToJSON(value.job),
    "plan": PlanToJSON(value.plan)
  };
}

// build/models/JobPlanResponse.ts
function instanceOfJobPlanResponse(value) {
  let isInstance = true;
  return isInstance;
}
function JobPlanResponseFromJSON(json) {
  return JobPlanResponseFromJSONTyped(json, false);
}
function JobPlanResponseFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "data": !exists(json, "data") ? void 0 : JobPlanFromJSON(json["data"])
  };
}
function JobPlanResponseToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "data": JobPlanToJSON(value.data)
  };
}

// build/models/JobResponse.ts
function instanceOfJobResponse(value) {
  let isInstance = true;
  return isInstance;
}
function JobResponseFromJSON(json) {
  return JobResponseFromJSONTyped(json, false);
}
function JobResponseFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "data": !exists(json, "data") ? void 0 : JobFromJSON(json["data"])
  };
}
function JobResponseToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "data": JobToJSON(value.data)
  };
}

// build/models/JobSplitDetailsParts.ts
function instanceOfJobSplitDetailsParts(value) {
  let isInstance = true;
  return isInstance;
}
function JobSplitDetailsPartsFromJSON(json) {
  return JobSplitDetailsPartsFromJSONTyped(json, false);
}
function JobSplitDetailsPartsFromJSONTyped(json, ignoreDiscriminator) {
  return json;
}
function JobSplitDetailsPartsToJSON(value) {
  return value;
}

// build/models/JobSplitDetails.ts
function instanceOfJobSplitDetails(value) {
  let isInstance = true;
  isInstance = isInstance && "parts" in value;
  return isInstance;
}
function JobSplitDetailsFromJSON(json) {
  return JobSplitDetailsFromJSONTyped(json, false);
}
function JobSplitDetailsFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "runInParallel": !exists(json, "runInParallel") ? void 0 : json["runInParallel"],
    "parts": JobSplitDetailsPartsFromJSON(json["parts"])
  };
}
function JobSplitDetailsToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "runInParallel": value.runInParallel,
    "parts": JobSplitDetailsPartsToJSON(value.parts)
  };
}

// build/models/JobStatsAccounts.ts
function instanceOfJobStatsAccounts(value) {
  let isInstance = true;
  return isInstance;
}
function JobStatsAccountsFromJSON(json) {
  return JobStatsAccountsFromJSONTyped(json, false);
}
function JobStatsAccountsFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "fewestAbandonedJobs": !exists(json, "fewestAbandonedJobs") ? void 0 : json["fewestAbandonedJobs"],
    "mostAbandonedJobs": !exists(json, "mostAbandonedJobs") ? void 0 : json["mostAbandonedJobs"]
  };
}
function JobStatsAccountsToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "fewestAbandonedJobs": value.fewestAbandonedJobs,
    "mostAbandonedJobs": value.mostAbandonedJobs
  };
}

// build/models/JobStatsJobsMap.ts
function instanceOfJobStatsJobsMap(value) {
  let isInstance = true;
  isInstance = isInstance && "created" in value;
  isInstance = isInstance && "completed" in value;
  isInstance = isInstance && "automated" in value;
  isInstance = isInstance && "failed" in value;
  isInstance = isInstance && "correctionsAverage" in value;
  return isInstance;
}
function JobStatsJobsMapFromJSON(json) {
  return JobStatsJobsMapFromJSONTyped(json, false);
}
function JobStatsJobsMapFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "created": json["created"],
    "completed": json["completed"],
    "automated": json["automated"],
    "failed": json["failed"],
    "correctionsAverage": json["correctionsAverage"]
  };
}
function JobStatsJobsMapToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "created": value.created,
    "completed": value.completed,
    "automated": value.automated,
    "failed": value.failed,
    "correctionsAverage": value.correctionsAverage
  };
}

// build/models/JobStatsJobs.ts
function instanceOfJobStatsJobs(value) {
  let isInstance = true;
  isInstance = isInstance && "map" in value;
  return isInstance;
}
function JobStatsJobsFromJSON(json) {
  return JobStatsJobsFromJSONTyped(json, false);
}
function JobStatsJobsFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "map": JobStatsJobsMapFromJSON(json["map"])
  };
}
function JobStatsJobsToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "map": JobStatsJobsMapToJSON(value.map)
  };
}

// build/models/JobStats.ts
function instanceOfJobStats(value) {
  let isInstance = true;
  return isInstance;
}
function JobStatsFromJSON(json) {
  return JobStatsFromJSONTyped(json, false);
}
function JobStatsFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "start": !exists(json, "start") ? void 0 : new Date(json["start"]),
    "end": !exists(json, "end") ? void 0 : new Date(json["end"]),
    "jobs": !exists(json, "jobs") ? void 0 : JobStatsJobsFromJSON(json["jobs"]),
    "accounts": !exists(json, "accounts") ? void 0 : JobStatsAccountsFromJSON(json["accounts"])
  };
}
function JobStatsToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "start": value.start === void 0 ? void 0 : value.start.toISOString(),
    "end": value.end === void 0 ? void 0 : value.end.toISOString(),
    "jobs": JobStatsJobsToJSON(value.jobs),
    "accounts": JobStatsAccountsToJSON(value.accounts)
  };
}

// build/models/JobStatsResponse.ts
function instanceOfJobStatsResponse(value) {
  let isInstance = true;
  return isInstance;
}
function JobStatsResponseFromJSON(json) {
  return JobStatsResponseFromJSONTyped(json, false);
}
function JobStatsResponseFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "data": !exists(json, "data") ? void 0 : JobStatsFromJSON(json["data"])
  };
}
function JobStatsResponseToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "data": JobStatsToJSON(value.data)
  };
}

// build/models/JobUpdateConfig.ts
function JobUpdateConfigFromJSON(json) {
  return JobUpdateConfigFromJSONTyped(json, false);
}
function JobUpdateConfigFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return { ...DeleteRecordsJobConfigFromJSONTyped(json, true), ...ExportJobConfigFromJSONTyped(json, true), ...FileJobConfigFromJSONTyped(json, true), ...FindAndReplaceJobConfigFromJSONTyped(json, true), ...MappingProgramJobConfigFromJSONTyped(json, true), ...MutateJobConfigFromJSONTyped(json, true), ...PipelineJobConfigFromJSONTyped(json, true) };
}
function JobUpdateConfigToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  if (instanceOfDeleteRecordsJobConfig(value)) {
    return DeleteRecordsJobConfigToJSON(value);
  }
  if (instanceOfExportJobConfig(value)) {
    return ExportJobConfigToJSON(value);
  }
  if (instanceOfFileJobConfig(value)) {
    return FileJobConfigToJSON(value);
  }
  if (instanceOfFindAndReplaceJobConfig(value)) {
    return FindAndReplaceJobConfigToJSON(value);
  }
  if (instanceOfMappingProgramJobConfig(value)) {
    return MappingProgramJobConfigToJSON(value);
  }
  if (instanceOfMutateJobConfig(value)) {
    return MutateJobConfigToJSON(value);
  }
  if (instanceOfPipelineJobConfig(value)) {
    return PipelineJobConfigToJSON(value);
  }
  return {};
}

// build/models/JobUpdate.ts
var JobUpdateStatusEnum = {
  Created: "created",
  Planning: "planning",
  Ready: "ready",
  Executing: "executing",
  Complete: "complete",
  Failed: "failed",
  Waiting: "waiting"
};
function instanceOfJobUpdate(value) {
  let isInstance = true;
  return isInstance;
}
function JobUpdateFromJSON(json) {
  return JobUpdateFromJSONTyped(json, false);
}
function JobUpdateFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "config": !exists(json, "config") ? void 0 : JobUpdateConfigFromJSON(json["config"]),
    "status": !exists(json, "status") ? void 0 : json["status"],
    "progress": !exists(json, "progress") ? void 0 : json["progress"],
    "outcomeAcknowledgedAt": !exists(json, "outcomeAcknowledgedAt") ? void 0 : json["outcomeAcknowledgedAt"] === null ? null : new Date(json["outcomeAcknowledgedAt"]),
    "info": !exists(json, "info") ? void 0 : json["info"]
  };
}
function JobUpdateToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "config": JobUpdateConfigToJSON(value.config),
    "status": value.status,
    "progress": value.progress,
    "outcomeAcknowledgedAt": value.outcomeAcknowledgedAt === void 0 ? void 0 : value.outcomeAcknowledgedAt === null ? null : value.outcomeAcknowledgedAt.toISOString(),
    "info": value.info
  };
}

// build/models/JobsResponse.ts
function instanceOfJobsResponse(value) {
  let isInstance = true;
  return isInstance;
}
function JobsResponseFromJSON(json) {
  return JobsResponseFromJSONTyped(json, false);
}
function JobsResponseFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "pagination": !exists(json, "pagination") ? void 0 : PaginationFromJSON(json["pagination"]),
    "data": !exists(json, "data") ? void 0 : json["data"].map(JobFromJSON)
  };
}
function JobsResponseToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "pagination": PaginationToJSON(value.pagination),
    "data": value.data === void 0 ? void 0 : value.data.map(JobToJSON)
  };
}

// build/models/LinkSidebarConfig.ts
var LinkSidebarConfigTypeEnum = {
  Workbook: "workbook",
  Document: "document",
  Link: "link"
};
function instanceOfLinkSidebarConfig(value) {
  let isInstance = true;
  isInstance = isInstance && "href" in value;
  isInstance = isInstance && "title" in value;
  return isInstance;
}
function LinkSidebarConfigFromJSON(json) {
  return LinkSidebarConfigFromJSONTyped(json, false);
}
function LinkSidebarConfigFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "type": !exists(json, "type") ? void 0 : json["type"],
    "href": json["href"],
    "title": json["title"]
  };
}
function LinkSidebarConfigToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "type": value.type,
    "href": value.href,
    "title": value.title
  };
}

// build/models/LinkSidebarConfigAllOf.ts
function instanceOfLinkSidebarConfigAllOf(value) {
  let isInstance = true;
  isInstance = isInstance && "href" in value;
  isInstance = isInstance && "title" in value;
  return isInstance;
}
function LinkSidebarConfigAllOfFromJSON(json) {
  return LinkSidebarConfigAllOfFromJSONTyped(json, false);
}
function LinkSidebarConfigAllOfFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "href": json["href"],
    "title": json["title"]
  };
}
function LinkSidebarConfigAllOfToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "href": value.href,
    "title": value.title
  };
}

// build/models/ListActorRolesResponse.ts
function instanceOfListActorRolesResponse(value) {
  let isInstance = true;
  return isInstance;
}
function ListActorRolesResponseFromJSON(json) {
  return ListActorRolesResponseFromJSONTyped(json, false);
}
function ListActorRolesResponseFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "data": !exists(json, "data") ? void 0 : json["data"].map(ActorRoleResponseFromJSON)
  };
}
function ListActorRolesResponseToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "data": value.data === void 0 ? void 0 : value.data.map(ActorRoleResponseToJSON)
  };
}

// build/models/RoleResponse.ts
function instanceOfRoleResponse(value) {
  let isInstance = true;
  isInstance = isInstance && "id" in value;
  isInstance = isInstance && "name" in value;
  isInstance = isInstance && "accountId" in value;
  return isInstance;
}
function RoleResponseFromJSON(json) {
  return RoleResponseFromJSONTyped(json, false);
}
function RoleResponseFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "id": json["id"],
    "name": json["name"],
    "accountId": json["accountId"],
    "created_at": !exists(json, "created_at") ? void 0 : new Date(json["created_at"]),
    "updated_at": !exists(json, "updated_at") ? void 0 : new Date(json["updated_at"])
  };
}
function RoleResponseToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "id": value.id,
    "name": value.name,
    "accountId": value.accountId,
    "created_at": value.created_at === void 0 ? void 0 : value.created_at.toISOString(),
    "updated_at": value.updated_at === void 0 ? void 0 : value.updated_at.toISOString()
  };
}

// build/models/ListRolesResponse.ts
function instanceOfListRolesResponse(value) {
  let isInstance = true;
  return isInstance;
}
function ListRolesResponseFromJSON(json) {
  return ListRolesResponseFromJSONTyped(json, false);
}
function ListRolesResponseFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "data": !exists(json, "data") ? void 0 : json["data"].map(RoleResponseFromJSON)
  };
}
function ListRolesResponseToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "data": value.data === void 0 ? void 0 : value.data.map(RoleResponseToJSON)
  };
}

// build/models/ListUsersSortField.ts
var ListUsersSortField = {
  Email: "email",
  Name: "name",
  Id: "id",
  Idp: "idp",
  IdpRef: "idp_ref",
  CreatedAt: "created_at",
  UpdatedAt: "updated_at",
  LastSeenAt: "last_seen_at",
  Dashboard: "dashboard"
};
function ListUsersSortFieldFromJSON(json) {
  return ListUsersSortFieldFromJSONTyped(json, false);
}
function ListUsersSortFieldFromJSONTyped(json, ignoreDiscriminator) {
  return json;
}
function ListUsersSortFieldToJSON(value) {
  return value;
}

// build/models/ManifestResponse.ts
function instanceOfManifestResponse(value) {
  let isInstance = true;
  return isInstance;
}
function ManifestResponseFromJSON(json) {
  return ManifestResponseFromJSONTyped(json, false);
}
function ManifestResponseFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "version": !exists(json, "version") ? void 0 : json["version"],
    "date_deployed": !exists(json, "date_deployed") ? void 0 : json["date_deployed"],
    "commit_sha": !exists(json, "commit_sha") ? void 0 : json["commit_sha"],
    "environment": !exists(json, "environment") ? void 0 : json["environment"]
  };
}
function ManifestResponseToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "version": value.version,
    "date_deployed": value.date_deployed,
    "commit_sha": value.commit_sha,
    "environment": value.environment
  };
}

// build/models/MappingProgramSummary.ts
function instanceOfMappingProgramSummary(value) {
  let isInstance = true;
  return isInstance;
}
function MappingProgramSummaryFromJSON(json) {
  return MappingProgramSummaryFromJSONTyped(json, false);
}
function MappingProgramSummaryFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "totalRuleCount": !exists(json, "totalRuleCount") ? void 0 : json["totalRuleCount"],
    "addedRuleCount": !exists(json, "addedRuleCount") ? void 0 : json["addedRuleCount"],
    "deletedRuleCount": !exists(json, "deletedRuleCount") ? void 0 : json["deletedRuleCount"]
  };
}
function MappingProgramSummaryToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "totalRuleCount": value.totalRuleCount,
    "addedRuleCount": value.addedRuleCount,
    "deletedRuleCount": value.deletedRuleCount
  };
}

// build/models/MappingRule.ts
function instanceOfMappingRule(value) {
  let isInstance = true;
  isInstance = isInstance && "name" in value;
  isInstance = isInstance && "type" in value;
  isInstance = isInstance && "id" in value;
  return isInstance;
}
function MappingRuleFromJSON(json) {
  return MappingRuleFromJSONTyped(json, false);
}
function MappingRuleFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "name": json["name"],
    "type": json["type"],
    "config": !exists(json, "config") ? void 0 : json["config"],
    "metadata": !exists(json, "metadata") ? void 0 : json["metadata"],
    "id": json["id"],
    "confidence": !exists(json, "confidence") ? void 0 : json["confidence"],
    "createdBy": !exists(json, "createdBy") ? void 0 : json["createdBy"],
    "createdAt": !exists(json, "createdAt") ? void 0 : new Date(json["createdAt"]),
    "updatedAt": !exists(json, "updatedAt") ? void 0 : new Date(json["updatedAt"]),
    "deletedAt": !exists(json, "deletedAt") ? void 0 : new Date(json["deletedAt"])
  };
}
function MappingRuleToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "name": value.name,
    "type": value.type,
    "config": value.config,
    "metadata": value.metadata,
    "id": value.id,
    "confidence": value.confidence,
    "createdBy": value.createdBy,
    "createdAt": value.createdAt === void 0 ? void 0 : value.createdAt.toISOString(),
    "updatedAt": value.updatedAt === void 0 ? void 0 : value.updatedAt.toISOString(),
    "deletedAt": value.deletedAt === void 0 ? void 0 : value.deletedAt.toISOString()
  };
}

// build/models/MappingProgram.ts
function instanceOfMappingProgram(value) {
  let isInstance = true;
  return isInstance;
}
function MappingProgramFromJSON(json) {
  return MappingProgramFromJSONTyped(json, false);
}
function MappingProgramFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "id": !exists(json, "id") ? void 0 : json["id"],
    "createdAt": !exists(json, "createdAt") ? void 0 : new Date(json["createdAt"]),
    "createdBy": !exists(json, "createdBy") ? void 0 : json["createdBy"],
    "rules": !exists(json, "rules") ? void 0 : json["rules"].map(MappingRuleFromJSON),
    "sourceKeys": !exists(json, "sourceKeys") ? void 0 : json["sourceKeys"],
    "destinationKeys": !exists(json, "destinationKeys") ? void 0 : json["destinationKeys"],
    "summary": !exists(json, "summary") ? void 0 : MappingProgramSummaryFromJSON(json["summary"]),
    "accessToken": !exists(json, "accessToken") ? void 0 : json["accessToken"]
  };
}
function MappingProgramToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "id": value.id,
    "createdAt": value.createdAt === void 0 ? void 0 : value.createdAt.toISOString(),
    "createdBy": value.createdBy,
    "rules": value.rules === void 0 ? void 0 : value.rules.map(MappingRuleToJSON),
    "sourceKeys": value.sourceKeys,
    "destinationKeys": value.destinationKeys,
    "summary": MappingProgramSummaryToJSON(value.summary),
    "accessToken": value.accessToken
  };
}

// build/models/MappingRuleBase.ts
function instanceOfMappingRuleBase(value) {
  let isInstance = true;
  isInstance = isInstance && "id" in value;
  return isInstance;
}
function MappingRuleBaseFromJSON(json) {
  return MappingRuleBaseFromJSONTyped(json, false);
}
function MappingRuleBaseFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "id": json["id"],
    "confidence": !exists(json, "confidence") ? void 0 : json["confidence"],
    "createdBy": !exists(json, "createdBy") ? void 0 : json["createdBy"],
    "createdAt": !exists(json, "createdAt") ? void 0 : new Date(json["createdAt"]),
    "updatedAt": !exists(json, "updatedAt") ? void 0 : new Date(json["updatedAt"]),
    "deletedAt": !exists(json, "deletedAt") ? void 0 : new Date(json["deletedAt"]),
    "metadata": !exists(json, "metadata") ? void 0 : json["metadata"]
  };
}
function MappingRuleBaseToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "id": value.id,
    "confidence": value.confidence,
    "createdBy": value.createdBy,
    "createdAt": value.createdAt === void 0 ? void 0 : value.createdAt.toISOString(),
    "updatedAt": value.updatedAt === void 0 ? void 0 : value.updatedAt.toISOString(),
    "deletedAt": value.deletedAt === void 0 ? void 0 : value.deletedAt.toISOString(),
    "metadata": value.metadata
  };
}

// build/models/MappingRuleConfig.ts
function instanceOfMappingRuleConfig(value) {
  let isInstance = true;
  isInstance = isInstance && "name" in value;
  isInstance = isInstance && "type" in value;
  return isInstance;
}
function MappingRuleConfigFromJSON(json) {
  return MappingRuleConfigFromJSONTyped(json, false);
}
function MappingRuleConfigFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "name": json["name"],
    "type": json["type"],
    "config": !exists(json, "config") ? void 0 : json["config"],
    "metadata": !exists(json, "metadata") ? void 0 : json["metadata"]
  };
}
function MappingRuleConfigToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "name": value.name,
    "type": value.type,
    "config": value.config,
    "metadata": value.metadata
  };
}

// build/models/ModelFile.ts
function instanceOfModelFile(value) {
  let isInstance = true;
  isInstance = isInstance && "id" in value;
  isInstance = isInstance && "name" in value;
  isInstance = isInstance && "ext" in value;
  isInstance = isInstance && "mimetype" in value;
  isInstance = isInstance && "encoding" in value;
  isInstance = isInstance && "status" in value;
  isInstance = isInstance && "size" in value;
  isInstance = isInstance && "bytesReceived" in value;
  isInstance = isInstance && "spaceId" in value;
  isInstance = isInstance && "createdAt" in value;
  isInstance = isInstance && "updatedAt" in value;
  return isInstance;
}
function ModelFileFromJSON(json) {
  return ModelFileFromJSONTyped(json, false);
}
function ModelFileFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "id": json["id"],
    "name": json["name"],
    "ext": json["ext"],
    "mimetype": json["mimetype"],
    "encoding": json["encoding"],
    "status": ModelFileStatusEnumFromJSON(json["status"]),
    "mode": !exists(json, "mode") ? void 0 : FileModeFromJSON(json["mode"]),
    "size": json["size"],
    "bytesReceived": json["bytesReceived"],
    "spaceId": json["spaceId"],
    "workbookId": !exists(json, "workbookId") ? void 0 : json["workbookId"],
    "createdAt": new Date(json["createdAt"]),
    "updatedAt": new Date(json["updatedAt"]),
    "expiredAt": !exists(json, "expiredAt") ? void 0 : new Date(json["expiredAt"]),
    "sheetId": !exists(json, "sheetId") ? void 0 : json["sheetId"],
    "actions": !exists(json, "actions") ? void 0 : json["actions"].map(ActionFromJSON),
    "origin": !exists(json, "origin") ? void 0 : FileOriginFromJSON(json["origin"])
  };
}
function ModelFileToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "id": value.id,
    "name": value.name,
    "ext": value.ext,
    "mimetype": value.mimetype,
    "encoding": value.encoding,
    "status": ModelFileStatusEnumToJSON(value.status),
    "mode": FileModeToJSON(value.mode),
    "size": value.size,
    "bytesReceived": value.bytesReceived,
    "spaceId": value.spaceId,
    "workbookId": value.workbookId,
    "createdAt": value.createdAt.toISOString(),
    "updatedAt": value.updatedAt.toISOString(),
    "expiredAt": value.expiredAt === void 0 ? void 0 : value.expiredAt.toISOString(),
    "sheetId": value.sheetId,
    "actions": value.actions === void 0 ? void 0 : value.actions.map(ActionToJSON),
    "origin": FileOriginToJSON(value.origin)
  };
}

// build/models/NumberPropertyAllOf.ts
var NumberPropertyAllOfTypeEnum = {
  Number: "number"
};
function instanceOfNumberPropertyAllOf(value) {
  let isInstance = true;
  return isInstance;
}
function NumberPropertyAllOfFromJSON(json) {
  return NumberPropertyAllOfFromJSONTyped(json, false);
}
function NumberPropertyAllOfFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "type": !exists(json, "type") ? void 0 : json["type"],
    "config": !exists(json, "config") ? void 0 : NumberPropertyAllOfConfigFromJSON(json["config"])
  };
}
function NumberPropertyAllOfToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "type": value.type,
    "config": NumberPropertyAllOfConfigToJSON(value.config)
  };
}

// build/models/PlanConfig.ts
function instanceOfPlanConfig(value) {
  let isInstance = true;
  return isInstance;
}
function PlanConfigFromJSON(json) {
  return PlanConfigFromJSONTyped(json, false);
}
function PlanConfigFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "fieldMapping": !exists(json, "fieldMapping") ? void 0 : json["fieldMapping"].map(EdgeFromJSON),
    "unmappedSourceFields": !exists(json, "unmappedSourceFields") ? void 0 : json["unmappedSourceFields"].map(SourceFieldFromJSON),
    "unmappedDestinationFields": !exists(json, "unmappedDestinationFields") ? void 0 : json["unmappedDestinationFields"].map(DestinationFieldFromJSON),
    "programId": !exists(json, "programId") ? void 0 : json["programId"]
  };
}
function PlanConfigToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "fieldMapping": value.fieldMapping === void 0 ? void 0 : value.fieldMapping.map(EdgeToJSON),
    "unmappedSourceFields": value.unmappedSourceFields === void 0 ? void 0 : value.unmappedSourceFields.map(SourceFieldToJSON),
    "unmappedDestinationFields": value.unmappedDestinationFields === void 0 ? void 0 : value.unmappedDestinationFields.map(DestinationFieldToJSON),
    "programId": value.programId
  };
}

// build/models/ProductsResponse.ts
function instanceOfProductsResponse(value) {
  let isInstance = true;
  return isInstance;
}
function ProductsResponseFromJSON(json) {
  return ProductsResponseFromJSONTyped(json, false);
}
function ProductsResponseFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "data": !exists(json, "data") ? void 0 : json["data"].map(ProductFromJSON)
  };
}
function ProductsResponseToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "data": value.data === void 0 ? void 0 : value.data.map(ProductToJSON)
  };
}

// build/models/ProgramResponse.ts
function instanceOfProgramResponse(value) {
  let isInstance = true;
  return isInstance;
}
function ProgramResponseFromJSON(json) {
  return ProgramResponseFromJSONTyped(json, false);
}
function ProgramResponseFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "data": !exists(json, "data") ? void 0 : MappingProgramFromJSON(json["data"])
  };
}
function ProgramResponseToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "data": MappingProgramToJSON(value.data)
  };
}

// build/models/ProgramsResponse.ts
function instanceOfProgramsResponse(value) {
  let isInstance = true;
  return isInstance;
}
function ProgramsResponseFromJSON(json) {
  return ProgramsResponseFromJSONTyped(json, false);
}
function ProgramsResponseFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "data": !exists(json, "data") ? void 0 : json["data"].map(MappingProgramFromJSON)
  };
}
function ProgramsResponseToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "data": value.data === void 0 ? void 0 : value.data.map(MappingProgramToJSON)
  };
}

// build/models/Prompt.ts
function instanceOfPrompt(value) {
  let isInstance = true;
  isInstance = isInstance && "id" in value;
  isInstance = isInstance && "createdById" in value;
  isInstance = isInstance && "accountId" in value;
  isInstance = isInstance && "createdAt" in value;
  isInstance = isInstance && "updatedAt" in value;
  return isInstance;
}
function PromptFromJSON(json) {
  return PromptFromJSONTyped(json, false);
}
function PromptFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "id": json["id"],
    "createdById": json["createdById"],
    "accountId": json["accountId"],
    "prompt": !exists(json, "prompt") ? void 0 : json["prompt"],
    "createdAt": new Date(json["createdAt"]),
    "updatedAt": new Date(json["updatedAt"]),
    "deletedAt": !exists(json, "deletedAt") ? void 0 : new Date(json["deletedAt"])
  };
}
function PromptToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "id": value.id,
    "createdById": value.createdById,
    "accountId": value.accountId,
    "prompt": value.prompt,
    "createdAt": value.createdAt.toISOString(),
    "updatedAt": value.updatedAt.toISOString(),
    "deletedAt": value.deletedAt === void 0 ? void 0 : value.deletedAt.toISOString()
  };
}

// build/models/PromptConfig.ts
function instanceOfPromptConfig(value) {
  let isInstance = true;
  isInstance = isInstance && "prompt" in value;
  isInstance = isInstance && "environmentId" in value;
  isInstance = isInstance && "spaceId" in value;
  return isInstance;
}
function PromptConfigFromJSON(json) {
  return PromptConfigFromJSONTyped(json, false);
}
function PromptConfigFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "prompt": json["prompt"],
    "environmentId": json["environmentId"],
    "spaceId": json["spaceId"]
  };
}
function PromptConfigToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "prompt": value.prompt,
    "environmentId": value.environmentId,
    "spaceId": value.spaceId
  };
}

// build/models/PromptResponse.ts
function instanceOfPromptResponse(value) {
  let isInstance = true;
  return isInstance;
}
function PromptResponseFromJSON(json) {
  return PromptResponseFromJSONTyped(json, false);
}
function PromptResponseFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "data": !exists(json, "data") ? void 0 : PromptFromJSON(json["data"])
  };
}
function PromptResponseToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "data": PromptToJSON(value.data)
  };
}

// build/models/PromptsResponse.ts
function instanceOfPromptsResponse(value) {
  let isInstance = true;
  return isInstance;
}
function PromptsResponseFromJSON(json) {
  return PromptsResponseFromJSONTyped(json, false);
}
function PromptsResponseFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "data": !exists(json, "data") ? void 0 : json["data"].map(PromptFromJSON)
  };
}
function PromptsResponseToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "data": value.data === void 0 ? void 0 : value.data.map(PromptToJSON)
  };
}

// build/models/RecordData.ts
function instanceOfRecordData(value) {
  let isInstance = true;
  return isInstance;
}
function RecordDataFromJSON(json) {
  return RecordDataFromJSONTyped(json, false);
}
function RecordDataFromJSONTyped(json, ignoreDiscriminator) {
  return json;
}
function RecordDataToJSON(value) {
  return value;
}

// build/models/Record.ts
function instanceOfRecord(value) {
  let isInstance = true;
  isInstance = isInstance && "id" in value;
  isInstance = isInstance && "values" in value;
  return isInstance;
}
function RecordFromJSON(json) {
  return RecordFromJSONTyped(json, false);
}
function RecordFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "id": json["id"],
    "versionId": !exists(json, "versionId") ? void 0 : json["versionId"],
    "commitId": !exists(json, "commitId") ? void 0 : json["commitId"],
    "values": RecordDataFromJSON(json["values"]),
    "valid": !exists(json, "valid") ? void 0 : json["valid"],
    "messages": !exists(json, "messages") ? void 0 : json["messages"].map(ValidationMessageFromJSON),
    "metadata": !exists(json, "metadata") ? void 0 : json["metadata"],
    "config": !exists(json, "config") ? void 0 : json["config"]
  };
}
function RecordToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "id": value.id,
    "versionId": value.versionId,
    "commitId": value.commitId,
    "values": RecordDataToJSON(value.values),
    "valid": value.valid,
    "messages": value.messages === void 0 ? void 0 : value.messages.map(ValidationMessageToJSON),
    "metadata": value.metadata,
    "config": value.config
  };
}

// build/models/RecordCountsResponseData.ts
function instanceOfRecordCountsResponseData(value) {
  let isInstance = true;
  isInstance = isInstance && "success" in value;
  isInstance = isInstance && "counts" in value;
  return isInstance;
}
function RecordCountsResponseDataFromJSON(json) {
  return RecordCountsResponseDataFromJSONTyped(json, false);
}
function RecordCountsResponseDataFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "success": json["success"],
    "counts": RecordCountsFromJSON(json["counts"])
  };
}
function RecordCountsResponseDataToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "success": value.success,
    "counts": RecordCountsToJSON(value.counts)
  };
}

// build/models/RecordCountsResponse.ts
function instanceOfRecordCountsResponse(value) {
  let isInstance = true;
  return isInstance;
}
function RecordCountsResponseFromJSON(json) {
  return RecordCountsResponseFromJSONTyped(json, false);
}
function RecordCountsResponseFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "data": !exists(json, "data") ? void 0 : RecordCountsResponseDataFromJSON(json["data"])
  };
}
function RecordCountsResponseToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "data": RecordCountsResponseDataToJSON(value.data)
  };
}

// build/models/RecordCountsResponseDataAllOf.ts
function instanceOfRecordCountsResponseDataAllOf(value) {
  let isInstance = true;
  isInstance = isInstance && "counts" in value;
  return isInstance;
}
function RecordCountsResponseDataAllOfFromJSON(json) {
  return RecordCountsResponseDataAllOfFromJSONTyped(json, false);
}
function RecordCountsResponseDataAllOfFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "counts": RecordCountsFromJSON(json["counts"])
  };
}
function RecordCountsResponseDataAllOfToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "counts": RecordCountsToJSON(value.counts)
  };
}

// build/models/RecordDataWithLinks.ts
function instanceOfRecordDataWithLinks(value) {
  let isInstance = true;
  return isInstance;
}
function RecordDataWithLinksFromJSON(json) {
  return RecordDataWithLinksFromJSONTyped(json, false);
}
function RecordDataWithLinksFromJSONTyped(json, ignoreDiscriminator) {
  return json;
}
function RecordDataWithLinksToJSON(value) {
  return value;
}

// build/models/RecordUpdates.ts
function instanceOfRecordUpdates(value) {
  let isInstance = true;
  isInstance = isInstance && "id" in value;
  isInstance = isInstance && "values" in value;
  return isInstance;
}
function RecordUpdatesFromJSON(json) {
  return RecordUpdatesFromJSONTyped(json, false);
}
function RecordUpdatesFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "versionId": !exists(json, "versionId") ? void 0 : json["versionId"],
    "commitId": !exists(json, "commitId") ? void 0 : json["commitId"],
    "id": json["id"],
    "values": RecordDataFromJSON(json["values"]),
    "valid": !exists(json, "valid") ? void 0 : json["valid"],
    "messages": !exists(json, "messages") ? void 0 : json["messages"].map(ValidationMessageFromJSON),
    "metadata": !exists(json, "metadata") ? void 0 : json["metadata"],
    "config": !exists(json, "config") ? void 0 : json["config"]
  };
}
function RecordUpdatesToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "versionId": value.versionId,
    "commitId": value.commitId,
    "id": value.id,
    "values": RecordDataToJSON(value.values),
    "valid": value.valid,
    "messages": value.messages === void 0 ? void 0 : value.messages.map(ValidationMessageToJSON),
    "metadata": value.metadata,
    "config": value.config
  };
}

// build/models/RecordUpdatesAllOf.ts
function instanceOfRecordUpdatesAllOf(value) {
  let isInstance = true;
  return isInstance;
}
function RecordUpdatesAllOfFromJSON(json) {
  return RecordUpdatesAllOfFromJSONTyped(json, false);
}
function RecordUpdatesAllOfFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "versionId": !exists(json, "versionId") ? void 0 : json["versionId"],
    "commitId": !exists(json, "commitId") ? void 0 : json["commitId"]
  };
}
function RecordUpdatesAllOfToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "versionId": value.versionId,
    "commitId": value.commitId
  };
}

// build/models/RecordWithLinks.ts
function instanceOfRecordWithLinks(value) {
  let isInstance = true;
  isInstance = isInstance && "id" in value;
  isInstance = isInstance && "values" in value;
  return isInstance;
}
function RecordWithLinksFromJSON(json) {
  return RecordWithLinksFromJSONTyped(json, false);
}
function RecordWithLinksFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "id": json["id"],
    "versionId": !exists(json, "versionId") ? void 0 : json["versionId"],
    "commitId": !exists(json, "commitId") ? void 0 : json["commitId"],
    "values": RecordDataWithLinksFromJSON(json["values"]),
    "valid": !exists(json, "valid") ? void 0 : json["valid"],
    "messages": !exists(json, "messages") ? void 0 : json["messages"].map(ValidationMessageFromJSON),
    "metadata": !exists(json, "metadata") ? void 0 : json["metadata"],
    "config": !exists(json, "config") ? void 0 : json["config"]
  };
}
function RecordWithLinksToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "id": value.id,
    "versionId": value.versionId,
    "commitId": value.commitId,
    "values": RecordDataWithLinksToJSON(value.values),
    "valid": value.valid,
    "messages": value.messages === void 0 ? void 0 : value.messages.map(ValidationMessageToJSON),
    "metadata": value.metadata,
    "config": value.config
  };
}

// build/models/RecordsData.ts
function instanceOfRecordsData(value) {
  let isInstance = true;
  return isInstance;
}
function RecordsDataFromJSON(json) {
  return RecordsDataFromJSONTyped(json, false);
}
function RecordsDataFromJSONTyped(json, ignoreDiscriminator) {
  return json;
}
function RecordsDataToJSON(value) {
  return value;
}

// build/models/RecordsResponseData.ts
function instanceOfRecordsResponseData(value) {
  let isInstance = true;
  isInstance = isInstance && "success" in value;
  return isInstance;
}
function RecordsResponseDataFromJSON(json) {
  return RecordsResponseDataFromJSONTyped(json, false);
}
function RecordsResponseDataFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "success": json["success"],
    "records": !exists(json, "records") ? void 0 : RecordsWithLinksFromJSON(json["records"]),
    "counts": !exists(json, "counts") ? void 0 : RecordCountsFromJSON(json["counts"]),
    "versionId": !exists(json, "versionId") ? void 0 : json["versionId"],
    "commitId": !exists(json, "commitId") ? void 0 : json["commitId"]
  };
}
function RecordsResponseDataToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "success": value.success,
    "records": RecordsWithLinksToJSON(value.records),
    "counts": RecordCountsToJSON(value.counts),
    "versionId": value.versionId,
    "commitId": value.commitId
  };
}

// build/models/RecordsResponse.ts
function instanceOfRecordsResponse(value) {
  let isInstance = true;
  return isInstance;
}
function RecordsResponseFromJSON(json) {
  return RecordsResponseFromJSONTyped(json, false);
}
function RecordsResponseFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "data": !exists(json, "data") ? void 0 : RecordsResponseDataFromJSON(json["data"])
  };
}
function RecordsResponseToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "data": RecordsResponseDataToJSON(value.data)
  };
}

// build/models/RecordsResponseDataAllOf.ts
function instanceOfRecordsResponseDataAllOf(value) {
  let isInstance = true;
  return isInstance;
}
function RecordsResponseDataAllOfFromJSON(json) {
  return RecordsResponseDataAllOfFromJSONTyped(json, false);
}
function RecordsResponseDataAllOfFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "records": !exists(json, "records") ? void 0 : RecordsWithLinksFromJSON(json["records"]),
    "counts": !exists(json, "counts") ? void 0 : RecordCountsFromJSON(json["counts"]),
    "versionId": !exists(json, "versionId") ? void 0 : json["versionId"],
    "commitId": !exists(json, "commitId") ? void 0 : json["commitId"]
  };
}
function RecordsResponseDataAllOfToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "records": RecordsWithLinksToJSON(value.records),
    "counts": RecordCountsToJSON(value.counts),
    "versionId": value.versionId,
    "commitId": value.commitId
  };
}

// build/models/RecordsUpdates.ts
function instanceOfRecordsUpdates(value) {
  let isInstance = true;
  return isInstance;
}
function RecordsUpdatesFromJSON(json) {
  return RecordsUpdatesFromJSONTyped(json, false);
}
function RecordsUpdatesFromJSONTyped(json, ignoreDiscriminator) {
  return json;
}
function RecordsUpdatesToJSON(value) {
  return value;
}

// build/models/ReferenceListPropertyAllOf.ts
var ReferenceListPropertyAllOfTypeEnum = {
  ReferenceList: "reference-list"
};
function instanceOfReferenceListPropertyAllOf(value) {
  let isInstance = true;
  isInstance = isInstance && "config" in value;
  return isInstance;
}
function ReferenceListPropertyAllOfFromJSON(json) {
  return ReferenceListPropertyAllOfFromJSONTyped(json, false);
}
function ReferenceListPropertyAllOfFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "type": !exists(json, "type") ? void 0 : json["type"],
    "config": ReferenceListPropertyAllOfConfigFromJSON(json["config"])
  };
}
function ReferenceListPropertyAllOfToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "type": value.type,
    "config": ReferenceListPropertyAllOfConfigToJSON(value.config)
  };
}

// build/models/ReferencePropertyAllOf.ts
var ReferencePropertyAllOfTypeEnum = {
  Reference: "reference"
};
function instanceOfReferencePropertyAllOf(value) {
  let isInstance = true;
  isInstance = isInstance && "config" in value;
  return isInstance;
}
function ReferencePropertyAllOfFromJSON(json) {
  return ReferencePropertyAllOfFromJSONTyped(json, false);
}
function ReferencePropertyAllOfFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "type": !exists(json, "type") ? void 0 : json["type"],
    "config": ReferencePropertyAllOfConfigFromJSON(json["config"])
  };
}
function ReferencePropertyAllOfToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "type": value.type,
    "config": ReferencePropertyAllOfConfigToJSON(value.config)
  };
}

// build/models/ReplaceRequest.ts
function instanceOfReplaceRequest(value) {
  let isInstance = true;
  return isInstance;
}
function ReplaceRequestFromJSON(json) {
  return ReplaceRequestFromJSONTyped(json, false);
}
function ReplaceRequestFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "replaceValue": !exists(json, "replaceValue") ? void 0 : ValueFromJSON(json["replaceValue"])
  };
}
function ReplaceRequestToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "replaceValue": ValueToJSON(value.replaceValue)
  };
}

// build/models/RequiredConstraintAllOf.ts
var RequiredConstraintAllOfTypeEnum = {
  Required: "required"
};
function instanceOfRequiredConstraintAllOf(value) {
  let isInstance = true;
  return isInstance;
}
function RequiredConstraintAllOfFromJSON(json) {
  return RequiredConstraintAllOfFromJSONTyped(json, false);
}
function RequiredConstraintAllOfFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "type": !exists(json, "type") ? void 0 : json["type"]
  };
}
function RequiredConstraintAllOfToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "type": value.type
  };
}

// build/models/Restore.ts
function instanceOfRestore(value) {
  let isInstance = true;
  isInstance = isInstance && "host" in value;
  isInstance = isInstance && "port" in value;
  isInstance = isInstance && "dbname" in value;
  return isInstance;
}
function RestoreFromJSON(json) {
  return RestoreFromJSONTyped(json, false);
}
function RestoreFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "host": json["host"],
    "port": json["port"],
    "dbname": json["dbname"]
  };
}
function RestoreToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "host": value.host,
    "port": value.port,
    "dbname": value.dbname
  };
}

// build/models/RestoreConfig.ts
function instanceOfRestoreConfig(value) {
  let isInstance = true;
  isInstance = isInstance && "fileId" in value;
  return isInstance;
}
function RestoreConfigFromJSON(json) {
  return RestoreConfigFromJSONTyped(json, false);
}
function RestoreConfigFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "fileId": json["fileId"]
  };
}
function RestoreConfigToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "fileId": value.fileId
  };
}

// build/models/RestoreResponse.ts
function instanceOfRestoreResponse(value) {
  let isInstance = true;
  return isInstance;
}
function RestoreResponseFromJSON(json) {
  return RestoreResponseFromJSONTyped(json, false);
}
function RestoreResponseFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "data": !exists(json, "data") ? void 0 : RestoreFromJSON(json["data"])
  };
}
function RestoreResponseToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "data": RestoreToJSON(value.data)
  };
}

// build/models/RestoreSnapshotRequest.ts
function instanceOfRestoreSnapshotRequest(value) {
  let isInstance = true;
  return isInstance;
}
function RestoreSnapshotRequestFromJSON(json) {
  return RestoreSnapshotRequestFromJSONTyped(json, false);
}
function RestoreSnapshotRequestFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "created": !exists(json, "created") ? void 0 : json["created"],
    "updated": !exists(json, "updated") ? void 0 : json["updated"],
    "deleted": !exists(json, "deleted") ? void 0 : json["deleted"]
  };
}
function RestoreSnapshotRequestToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "created": value.created,
    "updated": value.updated,
    "deleted": value.deleted
  };
}

// build/models/RoleActorResource.ts
function instanceOfRoleActorResource(value) {
  let isInstance = true;
  isInstance = isInstance && "actorId" in value;
  isInstance = isInstance && "resourceId" in value;
  isInstance = isInstance && "roleId" in value;
  return isInstance;
}
function RoleActorResourceFromJSON(json) {
  return RoleActorResourceFromJSONTyped(json, false);
}
function RoleActorResourceFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "actorId": ActorIdFromJSON(json["actorId"]),
    "resourceId": ResourceIdFromJSON(json["resourceId"]),
    "roleId": json["roleId"]
  };
}
function RoleActorResourceToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "actorId": ActorIdToJSON(value.actorId),
    "resourceId": ResourceIdToJSON(value.resourceId),
    "roleId": value.roleId
  };
}

// build/models/RoleActorResourceAllOf.ts
function instanceOfRoleActorResourceAllOf(value) {
  let isInstance = true;
  isInstance = isInstance && "roleId" in value;
  return isInstance;
}
function RoleActorResourceAllOfFromJSON(json) {
  return RoleActorResourceAllOfFromJSONTyped(json, false);
}
function RoleActorResourceAllOfFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "roleId": json["roleId"]
  };
}
function RoleActorResourceAllOfToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "roleId": value.roleId
  };
}

// build/models/RuleResponse.ts
function instanceOfRuleResponse(value) {
  let isInstance = true;
  return isInstance;
}
function RuleResponseFromJSON(json) {
  return RuleResponseFromJSONTyped(json, false);
}
function RuleResponseFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "data": !exists(json, "data") ? void 0 : MappingRuleFromJSON(json["data"])
  };
}
function RuleResponseToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "data": MappingRuleToJSON(value.data)
  };
}

// build/models/RulesResponse.ts
function instanceOfRulesResponse(value) {
  let isInstance = true;
  return isInstance;
}
function RulesResponseFromJSON(json) {
  return RulesResponseFromJSONTyped(json, false);
}
function RulesResponseFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "data": !exists(json, "data") ? void 0 : json["data"].map(MappingRuleFromJSON)
  };
}
function RulesResponseToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "data": value.data === void 0 ? void 0 : value.data.map(MappingRuleToJSON)
  };
}

// build/models/Secret.ts
function instanceOfSecret(value) {
  let isInstance = true;
  isInstance = isInstance && "id" in value;
  isInstance = isInstance && "name" in value;
  isInstance = isInstance && "value" in value;
  isInstance = isInstance && "environmentId" in value;
  return isInstance;
}
function SecretFromJSON(json) {
  return SecretFromJSONTyped(json, false);
}
function SecretFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "id": json["id"],
    "name": json["name"],
    "value": json["value"],
    "environmentId": json["environmentId"],
    "spaceId": !exists(json, "spaceId") ? void 0 : json["spaceId"]
  };
}
function SecretToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "id": value.id,
    "name": value.name,
    "value": value.value,
    "environmentId": value.environmentId,
    "spaceId": value.spaceId
  };
}

// build/models/Secrets.ts
function instanceOfSecrets(value) {
  let isInstance = true;
  return isInstance;
}
function SecretsFromJSON(json) {
  return SecretsFromJSONTyped(json, false);
}
function SecretsFromJSONTyped(json, ignoreDiscriminator) {
  return json;
}
function SecretsToJSON(value) {
  return value;
}

// build/models/SecretsResponse.ts
function instanceOfSecretsResponse(value) {
  let isInstance = true;
  isInstance = isInstance && "data" in value;
  return isInstance;
}
function SecretsResponseFromJSON(json) {
  return SecretsResponseFromJSONTyped(json, false);
}
function SecretsResponseFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "data": SecretsFromJSON(json["data"])
  };
}
function SecretsResponseToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "data": SecretsToJSON(value.data)
  };
}

// build/models/Sheet.ts
function instanceOfSheet(value) {
  let isInstance = true;
  isInstance = isInstance && "config" in value;
  isInstance = isInstance && "id" in value;
  isInstance = isInstance && "workbookId" in value;
  isInstance = isInstance && "name" in value;
  isInstance = isInstance && "createdAt" in value;
  isInstance = isInstance && "updatedAt" in value;
  return isInstance;
}
function SheetFromJSON(json) {
  return SheetFromJSONTyped(json, false);
}
function SheetFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "config": SheetConfigFromJSON(json["config"]),
    "recordCounts": !exists(json, "recordCounts") ? void 0 : RecordCountsFromJSON(json["recordCounts"]),
    "id": json["id"],
    "workbookId": json["workbookId"],
    "name": json["name"],
    "slug": !exists(json, "slug") ? void 0 : json["slug"],
    "metadata": !exists(json, "metadata") ? void 0 : json["metadata"],
    "namespace": !exists(json, "namespace") ? void 0 : json["namespace"],
    "lockedBy": !exists(json, "lockedBy") ? void 0 : json["lockedBy"],
    "createdAt": new Date(json["createdAt"]),
    "updatedAt": new Date(json["updatedAt"]),
    "lockedAt": !exists(json, "lockedAt") ? void 0 : new Date(json["lockedAt"])
  };
}
function SheetToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "config": SheetConfigToJSON(value.config),
    "recordCounts": RecordCountsToJSON(value.recordCounts),
    "id": value.id,
    "workbookId": value.workbookId,
    "name": value.name,
    "slug": value.slug,
    "metadata": value.metadata,
    "namespace": value.namespace,
    "lockedBy": value.lockedBy,
    "createdAt": value.createdAt.toISOString(),
    "updatedAt": value.updatedAt.toISOString(),
    "lockedAt": value.lockedAt === void 0 ? void 0 : value.lockedAt.toISOString()
  };
}

// build/models/SheetConfigOptions.ts
function instanceOfSheetConfigOptions(value) {
  let isInstance = true;
  return isInstance;
}
function SheetConfigOptionsFromJSON(json) {
  return SheetConfigOptionsFromJSONTyped(json, false);
}
function SheetConfigOptionsFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "name": !exists(json, "name") ? void 0 : json["name"],
    "description": !exists(json, "description") ? void 0 : json["description"],
    "slug": !exists(json, "slug") ? void 0 : json["slug"],
    "readonly": !exists(json, "readonly") ? void 0 : json["readonly"],
    "allowAdditionalFields": !exists(json, "allowAdditionalFields") ? void 0 : json["allowAdditionalFields"],
    "mappingConfidenceThreshold": !exists(json, "mappingConfidenceThreshold") ? void 0 : json["mappingConfidenceThreshold"],
    "access": !exists(json, "access") ? void 0 : json["access"].map(SheetAccessFromJSON),
    "fields": !exists(json, "fields") ? void 0 : json["fields"].map(PropertyFromJSON),
    "actions": !exists(json, "actions") ? void 0 : json["actions"].map(ActionFromJSON),
    "metadata": !exists(json, "metadata") ? void 0 : json["metadata"],
    "constraints": !exists(json, "constraints") ? void 0 : json["constraints"].map(SheetConstraintFromJSON)
  };
}
function SheetConfigOptionsToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "name": value.name,
    "description": value.description,
    "slug": value.slug,
    "readonly": value.readonly,
    "allowAdditionalFields": value.allowAdditionalFields,
    "mappingConfidenceThreshold": value.mappingConfidenceThreshold,
    "access": value.access === void 0 ? void 0 : value.access.map(SheetAccessToJSON),
    "fields": value.fields === void 0 ? void 0 : value.fields.map(PropertyToJSON),
    "actions": value.actions === void 0 ? void 0 : value.actions.map(ActionToJSON),
    "metadata": value.metadata,
    "constraints": value.constraints === void 0 ? void 0 : value.constraints.map(SheetConstraintToJSON)
  };
}

// build/models/SheetConfigUpdate.ts
function instanceOfSheetConfigUpdate(value) {
  let isInstance = true;
  return isInstance;
}
function SheetConfigUpdateFromJSON(json) {
  return SheetConfigUpdateFromJSONTyped(json, false);
}
function SheetConfigUpdateFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "name": !exists(json, "name") ? void 0 : json["name"],
    "description": !exists(json, "description") ? void 0 : json["description"],
    "slug": !exists(json, "slug") ? void 0 : json["slug"],
    "readonly": !exists(json, "readonly") ? void 0 : json["readonly"],
    "allowAdditionalFields": !exists(json, "allowAdditionalFields") ? void 0 : json["allowAdditionalFields"],
    "mappingConfidenceThreshold": !exists(json, "mappingConfidenceThreshold") ? void 0 : json["mappingConfidenceThreshold"],
    "access": !exists(json, "access") ? void 0 : json["access"].map(SheetAccessFromJSON),
    "fields": !exists(json, "fields") ? void 0 : json["fields"].map(PropertyFromJSON),
    "actions": !exists(json, "actions") ? void 0 : json["actions"].map(ActionFromJSON),
    "metadata": !exists(json, "metadata") ? void 0 : json["metadata"],
    "constraints": !exists(json, "constraints") ? void 0 : json["constraints"].map(SheetConstraintFromJSON)
  };
}
function SheetConfigUpdateToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "name": value.name,
    "description": value.description,
    "slug": value.slug,
    "readonly": value.readonly,
    "allowAdditionalFields": value.allowAdditionalFields,
    "mappingConfidenceThreshold": value.mappingConfidenceThreshold,
    "access": value.access === void 0 ? void 0 : value.access.map(SheetAccessToJSON),
    "fields": value.fields === void 0 ? void 0 : value.fields.map(PropertyToJSON),
    "actions": value.actions === void 0 ? void 0 : value.actions.map(ActionToJSON),
    "metadata": value.metadata,
    "constraints": value.constraints === void 0 ? void 0 : value.constraints.map(SheetConstraintToJSON)
  };
}

// build/models/SheetConfigOrUpdate.ts
function instanceOfSheetConfigOrUpdate(value) {
  let isInstance = true;
  isInstance = isInstance && "name" in value;
  isInstance = isInstance && "fields" in value;
  return isInstance;
}
function SheetConfigOrUpdateFromJSON(json) {
  return SheetConfigOrUpdateFromJSONTyped(json, false);
}
function SheetConfigOrUpdateFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "name": json["name"],
    "description": !exists(json, "description") ? void 0 : json["description"],
    "slug": !exists(json, "slug") ? void 0 : json["slug"],
    "readonly": !exists(json, "readonly") ? void 0 : json["readonly"],
    "allowAdditionalFields": !exists(json, "allowAdditionalFields") ? void 0 : json["allowAdditionalFields"],
    "mappingConfidenceThreshold": !exists(json, "mappingConfidenceThreshold") ? void 0 : json["mappingConfidenceThreshold"],
    "access": !exists(json, "access") ? void 0 : json["access"].map(SheetAccessFromJSON),
    "fields": json["fields"].map(PropertyFromJSON),
    "actions": !exists(json, "actions") ? void 0 : json["actions"].map(ActionFromJSON),
    "metadata": !exists(json, "metadata") ? void 0 : json["metadata"],
    "constraints": !exists(json, "constraints") ? void 0 : json["constraints"].map(SheetConstraintFromJSON),
    "id": !exists(json, "id") ? void 0 : json["id"],
    "workbookId": !exists(json, "workbookId") ? void 0 : json["workbookId"],
    "config": !exists(json, "config") ? void 0 : SheetConfigUpdateFromJSON(json["config"]),
    "namespace": !exists(json, "namespace") ? void 0 : json["namespace"],
    "lockedBy": !exists(json, "lockedBy") ? void 0 : json["lockedBy"],
    "createdAt": !exists(json, "createdAt") ? void 0 : new Date(json["createdAt"]),
    "updatedAt": !exists(json, "updatedAt") ? void 0 : new Date(json["updatedAt"]),
    "lockedAt": !exists(json, "lockedAt") ? void 0 : new Date(json["lockedAt"])
  };
}
function SheetConfigOrUpdateToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "name": value.name,
    "description": value.description,
    "slug": value.slug,
    "readonly": value.readonly,
    "allowAdditionalFields": value.allowAdditionalFields,
    "mappingConfidenceThreshold": value.mappingConfidenceThreshold,
    "access": value.access === void 0 ? void 0 : value.access.map(SheetAccessToJSON),
    "fields": value.fields.map(PropertyToJSON),
    "actions": value.actions === void 0 ? void 0 : value.actions.map(ActionToJSON),
    "metadata": value.metadata,
    "constraints": value.constraints === void 0 ? void 0 : value.constraints.map(SheetConstraintToJSON),
    "id": value.id,
    "workbookId": value.workbookId,
    "config": SheetConfigUpdateToJSON(value.config),
    "namespace": value.namespace,
    "lockedBy": value.lockedBy,
    "createdAt": value.createdAt === void 0 ? void 0 : value.createdAt.toISOString(),
    "updatedAt": value.updatedAt === void 0 ? void 0 : value.updatedAt.toISOString(),
    "lockedAt": value.lockedAt === void 0 ? void 0 : value.lockedAt.toISOString()
  };
}

// build/models/SheetResponse.ts
function instanceOfSheetResponse(value) {
  let isInstance = true;
  return isInstance;
}
function SheetResponseFromJSON(json) {
  return SheetResponseFromJSONTyped(json, false);
}
function SheetResponseFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "data": !exists(json, "data") ? void 0 : SheetFromJSON(json["data"])
  };
}
function SheetResponseToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "data": SheetToJSON(value.data)
  };
}

// build/models/SheetUpdate.ts
function instanceOfSheetUpdate(value) {
  let isInstance = true;
  return isInstance;
}
function SheetUpdateFromJSON(json) {
  return SheetUpdateFromJSONTyped(json, false);
}
function SheetUpdateFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "id": !exists(json, "id") ? void 0 : json["id"],
    "workbookId": !exists(json, "workbookId") ? void 0 : json["workbookId"],
    "name": !exists(json, "name") ? void 0 : json["name"],
    "slug": !exists(json, "slug") ? void 0 : json["slug"],
    "config": !exists(json, "config") ? void 0 : SheetConfigUpdateFromJSON(json["config"]),
    "metadata": !exists(json, "metadata") ? void 0 : json["metadata"],
    "namespace": !exists(json, "namespace") ? void 0 : json["namespace"],
    "lockedBy": !exists(json, "lockedBy") ? void 0 : json["lockedBy"],
    "createdAt": !exists(json, "createdAt") ? void 0 : new Date(json["createdAt"]),
    "updatedAt": !exists(json, "updatedAt") ? void 0 : new Date(json["updatedAt"]),
    "lockedAt": !exists(json, "lockedAt") ? void 0 : new Date(json["lockedAt"])
  };
}
function SheetUpdateToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "id": value.id,
    "workbookId": value.workbookId,
    "name": value.name,
    "slug": value.slug,
    "config": SheetConfigUpdateToJSON(value.config),
    "metadata": value.metadata,
    "namespace": value.namespace,
    "lockedBy": value.lockedBy,
    "createdAt": value.createdAt === void 0 ? void 0 : value.createdAt.toISOString(),
    "updatedAt": value.updatedAt === void 0 ? void 0 : value.updatedAt.toISOString(),
    "lockedAt": value.lockedAt === void 0 ? void 0 : value.lockedAt.toISOString()
  };
}

// build/models/SheetUpdateRequest.ts
function instanceOfSheetUpdateRequest(value) {
  let isInstance = true;
  return isInstance;
}
function SheetUpdateRequestFromJSON(json) {
  return SheetUpdateRequestFromJSONTyped(json, false);
}
function SheetUpdateRequestFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "name": !exists(json, "name") ? void 0 : json["name"],
    "slug": !exists(json, "slug") ? void 0 : json["slug"],
    "metadata": !exists(json, "metadata") ? void 0 : json["metadata"]
  };
}
function SheetUpdateRequestToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "name": value.name,
    "slug": value.slug,
    "metadata": value.metadata
  };
}

// build/models/SheetsResponse.ts
function instanceOfSheetsResponse(value) {
  let isInstance = true;
  return isInstance;
}
function SheetsResponseFromJSON(json) {
  return SheetsResponseFromJSONTyped(json, false);
}
function SheetsResponseFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "data": !exists(json, "data") ? void 0 : json["data"].map(SheetFromJSON)
  };
}
function SheetsResponseToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "data": value.data === void 0 ? void 0 : value.data.map(SheetToJSON)
  };
}

// build/models/WorkbookSidebarConfig.ts
var WorkbookSidebarConfigTypeEnum = {
  Workbook: "workbook",
  Document: "document",
  Link: "link"
};
function instanceOfWorkbookSidebarConfig(value) {
  let isInstance = true;
  isInstance = isInstance && "workbookId" in value;
  return isInstance;
}
function WorkbookSidebarConfigFromJSON(json) {
  return WorkbookSidebarConfigFromJSONTyped(json, false);
}
function WorkbookSidebarConfigFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "type": !exists(json, "type") ? void 0 : json["type"],
    "workbookId": json["workbookId"]
  };
}
function WorkbookSidebarConfigToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "type": value.type,
    "workbookId": value.workbookId
  };
}

// build/models/SidebarConfig.ts
function SidebarConfigFromJSON(json) {
  return SidebarConfigFromJSONTyped(json, false);
}
function SidebarConfigFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  switch (json["type"]) {
    case "document":
      return { ...DocumentSidebarConfigFromJSONTyped(json, true), type: "document" };
    case "link":
      return { ...LinkSidebarConfigFromJSONTyped(json, true), type: "link" };
    case "workbook":
      return { ...WorkbookSidebarConfigFromJSONTyped(json, true), type: "workbook" };
    default:
      throw new Error(`No variant of SidebarConfig exists with 'type=${json["type"]}'`);
  }
}
function SidebarConfigToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  switch (value["type"]) {
    case "document":
      return DocumentSidebarConfigToJSON(value);
    case "link":
      return LinkSidebarConfigToJSON(value);
    case "workbook":
      return WorkbookSidebarConfigToJSON(value);
    default:
      throw new Error(`No variant of SidebarConfig exists with 'type=${value["type"]}'`);
  }
}

// build/models/SnapshotSummaryCreatedSince.ts
function instanceOfSnapshotSummaryCreatedSince(value) {
  let isInstance = true;
  isInstance = isInstance && "total" in value;
  return isInstance;
}
function SnapshotSummaryCreatedSinceFromJSON(json) {
  return SnapshotSummaryCreatedSinceFromJSONTyped(json, false);
}
function SnapshotSummaryCreatedSinceFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "total": json["total"]
  };
}
function SnapshotSummaryCreatedSinceToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "total": value.total
  };
}

// build/models/SnapshotSummaryUpdatedSince.ts
function instanceOfSnapshotSummaryUpdatedSince(value) {
  let isInstance = true;
  isInstance = isInstance && "total" in value;
  return isInstance;
}
function SnapshotSummaryUpdatedSinceFromJSON(json) {
  return SnapshotSummaryUpdatedSinceFromJSONTyped(json, false);
}
function SnapshotSummaryUpdatedSinceFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "total": json["total"],
    "byField": !exists(json, "byField") ? void 0 : json["byField"]
  };
}
function SnapshotSummaryUpdatedSinceToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "total": value.total,
    "byField": value.byField
  };
}

// build/models/SnapshotSummary.ts
function instanceOfSnapshotSummary(value) {
  let isInstance = true;
  isInstance = isInstance && "createdSince" in value;
  isInstance = isInstance && "deletedSince" in value;
  isInstance = isInstance && "updatedSince" in value;
  return isInstance;
}
function SnapshotSummaryFromJSON(json) {
  return SnapshotSummaryFromJSONTyped(json, false);
}
function SnapshotSummaryFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "createdSince": SnapshotSummaryCreatedSinceFromJSON(json["createdSince"]),
    "deletedSince": SnapshotSummaryCreatedSinceFromJSON(json["deletedSince"]),
    "updatedSince": SnapshotSummaryUpdatedSinceFromJSON(json["updatedSince"])
  };
}
function SnapshotSummaryToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "createdSince": SnapshotSummaryCreatedSinceToJSON(value.createdSince),
    "deletedSince": SnapshotSummaryCreatedSinceToJSON(value.deletedSince),
    "updatedSince": SnapshotSummaryUpdatedSinceToJSON(value.updatedSince)
  };
}

// build/models/Snapshot.ts
function instanceOfSnapshot(value) {
  let isInstance = true;
  isInstance = isInstance && "id" in value;
  isInstance = isInstance && "sheetId" in value;
  isInstance = isInstance && "createdAt" in value;
  isInstance = isInstance && "createdBy" in value;
  return isInstance;
}
function SnapshotFromJSON(json) {
  return SnapshotFromJSONTyped(json, false);
}
function SnapshotFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "id": json["id"],
    "sheetId": json["sheetId"],
    "label": !exists(json, "label") ? void 0 : json["label"],
    "summary": !exists(json, "summary") ? void 0 : SnapshotSummaryFromJSON(json["summary"]),
    "createdAt": new Date(json["createdAt"]),
    "createdBy": json["createdBy"]
  };
}
function SnapshotToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "id": value.id,
    "sheetId": value.sheetId,
    "label": value.label,
    "summary": SnapshotSummaryToJSON(value.summary),
    "createdAt": value.createdAt.toISOString(),
    "createdBy": value.createdBy
  };
}

// build/models/SnapshotResponse.ts
function instanceOfSnapshotResponse(value) {
  let isInstance = true;
  return isInstance;
}
function SnapshotResponseFromJSON(json) {
  return SnapshotResponseFromJSONTyped(json, false);
}
function SnapshotResponseFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "data": !exists(json, "data") ? void 0 : SnapshotFromJSON(json["data"])
  };
}
function SnapshotResponseToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "data": SnapshotToJSON(value.data)
  };
}

// build/models/SnapshotSheetRequest.ts
function instanceOfSnapshotSheetRequest(value) {
  let isInstance = true;
  isInstance = isInstance && "sheetId" in value;
  return isInstance;
}
function SnapshotSheetRequestFromJSON(json) {
  return SnapshotSheetRequestFromJSONTyped(json, false);
}
function SnapshotSheetRequestFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "label": !exists(json, "label") ? void 0 : json["label"],
    "sheetId": json["sheetId"]
  };
}
function SnapshotSheetRequestToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "label": value.label,
    "sheetId": value.sheetId
  };
}

// build/models/SnapshotsResponse.ts
function instanceOfSnapshotsResponse(value) {
  let isInstance = true;
  return isInstance;
}
function SnapshotsResponseFromJSON(json) {
  return SnapshotsResponseFromJSONTyped(json, false);
}
function SnapshotsResponseFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "data": !exists(json, "data") ? void 0 : json["data"].map(SnapshotFromJSON)
  };
}
function SnapshotsResponseToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "data": value.data === void 0 ? void 0 : value.data.map(SnapshotToJSON)
  };
}

// build/models/SourceDestinationWeight.ts
function instanceOfSourceDestinationWeight(value) {
  let isInstance = true;
  return isInstance;
}
function SourceDestinationWeightFromJSON(json) {
  return SourceDestinationWeightFromJSONTyped(json, false);
}
function SourceDestinationWeightFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "source": !exists(json, "source") ? void 0 : json["source"],
    "destination": !exists(json, "destination") ? void 0 : json["destination"],
    "weight": !exists(json, "weight") ? void 0 : json["weight"]
  };
}
function SourceDestinationWeightToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "source": value.source,
    "destination": value.destination,
    "weight": value.weight
  };
}

// build/models/SourceDestinationWeightsResponseData.ts
function instanceOfSourceDestinationWeightsResponseData(value) {
  let isInstance = true;
  return isInstance;
}
function SourceDestinationWeightsResponseDataFromJSON(json) {
  return SourceDestinationWeightsResponseDataFromJSONTyped(json, false);
}
function SourceDestinationWeightsResponseDataFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "weights": !exists(json, "weights") ? void 0 : json["weights"].map(SourceDestinationWeightFromJSON)
  };
}
function SourceDestinationWeightsResponseDataToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "weights": value.weights === void 0 ? void 0 : value.weights.map(SourceDestinationWeightToJSON)
  };
}

// build/models/SourceDestinationWeightsResponse.ts
function instanceOfSourceDestinationWeightsResponse(value) {
  let isInstance = true;
  return isInstance;
}
function SourceDestinationWeightsResponseFromJSON(json) {
  return SourceDestinationWeightsResponseFromJSONTyped(json, false);
}
function SourceDestinationWeightsResponseFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "data": !exists(json, "data") ? void 0 : SourceDestinationWeightsResponseDataFromJSON(json["data"])
  };
}
function SourceDestinationWeightsResponseToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "data": SourceDestinationWeightsResponseDataToJSON(value.data)
  };
}

// build/models/SpaceAccess.ts
var SpaceAccess = {
  Star: "*",
  Upload: "upload"
};
function SpaceAccessFromJSON(json) {
  return SpaceAccessFromJSONTyped(json, false);
}
function SpaceAccessFromJSONTyped(json, ignoreDiscriminator) {
  return json;
}
function SpaceAccessToJSON(value) {
  return value;
}

// build/models/SpaceSidebarConfig.ts
function instanceOfSpaceSidebarConfig(value) {
  let isInstance = true;
  return isInstance;
}
function SpaceSidebarConfigFromJSON(json) {
  return SpaceSidebarConfigFromJSONTyped(json, false);
}
function SpaceSidebarConfigFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "workbookSidebarOrder": !exists(json, "workbookSidebarOrder") ? void 0 : json["workbookSidebarOrder"]
  };
}
function SpaceSidebarConfigToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "workbookSidebarOrder": value.workbookSidebarOrder
  };
}

// build/models/SpaceSettings.ts
function instanceOfSpaceSettings(value) {
  let isInstance = true;
  return isInstance;
}
function SpaceSettingsFromJSON(json) {
  return SpaceSettingsFromJSONTyped(json, false);
}
function SpaceSettingsFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "sidebarConfig": !exists(json, "sidebarConfig") ? void 0 : SpaceSidebarConfigFromJSON(json["sidebarConfig"])
  };
}
function SpaceSettingsToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "sidebarConfig": SpaceSidebarConfigToJSON(value.sidebarConfig)
  };
}

// build/models/SpaceSize.ts
function instanceOfSpaceSize(value) {
  let isInstance = true;
  isInstance = isInstance && "name" in value;
  isInstance = isInstance && "id" in value;
  isInstance = isInstance && "numUsers" in value;
  isInstance = isInstance && "pdv" in value;
  isInstance = isInstance && "numFiles" in value;
  return isInstance;
}
function SpaceSizeFromJSON(json) {
  return SpaceSizeFromJSONTyped(json, false);
}
function SpaceSizeFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "name": json["name"],
    "id": json["id"],
    "numUsers": json["numUsers"],
    "pdv": json["pdv"],
    "numFiles": json["numFiles"]
  };
}
function SpaceSizeToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "name": value.name,
    "id": value.id,
    "numUsers": value.numUsers,
    "pdv": value.pdv,
    "numFiles": value.numFiles
  };
}

// build/models/Space.ts
function instanceOfSpace(value) {
  let isInstance = true;
  isInstance = isInstance && "id" in value;
  isInstance = isInstance && "createdAt" in value;
  isInstance = isInstance && "updatedAt" in value;
  isInstance = isInstance && "environmentId" in value;
  isInstance = isInstance && "name" in value;
  isInstance = isInstance && "guestAuthentication" in value;
  return isInstance;
}
function SpaceFromJSON(json) {
  return SpaceFromJSONTyped(json, false);
}
function SpaceFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "id": json["id"],
    "workbooksCount": !exists(json, "workbooksCount") ? void 0 : json["workbooksCount"],
    "filesCount": !exists(json, "filesCount") ? void 0 : json["filesCount"],
    "createdByUserId": !exists(json, "createdByUserId") ? void 0 : json["createdByUserId"],
    "createdByUserName": !exists(json, "createdByUserName") ? void 0 : json["createdByUserName"],
    "createdAt": new Date(json["createdAt"]),
    "updatedAt": new Date(json["updatedAt"]),
    "archivedAt": !exists(json, "archivedAt") ? void 0 : new Date(json["archivedAt"]),
    "expiredAt": !exists(json, "expiredAt") ? void 0 : new Date(json["expiredAt"]),
    "lastActivityAt": !exists(json, "lastActivityAt") ? void 0 : new Date(json["lastActivityAt"]),
    "guestLink": !exists(json, "guestLink") ? void 0 : json["guestLink"],
    "accessToken": !exists(json, "accessToken") ? void 0 : json["accessToken"],
    "isCollaborative": !exists(json, "isCollaborative") ? void 0 : json["isCollaborative"],
    "size": !exists(json, "size") ? void 0 : SpaceSizeFromJSON(json["size"]),
    "upgradedAt": !exists(json, "upgradedAt") ? void 0 : new Date(json["upgradedAt"]),
    "app": !exists(json, "app") ? void 0 : json["app"],
    "spaceConfigId": !exists(json, "spaceConfigId") ? void 0 : json["spaceConfigId"],
    "environmentId": json["environmentId"],
    "primaryWorkbookId": !exists(json, "primaryWorkbookId") ? void 0 : json["primaryWorkbookId"],
    "appId": !exists(json, "appId") ? void 0 : json["appId"],
    "name": json["name"],
    "displayOrder": !exists(json, "displayOrder") ? void 0 : json["displayOrder"],
    "metadata": !exists(json, "metadata") ? void 0 : json["metadata"],
    "settings": !exists(json, "settings") ? void 0 : SpaceSettingsFromJSON(json["settings"]),
    "actions": !exists(json, "actions") ? void 0 : json["actions"].map(ActionFromJSON),
    "guestAuthentication": json["guestAuthentication"].map(GuestAuthenticationEnumFromJSON),
    "access": !exists(json, "access") ? void 0 : json["access"].map(SpaceAccessFromJSON),
    "autoConfigure": !exists(json, "autoConfigure") ? void 0 : json["autoConfigure"],
    "namespace": !exists(json, "namespace") ? void 0 : json["namespace"],
    "labels": !exists(json, "labels") ? void 0 : json["labels"],
    "translationsPath": !exists(json, "translationsPath") ? void 0 : json["translationsPath"],
    "languageOverride": !exists(json, "languageOverride") ? void 0 : json["languageOverride"]
  };
}
function SpaceToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "id": value.id,
    "workbooksCount": value.workbooksCount,
    "filesCount": value.filesCount,
    "createdByUserId": value.createdByUserId,
    "createdByUserName": value.createdByUserName,
    "createdAt": value.createdAt.toISOString(),
    "updatedAt": value.updatedAt.toISOString(),
    "archivedAt": value.archivedAt === void 0 ? void 0 : value.archivedAt.toISOString(),
    "expiredAt": value.expiredAt === void 0 ? void 0 : value.expiredAt.toISOString(),
    "lastActivityAt": value.lastActivityAt === void 0 ? void 0 : value.lastActivityAt.toISOString().substr(0, 10),
    "guestLink": value.guestLink,
    "accessToken": value.accessToken,
    "isCollaborative": value.isCollaborative,
    "size": SpaceSizeToJSON(value.size),
    "upgradedAt": value.upgradedAt === void 0 ? void 0 : value.upgradedAt.toISOString(),
    "app": value.app,
    "spaceConfigId": value.spaceConfigId,
    "environmentId": value.environmentId,
    "primaryWorkbookId": value.primaryWorkbookId,
    "appId": value.appId,
    "name": value.name,
    "displayOrder": value.displayOrder,
    "metadata": value.metadata,
    "settings": SpaceSettingsToJSON(value.settings),
    "actions": value.actions === void 0 ? void 0 : value.actions.map(ActionToJSON),
    "guestAuthentication": value.guestAuthentication.map(GuestAuthenticationEnumToJSON),
    "access": value.access === void 0 ? void 0 : value.access.map(SpaceAccessToJSON),
    "autoConfigure": value.autoConfigure,
    "namespace": value.namespace,
    "labels": value.labels,
    "translationsPath": value.translationsPath,
    "languageOverride": value.languageOverride
  };
}

// build/models/SpaceConfig.ts
function instanceOfSpaceConfig(value) {
  let isInstance = true;
  return isInstance;
}
function SpaceConfigFromJSON(json) {
  return SpaceConfigFromJSONTyped(json, false);
}
function SpaceConfigFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "spaceConfigId": !exists(json, "spaceConfigId") ? void 0 : json["spaceConfigId"],
    "environmentId": !exists(json, "environmentId") ? void 0 : json["environmentId"],
    "primaryWorkbookId": !exists(json, "primaryWorkbookId") ? void 0 : json["primaryWorkbookId"],
    "appId": !exists(json, "appId") ? void 0 : json["appId"],
    "name": !exists(json, "name") ? void 0 : json["name"],
    "displayOrder": !exists(json, "displayOrder") ? void 0 : json["displayOrder"],
    "metadata": !exists(json, "metadata") ? void 0 : json["metadata"],
    "settings": !exists(json, "settings") ? void 0 : SpaceSettingsFromJSON(json["settings"]),
    "actions": !exists(json, "actions") ? void 0 : json["actions"].map(ActionFromJSON),
    "guestAuthentication": !exists(json, "guestAuthentication") ? void 0 : json["guestAuthentication"].map(GuestAuthenticationEnumFromJSON),
    "access": !exists(json, "access") ? void 0 : json["access"].map(SpaceAccessFromJSON),
    "autoConfigure": !exists(json, "autoConfigure") ? void 0 : json["autoConfigure"],
    "namespace": !exists(json, "namespace") ? void 0 : json["namespace"],
    "labels": !exists(json, "labels") ? void 0 : json["labels"],
    "translationsPath": !exists(json, "translationsPath") ? void 0 : json["translationsPath"],
    "languageOverride": !exists(json, "languageOverride") ? void 0 : json["languageOverride"],
    "archivedAt": !exists(json, "archivedAt") ? void 0 : json["archivedAt"] === null ? null : new Date(json["archivedAt"])
  };
}
function SpaceConfigToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "spaceConfigId": value.spaceConfigId,
    "environmentId": value.environmentId,
    "primaryWorkbookId": value.primaryWorkbookId,
    "appId": value.appId,
    "name": value.name,
    "displayOrder": value.displayOrder,
    "metadata": value.metadata,
    "settings": SpaceSettingsToJSON(value.settings),
    "actions": value.actions === void 0 ? void 0 : value.actions.map(ActionToJSON),
    "guestAuthentication": value.guestAuthentication === void 0 ? void 0 : value.guestAuthentication.map(GuestAuthenticationEnumToJSON),
    "access": value.access === void 0 ? void 0 : value.access.map(SpaceAccessToJSON),
    "autoConfigure": value.autoConfigure,
    "namespace": value.namespace,
    "labels": value.labels,
    "translationsPath": value.translationsPath,
    "languageOverride": value.languageOverride,
    "archivedAt": value.archivedAt === void 0 ? void 0 : value.archivedAt === null ? null : value.archivedAt.toISOString()
  };
}

// build/models/SpacePattern.ts
function instanceOfSpacePattern(value) {
  let isInstance = true;
  isInstance = isInstance && "id" in value;
  isInstance = isInstance && "slug" in value;
  isInstance = isInstance && "name" in value;
  isInstance = isInstance && "blueprints" in value;
  return isInstance;
}
function SpacePatternFromJSON(json) {
  return SpacePatternFromJSONTyped(json, false);
}
function SpacePatternFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "id": json["id"],
    "slug": json["slug"],
    "name": json["name"],
    "blueprints": json["blueprints"].map(BlueprintFromJSON),
    "actions": !exists(json, "actions") ? void 0 : json["actions"].map(ActionFromJSON)
  };
}
function SpacePatternToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "id": value.id,
    "slug": value.slug,
    "name": value.name,
    "blueprints": value.blueprints.map(BlueprintToJSON),
    "actions": value.actions === void 0 ? void 0 : value.actions.map(ActionToJSON)
  };
}

// build/models/SpaceConfigResponse.ts
function instanceOfSpaceConfigResponse(value) {
  let isInstance = true;
  return isInstance;
}
function SpaceConfigResponseFromJSON(json) {
  return SpaceConfigResponseFromJSONTyped(json, false);
}
function SpaceConfigResponseFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "data": !exists(json, "data") ? void 0 : SpacePatternFromJSON(json["data"])
  };
}
function SpaceConfigResponseToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "data": SpacePatternToJSON(value.data)
  };
}

// build/models/SpaceConfigsResponse.ts
function instanceOfSpaceConfigsResponse(value) {
  let isInstance = true;
  return isInstance;
}
function SpaceConfigsResponseFromJSON(json) {
  return SpaceConfigsResponseFromJSONTyped(json, false);
}
function SpaceConfigsResponseFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "data": !exists(json, "data") ? void 0 : json["data"].map(SpacePatternFromJSON)
  };
}
function SpaceConfigsResponseToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "data": value.data === void 0 ? void 0 : value.data.map(SpacePatternToJSON)
  };
}

// build/models/SpacePatternConfig.ts
function instanceOfSpacePatternConfig(value) {
  let isInstance = true;
  isInstance = isInstance && "slug" in value;
  isInstance = isInstance && "name" in value;
  isInstance = isInstance && "blueprints" in value;
  return isInstance;
}
function SpacePatternConfigFromJSON(json) {
  return SpacePatternConfigFromJSONTyped(json, false);
}
function SpacePatternConfigFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "slug": json["slug"],
    "name": json["name"],
    "blueprints": json["blueprints"].map(BlueprintFromJSON),
    "actions": !exists(json, "actions") ? void 0 : json["actions"].map(ActionFromJSON)
  };
}
function SpacePatternConfigToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "slug": value.slug,
    "name": value.name,
    "blueprints": value.blueprints.map(BlueprintToJSON),
    "actions": value.actions === void 0 ? void 0 : value.actions.map(ActionToJSON)
  };
}

// build/models/SpaceResponse.ts
function instanceOfSpaceResponse(value) {
  let isInstance = true;
  return isInstance;
}
function SpaceResponseFromJSON(json) {
  return SpaceResponseFromJSONTyped(json, false);
}
function SpaceResponseFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "data": !exists(json, "data") ? void 0 : SpaceFromJSON(json["data"])
  };
}
function SpaceResponseToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "data": SpaceToJSON(value.data)
  };
}

// build/models/SpacesResponse.ts
function instanceOfSpacesResponse(value) {
  let isInstance = true;
  return isInstance;
}
function SpacesResponseFromJSON(json) {
  return SpacesResponseFromJSONTyped(json, false);
}
function SpacesResponseFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "pagination": !exists(json, "pagination") ? void 0 : PaginationFromJSON(json["pagination"]),
    "data": !exists(json, "data") ? void 0 : json["data"].map(SpaceFromJSON)
  };
}
function SpacesResponseToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "pagination": PaginationToJSON(value.pagination),
    "data": value.data === void 0 ? void 0 : value.data.map(SpaceToJSON)
  };
}

// build/models/StringListPropertyAllOf.ts
var StringListPropertyAllOfTypeEnum = {
  StringList: "string-list"
};
function instanceOfStringListPropertyAllOf(value) {
  let isInstance = true;
  return isInstance;
}
function StringListPropertyAllOfFromJSON(json) {
  return StringListPropertyAllOfFromJSONTyped(json, false);
}
function StringListPropertyAllOfFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "type": !exists(json, "type") ? void 0 : json["type"]
  };
}
function StringListPropertyAllOfToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "type": value.type
  };
}

// build/models/StringPropertyAllOf.ts
var StringPropertyAllOfTypeEnum = {
  String: "string"
};
function instanceOfStringPropertyAllOf(value) {
  let isInstance = true;
  return isInstance;
}
function StringPropertyAllOfFromJSON(json) {
  return StringPropertyAllOfFromJSONTyped(json, false);
}
function StringPropertyAllOfFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "type": !exists(json, "type") ? void 0 : json["type"]
  };
}
function StringPropertyAllOfToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "type": value.type
  };
}

// build/models/StripeWebhookRequestBodyRequest.ts
function instanceOfStripeWebhookRequestBodyRequest(value) {
  let isInstance = true;
  return isInstance;
}
function StripeWebhookRequestBodyRequestFromJSON(json) {
  return StripeWebhookRequestBodyRequestFromJSONTyped(json, false);
}
function StripeWebhookRequestBodyRequestFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "id": !exists(json, "id") ? void 0 : json["id"],
    "idempotency_key": !exists(json, "idempotency_key") ? void 0 : json["idempotency_key"]
  };
}
function StripeWebhookRequestBodyRequestToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "id": value.id,
    "idempotency_key": value.idempotency_key
  };
}

// build/models/StripeWebhookRequestBody.ts
function instanceOfStripeWebhookRequestBody(value) {
  let isInstance = true;
  return isInstance;
}
function StripeWebhookRequestBodyFromJSON(json) {
  return StripeWebhookRequestBodyFromJSONTyped(json, false);
}
function StripeWebhookRequestBodyFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "id": !exists(json, "id") ? void 0 : json["id"],
    "object": !exists(json, "object") ? void 0 : json["object"],
    "data": !exists(json, "data") ? void 0 : json["data"],
    "request": !exists(json, "request") ? void 0 : StripeWebhookRequestBodyRequestFromJSON(json["request"]),
    "type": !exists(json, "type") ? void 0 : json["type"]
  };
}
function StripeWebhookRequestBodyToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "id": value.id,
    "object": value.object,
    "data": value.data,
    "request": StripeWebhookRequestBodyRequestToJSON(value.request),
    "type": value.type
  };
}

// build/models/SubscriptionUpdatesBody.ts
function instanceOfSubscriptionUpdatesBody(value) {
  let isInstance = true;
  isInstance = isInstance && "lineItems" in value;
  return isInstance;
}
function SubscriptionUpdatesBodyFromJSON(json) {
  return SubscriptionUpdatesBodyFromJSONTyped(json, false);
}
function SubscriptionUpdatesBodyFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "lineItems": json["lineItems"].map(LineItemFromJSON)
  };
}
function SubscriptionUpdatesBodyToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "lineItems": value.lineItems.map(LineItemToJSON)
  };
}

// build/models/Success.ts
function instanceOfSuccess(value) {
  let isInstance = true;
  isInstance = isInstance && "success" in value;
  return isInstance;
}
function SuccessFromJSON(json) {
  return SuccessFromJSONTyped(json, false);
}
function SuccessFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "success": json["success"]
  };
}
function SuccessToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "success": value.success
  };
}

// build/models/SuccessResponse.ts
function instanceOfSuccessResponse(value) {
  let isInstance = true;
  return isInstance;
}
function SuccessResponseFromJSON(json) {
  return SuccessResponseFromJSONTyped(json, false);
}
function SuccessResponseFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "data": !exists(json, "data") ? void 0 : SuccessFromJSON(json["data"])
  };
}
function SuccessResponseToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "data": SuccessToJSON(value.data)
  };
}

// build/models/UniqueConstraintAllOf.ts
var UniqueConstraintAllOfTypeEnum = {
  Unique: "unique"
};
function instanceOfUniqueConstraintAllOf(value) {
  let isInstance = true;
  isInstance = isInstance && "type" in value;
  return isInstance;
}
function UniqueConstraintAllOfFromJSON(json) {
  return UniqueConstraintAllOfFromJSONTyped(json, false);
}
function UniqueConstraintAllOfFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "type": json["type"],
    "config": !exists(json, "config") ? void 0 : UniqueConstraintAllOfConfigFromJSON(json["config"])
  };
}
function UniqueConstraintAllOfToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "type": value.type,
    "config": UniqueConstraintAllOfConfigToJSON(value.config)
  };
}

// build/models/UpdateAccount.ts
function instanceOfUpdateAccount(value) {
  let isInstance = true;
  isInstance = isInstance && "defaultAppId" in value;
  return isInstance;
}
function UpdateAccountFromJSON(json) {
  return UpdateAccountFromJSONTyped(json, false);
}
function UpdateAccountFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "defaultAppId": json["defaultAppId"]
  };
}
function UpdateAccountToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "defaultAppId": value.defaultAppId
  };
}

// build/models/UpdateEnvironmentByIdRequest.ts
var UpdateEnvironmentByIdRequestDataRetentionPolicyEnum = {
  LastActivity: "lastActivity",
  SinceCreated: "sinceCreated"
};
function instanceOfUpdateEnvironmentByIdRequest(value) {
  let isInstance = true;
  return isInstance;
}
function UpdateEnvironmentByIdRequestFromJSON(json) {
  return UpdateEnvironmentByIdRequestFromJSONTyped(json, false);
}
function UpdateEnvironmentByIdRequestFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "name": !exists(json, "name") ? void 0 : json["name"],
    "isProd": !exists(json, "isProd") ? void 0 : json["isProd"],
    "guestAuthentication": !exists(json, "guestAuthentication") ? void 0 : json["guestAuthentication"].map(GuestAuthenticationEnumFromJSON),
    "metadata": !exists(json, "metadata") ? void 0 : json["metadata"],
    "translationsPath": !exists(json, "translationsPath") ? void 0 : json["translationsPath"],
    "namespaces": !exists(json, "namespaces") ? void 0 : json["namespaces"],
    "languageOverride": !exists(json, "languageOverride") ? void 0 : json["languageOverride"],
    "dataRetentionPolicy": !exists(json, "dataRetentionPolicy") ? void 0 : json["dataRetentionPolicy"]
  };
}
function UpdateEnvironmentByIdRequestToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "name": value.name,
    "isProd": value.isProd,
    "guestAuthentication": value.guestAuthentication === void 0 ? void 0 : value.guestAuthentication.map(GuestAuthenticationEnumToJSON),
    "metadata": value.metadata,
    "translationsPath": value.translationsPath,
    "namespaces": value.namespaces,
    "languageOverride": value.languageOverride,
    "dataRetentionPolicy": value.dataRetentionPolicy
  };
}

// build/models/UpdateJobExecutionPlanByIdRequest.ts
function instanceOfUpdateJobExecutionPlanByIdRequest(value) {
  let isInstance = true;
  isInstance = isInstance && "fieldMapping" in value;
  isInstance = isInstance && "unmappedSourceFields" in value;
  isInstance = isInstance && "unmappedDestinationFields" in value;
  return isInstance;
}
function UpdateJobExecutionPlanByIdRequestFromJSON(json) {
  return UpdateJobExecutionPlanByIdRequestFromJSONTyped(json, false);
}
function UpdateJobExecutionPlanByIdRequestFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "fileId": !exists(json, "fileId") ? void 0 : json["fileId"],
    "jobId": !exists(json, "jobId") ? void 0 : json["jobId"],
    "fieldMapping": json["fieldMapping"].map(EdgeFromJSON),
    "unmappedSourceFields": json["unmappedSourceFields"].map(SourceFieldFromJSON),
    "unmappedDestinationFields": json["unmappedDestinationFields"].map(DestinationFieldFromJSON),
    "programId": !exists(json, "programId") ? void 0 : json["programId"]
  };
}
function UpdateJobExecutionPlanByIdRequestToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "fileId": value.fileId,
    "jobId": value.jobId,
    "fieldMapping": value.fieldMapping.map(EdgeToJSON),
    "unmappedSourceFields": value.unmappedSourceFields.map(SourceFieldToJSON),
    "unmappedDestinationFields": value.unmappedDestinationFields.map(DestinationFieldToJSON),
    "programId": value.programId
  };
}

// build/models/UpdateJobExecutionPlanFieldsRequest.ts
function instanceOfUpdateJobExecutionPlanFieldsRequest(value) {
  let isInstance = true;
  return isInstance;
}
function UpdateJobExecutionPlanFieldsRequestFromJSON(json) {
  return UpdateJobExecutionPlanFieldsRequestFromJSONTyped(json, false);
}
function UpdateJobExecutionPlanFieldsRequestFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "fileId": !exists(json, "fileId") ? void 0 : json["fileId"],
    "jobId": !exists(json, "jobId") ? void 0 : json["jobId"],
    "fieldMapping": !exists(json, "fieldMapping") ? void 0 : json["fieldMapping"].map(EdgeFromJSON),
    "unmappedSourceFields": !exists(json, "unmappedSourceFields") ? void 0 : json["unmappedSourceFields"].map(SourceFieldFromJSON),
    "unmappedDestinationFields": !exists(json, "unmappedDestinationFields") ? void 0 : json["unmappedDestinationFields"].map(DestinationFieldFromJSON),
    "programId": !exists(json, "programId") ? void 0 : json["programId"]
  };
}
function UpdateJobExecutionPlanFieldsRequestToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "fileId": value.fileId,
    "jobId": value.jobId,
    "fieldMapping": value.fieldMapping === void 0 ? void 0 : value.fieldMapping.map(EdgeToJSON),
    "unmappedSourceFields": value.unmappedSourceFields === void 0 ? void 0 : value.unmappedSourceFields.map(SourceFieldToJSON),
    "unmappedDestinationFields": value.unmappedDestinationFields === void 0 ? void 0 : value.unmappedDestinationFields.map(DestinationFieldToJSON),
    "programId": value.programId
  };
}

// build/models/UpdateUserRequest.ts
function instanceOfUpdateUserRequest(value) {
  let isInstance = true;
  return isInstance;
}
function UpdateUserRequestFromJSON(json) {
  return UpdateUserRequestFromJSONTyped(json, false);
}
function UpdateUserRequestFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "name": !exists(json, "name") ? void 0 : json["name"],
    "dashboard": !exists(json, "dashboard") ? void 0 : json["dashboard"]
  };
}
function UpdateUserRequestToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "name": value.name,
    "dashboard": value.dashboard
  };
}

// build/models/UpdateViewRequest.ts
function instanceOfUpdateViewRequest(value) {
  let isInstance = true;
  isInstance = isInstance && "config" in value;
  return isInstance;
}
function UpdateViewRequestFromJSON(json) {
  return UpdateViewRequestFromJSONTyped(json, false);
}
function UpdateViewRequestFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "config": ViewConfigFromJSON(json["config"]),
    "name": !exists(json, "name") ? void 0 : json["name"]
  };
}
function UpdateViewRequestToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "config": ViewConfigToJSON(value.config),
    "name": value.name
  };
}

// build/models/UpgradeSpaceConfig.ts
function instanceOfUpgradeSpaceConfig(value) {
  let isInstance = true;
  isInstance = isInstance && "id" in value;
  return isInstance;
}
function UpgradeSpaceConfigFromJSON(json) {
  return UpgradeSpaceConfigFromJSONTyped(json, false);
}
function UpgradeSpaceConfigFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "id": json["id"]
  };
}
function UpgradeSpaceConfigToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "id": value.id
  };
}

// build/models/UploadConfig.ts
function instanceOfUploadConfig(value) {
  let isInstance = true;
  isInstance = isInstance && "fileId" in value;
  return isInstance;
}
function UploadConfigFromJSON(json) {
  return UploadConfigFromJSONTyped(json, false);
}
function UploadConfigFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "fileId": json["fileId"]
  };
}
function UploadConfigToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "fileId": value.fileId
  };
}

// build/models/User.ts
function instanceOfUser(value) {
  let isInstance = true;
  isInstance = isInstance && "id" in value;
  isInstance = isInstance && "email" in value;
  isInstance = isInstance && "name" in value;
  isInstance = isInstance && "accountId" in value;
  return isInstance;
}
function UserFromJSON(json) {
  return UserFromJSONTyped(json, false);
}
function UserFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "id": json["id"],
    "idp": !exists(json, "idp") ? void 0 : json["idp"],
    "idpRef": !exists(json, "idpRef") ? void 0 : json["idpRef"],
    "metadata": !exists(json, "metadata") ? void 0 : json["metadata"],
    "createdAt": !exists(json, "createdAt") ? void 0 : new Date(json["createdAt"]),
    "updatedAt": !exists(json, "updatedAt") ? void 0 : new Date(json["updatedAt"]),
    "lastSeenAt": !exists(json, "lastSeenAt") ? void 0 : new Date(json["lastSeenAt"]),
    "dashboard": !exists(json, "dashboard") ? void 0 : json["dashboard"],
    "email": json["email"],
    "name": json["name"],
    "accountId": json["accountId"]
  };
}
function UserToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "id": value.id,
    "idp": value.idp,
    "idpRef": value.idpRef,
    "metadata": value.metadata,
    "createdAt": value.createdAt === void 0 ? void 0 : value.createdAt.toISOString(),
    "updatedAt": value.updatedAt === void 0 ? void 0 : value.updatedAt.toISOString(),
    "lastSeenAt": value.lastSeenAt === void 0 ? void 0 : value.lastSeenAt.toISOString(),
    "dashboard": value.dashboard,
    "email": value.email,
    "name": value.name,
    "accountId": value.accountId
  };
}

// build/models/UserConfig.ts
function instanceOfUserConfig(value) {
  let isInstance = true;
  isInstance = isInstance && "email" in value;
  isInstance = isInstance && "name" in value;
  isInstance = isInstance && "accountId" in value;
  return isInstance;
}
function UserConfigFromJSON(json) {
  return UserConfigFromJSONTyped(json, false);
}
function UserConfigFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "email": json["email"],
    "name": json["name"],
    "accountId": json["accountId"],
    "createdAt": !exists(json, "createdAt") ? void 0 : new Date(json["createdAt"]),
    "updatedAt": !exists(json, "updatedAt") ? void 0 : new Date(json["updatedAt"])
  };
}
function UserConfigToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "email": value.email,
    "name": value.name,
    "accountId": value.accountId,
    "createdAt": value.createdAt === void 0 ? void 0 : value.createdAt.toISOString(),
    "updatedAt": value.updatedAt === void 0 ? void 0 : value.updatedAt.toISOString()
  };
}

// build/models/UserCreateAndInviteBody.ts
function instanceOfUserCreateAndInviteBody(value) {
  let isInstance = true;
  isInstance = isInstance && "email" in value;
  isInstance = isInstance && "name" in value;
  isInstance = isInstance && "actorRoles" in value;
  return isInstance;
}
function UserCreateAndInviteBodyFromJSON(json) {
  return UserCreateAndInviteBodyFromJSONTyped(json, false);
}
function UserCreateAndInviteBodyFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "email": json["email"],
    "name": json["name"],
    "actorRoles": json["actorRoles"].map(AssignActorRoleRequestFromJSON)
  };
}
function UserCreateAndInviteBodyToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "email": value.email,
    "name": value.name,
    "actorRoles": value.actorRoles.map(AssignActorRoleRequestToJSON)
  };
}

// build/models/UserResponse.ts
function instanceOfUserResponse(value) {
  let isInstance = true;
  return isInstance;
}
function UserResponseFromJSON(json) {
  return UserResponseFromJSONTyped(json, false);
}
function UserResponseFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "data": !exists(json, "data") ? void 0 : UserFromJSON(json["data"])
  };
}
function UserResponseToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "data": UserToJSON(value.data)
  };
}

// build/models/UserWithRoles.ts
function instanceOfUserWithRoles(value) {
  let isInstance = true;
  isInstance = isInstance && "email" in value;
  isInstance = isInstance && "name" in value;
  isInstance = isInstance && "accountId" in value;
  return isInstance;
}
function UserWithRolesFromJSON(json) {
  return UserWithRolesFromJSONTyped(json, false);
}
function UserWithRolesFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "actorRoles": !exists(json, "actorRoles") ? void 0 : json["actorRoles"].map(ActorRoleResponseFromJSON),
    "email": json["email"],
    "name": json["name"],
    "accountId": json["accountId"],
    "createdAt": !exists(json, "createdAt") ? void 0 : new Date(json["createdAt"]),
    "updatedAt": !exists(json, "updatedAt") ? void 0 : new Date(json["updatedAt"])
  };
}
function UserWithRolesToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "actorRoles": value.actorRoles === void 0 ? void 0 : value.actorRoles.map(ActorRoleResponseToJSON),
    "email": value.email,
    "name": value.name,
    "accountId": value.accountId,
    "createdAt": value.createdAt === void 0 ? void 0 : value.createdAt.toISOString(),
    "updatedAt": value.updatedAt === void 0 ? void 0 : value.updatedAt.toISOString()
  };
}

// build/models/UsersResponse.ts
function instanceOfUsersResponse(value) {
  let isInstance = true;
  return isInstance;
}
function UsersResponseFromJSON(json) {
  return UsersResponseFromJSONTyped(json, false);
}
function UsersResponseFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "data": !exists(json, "data") ? void 0 : json["data"].map(UserFromJSON)
  };
}
function UsersResponseToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "data": value.data === void 0 ? void 0 : value.data.map(UserToJSON)
  };
}

// build/models/Validation.ts
function instanceOfValidation(value) {
  let isInstance = true;
  isInstance = isInstance && "success" in value;
  return isInstance;
}
function ValidationFromJSON(json) {
  return ValidationFromJSONTyped(json, false);
}
function ValidationFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "success": json["success"],
    "errors": !exists(json, "errors") ? void 0 : json["errors"]
  };
}
function ValidationToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "success": value.success,
    "errors": value.errors
  };
}

// build/models/VersionResponseData.ts
function instanceOfVersionResponseData(value) {
  let isInstance = true;
  return isInstance;
}
function VersionResponseDataFromJSON(json) {
  return VersionResponseDataFromJSONTyped(json, false);
}
function VersionResponseDataFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "versionId": !exists(json, "versionId") ? void 0 : json["versionId"],
    "commitId": !exists(json, "commitId") ? void 0 : json["commitId"]
  };
}
function VersionResponseDataToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "versionId": value.versionId,
    "commitId": value.commitId
  };
}

// build/models/VersionResponse.ts
function instanceOfVersionResponse(value) {
  let isInstance = true;
  return isInstance;
}
function VersionResponseFromJSON(json) {
  return VersionResponseFromJSONTyped(json, false);
}
function VersionResponseFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "data": !exists(json, "data") ? void 0 : VersionResponseDataFromJSON(json["data"])
  };
}
function VersionResponseToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "data": VersionResponseDataToJSON(value.data)
  };
}

// build/models/View.ts
function instanceOfView(value) {
  let isInstance = true;
  isInstance = isInstance && "id" in value;
  isInstance = isInstance && "sheetId" in value;
  isInstance = isInstance && "name" in value;
  isInstance = isInstance && "config" in value;
  isInstance = isInstance && "createdBy" in value;
  return isInstance;
}
function ViewFromJSON(json) {
  return ViewFromJSONTyped(json, false);
}
function ViewFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "id": json["id"],
    "sheetId": json["sheetId"],
    "name": json["name"],
    "config": ViewConfigFromJSON(json["config"]),
    "createdBy": json["createdBy"]
  };
}
function ViewToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "id": value.id,
    "sheetId": value.sheetId,
    "name": value.name,
    "config": ViewConfigToJSON(value.config),
    "createdBy": value.createdBy
  };
}

// build/models/ViewResponse.ts
function instanceOfViewResponse(value) {
  let isInstance = true;
  return isInstance;
}
function ViewResponseFromJSON(json) {
  return ViewResponseFromJSONTyped(json, false);
}
function ViewResponseFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "data": !exists(json, "data") ? void 0 : ViewFromJSON(json["data"])
  };
}
function ViewResponseToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "data": ViewToJSON(value.data)
  };
}

// build/models/ViewsResponse.ts
function instanceOfViewsResponse(value) {
  let isInstance = true;
  return isInstance;
}
function ViewsResponseFromJSON(json) {
  return ViewsResponseFromJSONTyped(json, false);
}
function ViewsResponseFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "data": !exists(json, "data") ? void 0 : json["data"].map(ViewFromJSON)
  };
}
function ViewsResponseToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "data": value.data === void 0 ? void 0 : value.data.map(ViewToJSON)
  };
}

// build/models/WorkbookConfigSettings.ts
function instanceOfWorkbookConfigSettings(value) {
  let isInstance = true;
  return isInstance;
}
function WorkbookConfigSettingsFromJSON(json) {
  return WorkbookConfigSettingsFromJSONTyped(json, false);
}
function WorkbookConfigSettingsFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "trackChanges": !exists(json, "trackChanges") ? void 0 : json["trackChanges"]
  };
}
function WorkbookConfigSettingsToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "trackChanges": value.trackChanges
  };
}

// build/models/WorkbookTreatmentsEnum.ts
var WorkbookTreatmentsEnum = {
  ExtractedFromSource: "EXTRACTED_FROM_SOURCE"
};
function WorkbookTreatmentsEnumFromJSON(json) {
  return WorkbookTreatmentsEnumFromJSONTyped(json, false);
}
function WorkbookTreatmentsEnumFromJSONTyped(json, ignoreDiscriminator) {
  return json;
}
function WorkbookTreatmentsEnumToJSON(value) {
  return value;
}

// build/models/Workbook.ts
function instanceOfWorkbook(value) {
  let isInstance = true;
  isInstance = isInstance && "id" in value;
  isInstance = isInstance && "name" in value;
  isInstance = isInstance && "spaceId" in value;
  isInstance = isInstance && "environmentId" in value;
  isInstance = isInstance && "updatedAt" in value;
  isInstance = isInstance && "createdAt" in value;
  return isInstance;
}
function WorkbookFromJSON(json) {
  return WorkbookFromJSONTyped(json, false);
}
function WorkbookFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "treatments": !exists(json, "treatments") ? void 0 : json["treatments"].map(WorkbookTreatmentsEnumFromJSON),
    "expiredAt": !exists(json, "expiredAt") ? void 0 : new Date(json["expiredAt"]),
    "sheets": !exists(json, "sheets") ? void 0 : json["sheets"].map(SheetFromJSON),
    "id": json["id"],
    "name": json["name"],
    "spaceId": json["spaceId"],
    "environmentId": json["environmentId"],
    "labels": !exists(json, "labels") ? void 0 : json["labels"],
    "actions": !exists(json, "actions") ? void 0 : json["actions"].map(ActionFromJSON),
    "settings": !exists(json, "settings") ? void 0 : WorkbookConfigSettingsFromJSON(json["settings"]),
    "updatedAt": new Date(json["updatedAt"]),
    "createdAt": new Date(json["createdAt"]),
    "namespace": !exists(json, "namespace") ? void 0 : json["namespace"],
    "metadata": !exists(json, "metadata") ? void 0 : json["metadata"]
  };
}
function WorkbookToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "treatments": value.treatments === void 0 ? void 0 : value.treatments.map(WorkbookTreatmentsEnumToJSON),
    "expiredAt": value.expiredAt === void 0 ? void 0 : value.expiredAt.toISOString(),
    "sheets": value.sheets === void 0 ? void 0 : value.sheets.map(SheetToJSON),
    "id": value.id,
    "name": value.name,
    "spaceId": value.spaceId,
    "environmentId": value.environmentId,
    "labels": value.labels,
    "actions": value.actions === void 0 ? void 0 : value.actions.map(ActionToJSON),
    "settings": WorkbookConfigSettingsToJSON(value.settings),
    "updatedAt": value.updatedAt.toISOString(),
    "createdAt": value.createdAt.toISOString(),
    "namespace": value.namespace,
    "metadata": value.metadata
  };
}

// build/models/WorkbookConfig.ts
function instanceOfWorkbookConfig(value) {
  let isInstance = true;
  isInstance = isInstance && "name" in value;
  return isInstance;
}
function WorkbookConfigFromJSON(json) {
  return WorkbookConfigFromJSONTyped(json, false);
}
function WorkbookConfigFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "name": json["name"],
    "namespace": !exists(json, "namespace") ? void 0 : json["namespace"],
    "labels": !exists(json, "labels") ? void 0 : json["labels"],
    "spaceId": !exists(json, "spaceId") ? void 0 : json["spaceId"],
    "environmentId": !exists(json, "environmentId") ? void 0 : json["environmentId"],
    "sheets": !exists(json, "sheets") ? void 0 : json["sheets"].map(SheetConfigFromJSON),
    "actions": !exists(json, "actions") ? void 0 : json["actions"].map(ActionFromJSON),
    "settings": !exists(json, "settings") ? void 0 : WorkbookConfigSettingsFromJSON(json["settings"]),
    "metadata": !exists(json, "metadata") ? void 0 : json["metadata"],
    "treatments": !exists(json, "treatments") ? void 0 : json["treatments"].map(WorkbookTreatmentsEnumFromJSON)
  };
}
function WorkbookConfigToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "name": value.name,
    "namespace": value.namespace,
    "labels": value.labels,
    "spaceId": value.spaceId,
    "environmentId": value.environmentId,
    "sheets": value.sheets === void 0 ? void 0 : value.sheets.map(SheetConfigToJSON),
    "actions": value.actions === void 0 ? void 0 : value.actions.map(ActionToJSON),
    "settings": WorkbookConfigSettingsToJSON(value.settings),
    "metadata": value.metadata,
    "treatments": value.treatments === void 0 ? void 0 : value.treatments.map(WorkbookTreatmentsEnumToJSON)
  };
}

// build/models/WorkbookResponse.ts
function instanceOfWorkbookResponse(value) {
  let isInstance = true;
  return isInstance;
}
function WorkbookResponseFromJSON(json) {
  return WorkbookResponseFromJSONTyped(json, false);
}
function WorkbookResponseFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "data": !exists(json, "data") ? void 0 : WorkbookFromJSON(json["data"])
  };
}
function WorkbookResponseToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "data": WorkbookToJSON(value.data)
  };
}

// build/models/WorkbookSidebarConfigAllOf.ts
function instanceOfWorkbookSidebarConfigAllOf(value) {
  let isInstance = true;
  isInstance = isInstance && "workbookId" in value;
  return isInstance;
}
function WorkbookSidebarConfigAllOfFromJSON(json) {
  return WorkbookSidebarConfigAllOfFromJSONTyped(json, false);
}
function WorkbookSidebarConfigAllOfFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "workbookId": json["workbookId"]
  };
}
function WorkbookSidebarConfigAllOfToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "workbookId": value.workbookId
  };
}

// build/models/WorkbookUpdate.ts
function instanceOfWorkbookUpdate(value) {
  let isInstance = true;
  return isInstance;
}
function WorkbookUpdateFromJSON(json) {
  return WorkbookUpdateFromJSONTyped(json, false);
}
function WorkbookUpdateFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "id": !exists(json, "id") ? void 0 : json["id"],
    "name": !exists(json, "name") ? void 0 : json["name"],
    "spaceId": !exists(json, "spaceId") ? void 0 : json["spaceId"],
    "environmentId": !exists(json, "environmentId") ? void 0 : json["environmentId"],
    "sheets": !exists(json, "sheets") ? void 0 : json["sheets"].map(SheetConfigOrUpdateFromJSON),
    "labels": !exists(json, "labels") ? void 0 : json["labels"],
    "actions": !exists(json, "actions") ? void 0 : json["actions"].map(ActionFromJSON),
    "settings": !exists(json, "settings") ? void 0 : WorkbookConfigSettingsFromJSON(json["settings"]),
    "updatedAt": !exists(json, "updatedAt") ? void 0 : new Date(json["updatedAt"]),
    "createdAt": !exists(json, "createdAt") ? void 0 : new Date(json["createdAt"]),
    "namespace": !exists(json, "namespace") ? void 0 : json["namespace"],
    "metadata": !exists(json, "metadata") ? void 0 : json["metadata"]
  };
}
function WorkbookUpdateToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "id": value.id,
    "name": value.name,
    "spaceId": value.spaceId,
    "environmentId": value.environmentId,
    "sheets": value.sheets === void 0 ? void 0 : value.sheets.map(SheetConfigOrUpdateToJSON),
    "labels": value.labels,
    "actions": value.actions === void 0 ? void 0 : value.actions.map(ActionToJSON),
    "settings": WorkbookConfigSettingsToJSON(value.settings),
    "updatedAt": value.updatedAt === void 0 ? void 0 : value.updatedAt.toISOString(),
    "createdAt": value.createdAt === void 0 ? void 0 : value.createdAt.toISOString(),
    "namespace": value.namespace,
    "metadata": value.metadata
  };
}

// build/models/WorkbooksResponse.ts
function instanceOfWorkbooksResponse(value) {
  let isInstance = true;
  return isInstance;
}
function WorkbooksResponseFromJSON(json) {
  return WorkbooksResponseFromJSONTyped(json, false);
}
function WorkbooksResponseFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "data": !exists(json, "data") ? void 0 : json["data"].map(WorkbookFromJSON)
  };
}
function WorkbooksResponseToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "data": value.data === void 0 ? void 0 : value.data.map(WorkbookToJSON)
  };
}

// build/models/WriteSecret.ts
function instanceOfWriteSecret(value) {
  let isInstance = true;
  isInstance = isInstance && "name" in value;
  isInstance = isInstance && "value" in value;
  return isInstance;
}
function WriteSecretFromJSON(json) {
  return WriteSecretFromJSONTyped(json, false);
}
function WriteSecretFromJSONTyped(json, ignoreDiscriminator) {
  if (json === void 0 || json === null) {
    return json;
  }
  return {
    "name": json["name"],
    "value": json["value"],
    "environmentId": !exists(json, "environmentId") ? void 0 : json["environmentId"],
    "spaceId": !exists(json, "spaceId") ? void 0 : json["spaceId"]
  };
}
function WriteSecretToJSON(value) {
  if (value === void 0) {
    return void 0;
  }
  if (value === null) {
    return null;
  }
  return {
    "name": value.name,
    "value": value.value,
    "environmentId": value.environmentId,
    "spaceId": value.spaceId
  };
}

// build/apis/DefaultApi.ts
var DefaultApi = class extends BaseAPI {
  /**
   * Acknowledge an event
   */
  async ackEventRaw(requestParameters, initOverrides) {
    if (requestParameters.eventId === null || requestParameters.eventId === void 0) {
      throw new RequiredError("eventId", "Required parameter requestParameters.eventId was null or undefined when calling ackEvent.");
    }
    const queryParameters = {};
    const headerParameters = {};
    if (this.configuration && this.configuration.accessToken) {
      const token = this.configuration.accessToken;
      const tokenString = await token("BearerAuth", []);
      if (tokenString) {
        headerParameters["Authorization"] = `Bearer ${tokenString}`;
      }
    }
    const response = await this.request({
      path: `/events/{eventId}/ack`.replace(`{${"eventId"}}`, encodeURIComponent(String(requestParameters.eventId))),
      method: "POST",
      headers: headerParameters,
      query: queryParameters
    }, initOverrides);
    return new JSONApiResponse(response, (jsonValue) => SuccessResponseFromJSON(jsonValue));
  }
  /**
   * Acknowledge an event
   */
  async ackEvent(requestParameters, initOverrides) {
    const response = await this.ackEventRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Acknowledge an event
   */
  async ackEventDeprecatedRaw(requestParameters, initOverrides) {
    if (requestParameters.environmentId === null || requestParameters.environmentId === void 0) {
      throw new RequiredError("environmentId", "Required parameter requestParameters.environmentId was null or undefined when calling ackEventDeprecated.");
    }
    if (requestParameters.eventId === null || requestParameters.eventId === void 0) {
      throw new RequiredError("eventId", "Required parameter requestParameters.eventId was null or undefined when calling ackEventDeprecated.");
    }
    const queryParameters = {};
    const headerParameters = {};
    if (this.configuration && this.configuration.accessToken) {
      const token = this.configuration.accessToken;
      const tokenString = await token("BearerAuth", []);
      if (tokenString) {
        headerParameters["Authorization"] = `Bearer ${tokenString}`;
      }
    }
    const response = await this.request({
      path: `/environments/{environmentId}/events/{eventId}/ack`.replace(`{${"environmentId"}}`, encodeURIComponent(String(requestParameters.environmentId))).replace(`{${"eventId"}}`, encodeURIComponent(String(requestParameters.eventId))),
      method: "POST",
      headers: headerParameters,
      query: queryParameters
    }, initOverrides);
    return new JSONApiResponse(response, (jsonValue) => SuccessResponseFromJSON(jsonValue));
  }
  /**
   * Acknowledge an event
   */
  async ackEventDeprecated(requestParameters, initOverrides) {
    const response = await this.ackEventDeprecatedRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Acknowledge a job and return the job
   * Acknowledge a job
   */
  async ackJobRaw(requestParameters, initOverrides) {
    if (requestParameters.jobId === null || requestParameters.jobId === void 0) {
      throw new RequiredError("jobId", "Required parameter requestParameters.jobId was null or undefined when calling ackJob.");
    }
    const queryParameters = {};
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    if (this.configuration && this.configuration.accessToken) {
      const token = this.configuration.accessToken;
      const tokenString = await token("BearerAuth", []);
      if (tokenString) {
        headerParameters["Authorization"] = `Bearer ${tokenString}`;
      }
    }
    const response = await this.request({
      path: `/jobs/{jobId}/ack`.replace(`{${"jobId"}}`, encodeURIComponent(String(requestParameters.jobId))),
      method: "POST",
      headers: headerParameters,
      query: queryParameters,
      body: JobAckDetailsToJSON(requestParameters.jobAckDetails)
    }, initOverrides);
    return new JSONApiResponse(response, (jsonValue) => JobResponseFromJSON(jsonValue));
  }
  /**
   * Acknowledge a job and return the job
   * Acknowledge a job
   */
  async ackJob(requestParameters, initOverrides) {
    const response = await this.ackJobRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Acknowledge a job outcome and return the job
   * Acknowledges a job outcome
   */
  async acknowledgeJobOutcomeRaw(requestParameters, initOverrides) {
    if (requestParameters.jobId === null || requestParameters.jobId === void 0) {
      throw new RequiredError("jobId", "Required parameter requestParameters.jobId was null or undefined when calling acknowledgeJobOutcome.");
    }
    const queryParameters = {};
    const headerParameters = {};
    if (this.configuration && this.configuration.accessToken) {
      const token = this.configuration.accessToken;
      const tokenString = await token("BearerAuth", []);
      if (tokenString) {
        headerParameters["Authorization"] = `Bearer ${tokenString}`;
      }
    }
    const response = await this.request({
      path: `/jobs/{jobId}/outcome/ack`.replace(`{${"jobId"}}`, encodeURIComponent(String(requestParameters.jobId))),
      method: "POST",
      headers: headerParameters,
      query: queryParameters
    }, initOverrides);
    return new JSONApiResponse(response, (jsonValue) => JobResponseFromJSON(jsonValue));
  }
  /**
   * Acknowledge a job outcome and return the job
   * Acknowledges a job outcome
   */
  async acknowledgeJobOutcome(requestParameters, initOverrides) {
    const response = await this.acknowledgeJobOutcomeRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Add a new document to the space
   * Create a document
   */
  async addDocumentToSpaceRaw(requestParameters, initOverrides) {
    if (requestParameters.spaceId === null || requestParameters.spaceId === void 0) {
      throw new RequiredError("spaceId", "Required parameter requestParameters.spaceId was null or undefined when calling addDocumentToSpace.");
    }
    if (requestParameters.documentConfig === null || requestParameters.documentConfig === void 0) {
      throw new RequiredError("documentConfig", "Required parameter requestParameters.documentConfig was null or undefined when calling addDocumentToSpace.");
    }
    const queryParameters = {};
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    if (this.configuration && this.configuration.accessToken) {
      const token = this.configuration.accessToken;
      const tokenString = await token("BearerAuth", []);
      if (tokenString) {
        headerParameters["Authorization"] = `Bearer ${tokenString}`;
      }
    }
    const response = await this.request({
      path: `/spaces/{spaceId}/documents`.replace(`{${"spaceId"}}`, encodeURIComponent(String(requestParameters.spaceId))),
      method: "POST",
      headers: headerParameters,
      query: queryParameters,
      body: DocumentConfigToJSON(requestParameters.documentConfig)
    }, initOverrides);
    return new JSONApiResponse(response, (jsonValue) => DocumentResponseFromJSON(jsonValue));
  }
  /**
   * Add a new document to the space
   * Create a document
   */
  async addDocumentToSpace(requestParameters, initOverrides) {
    const response = await this.addDocumentToSpaceRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Adds a new field to a sheet
   */
  async addFieldRaw(requestParameters, initOverrides) {
    if (requestParameters.sheetId === null || requestParameters.sheetId === void 0) {
      throw new RequiredError("sheetId", "Required parameter requestParameters.sheetId was null or undefined when calling addField.");
    }
    if (requestParameters.fieldConfig === null || requestParameters.fieldConfig === void 0) {
      throw new RequiredError("fieldConfig", "Required parameter requestParameters.fieldConfig was null or undefined when calling addField.");
    }
    const queryParameters = {};
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    if (this.configuration && this.configuration.accessToken) {
      const token = this.configuration.accessToken;
      const tokenString = await token("BearerAuth", []);
      if (tokenString) {
        headerParameters["Authorization"] = `Bearer ${tokenString}`;
      }
    }
    const response = await this.request({
      path: `/sheets/{sheetId}/fields`.replace(`{${"sheetId"}}`, encodeURIComponent(String(requestParameters.sheetId))),
      method: "POST",
      headers: headerParameters,
      query: queryParameters,
      body: FieldConfigToJSON(requestParameters.fieldConfig)
    }, initOverrides);
    return new JSONApiResponse(response, (jsonValue) => FieldConfigResponseFromJSON(jsonValue));
  }
  /**
   * Adds a new field to a sheet
   */
  async addField(requestParameters, initOverrides) {
    const response = await this.addFieldRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Add a new guest to the space
   * Create a guest
   */
  async addGuestToSpaceRaw(requestParameters, initOverrides) {
    if (requestParameters.guestConfig === null || requestParameters.guestConfig === void 0) {
      throw new RequiredError("guestConfig", "Required parameter requestParameters.guestConfig was null or undefined when calling addGuestToSpace.");
    }
    const queryParameters = {};
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    if (this.configuration && this.configuration.accessToken) {
      const token = this.configuration.accessToken;
      const tokenString = await token("BearerAuth", []);
      if (tokenString) {
        headerParameters["Authorization"] = `Bearer ${tokenString}`;
      }
    }
    const response = await this.request({
      path: `/guests`,
      method: "POST",
      headers: headerParameters,
      query: queryParameters,
      body: requestParameters.guestConfig.map(GuestConfigToJSON)
    }, initOverrides);
    return new JSONApiResponse(response, (jsonValue) => GuestsResponseFromJSON(jsonValue));
  }
  /**
   * Add a new guest to the space
   * Create a guest
   */
  async addGuestToSpace(requestParameters, initOverrides) {
    const response = await this.addGuestToSpaceRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Adds records to a workbook sheet
   * Insert records
   */
  async addRecordsRaw(requestParameters, initOverrides) {
    if (requestParameters.sheetId === null || requestParameters.sheetId === void 0) {
      throw new RequiredError("sheetId", "Required parameter requestParameters.sheetId was null or undefined when calling addRecords.");
    }
    if (requestParameters.recordsData === null || requestParameters.recordsData === void 0) {
      throw new RequiredError("recordsData", "Required parameter requestParameters.recordsData was null or undefined when calling addRecords.");
    }
    const queryParameters = {};
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    if (this.configuration && this.configuration.accessToken) {
      const token = this.configuration.accessToken;
      const tokenString = await token("BearerAuth", []);
      if (tokenString) {
        headerParameters["Authorization"] = `Bearer ${tokenString}`;
      }
    }
    const response = await this.request({
      path: `/sheets/{sheetId}/records`.replace(`{${"sheetId"}}`, encodeURIComponent(String(requestParameters.sheetId))),
      method: "POST",
      headers: headerParameters,
      query: queryParameters,
      body: RecordsDataToJSON(requestParameters.recordsData)
    }, initOverrides);
    return new JSONApiResponse(response, (jsonValue) => RecordsResponseFromJSON(jsonValue));
  }
  /**
   * Adds records to a workbook sheet
   * Insert records
   */
  async addRecords(requestParameters, initOverrides) {
    const response = await this.addRecordsRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Adds records to a workbook sheet
   * Insert records
   */
  async addRecordsDeprecatedRaw(requestParameters, initOverrides) {
    if (requestParameters.workbookId === null || requestParameters.workbookId === void 0) {
      throw new RequiredError("workbookId", "Required parameter requestParameters.workbookId was null or undefined when calling addRecordsDeprecated.");
    }
    if (requestParameters.sheetId === null || requestParameters.sheetId === void 0) {
      throw new RequiredError("sheetId", "Required parameter requestParameters.sheetId was null or undefined when calling addRecordsDeprecated.");
    }
    if (requestParameters.recordsData === null || requestParameters.recordsData === void 0) {
      throw new RequiredError("recordsData", "Required parameter requestParameters.recordsData was null or undefined when calling addRecordsDeprecated.");
    }
    const queryParameters = {};
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    if (this.configuration && this.configuration.accessToken) {
      const token = this.configuration.accessToken;
      const tokenString = await token("BearerAuth", []);
      if (tokenString) {
        headerParameters["Authorization"] = `Bearer ${tokenString}`;
      }
    }
    const response = await this.request({
      path: `/workbooks/{workbookId}/sheets/{sheetId}/records`.replace(`{${"workbookId"}}`, encodeURIComponent(String(requestParameters.workbookId))).replace(`{${"sheetId"}}`, encodeURIComponent(String(requestParameters.sheetId))),
      method: "POST",
      headers: headerParameters,
      query: queryParameters,
      body: RecordsDataToJSON(requestParameters.recordsData)
    }, initOverrides);
    return new JSONApiResponse(response, (jsonValue) => RecordsResponseFromJSON(jsonValue));
  }
  /**
   * Adds records to a workbook sheet
   * Insert records
   */
  async addRecordsDeprecated(requestParameters, initOverrides) {
    const response = await this.addRecordsDeprecatedRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Creates a new space based on an existing Space Config
   * Create a space
   */
  async addSpaceRaw(requestParameters, initOverrides) {
    if (requestParameters.spaceConfig === null || requestParameters.spaceConfig === void 0) {
      throw new RequiredError("spaceConfig", "Required parameter requestParameters.spaceConfig was null or undefined when calling addSpace.");
    }
    const queryParameters = {};
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    if (this.configuration && this.configuration.accessToken) {
      const token = this.configuration.accessToken;
      const tokenString = await token("BearerAuth", []);
      if (tokenString) {
        headerParameters["Authorization"] = `Bearer ${tokenString}`;
      }
    }
    const response = await this.request({
      path: `/spaces`,
      method: "POST",
      headers: headerParameters,
      query: queryParameters,
      body: SpaceConfigToJSON(requestParameters.spaceConfig)
    }, initOverrides);
    return new JSONApiResponse(response, (jsonValue) => SpaceResponseFromJSON(jsonValue));
  }
  /**
   * Creates a new space based on an existing Space Config
   * Create a space
   */
  async addSpace(requestParameters, initOverrides) {
    const response = await this.addSpaceRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Creates a new Space Config
   * Create a space config
   */
  async addSpaceConfigRaw(requestParameters, initOverrides) {
    if (requestParameters.spacePatternConfig === null || requestParameters.spacePatternConfig === void 0) {
      throw new RequiredError("spacePatternConfig", "Required parameter requestParameters.spacePatternConfig was null or undefined when calling addSpaceConfig.");
    }
    const queryParameters = {};
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    if (this.configuration && this.configuration.accessToken) {
      const token = this.configuration.accessToken;
      const tokenString = await token("BearerAuth", []);
      if (tokenString) {
        headerParameters["Authorization"] = `Bearer ${tokenString}`;
      }
    }
    const response = await this.request({
      path: `/space-configs`,
      method: "POST",
      headers: headerParameters,
      query: queryParameters,
      body: SpacePatternConfigToJSON(requestParameters.spacePatternConfig)
    }, initOverrides);
    return new JSONApiResponse(response, (jsonValue) => SpaceConfigResponseFromJSON(jsonValue));
  }
  /**
   * Creates a new Space Config
   * Create a space config
   */
  async addSpaceConfig(requestParameters, initOverrides) {
    const response = await this.addSpaceConfigRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Creates a new Space Config
   * Create a space config
   */
  async addSpaceConfigDeprecatedRaw(requestParameters, initOverrides) {
    if (requestParameters.spacePatternConfig === null || requestParameters.spacePatternConfig === void 0) {
      throw new RequiredError("spacePatternConfig", "Required parameter requestParameters.spacePatternConfig was null or undefined when calling addSpaceConfigDeprecated.");
    }
    const queryParameters = {};
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    if (this.configuration && this.configuration.accessToken) {
      const token = this.configuration.accessToken;
      const tokenString = await token("BearerAuth", []);
      if (tokenString) {
        headerParameters["Authorization"] = `Bearer ${tokenString}`;
      }
    }
    const response = await this.request({
      path: `/spaces/configs`,
      method: "POST",
      headers: headerParameters,
      query: queryParameters,
      body: SpacePatternConfigToJSON(requestParameters.spacePatternConfig)
    }, initOverrides);
    return new JSONApiResponse(response, (jsonValue) => SpaceConfigResponseFromJSON(jsonValue));
  }
  /**
   * Creates a new Space Config
   * Create a space config
   */
  async addSpaceConfigDeprecated(requestParameters, initOverrides) {
    const response = await this.addSpaceConfigDeprecatedRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Creates a workbook and adds it to a space
   * Create a workbook
   */
  async addWorkbookRaw(requestParameters, initOverrides) {
    if (requestParameters.workbookConfig === null || requestParameters.workbookConfig === void 0) {
      throw new RequiredError("workbookConfig", "Required parameter requestParameters.workbookConfig was null or undefined when calling addWorkbook.");
    }
    const queryParameters = {};
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    if (this.configuration && this.configuration.accessToken) {
      const token = this.configuration.accessToken;
      const tokenString = await token("BearerAuth", []);
      if (tokenString) {
        headerParameters["Authorization"] = `Bearer ${tokenString}`;
      }
    }
    const response = await this.request({
      path: `/workbooks`,
      method: "POST",
      headers: headerParameters,
      query: queryParameters,
      body: WorkbookConfigToJSON(requestParameters.workbookConfig)
    }, initOverrides);
    return new JSONApiResponse(response, (jsonValue) => WorkbookResponseFromJSON(jsonValue));
  }
  /**
   * Creates a workbook and adds it to a space
   * Create a workbook
   */
  async addWorkbook(requestParameters, initOverrides) {
    const response = await this.addWorkbookRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Get the AI assistant to write a mutateRecord function
   * AI generated mutateRecord
   */
  async aiAssistGenerateMutationRaw(requestParameters, initOverrides) {
    if (requestParameters.generateMutationConfig === null || requestParameters.generateMutationConfig === void 0) {
      throw new RequiredError("generateMutationConfig", "Required parameter requestParameters.generateMutationConfig was null or undefined when calling aiAssistGenerateMutation.");
    }
    const queryParameters = {};
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    const response = await this.request({
      path: `/assistant/generate-mutation`,
      method: "POST",
      headers: headerParameters,
      query: queryParameters,
      body: GenerateMutationConfigToJSON(requestParameters.generateMutationConfig)
    }, initOverrides);
    return new JSONApiResponse(response, (jsonValue) => GeneratedMutationResponseFromJSON(jsonValue));
  }
  /**
   * Get the AI assistant to write a mutateRecord function
   * AI generated mutateRecord
   */
  async aiAssistGenerateMutation(requestParameters, initOverrides) {
    const response = await this.aiAssistGenerateMutationRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Get the AI assistant to write a query function
   * AI generated query
   */
  async aiAssistGenerateQueryRaw(requestParameters, initOverrides) {
    if (requestParameters.generateQueryConfig === null || requestParameters.generateQueryConfig === void 0) {
      throw new RequiredError("generateQueryConfig", "Required parameter requestParameters.generateQueryConfig was null or undefined when calling aiAssistGenerateQuery.");
    }
    const queryParameters = {};
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    const response = await this.request({
      path: `/assistant/generate-query`,
      method: "POST",
      headers: headerParameters,
      query: queryParameters,
      body: GenerateQueryConfigToJSON(requestParameters.generateQueryConfig)
    }, initOverrides);
    return new JSONApiResponse(response, (jsonValue) => GeneratedQueryResponseFromJSON(jsonValue));
  }
  /**
   * Get the AI assistant to write a query function
   * AI generated query
   */
  async aiAssistGenerateQuery(requestParameters, initOverrides) {
    const response = await this.aiAssistGenerateQueryRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Archives a space
   */
  async archiveSpaceRaw(requestParameters, initOverrides) {
    if (requestParameters.spaceId === null || requestParameters.spaceId === void 0) {
      throw new RequiredError("spaceId", "Required parameter requestParameters.spaceId was null or undefined when calling archiveSpace.");
    }
    const queryParameters = {};
    const headerParameters = {};
    if (this.configuration && this.configuration.accessToken) {
      const token = this.configuration.accessToken;
      const tokenString = await token("BearerAuth", []);
      if (tokenString) {
        headerParameters["Authorization"] = `Bearer ${tokenString}`;
      }
    }
    const response = await this.request({
      path: `/spaces/{spaceId}/archive`.replace(`{${"spaceId"}}`, encodeURIComponent(String(requestParameters.spaceId))),
      method: "POST",
      headers: headerParameters,
      query: queryParameters
    }, initOverrides);
    return new JSONApiResponse(response, (jsonValue) => SuccessResponseFromJSON(jsonValue));
  }
  /**
   * Archives a space
   */
  async archiveSpace(requestParameters, initOverrides) {
    const response = await this.archiveSpaceRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Assigns a role to an agent
   * Assign Role to Agent
   */
  async assignAgentRoleRaw(requestParameters, initOverrides) {
    if (requestParameters.agentId === null || requestParameters.agentId === void 0) {
      throw new RequiredError("agentId", "Required parameter requestParameters.agentId was null or undefined when calling assignAgentRole.");
    }
    const queryParameters = {};
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    if (this.configuration && this.configuration.accessToken) {
      const token = this.configuration.accessToken;
      const tokenString = await token("BearerAuth", []);
      if (tokenString) {
        headerParameters["Authorization"] = `Bearer ${tokenString}`;
      }
    }
    const response = await this.request({
      path: `/agents/{agentId}/roles`.replace(`{${"agentId"}}`, encodeURIComponent(String(requestParameters.agentId))),
      method: "POST",
      headers: headerParameters,
      query: queryParameters,
      body: AssignActorRoleRequestToJSON(requestParameters.assignActorRoleRequest)
    }, initOverrides);
    return new JSONApiResponse(response, (jsonValue) => AssignRoleResponseFromJSON(jsonValue));
  }
  /**
   * Assigns a role to an agent
   * Assign Role to Agent
   */
  async assignAgentRole(requestParameters, initOverrides) {
    const response = await this.assignAgentRoleRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Assigns a role to a guest
   * Assign Role to Guest
   */
  async assignGuestRoleRaw(requestParameters, initOverrides) {
    if (requestParameters.guestId === null || requestParameters.guestId === void 0) {
      throw new RequiredError("guestId", "Required parameter requestParameters.guestId was null or undefined when calling assignGuestRole.");
    }
    const queryParameters = {};
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    if (this.configuration && this.configuration.accessToken) {
      const token = this.configuration.accessToken;
      const tokenString = await token("BearerAuth", []);
      if (tokenString) {
        headerParameters["Authorization"] = `Bearer ${tokenString}`;
      }
    }
    const response = await this.request({
      path: `/guests/{guestId}/roles`.replace(`{${"guestId"}}`, encodeURIComponent(String(requestParameters.guestId))),
      method: "POST",
      headers: headerParameters,
      query: queryParameters,
      body: AssignActorRoleRequestToJSON(requestParameters.assignActorRoleRequest)
    }, initOverrides);
    return new JSONApiResponse(response, (jsonValue) => AssignRoleResponseFromJSON(jsonValue));
  }
  /**
   * Assigns a role to a guest
   * Assign Role to Guest
   */
  async assignGuestRole(requestParameters, initOverrides) {
    const response = await this.assignGuestRoleRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Assigns a role to a user
   * Assign Role to User
   */
  async assignUserRoleRaw(requestParameters, initOverrides) {
    if (requestParameters.userId === null || requestParameters.userId === void 0) {
      throw new RequiredError("userId", "Required parameter requestParameters.userId was null or undefined when calling assignUserRole.");
    }
    const queryParameters = {};
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    if (this.configuration && this.configuration.accessToken) {
      const token = this.configuration.accessToken;
      const tokenString = await token("BearerAuth", []);
      if (tokenString) {
        headerParameters["Authorization"] = `Bearer ${tokenString}`;
      }
    }
    const response = await this.request({
      path: `/users/{userId}/roles`.replace(`{${"userId"}}`, encodeURIComponent(String(requestParameters.userId))),
      method: "POST",
      headers: headerParameters,
      query: queryParameters,
      body: AssignActorRoleRequestToJSON(requestParameters.assignActorRoleRequest)
    }, initOverrides);
    return new JSONApiResponse(response, (jsonValue) => AssignRoleResponseFromJSON(jsonValue));
  }
  /**
   * Assigns a role to a user
   * Assign Role to User
   */
  async assignUserRole(requestParameters, initOverrides) {
    const response = await this.assignUserRoleRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Cancel a job and return the job
   * Cancel a job
   */
  async cancelJobRaw(requestParameters, initOverrides) {
    if (requestParameters.jobId === null || requestParameters.jobId === void 0) {
      throw new RequiredError("jobId", "Required parameter requestParameters.jobId was null or undefined when calling cancelJob.");
    }
    const queryParameters = {};
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    if (this.configuration && this.configuration.accessToken) {
      const token = this.configuration.accessToken;
      const tokenString = await token("BearerAuth", []);
      if (tokenString) {
        headerParameters["Authorization"] = `Bearer ${tokenString}`;
      }
    }
    const response = await this.request({
      path: `/jobs/{jobId}/cancel`.replace(`{${"jobId"}}`, encodeURIComponent(String(requestParameters.jobId))),
      method: "POST",
      headers: headerParameters,
      query: queryParameters,
      body: JobCancelDetailsToJSON(requestParameters.jobCancelDetails)
    }, initOverrides);
    return new JSONApiResponse(response, (jsonValue) => JobResponseFromJSON(jsonValue));
  }
  /**
   * Cancel a job and return the job
   * Cancel a job
   */
  async cancelJob(requestParameters, initOverrides) {
    const response = await this.cancelJobRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Cancels the given subscription
   */
  async cancelSubscriptionRaw(requestParameters, initOverrides) {
    if (requestParameters.subscriptionId === null || requestParameters.subscriptionId === void 0) {
      throw new RequiredError("subscriptionId", "Required parameter requestParameters.subscriptionId was null or undefined when calling cancelSubscription.");
    }
    const queryParameters = {};
    const headerParameters = {};
    const response = await this.request({
      path: `/billing/subscription/{subscriptionId}`.replace(`{${"subscriptionId"}}`, encodeURIComponent(String(requestParameters.subscriptionId))),
      method: "DELETE",
      headers: headerParameters,
      query: queryParameters
    }, initOverrides);
    return new JSONApiResponse(response, (jsonValue) => SuccessResponseFromJSON(jsonValue));
  }
  /**
   * Cancels the given subscription
   */
  async cancelSubscription(requestParameters, initOverrides) {
    const response = await this.cancelSubscriptionRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Cancels all the subscriptions for the current user
   */
  async cancelSubscriptionsRaw(initOverrides) {
    const queryParameters = {};
    const headerParameters = {};
    const response = await this.request({
      path: `/billing/subscriptions`,
      method: "DELETE",
      headers: headerParameters,
      query: queryParameters
    }, initOverrides);
    return new JSONApiResponse(response, (jsonValue) => SuccessResponseFromJSON(jsonValue));
  }
  /**
   * Cancels all the subscriptions for the current user
   */
  async cancelSubscriptions(initOverrides) {
    const response = await this.cancelSubscriptionsRaw(initOverrides);
    return await response.value();
  }
  /**
   * Clone a workbook
   * Clone a workbook within a space
   */
  async cloneWorkbookRaw(requestParameters, initOverrides) {
    if (requestParameters.workbookId === null || requestParameters.workbookId === void 0) {
      throw new RequiredError("workbookId", "Required parameter requestParameters.workbookId was null or undefined when calling cloneWorkbook.");
    }
    const queryParameters = {};
    const headerParameters = {};
    if (this.configuration && this.configuration.accessToken) {
      const token = this.configuration.accessToken;
      const tokenString = await token("BearerAuth", []);
      if (tokenString) {
        headerParameters["Authorization"] = `Bearer ${tokenString}`;
      }
    }
    const response = await this.request({
      path: `/workbooks/{workbookId}/clone`.replace(`{${"workbookId"}}`, encodeURIComponent(String(requestParameters.workbookId))),
      method: "POST",
      headers: headerParameters,
      query: queryParameters
    }, initOverrides);
    return new JSONApiResponse(response, (jsonValue) => WorkbookResponseFromJSON(jsonValue));
  }
  /**
   * Clone a workbook
   * Clone a workbook within a space
   */
  async cloneWorkbook(requestParameters, initOverrides) {
    const response = await this.cloneWorkbookRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Completes a commit version. This marks the commit as complete and acknowledges that the changes have been applied to the sheet.
   * Complete a commit version
   */
  async completeCommitRaw(requestParameters, initOverrides) {
    if (requestParameters.commitId === null || requestParameters.commitId === void 0) {
      throw new RequiredError("commitId", "Required parameter requestParameters.commitId was null or undefined when calling completeCommit.");
    }
    const queryParameters = {};
    const headerParameters = {};
    if (this.configuration && this.configuration.accessToken) {
      const token = this.configuration.accessToken;
      const tokenString = await token("BearerAuth", []);
      if (tokenString) {
        headerParameters["Authorization"] = `Bearer ${tokenString}`;
      }
    }
    const response = await this.request({
      path: `/commits/{commitId}/complete`.replace(`{${"commitId"}}`, encodeURIComponent(String(requestParameters.commitId))),
      method: "POST",
      headers: headerParameters,
      query: queryParameters
    }, initOverrides);
    return new JSONApiResponse(response, (jsonValue) => SuccessResponseFromJSON(jsonValue));
  }
  /**
   * Completes a commit version. This marks the commit as complete and acknowledges that the changes have been applied to the sheet.
   * Complete a commit version
   */
  async completeCommit(requestParameters, initOverrides) {
    const response = await this.completeCommitRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Complete a job and return the job
   * Complete a job
   */
  async completeJobRaw(requestParameters, initOverrides) {
    if (requestParameters.jobId === null || requestParameters.jobId === void 0) {
      throw new RequiredError("jobId", "Required parameter requestParameters.jobId was null or undefined when calling completeJob.");
    }
    const queryParameters = {};
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    if (this.configuration && this.configuration.accessToken) {
      const token = this.configuration.accessToken;
      const tokenString = await token("BearerAuth", []);
      if (tokenString) {
        headerParameters["Authorization"] = `Bearer ${tokenString}`;
      }
    }
    const response = await this.request({
      path: `/jobs/{jobId}/complete`.replace(`{${"jobId"}}`, encodeURIComponent(String(requestParameters.jobId))),
      method: "POST",
      headers: headerParameters,
      query: queryParameters,
      body: JobCompleteDetailsToJSON(requestParameters.jobCompleteDetails)
    }, initOverrides);
    return new JSONApiResponse(response, (jsonValue) => JobResponseFromJSON(jsonValue));
  }
  /**
   * Complete a job and return the job
   * Complete a job
   */
  async completeJob(requestParameters, initOverrides) {
    const response = await this.completeJobRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Exchange credentials for an access token. Credentials can be a Client ID and Secret or an Email and Password
   * Create an access token
   */
  async createAccessTokenRaw(requestParameters, initOverrides) {
    if (requestParameters.createAccessTokenRequest === null || requestParameters.createAccessTokenRequest === void 0) {
      throw new RequiredError("createAccessTokenRequest", "Required parameter requestParameters.createAccessTokenRequest was null or undefined when calling createAccessToken.");
    }
    const queryParameters = {};
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    const response = await this.request({
      path: `/auth`,
      method: "POST",
      headers: headerParameters,
      query: queryParameters,
      body: CreateAccessTokenRequestToJSON(requestParameters.createAccessTokenRequest)
    }, initOverrides);
    return new JSONApiResponse(response, (jsonValue) => AccessTokenResponseFromJSON(jsonValue));
  }
  /**
   * Exchange credentials for an access token. Credentials can be a Client ID and Secret or an Email and Password
   * Create an access token
   */
  async createAccessToken(requestParameters, initOverrides) {
    const response = await this.createAccessTokenRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Create an agent
   */
  async createAgentRaw(requestParameters, initOverrides) {
    if (requestParameters.environmentId === null || requestParameters.environmentId === void 0) {
      throw new RequiredError("environmentId", "Required parameter requestParameters.environmentId was null or undefined when calling createAgent.");
    }
    if (requestParameters.agentConfig === null || requestParameters.agentConfig === void 0) {
      throw new RequiredError("agentConfig", "Required parameter requestParameters.agentConfig was null or undefined when calling createAgent.");
    }
    const queryParameters = {};
    if (requestParameters.environmentId !== void 0) {
      queryParameters["environmentId"] = requestParameters.environmentId;
    }
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    if (this.configuration && this.configuration.accessToken) {
      const token = this.configuration.accessToken;
      const tokenString = await token("BearerAuth", []);
      if (tokenString) {
        headerParameters["Authorization"] = `Bearer ${tokenString}`;
      }
    }
    const response = await this.request({
      path: `/agents`,
      method: "POST",
      headers: headerParameters,
      query: queryParameters,
      body: AgentConfigToJSON(requestParameters.agentConfig)
    }, initOverrides);
    return new JSONApiResponse(response, (jsonValue) => AgentResponseFromJSON(jsonValue));
  }
  /**
   * Create an agent
   */
  async createAgent(requestParameters, initOverrides) {
    const response = await this.createAgentRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Create an agent
   */
  async createAgentDeprecatedRaw(requestParameters, initOverrides) {
    if (requestParameters.environmentId === null || requestParameters.environmentId === void 0) {
      throw new RequiredError("environmentId", "Required parameter requestParameters.environmentId was null or undefined when calling createAgentDeprecated.");
    }
    if (requestParameters.agentConfig === null || requestParameters.agentConfig === void 0) {
      throw new RequiredError("agentConfig", "Required parameter requestParameters.agentConfig was null or undefined when calling createAgentDeprecated.");
    }
    const queryParameters = {};
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    if (this.configuration && this.configuration.accessToken) {
      const token = this.configuration.accessToken;
      const tokenString = await token("BearerAuth", []);
      if (tokenString) {
        headerParameters["Authorization"] = `Bearer ${tokenString}`;
      }
    }
    const response = await this.request({
      path: `/environments/{environmentId}/agents`.replace(`{${"environmentId"}}`, encodeURIComponent(String(requestParameters.environmentId))),
      method: "POST",
      headers: headerParameters,
      query: queryParameters,
      body: AgentConfigToJSON(requestParameters.agentConfig)
    }, initOverrides);
    return new JSONApiResponse(response, (jsonValue) => AgentResponseFromJSON(jsonValue));
  }
  /**
   * Create an agent
   */
  async createAgentDeprecated(requestParameters, initOverrides) {
    const response = await this.createAgentDeprecatedRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Creates a user
   * Create a user
   */
  async createAndInviteUserRaw(requestParameters, initOverrides) {
    if (requestParameters.userCreateAndInviteBody === null || requestParameters.userCreateAndInviteBody === void 0) {
      throw new RequiredError("userCreateAndInviteBody", "Required parameter requestParameters.userCreateAndInviteBody was null or undefined when calling createAndInviteUser.");
    }
    const queryParameters = {};
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    if (this.configuration && this.configuration.accessToken) {
      const token = this.configuration.accessToken;
      const tokenString = await token("BearerAuth", []);
      if (tokenString) {
        headerParameters["Authorization"] = `Bearer ${tokenString}`;
      }
    }
    const response = await this.request({
      path: `/users/invite`,
      method: "POST",
      headers: headerParameters,
      query: queryParameters,
      body: UserCreateAndInviteBodyToJSON(requestParameters.userCreateAndInviteBody)
    }, initOverrides);
    return new JSONApiResponse(response, (jsonValue) => UserResponseFromJSON(jsonValue));
  }
  /**
   * Creates a user
   * Create a user
   */
  async createAndInviteUser(requestParameters, initOverrides) {
    const response = await this.createAndInviteUserRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Creates an api token for authenticating against Flatfile APIs
   * Create an API token
   */
  async createApiTokenRaw(requestParameters, initOverrides) {
    if (requestParameters.userId === null || requestParameters.userId === void 0) {
      throw new RequiredError("userId", "Required parameter requestParameters.userId was null or undefined when calling createApiToken.");
    }
    if (requestParameters.tenantId === null || requestParameters.tenantId === void 0) {
      throw new RequiredError("tenantId", "Required parameter requestParameters.tenantId was null or undefined when calling createApiToken.");
    }
    const queryParameters = {};
    if (requestParameters.tenantId !== void 0) {
      queryParameters["tenantId"] = requestParameters.tenantId;
    }
    const headerParameters = {};
    if (this.configuration && this.configuration.accessToken) {
      const token = this.configuration.accessToken;
      const tokenString = await token("BearerAuth", []);
      if (tokenString) {
        headerParameters["Authorization"] = `Bearer ${tokenString}`;
      }
    }
    const response = await this.request({
      path: `/users/{userId}/api-token`.replace(`{${"userId"}}`, encodeURIComponent(String(requestParameters.userId))),
      method: "POST",
      headers: headerParameters,
      query: queryParameters
    }, initOverrides);
    return new JSONApiResponse(response, (jsonValue) => ApiTokenResponseFromJSON(jsonValue));
  }
  /**
   * Creates an api token for authenticating against Flatfile APIs
   * Create an API token
   */
  async createApiToken(requestParameters, initOverrides) {
    const response = await this.createApiTokenRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Creates a checkout session
   */
  async createCheckoutSessionRaw(requestParameters, initOverrides) {
    const queryParameters = {};
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    const response = await this.request({
      path: `/billing/checkoutsession/create`,
      method: "POST",
      headers: headerParameters,
      query: queryParameters,
      body: CheckoutSessionBodyToJSON(requestParameters.checkoutSessionBody)
    }, initOverrides);
    return new JSONApiResponse(response, (jsonValue) => CreateCheckoutSessionResponseFromJSON(jsonValue));
  }
  /**
   * Creates a checkout session
   */
  async createCheckoutSession(requestParameters = {}, initOverrides) {
    const response = await this.createCheckoutSessionRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Creates a new commit id that can be used to group record updates
   * Create a commit id
   */
  async createCommitIdRaw(requestParameters, initOverrides) {
    const queryParameters = {};
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    if (this.configuration && this.configuration.accessToken) {
      const token = this.configuration.accessToken;
      const tokenString = await token("BearerAuth", []);
      if (tokenString) {
        headerParameters["Authorization"] = `Bearer ${tokenString}`;
      }
    }
    const response = await this.request({
      path: `/commits`,
      method: "POST",
      headers: headerParameters,
      query: queryParameters,
      body: CreateCommitIdRequestToJSON(requestParameters.createCommitIdRequest)
    }, initOverrides);
    return new JSONApiResponse(response, (jsonValue) => VersionResponseFromJSON(jsonValue));
  }
  /**
   * Creates a new commit id that can be used to group record updates
   * Create a commit id
   */
  async createCommitId(requestParameters = {}, initOverrides) {
    const response = await this.createCommitIdRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Creates a Merge Link
   */
  async createConnectionLinkRaw(requestParameters, initOverrides) {
    if (requestParameters.spaceId === null || requestParameters.spaceId === void 0) {
      throw new RequiredError("spaceId", "Required parameter requestParameters.spaceId was null or undefined when calling createConnectionLink.");
    }
    const queryParameters = {};
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    if (this.configuration && this.configuration.accessToken) {
      const token = this.configuration.accessToken;
      const tokenString = await token("BearerAuth", []);
      if (tokenString) {
        headerParameters["Authorization"] = `Bearer ${tokenString}`;
      }
    }
    const response = await this.request({
      path: `/spaces/{spaceId}/createLink`.replace(`{${"spaceId"}}`, encodeURIComponent(String(requestParameters.spaceId))),
      method: "POST",
      headers: headerParameters,
      query: queryParameters,
      body: ConnectionLinkBodyToJSON(requestParameters.connectionLinkBody)
    }, initOverrides);
    return new JSONApiResponse(response, (jsonValue) => CreateConnectionLinkResponseFromJSON(jsonValue));
  }
  /**
   * Creates a Merge Link
   */
  async createConnectionLink(requestParameters, initOverrides) {
    const response = await this.createConnectionLinkRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Create a data retention policy on an environment
   * Create a data retention policy
   */
  async createDataRetentionPolicyRaw(requestParameters, initOverrides) {
    const queryParameters = {};
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    if (this.configuration && this.configuration.accessToken) {
      const token = this.configuration.accessToken;
      const tokenString = await token("BearerAuth", []);
      if (tokenString) {
        headerParameters["Authorization"] = `Bearer ${tokenString}`;
      }
    }
    const response = await this.request({
      path: `/data-retention-policies`,
      method: "POST",
      headers: headerParameters,
      query: queryParameters,
      body: DataRetentionPolicyConfigToJSON(requestParameters.dataRetentionPolicyConfig)
    }, initOverrides);
    return new JSONApiResponse(response, (jsonValue) => DataRetentionPolicyResponseFromJSON(jsonValue));
  }
  /**
   * Create a data retention policy on an environment
   * Create a data retention policy
   */
  async createDataRetentionPolicy(requestParameters = {}, initOverrides) {
    const response = await this.createDataRetentionPolicyRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Create a new environment
   * Create an environment
   */
  async createEnvironmentRaw(requestParameters, initOverrides) {
    const queryParameters = {};
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    if (this.configuration && this.configuration.accessToken) {
      const token = this.configuration.accessToken;
      const tokenString = await token("BearerAuth", []);
      if (tokenString) {
        headerParameters["Authorization"] = `Bearer ${tokenString}`;
      }
    }
    const response = await this.request({
      path: `/environments`,
      method: "POST",
      headers: headerParameters,
      query: queryParameters,
      body: CreateEnvironmentRequestToJSON(requestParameters.createEnvironmentRequest)
    }, initOverrides);
    return new JSONApiResponse(response, (jsonValue) => EnvironmentResponseFromJSON(jsonValue));
  }
  /**
   * Create a new environment
   * Create an environment
   */
  async createEnvironment(requestParameters = {}, initOverrides) {
    const response = await this.createEnvironmentRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Create an event
   */
  async createEventRaw(requestParameters, initOverrides) {
    if (requestParameters.eventConfig === null || requestParameters.eventConfig === void 0) {
      throw new RequiredError("eventConfig", "Required parameter requestParameters.eventConfig was null or undefined when calling createEvent.");
    }
    const queryParameters = {};
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    if (this.configuration && this.configuration.accessToken) {
      const token = this.configuration.accessToken;
      const tokenString = await token("BearerAuth", []);
      if (tokenString) {
        headerParameters["Authorization"] = `Bearer ${tokenString}`;
      }
    }
    const response = await this.request({
      path: `/events`,
      method: "POST",
      headers: headerParameters,
      query: queryParameters,
      body: EventConfigToJSON(requestParameters.eventConfig)
    }, initOverrides);
    return new JSONApiResponse(response, (jsonValue) => EventResponseFromJSON(jsonValue));
  }
  /**
   * Create an event
   */
  async createEvent(requestParameters, initOverrides) {
    const response = await this.createEventRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Create an event
   */
  async createEventDeprecatedRaw(requestParameters, initOverrides) {
    if (requestParameters.environmentId === null || requestParameters.environmentId === void 0) {
      throw new RequiredError("environmentId", "Required parameter requestParameters.environmentId was null or undefined when calling createEventDeprecated.");
    }
    if (requestParameters.eventConfig === null || requestParameters.eventConfig === void 0) {
      throw new RequiredError("eventConfig", "Required parameter requestParameters.eventConfig was null or undefined when calling createEventDeprecated.");
    }
    const queryParameters = {};
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    if (this.configuration && this.configuration.accessToken) {
      const token = this.configuration.accessToken;
      const tokenString = await token("BearerAuth", []);
      if (tokenString) {
        headerParameters["Authorization"] = `Bearer ${tokenString}`;
      }
    }
    const response = await this.request({
      path: `/environments/{environmentId}/events`.replace(`{${"environmentId"}}`, encodeURIComponent(String(requestParameters.environmentId))),
      method: "POST",
      headers: headerParameters,
      query: queryParameters,
      body: EventConfigToJSON(requestParameters.eventConfig)
    }, initOverrides);
    return new JSONApiResponse(response, (jsonValue) => EventResponseFromJSON(jsonValue));
  }
  /**
   * Create an event
   */
  async createEventDeprecated(requestParameters, initOverrides) {
    const response = await this.createEventDeprecatedRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Upload a file
   */
  async createFileRaw(requestParameters, initOverrides) {
    const queryParameters = {};
    const headerParameters = {};
    if (this.configuration && this.configuration.accessToken) {
      const token = this.configuration.accessToken;
      const tokenString = await token("BearerAuth", []);
      if (tokenString) {
        headerParameters["Authorization"] = `Bearer ${tokenString}`;
      }
    }
    const consumes = [
      { contentType: "multipart/form-data" }
    ];
    const canConsumeForm2 = canConsumeForm(consumes);
    let formParams;
    let useForm = false;
    useForm = canConsumeForm2;
    if (useForm) {
      formParams = new FormData();
    } else {
      formParams = new URLSearchParams();
    }
    if (requestParameters.spaceId !== void 0) {
      formParams.append("spaceId", requestParameters.spaceId);
    }
    if (requestParameters.environmentId !== void 0) {
      formParams.append("environmentId", requestParameters.environmentId);
    }
    if (requestParameters.mode !== void 0) {
      formParams.append("mode", new Blob([JSON.stringify(FileModeToJSON(requestParameters.mode))], { type: "application/json" }));
    }
    if (requestParameters.origin !== void 0) {
      formParams.append("origin", new Blob([JSON.stringify(FileOriginToJSON(requestParameters.origin))], { type: "application/json" }));
    }
    if (requestParameters.file !== void 0) {
      formParams.append("file", requestParameters.file);
    }
    if (requestParameters.actions) {
      formParams.append("actions", requestParameters.actions.join(COLLECTION_FORMATS["csv"]));
    }
    const response = await this.request({
      path: `/files`,
      method: "POST",
      headers: headerParameters,
      query: queryParameters,
      body: formParams
    }, initOverrides);
    return new JSONApiResponse(response, (jsonValue) => FileResponseFromJSON(jsonValue));
  }
  /**
   * Upload a file
   */
  async createFile(requestParameters = {}, initOverrides) {
    const response = await this.createFileRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Create a job
   */
  async createJobRaw(requestParameters, initOverrides) {
    if (requestParameters.jobConfig === null || requestParameters.jobConfig === void 0) {
      throw new RequiredError("jobConfig", "Required parameter requestParameters.jobConfig was null or undefined when calling createJob.");
    }
    const queryParameters = {};
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    if (this.configuration && this.configuration.accessToken) {
      const token = this.configuration.accessToken;
      const tokenString = await token("BearerAuth", []);
      if (tokenString) {
        headerParameters["Authorization"] = `Bearer ${tokenString}`;
      }
    }
    const response = await this.request({
      path: `/jobs`,
      method: "POST",
      headers: headerParameters,
      query: queryParameters,
      body: JobConfigToJSON(requestParameters.jobConfig)
    }, initOverrides);
    return new JSONApiResponse(response, (jsonValue) => JobResponseFromJSON(jsonValue));
  }
  /**
   * Create a job
   */
  async createJob(requestParameters, initOverrides) {
    const response = await this.createJobRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Create a mapping plan from two blueprints
   */
  async createMappingRaw(requestParameters, initOverrides) {
    const queryParameters = {};
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    if (this.configuration && this.configuration.accessToken) {
      const token = this.configuration.accessToken;
      const tokenString = await token("BearerAuth", []);
      if (tokenString) {
        headerParameters["Authorization"] = `Bearer ${tokenString}`;
      }
    }
    const response = await this.request({
      path: `/mapping`,
      method: "POST",
      headers: headerParameters,
      query: queryParameters,
      body: CreateMappingRequestToJSON(requestParameters.createMappingRequest)
    }, initOverrides);
    return new JSONApiResponse(response, (jsonValue) => ProgramResponseFromJSON(jsonValue));
  }
  /**
   * Create a mapping plan from two blueprints
   */
  async createMapping(requestParameters = {}, initOverrides) {
    const response = await this.createMappingRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Create a new API key of the specified type for the given environment
   */
  async createNewApiKeyRaw(requestParameters, initOverrides) {
    if (requestParameters.environmentId === null || requestParameters.environmentId === void 0) {
      throw new RequiredError("environmentId", "Required parameter requestParameters.environmentId was null or undefined when calling createNewApiKey.");
    }
    if (requestParameters.type === null || requestParameters.type === void 0) {
      throw new RequiredError("type", "Required parameter requestParameters.type was null or undefined when calling createNewApiKey.");
    }
    const queryParameters = {};
    if (requestParameters.environmentId !== void 0) {
      queryParameters["environmentId"] = requestParameters.environmentId;
    }
    if (requestParameters.type !== void 0) {
      queryParameters["type"] = requestParameters.type;
    }
    const headerParameters = {};
    const response = await this.request({
      path: `/auth/api-key`,
      method: "POST",
      headers: headerParameters,
      query: queryParameters
    }, initOverrides);
    return new JSONApiResponse(response, (jsonValue) => ApiKeysResponseFromJSON(jsonValue));
  }
  /**
   * Create a new API key of the specified type for the given environment
   */
  async createNewApiKey(requestParameters, initOverrides) {
    const response = await this.createNewApiKeyRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Creates a prompt
   * Creates a prompt
   */
  async createPromptRaw(requestParameters, initOverrides) {
    const queryParameters = {};
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    if (this.configuration && this.configuration.accessToken) {
      const token = this.configuration.accessToken;
      const tokenString = await token("BearerAuth", []);
      if (tokenString) {
        headerParameters["Authorization"] = `Bearer ${tokenString}`;
      }
    }
    const response = await this.request({
      path: `/prompts`,
      method: "POST",
      headers: headerParameters,
      query: queryParameters,
      body: PromptConfigToJSON(requestParameters.promptConfig)
    }, initOverrides);
    return new JSONApiResponse(response, (jsonValue) => PromptResponseFromJSON(jsonValue));
  }
  /**
   * Creates a prompt
   * Creates a prompt
   */
  async createPrompt(requestParameters = {}, initOverrides) {
    const response = await this.createPromptRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   */
  async createRuleRaw(requestParameters, initOverrides) {
    if (requestParameters.programId === null || requestParameters.programId === void 0) {
      throw new RequiredError("programId", "Required parameter requestParameters.programId was null or undefined when calling createRule.");
    }
    if (requestParameters.mappingRuleConfig === null || requestParameters.mappingRuleConfig === void 0) {
      throw new RequiredError("mappingRuleConfig", "Required parameter requestParameters.mappingRuleConfig was null or undefined when calling createRule.");
    }
    const queryParameters = {};
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    if (this.configuration && this.configuration.accessToken) {
      const token = this.configuration.accessToken;
      const tokenString = await token("BearerAuth", []);
      if (tokenString) {
        headerParameters["Authorization"] = `Bearer ${tokenString}`;
      }
    }
    const response = await this.request({
      path: `/mapping/{programId}/rules`.replace(`{${"programId"}}`, encodeURIComponent(String(requestParameters.programId))),
      method: "POST",
      headers: headerParameters,
      query: queryParameters,
      body: requestParameters.mappingRuleConfig.map(MappingRuleConfigToJSON)
    }, initOverrides);
    return new JSONApiResponse(response, (jsonValue) => RulesResponseFromJSON(jsonValue));
  }
  /**
   */
  async createRule(requestParameters, initOverrides) {
    const response = await this.createRuleRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Creates a user
   * Create a user
   */
  async createUserRaw(requestParameters, initOverrides) {
    if (requestParameters.userConfig === null || requestParameters.userConfig === void 0) {
      throw new RequiredError("userConfig", "Required parameter requestParameters.userConfig was null or undefined when calling createUser.");
    }
    const queryParameters = {};
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    if (this.configuration && this.configuration.accessToken) {
      const token = this.configuration.accessToken;
      const tokenString = await token("BearerAuth", []);
      if (tokenString) {
        headerParameters["Authorization"] = `Bearer ${tokenString}`;
      }
    }
    const response = await this.request({
      path: `/users`,
      method: "POST",
      headers: headerParameters,
      query: queryParameters,
      body: UserConfigToJSON(requestParameters.userConfig)
    }, initOverrides);
    return new JSONApiResponse(response, (jsonValue) => UserResponseFromJSON(jsonValue));
  }
  /**
   * Creates a user
   * Create a user
   */
  async createUser(requestParameters, initOverrides) {
    const response = await this.createUserRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Creates a new version of a workbook sheet
   * Create a version
   */
  async createVersionDeprecatedRaw(requestParameters, initOverrides) {
    if (requestParameters.workbookId === null || requestParameters.workbookId === void 0) {
      throw new RequiredError("workbookId", "Required parameter requestParameters.workbookId was null or undefined when calling createVersionDeprecated.");
    }
    if (requestParameters.sheetId === null || requestParameters.sheetId === void 0) {
      throw new RequiredError("sheetId", "Required parameter requestParameters.sheetId was null or undefined when calling createVersionDeprecated.");
    }
    const queryParameters = {};
    const headerParameters = {};
    if (this.configuration && this.configuration.accessToken) {
      const token = this.configuration.accessToken;
      const tokenString = await token("BearerAuth", []);
      if (tokenString) {
        headerParameters["Authorization"] = `Bearer ${tokenString}`;
      }
    }
    const response = await this.request({
      path: `/workbooks/{workbookId}/sheets/{sheetId}/versions`.replace(`{${"workbookId"}}`, encodeURIComponent(String(requestParameters.workbookId))).replace(`{${"sheetId"}}`, encodeURIComponent(String(requestParameters.sheetId))),
      method: "POST",
      headers: headerParameters,
      query: queryParameters
    }, initOverrides);
    return new JSONApiResponse(response, (jsonValue) => VersionResponseFromJSON(jsonValue));
  }
  /**
   * Creates a new version of a workbook sheet
   * Create a version
   */
  async createVersionDeprecated(requestParameters, initOverrides) {
    const response = await this.createVersionDeprecatedRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Deprecated. Use `/commits` endpoint instead.
   * Create a version id
   */
  async createVersionIdRaw(requestParameters, initOverrides) {
    const queryParameters = {};
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    if (this.configuration && this.configuration.accessToken) {
      const token = this.configuration.accessToken;
      const tokenString = await token("BearerAuth", []);
      if (tokenString) {
        headerParameters["Authorization"] = `Bearer ${tokenString}`;
      }
    }
    const response = await this.request({
      path: `/versions`,
      method: "POST",
      headers: headerParameters,
      query: queryParameters,
      body: CreateVersionIdRequestToJSON(requestParameters.createVersionIdRequest)
    }, initOverrides);
    return new JSONApiResponse(response, (jsonValue) => VersionResponseFromJSON(jsonValue));
  }
  /**
   * Deprecated. Use `/commits` endpoint instead.
   * Create a version id
   */
  async createVersionId(requestParameters = {}, initOverrides) {
    const response = await this.createVersionIdRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Create a view
   * Create a view
   */
  async createViewRaw(requestParameters, initOverrides) {
    const queryParameters = {};
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    if (this.configuration && this.configuration.accessToken) {
      const token = this.configuration.accessToken;
      const tokenString = await token("BearerAuth", []);
      if (tokenString) {
        headerParameters["Authorization"] = `Bearer ${tokenString}`;
      }
    }
    const response = await this.request({
      path: `/views`,
      method: "POST",
      headers: headerParameters,
      query: queryParameters,
      body: CreateViewRequestToJSON(requestParameters.createViewRequest)
    }, initOverrides);
    return new JSONApiResponse(response, (jsonValue) => ViewResponseFromJSON(jsonValue));
  }
  /**
   * Create a view
   * Create a view
   */
  async createView(requestParameters = {}, initOverrides) {
    const response = await this.createViewRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Deletes a single agent
   * Delete an agent
   */
  async deleteAgentRaw(requestParameters, initOverrides) {
    if (requestParameters.environmentId === null || requestParameters.environmentId === void 0) {
      throw new RequiredError("environmentId", "Required parameter requestParameters.environmentId was null or undefined when calling deleteAgent.");
    }
    if (requestParameters.agentId === null || requestParameters.agentId === void 0) {
      throw new RequiredError("agentId", "Required parameter requestParameters.agentId was null or undefined when calling deleteAgent.");
    }
    const queryParameters = {};
    if (requestParameters.environmentId !== void 0) {
      queryParameters["environmentId"] = requestParameters.environmentId;
    }
    const headerParameters = {};
    if (this.configuration && this.configuration.accessToken) {
      const token = this.configuration.accessToken;
      const tokenString = await token("BearerAuth", []);
      if (tokenString) {
        headerParameters["Authorization"] = `Bearer ${tokenString}`;
      }
    }
    const response = await this.request({
      path: `/agents/{agentId}`.replace(`{${"agentId"}}`, encodeURIComponent(String(requestParameters.agentId))),
      method: "DELETE",
      headers: headerParameters,
      query: queryParameters
    }, initOverrides);
    return new JSONApiResponse(response, (jsonValue) => SuccessResponseFromJSON(jsonValue));
  }
  /**
   * Deletes a single agent
   * Delete an agent
   */
  async deleteAgent(requestParameters, initOverrides) {
    const response = await this.deleteAgentRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Delete the specified API key
   */
  async deleteApiKeyRaw(requestParameters, initOverrides) {
    if (requestParameters.environmentId === null || requestParameters.environmentId === void 0) {
      throw new RequiredError("environmentId", "Required parameter requestParameters.environmentId was null or undefined when calling deleteApiKey.");
    }
    if (requestParameters.key === null || requestParameters.key === void 0) {
      throw new RequiredError("key", "Required parameter requestParameters.key was null or undefined when calling deleteApiKey.");
    }
    const queryParameters = {};
    if (requestParameters.environmentId !== void 0) {
      queryParameters["environmentId"] = requestParameters.environmentId;
    }
    if (requestParameters.key !== void 0) {
      queryParameters["key"] = requestParameters.key;
    }
    const headerParameters = {};
    const response = await this.request({
      path: `/auth/api-key`,
      method: "DELETE",
      headers: headerParameters,
      query: queryParameters
    }, initOverrides);
    return new JSONApiResponse(response, (jsonValue) => SuccessResponseFromJSON(jsonValue));
  }
  /**
   * Delete the specified API key
   */
  async deleteApiKey(requestParameters, initOverrides) {
    const response = await this.deleteApiKeyRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Deletes the given data retention policy
   */
  async deleteDataRetentionPolicyRaw(requestParameters, initOverrides) {
    if (requestParameters.id === null || requestParameters.id === void 0) {
      throw new RequiredError("id", "Required parameter requestParameters.id was null or undefined when calling deleteDataRetentionPolicy.");
    }
    const queryParameters = {};
    const headerParameters = {};
    const response = await this.request({
      path: `/data-retention-policies/{id}`.replace(`{${"id"}}`, encodeURIComponent(String(requestParameters.id))),
      method: "DELETE",
      headers: headerParameters,
      query: queryParameters
    }, initOverrides);
    return new JSONApiResponse(response, (jsonValue) => SuccessResponseFromJSON(jsonValue));
  }
  /**
   * Deletes the given data retention policy
   */
  async deleteDataRetentionPolicy(requestParameters, initOverrides) {
    const response = await this.deleteDataRetentionPolicyRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Delete the database
   * Delete the database
   */
  async deleteDatabaseRaw(requestParameters, initOverrides) {
    if (requestParameters.workbookId === null || requestParameters.workbookId === void 0) {
      throw new RequiredError("workbookId", "Required parameter requestParameters.workbookId was null or undefined when calling deleteDatabase.");
    }
    const queryParameters = {};
    const headerParameters = {};
    const response = await this.request({
      path: `/foreigndb/{workbookId}`.replace(`{${"workbookId"}}`, encodeURIComponent(String(requestParameters.workbookId))),
      method: "DELETE",
      headers: headerParameters,
      query: queryParameters
    }, initOverrides);
    return new JSONApiResponse(response, (jsonValue) => SuccessResponseFromJSON(jsonValue));
  }
  /**
   * Delete the database
   * Delete the database
   */
  async deleteDatabase(requestParameters, initOverrides) {
    const response = await this.deleteDatabaseRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Deletes a single document
   * Delete a document
   */
  async deleteDocumentRaw(requestParameters, initOverrides) {
    if (requestParameters.documentId === null || requestParameters.documentId === void 0) {
      throw new RequiredError("documentId", "Required parameter requestParameters.documentId was null or undefined when calling deleteDocument.");
    }
    const queryParameters = {};
    const headerParameters = {};
    if (this.configuration && this.configuration.accessToken) {
      const token = this.configuration.accessToken;
      const tokenString = await token("BearerAuth", []);
      if (tokenString) {
        headerParameters["Authorization"] = `Bearer ${tokenString}`;
      }
    }
    const response = await this.request({
      path: `/documents/{documentId}`.replace(`{${"documentId"}}`, encodeURIComponent(String(requestParameters.documentId))),
      method: "DELETE",
      headers: headerParameters,
      query: queryParameters
    }, initOverrides);
    return new JSONApiResponse(response, (jsonValue) => SuccessResponseFromJSON(jsonValue));
  }
  /**
   * Deletes a single document
   * Delete a document
   */
  async deleteDocument(requestParameters, initOverrides) {
    const response = await this.deleteDocumentRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Deletes a single environment
   * Delete an environment
   */
  async deleteEnvironmentByIdRaw(requestParameters, initOverrides) {
    if (requestParameters.environmentId === null || requestParameters.environmentId === void 0) {
      throw new RequiredError("environmentId", "Required parameter requestParameters.environmentId was null or undefined when calling deleteEnvironmentById.");
    }
    const queryParameters = {};
    const headerParameters = {};
    if (this.configuration && this.configuration.accessToken) {
      const token = this.configuration.accessToken;
      const tokenString = await token("BearerAuth", []);
      if (tokenString) {
        headerParameters["Authorization"] = `Bearer ${tokenString}`;
      }
    }
    const response = await this.request({
      path: `/environments/{environmentId}`.replace(`{${"environmentId"}}`, encodeURIComponent(String(requestParameters.environmentId))),
      method: "DELETE",
      headers: headerParameters,
      query: queryParameters
    }, initOverrides);
    return new JSONApiResponse(response, (jsonValue) => SuccessResponseFromJSON(jsonValue));
  }
  /**
   * Deletes a single environment
   * Delete an environment
   */
  async deleteEnvironmentById(requestParameters, initOverrides) {
    const response = await this.deleteEnvironmentByIdRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Delete a file
   */
  async deleteFileRaw(requestParameters, initOverrides) {
    if (requestParameters.fileId === null || requestParameters.fileId === void 0) {
      throw new RequiredError("fileId", "Required parameter requestParameters.fileId was null or undefined when calling deleteFile.");
    }
    const queryParameters = {};
    const headerParameters = {};
    if (this.configuration && this.configuration.accessToken) {
      const token = this.configuration.accessToken;
      const tokenString = await token("BearerAuth", []);
      if (tokenString) {
        headerParameters["Authorization"] = `Bearer ${tokenString}`;
      }
    }
    const response = await this.request({
      path: `/files/{fileId}`.replace(`{${"fileId"}}`, encodeURIComponent(String(requestParameters.fileId))),
      method: "DELETE",
      headers: headerParameters,
      query: queryParameters
    }, initOverrides);
    return new JSONApiResponse(response, (jsonValue) => SuccessResponseFromJSON(jsonValue));
  }
  /**
   * Delete a file
   */
  async deleteFile(requestParameters, initOverrides) {
    const response = await this.deleteFileRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Deletes a single guest
   * Delete a guest
   */
  async deleteGuestRaw(requestParameters, initOverrides) {
    if (requestParameters.guestId === null || requestParameters.guestId === void 0) {
      throw new RequiredError("guestId", "Required parameter requestParameters.guestId was null or undefined when calling deleteGuest.");
    }
    const queryParameters = {};
    const headerParameters = {};
    if (this.configuration && this.configuration.accessToken) {
      const token = this.configuration.accessToken;
      const tokenString = await token("BearerAuth", []);
      if (tokenString) {
        headerParameters["Authorization"] = `Bearer ${tokenString}`;
      }
    }
    const response = await this.request({
      path: `/guests/{guestId}`.replace(`{${"guestId"}}`, encodeURIComponent(String(requestParameters.guestId))),
      method: "DELETE",
      headers: headerParameters,
      query: queryParameters
    }, initOverrides);
    return new JSONApiResponse(response, (jsonValue) => SuccessResponseFromJSON(jsonValue));
  }
  /**
   * Deletes a single guest
   * Delete a guest
   */
  async deleteGuest(requestParameters, initOverrides) {
    const response = await this.deleteGuestRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Removes a role from a guest
   * Remove Role from Guest
   */
  async deleteGuestRoleRaw(requestParameters, initOverrides) {
    if (requestParameters.guestId === null || requestParameters.guestId === void 0) {
      throw new RequiredError("guestId", "Required parameter requestParameters.guestId was null or undefined when calling deleteGuestRole.");
    }
    if (requestParameters.actorRoleId === null || requestParameters.actorRoleId === void 0) {
      throw new RequiredError("actorRoleId", "Required parameter requestParameters.actorRoleId was null or undefined when calling deleteGuestRole.");
    }
    const queryParameters = {};
    const headerParameters = {};
    if (this.configuration && this.configuration.accessToken) {
      const token = this.configuration.accessToken;
      const tokenString = await token("BearerAuth", []);
      if (tokenString) {
        headerParameters["Authorization"] = `Bearer ${tokenString}`;
      }
    }
    const response = await this.request({
      path: `/guests/{guestId}/roles/{actorRoleId}`.replace(`{${"guestId"}}`, encodeURIComponent(String(requestParameters.guestId))).replace(`{${"actorRoleId"}}`, encodeURIComponent(String(requestParameters.actorRoleId))),
      method: "DELETE",
      headers: headerParameters,
      query: queryParameters
    }, initOverrides);
    return new JSONApiResponse(response, (jsonValue) => SuccessResponseFromJSON(jsonValue));
  }
  /**
   * Removes a role from a guest
   * Remove Role from Guest
   */
  async deleteGuestRole(requestParameters, initOverrides) {
    const response = await this.deleteGuestRoleRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Delete a job
   */
  async deleteJobRaw(requestParameters, initOverrides) {
    if (requestParameters.jobId === null || requestParameters.jobId === void 0) {
      throw new RequiredError("jobId", "Required parameter requestParameters.jobId was null or undefined when calling deleteJob.");
    }
    const queryParameters = {};
    const headerParameters = {};
    if (this.configuration && this.configuration.accessToken) {
      const token = this.configuration.accessToken;
      const tokenString = await token("BearerAuth", []);
      if (tokenString) {
        headerParameters["Authorization"] = `Bearer ${tokenString}`;
      }
    }
    const response = await this.request({
      path: `/jobs/{jobId}`.replace(`{${"jobId"}}`, encodeURIComponent(String(requestParameters.jobId))),
      method: "DELETE",
      headers: headerParameters,
      query: queryParameters
    }, initOverrides);
    return new JSONApiResponse(response, (jsonValue) => SuccessResponseFromJSON(jsonValue));
  }
  /**
   * Delete a job
   */
  async deleteJob(requestParameters, initOverrides) {
    const response = await this.deleteJobRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Deletes a prompts
   */
  async deletePromptRaw(requestParameters, initOverrides) {
    if (requestParameters.promptId === null || requestParameters.promptId === void 0) {
      throw new RequiredError("promptId", "Required parameter requestParameters.promptId was null or undefined when calling deletePrompt.");
    }
    const queryParameters = {};
    const headerParameters = {};
    const response = await this.request({
      path: `/prompts/{promptId}`.replace(`{${"promptId"}}`, encodeURIComponent(String(requestParameters.promptId))),
      method: "DELETE",
      headers: headerParameters,
      query: queryParameters
    }, initOverrides);
    return new JSONApiResponse(response, (jsonValue) => SuccessResponseFromJSON(jsonValue));
  }
  /**
   * Deletes a prompts
   */
  async deletePrompt(requestParameters, initOverrides) {
    const response = await this.deletePromptRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Deletes records from a workbook sheet
   * Delete records
   */
  async deleteRecordsRaw(requestParameters, initOverrides) {
    if (requestParameters.sheetId === null || requestParameters.sheetId === void 0) {
      throw new RequiredError("sheetId", "Required parameter requestParameters.sheetId was null or undefined when calling deleteRecords.");
    }
    if (requestParameters.ids === null || requestParameters.ids === void 0) {
      throw new RequiredError("ids", "Required parameter requestParameters.ids was null or undefined when calling deleteRecords.");
    }
    const queryParameters = {};
    if (requestParameters.ids) {
      queryParameters["ids"] = requestParameters.ids;
    }
    const headerParameters = {};
    if (this.configuration && this.configuration.accessToken) {
      const token = this.configuration.accessToken;
      const tokenString = await token("BearerAuth", []);
      if (tokenString) {
        headerParameters["Authorization"] = `Bearer ${tokenString}`;
      }
    }
    const response = await this.request({
      path: `/sheets/{sheetId}/records`.replace(`{${"sheetId"}}`, encodeURIComponent(String(requestParameters.sheetId))),
      method: "DELETE",
      headers: headerParameters,
      query: queryParameters
    }, initOverrides);
    return new JSONApiResponse(response, (jsonValue) => SuccessResponseFromJSON(jsonValue));
  }
  /**
   * Deletes records from a workbook sheet
   * Delete records
   */
  async deleteRecords(requestParameters, initOverrides) {
    const response = await this.deleteRecordsRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Deletes records from a workbook sheet
   * Delete records
   */
  async deleteRecordsDeprecatedRaw(requestParameters, initOverrides) {
    if (requestParameters.workbookId === null || requestParameters.workbookId === void 0) {
      throw new RequiredError("workbookId", "Required parameter requestParameters.workbookId was null or undefined when calling deleteRecordsDeprecated.");
    }
    if (requestParameters.sheetId === null || requestParameters.sheetId === void 0) {
      throw new RequiredError("sheetId", "Required parameter requestParameters.sheetId was null or undefined when calling deleteRecordsDeprecated.");
    }
    const queryParameters = {};
    if (requestParameters.ids) {
      queryParameters["ids"] = requestParameters.ids;
    }
    const headerParameters = {};
    if (this.configuration && this.configuration.accessToken) {
      const token = this.configuration.accessToken;
      const tokenString = await token("BearerAuth", []);
      if (tokenString) {
        headerParameters["Authorization"] = `Bearer ${tokenString}`;
      }
    }
    const response = await this.request({
      path: `/workbooks/{workbookId}/sheets/{sheetId}/records`.replace(`{${"workbookId"}}`, encodeURIComponent(String(requestParameters.workbookId))).replace(`{${"sheetId"}}`, encodeURIComponent(String(requestParameters.sheetId))),
      method: "DELETE",
      headers: headerParameters,
      query: queryParameters
    }, initOverrides);
    return new JSONApiResponse(response, (jsonValue) => SuccessResponseFromJSON(jsonValue));
  }
  /**
   * Deletes records from a workbook sheet
   * Delete records
   */
  async deleteRecordsDeprecated(requestParameters, initOverrides) {
    const response = await this.deleteRecordsDeprecatedRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   */
  async deleteRuleRaw(requestParameters, initOverrides) {
    if (requestParameters.programId === null || requestParameters.programId === void 0) {
      throw new RequiredError("programId", "Required parameter requestParameters.programId was null or undefined when calling deleteRule.");
    }
    if (requestParameters.id === null || requestParameters.id === void 0) {
      throw new RequiredError("id", "Required parameter requestParameters.id was null or undefined when calling deleteRule.");
    }
    const queryParameters = {};
    const headerParameters = {};
    if (this.configuration && this.configuration.accessToken) {
      const token = this.configuration.accessToken;
      const tokenString = await token("BearerAuth", []);
      if (tokenString) {
        headerParameters["Authorization"] = `Bearer ${tokenString}`;
      }
    }
    const response = await this.request({
      path: `/mapping/{programId}/rules/{id}`.replace(`{${"programId"}}`, encodeURIComponent(String(requestParameters.programId))).replace(`{${"id"}}`, encodeURIComponent(String(requestParameters.id))),
      method: "DELETE",
      headers: headerParameters,
      query: queryParameters
    }, initOverrides);
    return new JSONApiResponse(response, (jsonValue) => SuccessResponseFromJSON(jsonValue));
  }
  /**
   */
  async deleteRule(requestParameters, initOverrides) {
    const response = await this.deleteRuleRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   */
  async deleteRulesRaw(requestParameters, initOverrides) {
    if (requestParameters.programId === null || requestParameters.programId === void 0) {
      throw new RequiredError("programId", "Required parameter requestParameters.programId was null or undefined when calling deleteRules.");
    }
    if (requestParameters.deleteRulesRequest === null || requestParameters.deleteRulesRequest === void 0) {
      throw new RequiredError("deleteRulesRequest", "Required parameter requestParameters.deleteRulesRequest was null or undefined when calling deleteRules.");
    }
    const queryParameters = {};
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    if (this.configuration && this.configuration.accessToken) {
      const token = this.configuration.accessToken;
      const tokenString = await token("BearerAuth", []);
      if (tokenString) {
        headerParameters["Authorization"] = `Bearer ${tokenString}`;
      }
    }
    const response = await this.request({
      path: `/mapping/{programId}/rules`.replace(`{${"programId"}}`, encodeURIComponent(String(requestParameters.programId))),
      method: "DELETE",
      headers: headerParameters,
      query: queryParameters,
      body: DeleteRulesRequestToJSON(requestParameters.deleteRulesRequest)
    }, initOverrides);
    return new JSONApiResponse(response, (jsonValue) => SuccessResponseFromJSON(jsonValue));
  }
  /**
   */
  async deleteRules(requestParameters, initOverrides) {
    const response = await this.deleteRulesRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Deletes a specific Secret from the Environment or Space as is the case
   * Delete a Secret by ID
   */
  async deleteSecretsRaw(requestParameters, initOverrides) {
    if (requestParameters.secretId === null || requestParameters.secretId === void 0) {
      throw new RequiredError("secretId", "Required parameter requestParameters.secretId was null or undefined when calling deleteSecrets.");
    }
    const queryParameters = {};
    const headerParameters = {};
    if (this.configuration && this.configuration.accessToken) {
      const token = this.configuration.accessToken;
      const tokenString = await token("BearerAuth", []);
      if (tokenString) {
        headerParameters["Authorization"] = `Bearer ${tokenString}`;
      }
    }
    const response = await this.request({
      path: `/secrets/{secretId}`.replace(`{${"secretId"}}`, encodeURIComponent(String(requestParameters.secretId))),
      method: "DELETE",
      headers: headerParameters,
      query: queryParameters
    }, initOverrides);
    return new JSONApiResponse(response, (jsonValue) => SuccessResponseFromJSON(jsonValue));
  }
  /**
   * Deletes a specific Secret from the Environment or Space as is the case
   * Delete a Secret by ID
   */
  async deleteSecrets(requestParameters, initOverrides) {
    const response = await this.deleteSecretsRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Deletes a specific sheet from a workbook
   * Delete a sheet
   */
  async deleteSheetRaw(requestParameters, initOverrides) {
    if (requestParameters.sheetId === null || requestParameters.sheetId === void 0) {
      throw new RequiredError("sheetId", "Required parameter requestParameters.sheetId was null or undefined when calling deleteSheet.");
    }
    const queryParameters = {};
    const headerParameters = {};
    if (this.configuration && this.configuration.accessToken) {
      const token = this.configuration.accessToken;
      const tokenString = await token("BearerAuth", []);
      if (tokenString) {
        headerParameters["Authorization"] = `Bearer ${tokenString}`;
      }
    }
    const response = await this.request({
      path: `/sheets/{sheetId}`.replace(`{${"sheetId"}}`, encodeURIComponent(String(requestParameters.sheetId))),
      method: "DELETE",
      headers: headerParameters,
      query: queryParameters
    }, initOverrides);
    return new JSONApiResponse(response, (jsonValue) => SuccessResponseFromJSON(jsonValue));
  }
  /**
   * Deletes a specific sheet from a workbook
   * Delete a sheet
   */
  async deleteSheet(requestParameters, initOverrides) {
    const response = await this.deleteSheetRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Delete a snapshot of a sheet
   * Delete snapshot
   */
  async deleteSnapshotRaw(requestParameters, initOverrides) {
    if (requestParameters.snapshotId === null || requestParameters.snapshotId === void 0) {
      throw new RequiredError("snapshotId", "Required parameter requestParameters.snapshotId was null or undefined when calling deleteSnapshot.");
    }
    const queryParameters = {};
    const headerParameters = {};
    if (this.configuration && this.configuration.accessToken) {
      const token = this.configuration.accessToken;
      const tokenString = await token("BearerAuth", []);
      if (tokenString) {
        headerParameters["Authorization"] = `Bearer ${tokenString}`;
      }
    }
    const response = await this.request({
      path: `/snapshots/{snapshotId}`.replace(`{${"snapshotId"}}`, encodeURIComponent(String(requestParameters.snapshotId))),
      method: "DELETE",
      headers: headerParameters,
      query: queryParameters
    }, initOverrides);
    return new JSONApiResponse(response, (jsonValue) => SuccessResponseFromJSON(jsonValue));
  }
  /**
   * Delete a snapshot of a sheet
   * Delete snapshot
   */
  async deleteSnapshot(requestParameters, initOverrides) {
    const response = await this.deleteSnapshotRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Delete a space
   * Delete a space
   */
  async deleteSpaceByIdRaw(requestParameters, initOverrides) {
    if (requestParameters.spaceId === null || requestParameters.spaceId === void 0) {
      throw new RequiredError("spaceId", "Required parameter requestParameters.spaceId was null or undefined when calling deleteSpaceById.");
    }
    const queryParameters = {};
    const headerParameters = {};
    if (this.configuration && this.configuration.accessToken) {
      const token = this.configuration.accessToken;
      const tokenString = await token("BearerAuth", []);
      if (tokenString) {
        headerParameters["Authorization"] = `Bearer ${tokenString}`;
      }
    }
    const response = await this.request({
      path: `/spaces/{spaceId}`.replace(`{${"spaceId"}}`, encodeURIComponent(String(requestParameters.spaceId))),
      method: "DELETE",
      headers: headerParameters,
      query: queryParameters
    }, initOverrides);
    return new JSONApiResponse(response, (jsonValue) => SuccessResponseFromJSON(jsonValue));
  }
  /**
   * Delete a space
   * Delete a space
   */
  async deleteSpaceById(requestParameters, initOverrides) {
    const response = await this.deleteSpaceByIdRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Deletes a single document
   * Delete a document
   */
  async deleteSpaceDocumentRaw(requestParameters, initOverrides) {
    if (requestParameters.spaceId === null || requestParameters.spaceId === void 0) {
      throw new RequiredError("spaceId", "Required parameter requestParameters.spaceId was null or undefined when calling deleteSpaceDocument.");
    }
    if (requestParameters.documentId === null || requestParameters.documentId === void 0) {
      throw new RequiredError("documentId", "Required parameter requestParameters.documentId was null or undefined when calling deleteSpaceDocument.");
    }
    const queryParameters = {};
    const headerParameters = {};
    if (this.configuration && this.configuration.accessToken) {
      const token = this.configuration.accessToken;
      const tokenString = await token("BearerAuth", []);
      if (tokenString) {
        headerParameters["Authorization"] = `Bearer ${tokenString}`;
      }
    }
    const response = await this.request({
      path: `/spaces/{spaceId}/documents/{documentId}`.replace(`{${"spaceId"}}`, encodeURIComponent(String(requestParameters.spaceId))).replace(`{${"documentId"}}`, encodeURIComponent(String(requestParameters.documentId))),
      method: "DELETE",
      headers: headerParameters,
      query: queryParameters
    }, initOverrides);
    return new JSONApiResponse(response, (jsonValue) => SuccessResponseFromJSON(jsonValue));
  }
  /**
   * Deletes a single document
   * Delete a document
   */
  async deleteSpaceDocument(requestParameters, initOverrides) {
    const response = await this.deleteSpaceDocumentRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Delete multiple Spaces from an Environment
   * Delete Spaces
   */
  async deleteSpacesByIdRaw(requestParameters, initOverrides) {
    const queryParameters = {};
    if (requestParameters.spaceIds) {
      queryParameters["spaceIds"] = requestParameters.spaceIds;
    }
    const headerParameters = {};
    if (this.configuration && this.configuration.accessToken) {
      const token = this.configuration.accessToken;
      const tokenString = await token("BearerAuth", []);
      if (tokenString) {
        headerParameters["Authorization"] = `Bearer ${tokenString}`;
      }
    }
    const response = await this.request({
      path: `/spaces`,
      method: "DELETE",
      headers: headerParameters,
      query: queryParameters
    }, initOverrides);
    return new JSONApiResponse(response, (jsonValue) => SuccessResponseFromJSON(jsonValue));
  }
  /**
   * Delete multiple Spaces from an Environment
   * Delete Spaces
   */
  async deleteSpacesById(requestParameters = {}, initOverrides) {
    const response = await this.deleteSpacesByIdRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Deletes a user
   * Deletes a user
   */
  async deleteUserRaw(requestParameters, initOverrides) {
    if (requestParameters.userId === null || requestParameters.userId === void 0) {
      throw new RequiredError("userId", "Required parameter requestParameters.userId was null or undefined when calling deleteUser.");
    }
    const queryParameters = {};
    const headerParameters = {};
    if (this.configuration && this.configuration.accessToken) {
      const token = this.configuration.accessToken;
      const tokenString = await token("BearerAuth", []);
      if (tokenString) {
        headerParameters["Authorization"] = `Bearer ${tokenString}`;
      }
    }
    const response = await this.request({
      path: `/users/{userId}`.replace(`{${"userId"}}`, encodeURIComponent(String(requestParameters.userId))),
      method: "DELETE",
      headers: headerParameters,
      query: queryParameters
    }, initOverrides);
    return new JSONApiResponse(response, (jsonValue) => SuccessResponseFromJSON(jsonValue));
  }
  /**
   * Deletes a user
   * Deletes a user
   */
  async deleteUser(requestParameters, initOverrides) {
    const response = await this.deleteUserRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Removes a role from a user
   * Remove Role from User
   */
  async deleteUserRoleRaw(requestParameters, initOverrides) {
    if (requestParameters.userId === null || requestParameters.userId === void 0) {
      throw new RequiredError("userId", "Required parameter requestParameters.userId was null or undefined when calling deleteUserRole.");
    }
    if (requestParameters.actorRoleId === null || requestParameters.actorRoleId === void 0) {
      throw new RequiredError("actorRoleId", "Required parameter requestParameters.actorRoleId was null or undefined when calling deleteUserRole.");
    }
    const queryParameters = {};
    const headerParameters = {};
    if (this.configuration && this.configuration.accessToken) {
      const token = this.configuration.accessToken;
      const tokenString = await token("BearerAuth", []);
      if (tokenString) {
        headerParameters["Authorization"] = `Bearer ${tokenString}`;
      }
    }
    const response = await this.request({
      path: `/users/{userId}/roles/{actorRoleId}`.replace(`{${"userId"}}`, encodeURIComponent(String(requestParameters.userId))).replace(`{${"actorRoleId"}}`, encodeURIComponent(String(requestParameters.actorRoleId))),
      method: "DELETE",
      headers: headerParameters,
      query: queryParameters
    }, initOverrides);
    return new JSONApiResponse(response, (jsonValue) => SuccessResponseFromJSON(jsonValue));
  }
  /**
   * Removes a role from a user
   * Remove Role from User
   */
  async deleteUserRole(requestParameters, initOverrides) {
    const response = await this.deleteUserRoleRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Deletes a view
   */
  async deleteViewRaw(requestParameters, initOverrides) {
    if (requestParameters.viewId === null || requestParameters.viewId === void 0) {
      throw new RequiredError("viewId", "Required parameter requestParameters.viewId was null or undefined when calling deleteView.");
    }
    const queryParameters = {};
    const headerParameters = {};
    const response = await this.request({
      path: `/views/{viewId}`.replace(`{${"viewId"}}`, encodeURIComponent(String(requestParameters.viewId))),
      method: "DELETE",
      headers: headerParameters,
      query: queryParameters
    }, initOverrides);
    return new JSONApiResponse(response, (jsonValue) => SuccessResponseFromJSON(jsonValue));
  }
  /**
   * Deletes a view
   */
  async deleteView(requestParameters, initOverrides) {
    const response = await this.deleteViewRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Deletes a workbook and all of its record data permanently
   * Delete a workbook
   */
  async deleteWorkbookByIdRaw(requestParameters, initOverrides) {
    if (requestParameters.workbookId === null || requestParameters.workbookId === void 0) {
      throw new RequiredError("workbookId", "Required parameter requestParameters.workbookId was null or undefined when calling deleteWorkbookById.");
    }
    const queryParameters = {};
    const headerParameters = {};
    if (this.configuration && this.configuration.accessToken) {
      const token = this.configuration.accessToken;
      const tokenString = await token("BearerAuth", []);
      if (tokenString) {
        headerParameters["Authorization"] = `Bearer ${tokenString}`;
      }
    }
    const response = await this.request({
      path: `/workbooks/{workbookId}`.replace(`{${"workbookId"}}`, encodeURIComponent(String(requestParameters.workbookId))),
      method: "DELETE",
      headers: headerParameters,
      query: queryParameters
    }, initOverrides);
    return new JSONApiResponse(response, (jsonValue) => SuccessResponseFromJSON(jsonValue));
  }
  /**
   * Deletes a workbook and all of its record data permanently
   * Delete a workbook
   */
  async deleteWorkbookById(requestParameters, initOverrides) {
    const response = await this.deleteWorkbookByIdRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Download an agent
   */
  async downloadAgentRaw(requestParameters, initOverrides) {
    if (requestParameters.agentId === null || requestParameters.agentId === void 0) {
      throw new RequiredError("agentId", "Required parameter requestParameters.agentId was null or undefined when calling downloadAgent.");
    }
    const queryParameters = {};
    const headerParameters = {};
    if (this.configuration && this.configuration.accessToken) {
      const token = this.configuration.accessToken;
      const tokenString = await token("BearerAuth", []);
      if (tokenString) {
        headerParameters["Authorization"] = `Bearer ${tokenString}`;
      }
    }
    const response = await this.request({
      path: `/agents/{agentId}/download`.replace(`{${"agentId"}}`, encodeURIComponent(String(requestParameters.agentId))),
      method: "GET",
      headers: headerParameters,
      query: queryParameters
    }, initOverrides);
    return new BlobApiResponse(response);
  }
  /**
   * Download an agent
   */
  async downloadAgent(requestParameters, initOverrides) {
    const response = await this.downloadAgentRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Download a file
   */
  async downloadFileRaw(requestParameters, initOverrides) {
    if (requestParameters.fileId === null || requestParameters.fileId === void 0) {
      throw new RequiredError("fileId", "Required parameter requestParameters.fileId was null or undefined when calling downloadFile.");
    }
    const queryParameters = {};
    const headerParameters = {};
    if (this.configuration && this.configuration.accessToken) {
      const token = this.configuration.accessToken;
      const tokenString = await token("BearerAuth", []);
      if (tokenString) {
        headerParameters["Authorization"] = `Bearer ${tokenString}`;
      }
    }
    const response = await this.request({
      path: `/files/{fileId}/download`.replace(`{${"fileId"}}`, encodeURIComponent(String(requestParameters.fileId))),
      method: "GET",
      headers: headerParameters,
      query: queryParameters
    }, initOverrides);
    return new BlobApiResponse(response);
  }
  /**
   * Download a file
   */
  async downloadFile(requestParameters, initOverrides) {
    const response = await this.downloadFileRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Exchange an invitation for an access token
   * Exchange an invitation for an access token
   */
  async exchangeInvitationRaw(requestParameters, initOverrides) {
    const queryParameters = {};
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    const response = await this.request({
      path: `/invitations/exchange`,
      method: "POST",
      headers: headerParameters,
      query: queryParameters,
      body: ExchangeInvitationRequestToJSON(requestParameters.exchangeInvitationRequest)
    }, initOverrides);
    return new JSONApiResponse(response, (jsonValue) => ExchangeTokenResponseFromJSON(jsonValue));
  }
  /**
   * Exchange an invitation for an access token
   * Exchange an invitation for an access token
   */
  async exchangeInvitation(requestParameters = {}, initOverrides) {
    const response = await this.exchangeInvitationRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Execute a job and return the job
   * Execute a job
   */
  async executeJobRaw(requestParameters, initOverrides) {
    if (requestParameters.jobId === null || requestParameters.jobId === void 0) {
      throw new RequiredError("jobId", "Required parameter requestParameters.jobId was null or undefined when calling executeJob.");
    }
    const queryParameters = {};
    const headerParameters = {};
    if (this.configuration && this.configuration.accessToken) {
      const token = this.configuration.accessToken;
      const tokenString = await token("BearerAuth", []);
      if (tokenString) {
        headerParameters["Authorization"] = `Bearer ${tokenString}`;
      }
    }
    const response = await this.request({
      path: `/jobs/{jobId}/execute`.replace(`{${"jobId"}}`, encodeURIComponent(String(requestParameters.jobId))),
      method: "POST",
      headers: headerParameters,
      query: queryParameters
    }, initOverrides);
    return new JSONApiResponse(response, (jsonValue) => SuccessResponseFromJSON(jsonValue));
  }
  /**
   * Execute a job and return the job
   * Execute a job
   */
  async executeJob(requestParameters, initOverrides) {
    const response = await this.executeJobRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Expire a space
   * Expire a Space, its Workbooks and Files
   */
  async expireSpaceByIdRaw(requestParameters, initOverrides) {
    if (requestParameters.spaceId === null || requestParameters.spaceId === void 0) {
      throw new RequiredError("spaceId", "Required parameter requestParameters.spaceId was null or undefined when calling expireSpaceById.");
    }
    const queryParameters = {};
    const headerParameters = {};
    if (this.configuration && this.configuration.accessToken) {
      const token = this.configuration.accessToken;
      const tokenString = await token("BearerAuth", []);
      if (tokenString) {
        headerParameters["Authorization"] = `Bearer ${tokenString}`;
      }
    }
    const response = await this.request({
      path: `/spaces/{spaceId}/expire`.replace(`{${"spaceId"}}`, encodeURIComponent(String(requestParameters.spaceId))),
      method: "POST",
      headers: headerParameters,
      query: queryParameters
    }, initOverrides);
    return new JSONApiResponse(response, (jsonValue) => SuccessResponseFromJSON(jsonValue));
  }
  /**
   * Expire a space
   * Expire a Space, its Workbooks and Files
   */
  async expireSpaceById(requestParameters, initOverrides) {
    const response = await this.expireSpaceByIdRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Fail a job and return the job
   * Fail a job
   */
  async failJobRaw(requestParameters, initOverrides) {
    if (requestParameters.jobId === null || requestParameters.jobId === void 0) {
      throw new RequiredError("jobId", "Required parameter requestParameters.jobId was null or undefined when calling failJob.");
    }
    const queryParameters = {};
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    if (this.configuration && this.configuration.accessToken) {
      const token = this.configuration.accessToken;
      const tokenString = await token("BearerAuth", []);
      if (tokenString) {
        headerParameters["Authorization"] = `Bearer ${tokenString}`;
      }
    }
    const response = await this.request({
      path: `/jobs/{jobId}/fail`.replace(`{${"jobId"}}`, encodeURIComponent(String(requestParameters.jobId))),
      method: "POST",
      headers: headerParameters,
      query: queryParameters,
      body: JobCompleteDetailsToJSON(requestParameters.jobCompleteDetails)
    }, initOverrides);
    return new JSONApiResponse(response, (jsonValue) => JobResponseFromJSON(jsonValue));
  }
  /**
   * Fail a job and return the job
   * Fail a job
   */
  async failJob(requestParameters, initOverrides) {
    const response = await this.failJobRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Searches for all values that match the \'find\' value (globally or for a specific field via \'fieldKey\') and replaces them with the \'replace\' value. Wrap \'find\' value in double quotes for exact match (\"\"). Returns a commitId for the updated records
   * Finds and replaces all field values matching search criteria with a given value
   */
  async findAndReplaceRaw(requestParameters, initOverrides) {
    if (requestParameters.sheetId === null || requestParameters.sheetId === void 0) {
      throw new RequiredError("sheetId", "Required parameter requestParameters.sheetId was null or undefined when calling findAndReplace.");
    }
    if (requestParameters.findAndReplaceRequest === null || requestParameters.findAndReplaceRequest === void 0) {
      throw new RequiredError("findAndReplaceRequest", "Required parameter requestParameters.findAndReplaceRequest was null or undefined when calling findAndReplace.");
    }
    const queryParameters = {};
    if (requestParameters.filter !== void 0) {
      queryParameters["filter"] = requestParameters.filter;
    }
    if (requestParameters.filterField !== void 0) {
      queryParameters["filterField"] = requestParameters.filterField;
    }
    if (requestParameters.searchValue !== void 0) {
      queryParameters["searchValue"] = requestParameters.searchValue;
    }
    if (requestParameters.searchField !== void 0) {
      queryParameters["searchField"] = requestParameters.searchField;
    }
    if (requestParameters.ids) {
      queryParameters["ids"] = requestParameters.ids;
    }
    if (requestParameters.q !== void 0) {
      queryParameters["q"] = requestParameters.q;
    }
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    if (this.configuration && this.configuration.accessToken) {
      const token = this.configuration.accessToken;
      const tokenString = await token("BearerAuth", []);
      if (tokenString) {
        headerParameters["Authorization"] = `Bearer ${tokenString}`;
      }
    }
    const response = await this.request({
      path: `/sheets/{sheetId}/find-replace`.replace(`{${"sheetId"}}`, encodeURIComponent(String(requestParameters.sheetId))),
      method: "PUT",
      headers: headerParameters,
      query: queryParameters,
      body: FindAndReplaceRequestToJSON(requestParameters.findAndReplaceRequest)
    }, initOverrides);
    return new JSONApiResponse(response, (jsonValue) => VersionResponseFromJSON(jsonValue));
  }
  /**
   * Searches for all values that match the \'find\' value (globally or for a specific field via \'fieldKey\') and replaces them with the \'replace\' value. Wrap \'find\' value in double quotes for exact match (\"\"). Returns a commitId for the updated records
   * Finds and replaces all field values matching search criteria with a given value
   */
  async findAndReplace(requestParameters, initOverrides) {
    const response = await this.findAndReplaceRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Authenticate with secret and clientId
   * Authenticate with secret and clientId
   */
  async getAccessTokenRaw(requestParameters, initOverrides) {
    const queryParameters = {};
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    const response = await this.request({
      path: `/auth/access-token`,
      method: "POST",
      headers: headerParameters,
      query: queryParameters,
      body: ApiCredentialsToJSON(requestParameters.apiCredentials)
    }, initOverrides);
    return new JSONApiResponse(response, (jsonValue) => AccessTokenResponseFromJSON(jsonValue));
  }
  /**
   * Authenticate with secret and clientId
   * Authenticate with secret and clientId
   */
  async getAccessToken(requestParameters = {}, initOverrides) {
    const response = await this.getAccessTokenRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Get an agent
   */
  async getAgentRaw(requestParameters, initOverrides) {
    if (requestParameters.agentId === null || requestParameters.agentId === void 0) {
      throw new RequiredError("agentId", "Required parameter requestParameters.agentId was null or undefined when calling getAgent.");
    }
    const queryParameters = {};
    if (requestParameters.environmentId !== void 0) {
      queryParameters["environmentId"] = requestParameters.environmentId;
    }
    const headerParameters = {};
    if (this.configuration && this.configuration.accessToken) {
      const token = this.configuration.accessToken;
      const tokenString = await token("BearerAuth", []);
      if (tokenString) {
        headerParameters["Authorization"] = `Bearer ${tokenString}`;
      }
    }
    const response = await this.request({
      path: `/agents/{agentId}`.replace(`{${"agentId"}}`, encodeURIComponent(String(requestParameters.agentId))),
      method: "GET",
      headers: headerParameters,
      query: queryParameters
    }, initOverrides);
    return new JSONApiResponse(response, (jsonValue) => AgentResponseFromJSON(jsonValue));
  }
  /**
   * Get an agent
   */
  async getAgent(requestParameters, initOverrides) {
    const response = await this.getAgentRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Get an agent
   */
  async getAgentDeprecatedRaw(requestParameters, initOverrides) {
    if (requestParameters.environmentId === null || requestParameters.environmentId === void 0) {
      throw new RequiredError("environmentId", "Required parameter requestParameters.environmentId was null or undefined when calling getAgentDeprecated.");
    }
    if (requestParameters.agentId === null || requestParameters.agentId === void 0) {
      throw new RequiredError("agentId", "Required parameter requestParameters.agentId was null or undefined when calling getAgentDeprecated.");
    }
    const queryParameters = {};
    const headerParameters = {};
    if (this.configuration && this.configuration.accessToken) {
      const token = this.configuration.accessToken;
      const tokenString = await token("BearerAuth", []);
      if (tokenString) {
        headerParameters["Authorization"] = `Bearer ${tokenString}`;
      }
    }
    const response = await this.request({
      path: `/environments/{environmentId}/agents/{agentId}`.replace(`{${"environmentId"}}`, encodeURIComponent(String(requestParameters.environmentId))).replace(`{${"agentId"}}`, encodeURIComponent(String(requestParameters.agentId))),
      method: "GET",
      headers: headerParameters,
      query: queryParameters
    }, initOverrides);
    return new JSONApiResponse(response, (jsonValue) => AgentResponseFromJSON(jsonValue));
  }
  /**
   * Get an agent
   */
  async getAgentDeprecated(requestParameters, initOverrides) {
    const response = await this.getAgentDeprecatedRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Get agent logs
   */
  async getAgentLogsRaw(requestParameters, initOverrides) {
    if (requestParameters.agentId === null || requestParameters.agentId === void 0) {
      throw new RequiredError("agentId", "Required parameter requestParameters.agentId was null or undefined when calling getAgentLogs.");
    }
    if (requestParameters.environmentId === null || requestParameters.environmentId === void 0) {
      throw new RequiredError("environmentId", "Required parameter requestParameters.environmentId was null or undefined when calling getAgentLogs.");
    }
    const queryParameters = {};
    if (requestParameters.environmentId !== void 0) {
      queryParameters["environmentId"] = requestParameters.environmentId;
    }
    if (requestParameters.pageSize !== void 0) {
      queryParameters["pageSize"] = requestParameters.pageSize;
    }
    if (requestParameters.pageNumber !== void 0) {
      queryParameters["pageNumber"] = requestParameters.pageNumber;
    }
    const headerParameters = {};
    if (this.configuration && this.configuration.accessToken) {
      const token = this.configuration.accessToken;
      const tokenString = await token("BearerAuth", []);
      if (tokenString) {
        headerParameters["Authorization"] = `Bearer ${tokenString}`;
      }
    }
    const response = await this.request({
      path: `/agents/{agentId}/logs`.replace(`{${"agentId"}}`, encodeURIComponent(String(requestParameters.agentId))),
      method: "GET",
      headers: headerParameters,
      query: queryParameters
    }, initOverrides);
    return new JSONApiResponse(response, (jsonValue) => AgentLogsResponseFromJSON(jsonValue));
  }
  /**
   * Get agent logs
   */
  async getAgentLogs(requestParameters, initOverrides) {
    const response = await this.getAgentLogsRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Get logs for an agent
   */
  async getAgentLogsDeprecatedRaw(requestParameters, initOverrides) {
    if (requestParameters.environmentId === null || requestParameters.environmentId === void 0) {
      throw new RequiredError("environmentId", "Required parameter requestParameters.environmentId was null or undefined when calling getAgentLogsDeprecated.");
    }
    if (requestParameters.agentId === null || requestParameters.agentId === void 0) {
      throw new RequiredError("agentId", "Required parameter requestParameters.agentId was null or undefined when calling getAgentLogsDeprecated.");
    }
    const queryParameters = {};
    const headerParameters = {};
    if (this.configuration && this.configuration.accessToken) {
      const token = this.configuration.accessToken;
      const tokenString = await token("BearerAuth", []);
      if (tokenString) {
        headerParameters["Authorization"] = `Bearer ${tokenString}`;
      }
    }
    const response = await this.request({
      path: `/environments/{environmentId}/agents/{agentId}/logs`.replace(`{${"environmentId"}}`, encodeURIComponent(String(requestParameters.environmentId))).replace(`{${"agentId"}}`, encodeURIComponent(String(requestParameters.agentId))),
      method: "GET",
      headers: headerParameters,
      query: queryParameters
    }, initOverrides);
    return new JSONApiResponse(response, (jsonValue) => AgentLogsResponseFromJSON(jsonValue));
  }
  /**
   * Get logs for an agent
   */
  async getAgentLogsDeprecated(requestParameters, initOverrides) {
    const response = await this.getAgentLogsDeprecatedRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Get all agents
   */
  async getAgentsRaw(requestParameters, initOverrides) {
    if (requestParameters.environmentId === null || requestParameters.environmentId === void 0) {
      throw new RequiredError("environmentId", "Required parameter requestParameters.environmentId was null or undefined when calling getAgents.");
    }
    const queryParameters = {};
    if (requestParameters.environmentId !== void 0) {
      queryParameters["environmentId"] = requestParameters.environmentId;
    }
    const headerParameters = {};
    if (this.configuration && this.configuration.accessToken) {
      const token = this.configuration.accessToken;
      const tokenString = await token("BearerAuth", []);
      if (tokenString) {
        headerParameters["Authorization"] = `Bearer ${tokenString}`;
      }
    }
    const response = await this.request({
      path: `/agents`,
      method: "GET",
      headers: headerParameters,
      query: queryParameters
    }, initOverrides);
    return new JSONApiResponse(response, (jsonValue) => AgentsResponseFromJSON(jsonValue));
  }
  /**
   * Get all agents
   */
  async getAgents(requestParameters, initOverrides) {
    const response = await this.getAgentsRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * List agents
   */
  async getAgentsDeprecatedRaw(requestParameters, initOverrides) {
    if (requestParameters.environmentId === null || requestParameters.environmentId === void 0) {
      throw new RequiredError("environmentId", "Required parameter requestParameters.environmentId was null or undefined when calling getAgentsDeprecated.");
    }
    const queryParameters = {};
    const headerParameters = {};
    if (this.configuration && this.configuration.accessToken) {
      const token = this.configuration.accessToken;
      const tokenString = await token("BearerAuth", []);
      if (tokenString) {
        headerParameters["Authorization"] = `Bearer ${tokenString}`;
      }
    }
    const response = await this.request({
      path: `/environments/{environmentId}/agents`.replace(`{${"environmentId"}}`, encodeURIComponent(String(requestParameters.environmentId))),
      method: "GET",
      headers: headerParameters,
      query: queryParameters
    }, initOverrides);
    return new JSONApiResponse(response, (jsonValue) => AgentsResponseFromJSON(jsonValue));
  }
  /**
   * List agents
   */
  async getAgentsDeprecated(requestParameters, initOverrides) {
    const response = await this.getAgentsDeprecatedRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Returns all space configs for an account
   * Get all space configs
   */
  async getAllSpaceConfigsRaw(initOverrides) {
    const queryParameters = {};
    const headerParameters = {};
    if (this.configuration && this.configuration.accessToken) {
      const token = this.configuration.accessToken;
      const tokenString = await token("BearerAuth", []);
      if (tokenString) {
        headerParameters["Authorization"] = `Bearer ${tokenString}`;
      }
    }
    const response = await this.request({
      path: `/space-configs`,
      method: "GET",
      headers: headerParameters,
      query: queryParameters
    }, initOverrides);
    return new JSONApiResponse(response, (jsonValue) => SpaceConfigsResponseFromJSON(jsonValue));
  }
  /**
   * Returns all space configs for an account
   * Get all space configs
   */
  async getAllSpaceConfigs(initOverrides) {
    const response = await this.getAllSpaceConfigsRaw(initOverrides);
    return await response.value();
  }
  /**
   * Returns all space configs for an account
   * List space configs
   */
  async getAllSpaceConfigsDeprecatedRaw(requestParameters, initOverrides) {
    const queryParameters = {};
    if (requestParameters.environmentId !== void 0) {
      queryParameters["environmentId"] = requestParameters.environmentId;
    }
    const headerParameters = {};
    if (this.configuration && this.configuration.accessToken) {
      const token = this.configuration.accessToken;
      const tokenString = await token("BearerAuth", []);
      if (tokenString) {
        headerParameters["Authorization"] = `Bearer ${tokenString}`;
      }
    }
    const response = await this.request({
      path: `/spaces/configs`,
      method: "GET",
      headers: headerParameters,
      query: queryParameters
    }, initOverrides);
    return new JSONApiResponse(response, (jsonValue) => SpaceConfigsResponseFromJSON(jsonValue));
  }
  /**
   * Returns all space configs for an account
   * List space configs
   */
  async getAllSpaceConfigsDeprecated(requestParameters = {}, initOverrides) {
    const response = await this.getAllSpaceConfigsDeprecatedRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Returns all spaces for an account or environment
   * Get all spaces
   */
  async getAllSpacesRaw(requestParameters, initOverrides) {
    const queryParameters = {};
    if (requestParameters.environmentId !== void 0) {
      queryParameters["environmentId"] = requestParameters.environmentId;
    }
    if (requestParameters.pageSize !== void 0) {
      queryParameters["pageSize"] = requestParameters.pageSize;
    }
    if (requestParameters.pageNumber !== void 0) {
      queryParameters["pageNumber"] = requestParameters.pageNumber;
    }
    if (requestParameters.namespace !== void 0) {
      queryParameters["namespace"] = requestParameters.namespace;
    }
    if (requestParameters.app !== void 0) {
      queryParameters["app"] = requestParameters.app;
    }
    if (requestParameters.search !== void 0) {
      queryParameters["search"] = requestParameters.search;
    }
    if (requestParameters.sortField !== void 0) {
      queryParameters["sortField"] = requestParameters.sortField;
    }
    if (requestParameters.sortDirection !== void 0) {
      queryParameters["sortDirection"] = requestParameters.sortDirection;
    }
    if (requestParameters.archived !== void 0) {
      queryParameters["archived"] = requestParameters.archived;
    }
    if (requestParameters.isCollaborative !== void 0) {
      queryParameters["isCollaborative"] = requestParameters.isCollaborative;
    }
    const headerParameters = {};
    if (this.configuration && this.configuration.accessToken) {
      const token = this.configuration.accessToken;
      const tokenString = await token("BearerAuth", []);
      if (tokenString) {
        headerParameters["Authorization"] = `Bearer ${tokenString}`;
      }
    }
    const response = await this.request({
      path: `/spaces`,
      method: "GET",
      headers: headerParameters,
      query: queryParameters
    }, initOverrides);
    return new JSONApiResponse(response, (jsonValue) => SpacesResponseFromJSON(jsonValue));
  }
  /**
   * Returns all spaces for an account or environment
   * Get all spaces
   */
  async getAllSpaces(requestParameters = {}, initOverrides) {
    const response = await this.getAllSpacesRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Returns all workbooks matching a filter for an account or space
   * List workbooks
   */
  async getAllWorkbooksRaw(requestParameters, initOverrides) {
    const queryParameters = {};
    if (requestParameters.spaceId !== void 0) {
      queryParameters["spaceId"] = requestParameters.spaceId;
    }
    if (requestParameters.name !== void 0) {
      queryParameters["name"] = requestParameters.name;
    }
    if (requestParameters.namespace !== void 0) {
      queryParameters["namespace"] = requestParameters.namespace;
    }
    if (requestParameters.label !== void 0) {
      queryParameters["label"] = requestParameters.label;
    }
    if (requestParameters.treatment !== void 0) {
      queryParameters["treatment"] = requestParameters.treatment;
    }
    if (requestParameters.includeSheets !== void 0) {
      queryParameters["includeSheets"] = requestParameters.includeSheets;
    }
    if (requestParameters.includeCounts !== void 0) {
      queryParameters["includeCounts"] = requestParameters.includeCounts;
    }
    const headerParameters = {};
    if (this.configuration && this.configuration.accessToken) {
      const token = this.configuration.accessToken;
      const tokenString = await token("BearerAuth", []);
      if (tokenString) {
        headerParameters["Authorization"] = `Bearer ${tokenString}`;
      }
    }
    const response = await this.request({
      path: `/workbooks`,
      method: "GET",
      headers: headerParameters,
      query: queryParameters
    }, initOverrides);
    return new JSONApiResponse(response, (jsonValue) => WorkbooksResponseFromJSON(jsonValue));
  }
  /**
   * Returns all workbooks matching a filter for an account or space
   * List workbooks
   */
  async getAllWorkbooks(requestParameters = {}, initOverrides) {
    const response = await this.getAllWorkbooksRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Obtain the API keys for a specific environment
   */
  async getApiKeysRaw(requestParameters, initOverrides) {
    if (requestParameters.environmentId === null || requestParameters.environmentId === void 0) {
      throw new RequiredError("environmentId", "Required parameter requestParameters.environmentId was null or undefined when calling getApiKeys.");
    }
    const queryParameters = {};
    if (requestParameters.environmentId !== void 0) {
      queryParameters["environmentId"] = requestParameters.environmentId;
    }
    const headerParameters = {};
    const response = await this.request({
      path: `/auth/api-keys`,
      method: "GET",
      headers: headerParameters,
      query: queryParameters
    }, initOverrides);
    return new JSONApiResponse(response, (jsonValue) => ApiKeysResponseFromJSON(jsonValue));
  }
  /**
   * Obtain the API keys for a specific environment
   */
  async getApiKeys(requestParameters, initOverrides) {
    const response = await this.getApiKeysRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Gets all the api tokens for a user
   * List all tokens
   */
  async getApiTokensRaw(requestParameters, initOverrides) {
    if (requestParameters.userId === null || requestParameters.userId === void 0) {
      throw new RequiredError("userId", "Required parameter requestParameters.userId was null or undefined when calling getApiTokens.");
    }
    if (requestParameters.tenantId === null || requestParameters.tenantId === void 0) {
      throw new RequiredError("tenantId", "Required parameter requestParameters.tenantId was null or undefined when calling getApiTokens.");
    }
    const queryParameters = {};
    if (requestParameters.tenantId !== void 0) {
      queryParameters["tenantId"] = requestParameters.tenantId;
    }
    if (requestParameters.pageSize !== void 0) {
      queryParameters["pageSize"] = requestParameters.pageSize;
    }
    if (requestParameters.pageNumber !== void 0) {
      queryParameters["pageNumber"] = requestParameters.pageNumber;
    }
    const headerParameters = {};
    if (this.configuration && this.configuration.accessToken) {
      const token = this.configuration.accessToken;
      const tokenString = await token("BearerAuth", []);
      if (tokenString) {
        headerParameters["Authorization"] = `Bearer ${tokenString}`;
      }
    }
    const response = await this.request({
      path: `/users/{userId}/api-token`.replace(`{${"userId"}}`, encodeURIComponent(String(requestParameters.userId))),
      method: "GET",
      headers: headerParameters,
      query: queryParameters
    }, initOverrides);
    return new JSONApiResponse(response, (jsonValue) => ApiTokensResponseFromJSON(jsonValue));
  }
  /**
   * Gets all the api tokens for a user
   * List all tokens
   */
  async getApiTokens(requestParameters, initOverrides) {
    const response = await this.getApiTokensRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Version details for the API
   */
  async getApiVersionDetailsRaw(initOverrides) {
    const queryParameters = {};
    const headerParameters = {};
    const response = await this.request({
      path: `/version`,
      method: "GET",
      headers: headerParameters,
      query: queryParameters
    }, initOverrides);
    return new JSONApiResponse(response, (jsonValue) => AppVersionResponseFromJSON(jsonValue));
  }
  /**
   * Version details for the API
   */
  async getApiVersionDetails(initOverrides) {
    const response = await this.getApiVersionDetailsRaw(initOverrides);
    return await response.value();
  }
  /**
   * Returns record cell values grouped by all fields in the sheet
   * Get record cells by field
   */
  async getCellValuesRaw(requestParameters, initOverrides) {
    if (requestParameters.sheetId === null || requestParameters.sheetId === void 0) {
      throw new RequiredError("sheetId", "Required parameter requestParameters.sheetId was null or undefined when calling getCellValues.");
    }
    if (requestParameters.distinct === null || requestParameters.distinct === void 0) {
      throw new RequiredError("distinct", "Required parameter requestParameters.distinct was null or undefined when calling getCellValues.");
    }
    const queryParameters = {};
    if (requestParameters.fieldKey !== void 0) {
      queryParameters["fieldKey"] = requestParameters.fieldKey;
    }
    if (requestParameters.sortField !== void 0) {
      queryParameters["sortField"] = requestParameters.sortField;
    }
    if (requestParameters.sortDirection !== void 0) {
      queryParameters["sortDirection"] = requestParameters.sortDirection;
    }
    if (requestParameters.filter !== void 0) {
      queryParameters["filter"] = requestParameters.filter;
    }
    if (requestParameters.filterField !== void 0) {
      queryParameters["filterField"] = requestParameters.filterField;
    }
    if (requestParameters.pageSize !== void 0) {
      queryParameters["pageSize"] = requestParameters.pageSize;
    }
    if (requestParameters.pageNumber !== void 0) {
      queryParameters["pageNumber"] = requestParameters.pageNumber;
    }
    if (requestParameters.distinct !== void 0) {
      queryParameters["distinct"] = requestParameters.distinct;
    }
    if (requestParameters.includeCounts !== void 0) {
      queryParameters["includeCounts"] = requestParameters.includeCounts;
    }
    if (requestParameters.searchValue !== void 0) {
      queryParameters["searchValue"] = requestParameters.searchValue;
    }
    const headerParameters = {};
    if (this.configuration && this.configuration.accessToken) {
      const token = this.configuration.accessToken;
      const tokenString = await token("BearerAuth", []);
      if (tokenString) {
        headerParameters["Authorization"] = `Bearer ${tokenString}`;
      }
    }
    const response = await this.request({
      path: `/sheets/{sheetId}/cells`.replace(`{${"sheetId"}}`, encodeURIComponent(String(requestParameters.sheetId))),
      method: "GET",
      headers: headerParameters,
      query: queryParameters
    }, initOverrides);
    return new JSONApiResponse(response, (jsonValue) => CellsResponseFromJSON(jsonValue));
  }
  /**
   * Returns record cell values grouped by all fields in the sheet
   * Get record cells by field
   */
  async getCellValues(requestParameters, initOverrides) {
    const response = await this.getCellValuesRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Returns the details of a commit version
   */
  async getCommitRaw(requestParameters, initOverrides) {
    if (requestParameters.commitId === null || requestParameters.commitId === void 0) {
      throw new RequiredError("commitId", "Required parameter requestParameters.commitId was null or undefined when calling getCommit.");
    }
    const queryParameters = {};
    const headerParameters = {};
    const response = await this.request({
      path: `/commits/{commitId}`.replace(`{${"commitId"}}`, encodeURIComponent(String(requestParameters.commitId))),
      method: "GET",
      headers: headerParameters,
      query: queryParameters
    }, initOverrides);
    return new JSONApiResponse(response, (jsonValue) => GetCommitResponseFromJSON(jsonValue));
  }
  /**
   * Returns the details of a commit version
   */
  async getCommit(requestParameters, initOverrides) {
    const response = await this.getCommitRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Returns counts of records from a sheet
   * Get record counts
   */
  async getCountsRaw(requestParameters, initOverrides) {
    if (requestParameters.sheetId === null || requestParameters.sheetId === void 0) {
      throw new RequiredError("sheetId", "Required parameter requestParameters.sheetId was null or undefined when calling getCounts.");
    }
    const queryParameters = {};
    if (requestParameters.versionId !== void 0) {
      queryParameters["versionId"] = requestParameters.versionId;
    }
    if (requestParameters.commitId !== void 0) {
      queryParameters["commitId"] = requestParameters.commitId;
    }
    if (requestParameters.sinceVersionId !== void 0) {
      queryParameters["sinceVersionId"] = requestParameters.sinceVersionId;
    }
    if (requestParameters.sinceCommitId !== void 0) {
      queryParameters["sinceCommitId"] = requestParameters.sinceCommitId;
    }
    if (requestParameters.filter !== void 0) {
      queryParameters["filter"] = requestParameters.filter;
    }
    if (requestParameters.filterField !== void 0) {
      queryParameters["filterField"] = requestParameters.filterField;
    }
    if (requestParameters.searchValue !== void 0) {
      queryParameters["searchValue"] = requestParameters.searchValue;
    }
    if (requestParameters.searchField !== void 0) {
      queryParameters["searchField"] = requestParameters.searchField;
    }
    if (requestParameters.byField !== void 0) {
      queryParameters["byField"] = requestParameters.byField;
    }
    if (requestParameters.q !== void 0) {
      queryParameters["q"] = requestParameters.q;
    }
    const headerParameters = {};
    if (this.configuration && this.configuration.accessToken) {
      const token = this.configuration.accessToken;
      const tokenString = await token("BearerAuth", []);
      if (tokenString) {
        headerParameters["Authorization"] = `Bearer ${tokenString}`;
      }
    }
    const response = await this.request({
      path: `/sheets/{sheetId}/counts`.replace(`{${"sheetId"}}`, encodeURIComponent(String(requestParameters.sheetId))),
      method: "GET",
      headers: headerParameters,
      query: queryParameters
    }, initOverrides);
    return new JSONApiResponse(response, (jsonValue) => RecordCountsResponseFromJSON(jsonValue));
  }
  /**
   * Returns counts of records from a sheet
   * Get record counts
   */
  async getCounts(requestParameters, initOverrides) {
    const response = await this.getCountsRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Get the account information for the currently authenticated user.
   * Get the current account
   */
  async getCurrentAccountRaw(initOverrides) {
    const queryParameters = {};
    const headerParameters = {};
    if (this.configuration && this.configuration.accessToken) {
      const token = this.configuration.accessToken;
      const tokenString = await token("BearerAuth", []);
      if (tokenString) {
        headerParameters["Authorization"] = `Bearer ${tokenString}`;
      }
    }
    const response = await this.request({
      path: `/accounts/current`,
      method: "GET",
      headers: headerParameters,
      query: queryParameters
    }, initOverrides);
    return new JSONApiResponse(response, (jsonValue) => AccountResponseFromJSON(jsonValue));
  }
  /**
   * Get the account information for the currently authenticated user.
   * Get the current account
   */
  async getCurrentAccount(initOverrides) {
    const response = await this.getCurrentAccountRaw(initOverrides);
    return await response.value();
  }
  /**
   * Get a data retention policy by account or filter by environment id
   * Get data retention policy by account or filter by environment id
   */
  async getDataRetentionPoliciesByAccountRaw(requestParameters, initOverrides) {
    const queryParameters = {};
    if (requestParameters.environmentId !== void 0) {
      queryParameters["environmentId"] = requestParameters.environmentId;
    }
    const headerParameters = {};
    if (this.configuration && this.configuration.accessToken) {
      const token = this.configuration.accessToken;
      const tokenString = await token("BearerAuth", []);
      if (tokenString) {
        headerParameters["Authorization"] = `Bearer ${tokenString}`;
      }
    }
    const response = await this.request({
      path: `/data-retention-policies`,
      method: "GET",
      headers: headerParameters,
      query: queryParameters
    }, initOverrides);
    return new JSONApiResponse(response, (jsonValue) => DataRetentionPolicyResponseFromJSON(jsonValue));
  }
  /**
   * Get a data retention policy by account or filter by environment id
   * Get data retention policy by account or filter by environment id
   */
  async getDataRetentionPoliciesByAccount(requestParameters = {}, initOverrides) {
    const response = await this.getDataRetentionPoliciesByAccountRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Get a data retention policy on an environment
   * Get a data retention policy
   */
  async getDataRetentionPolicyRaw(requestParameters, initOverrides) {
    if (requestParameters.id === null || requestParameters.id === void 0) {
      throw new RequiredError("id", "Required parameter requestParameters.id was null or undefined when calling getDataRetentionPolicy.");
    }
    const queryParameters = {};
    const headerParameters = {};
    if (this.configuration && this.configuration.accessToken) {
      const token = this.configuration.accessToken;
      const tokenString = await token("BearerAuth", []);
      if (tokenString) {
        headerParameters["Authorization"] = `Bearer ${tokenString}`;
      }
    }
    const response = await this.request({
      path: `/data-retention-policies/{id}`.replace(`{${"id"}}`, encodeURIComponent(String(requestParameters.id))),
      method: "GET",
      headers: headerParameters,
      query: queryParameters
    }, initOverrides);
    return new JSONApiResponse(response, (jsonValue) => DataRetentionPolicyResponseFromJSON(jsonValue));
  }
  /**
   * Get a data retention policy on an environment
   * Get a data retention policy
   */
  async getDataRetentionPolicy(requestParameters, initOverrides) {
    const response = await this.getDataRetentionPolicyRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Get the database information
   * Get the database information
   */
  async getDatabaseInfoRaw(requestParameters, initOverrides) {
    if (requestParameters.workbookId === null || requestParameters.workbookId === void 0) {
      throw new RequiredError("workbookId", "Required parameter requestParameters.workbookId was null or undefined when calling getDatabaseInfo.");
    }
    const queryParameters = {};
    const headerParameters = {};
    const response = await this.request({
      path: `/foreigndb/{workbookId}`.replace(`{${"workbookId"}}`, encodeURIComponent(String(requestParameters.workbookId))),
      method: "GET",
      headers: headerParameters,
      query: queryParameters
    }, initOverrides);
    return new JSONApiResponse(response, (jsonValue) => GetDatabaseResponseResponseFromJSON(jsonValue));
  }
  /**
   * Get the database information
   * Get the database information
   */
  async getDatabaseInfo(requestParameters, initOverrides) {
    const response = await this.getDatabaseInfoRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Get the user of a database
   * Get database user
   */
  async getDatabaseUserRaw(requestParameters, initOverrides) {
    if (requestParameters.workbookId === null || requestParameters.workbookId === void 0) {
      throw new RequiredError("workbookId", "Required parameter requestParameters.workbookId was null or undefined when calling getDatabaseUser.");
    }
    const queryParameters = {};
    const headerParameters = {};
    const response = await this.request({
      path: `/foreigndb/{workbookId}/user`.replace(`{${"workbookId"}}`, encodeURIComponent(String(requestParameters.workbookId))),
      method: "GET",
      headers: headerParameters,
      query: queryParameters
    }, initOverrides);
    return new JSONApiResponse(response, (jsonValue) => GetUserResponseFromJSON(jsonValue));
  }
  /**
   * Get the user of a database
   * Get database user
   */
  async getDatabaseUser(requestParameters, initOverrides) {
    const response = await this.getDatabaseUserRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Returns a single document
   * Get a document
   */
  async getDocumentByIdRaw(requestParameters, initOverrides) {
    if (requestParameters.documentId === null || requestParameters.documentId === void 0) {
      throw new RequiredError("documentId", "Required parameter requestParameters.documentId was null or undefined when calling getDocumentById.");
    }
    const queryParameters = {};
    const headerParameters = {};
    if (this.configuration && this.configuration.accessToken) {
      const token = this.configuration.accessToken;
      const tokenString = await token("BearerAuth", []);
      if (tokenString) {
        headerParameters["Authorization"] = `Bearer ${tokenString}`;
      }
    }
    const response = await this.request({
      path: `/documents/{documentId}`.replace(`{${"documentId"}}`, encodeURIComponent(String(requestParameters.documentId))),
      method: "GET",
      headers: headerParameters,
      query: queryParameters
    }, initOverrides);
    return new JSONApiResponse(response, (jsonValue) => DocumentResponseFromJSON(jsonValue));
  }
  /**
   * Returns a single document
   * Get a document
   */
  async getDocumentById(requestParameters, initOverrides) {
    const response = await this.getDocumentByIdRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Gets all entitlements for a resource
   * Gets all entitlements for a resource
   */
  async getEntitlementsForResourceRaw(requestParameters, initOverrides) {
    if (requestParameters.resourceId === null || requestParameters.resourceId === void 0) {
      throw new RequiredError("resourceId", "Required parameter requestParameters.resourceId was null or undefined when calling getEntitlementsForResource.");
    }
    const queryParameters = {};
    if (requestParameters.resourceId !== void 0) {
      queryParameters["resourceId"] = requestParameters.resourceId;
    }
    const headerParameters = {};
    if (this.configuration && this.configuration.accessToken) {
      const token = this.configuration.accessToken;
      const tokenString = await token("BearerAuth", []);
      if (tokenString) {
        headerParameters["Authorization"] = `Bearer ${tokenString}`;
      }
    }
    const response = await this.request({
      path: `/entitlements`,
      method: "GET",
      headers: headerParameters,
      query: queryParameters
    }, initOverrides);
    return new JSONApiResponse(response, (jsonValue) => EntitlementsResponseFromJSON(jsonValue));
  }
  /**
   * Gets all entitlements for a resource
   * Gets all entitlements for a resource
   */
  async getEntitlementsForResource(requestParameters, initOverrides) {
    const response = await this.getEntitlementsForResourceRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   */
  async getEnumWeightsRaw(requestParameters, initOverrides) {
    if (requestParameters.getEnumWeightsRequest === null || requestParameters.getEnumWeightsRequest === void 0) {
      throw new RequiredError("getEnumWeightsRequest", "Required parameter requestParameters.getEnumWeightsRequest was null or undefined when calling getEnumWeights.");
    }
    const queryParameters = {};
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    const response = await this.request({
      path: `/mapping/enum-weights`,
      method: "POST",
      headers: headerParameters,
      query: queryParameters,
      body: GetEnumWeightsRequestToJSON(requestParameters.getEnumWeightsRequest)
    }, initOverrides);
    return new JSONApiResponse(response, (jsonValue) => SourceDestinationWeightsResponseFromJSON(jsonValue));
  }
  /**
   */
  async getEnumWeights(requestParameters, initOverrides) {
    const response = await this.getEnumWeightsRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Get all executions in your environment
   */
  async getEnvironmentAgentExecutionsRaw(requestParameters, initOverrides) {
    if (requestParameters.environmentId === null || requestParameters.environmentId === void 0) {
      throw new RequiredError("environmentId", "Required parameter requestParameters.environmentId was null or undefined when calling getEnvironmentAgentExecutions.");
    }
    const queryParameters = {};
    if (requestParameters.environmentId !== void 0) {
      queryParameters["environmentId"] = requestParameters.environmentId;
    }
    if (requestParameters.spaceId !== void 0) {
      queryParameters["spaceId"] = requestParameters.spaceId;
    }
    if (requestParameters.success !== void 0) {
      queryParameters["success"] = requestParameters.success;
    }
    if (requestParameters.pageSize !== void 0) {
      queryParameters["pageSize"] = requestParameters.pageSize;
    }
    if (requestParameters.pageNumber !== void 0) {
      queryParameters["pageNumber"] = requestParameters.pageNumber;
    }
    const headerParameters = {};
    if (this.configuration && this.configuration.accessToken) {
      const token = this.configuration.accessToken;
      const tokenString = await token("BearerAuth", []);
      if (tokenString) {
        headerParameters["Authorization"] = `Bearer ${tokenString}`;
      }
    }
    const response = await this.request({
      path: `/agents/executions`,
      method: "GET",
      headers: headerParameters,
      query: queryParameters
    }, initOverrides);
    return new JSONApiResponse(response, (jsonValue) => ExecutionsResponseFromJSON(jsonValue));
  }
  /**
   * Get all executions in your environment
   */
  async getEnvironmentAgentExecutions(requestParameters, initOverrides) {
    const response = await this.getEnvironmentAgentExecutionsRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Get an agent log
   */
  async getEnvironmentAgentLogRaw(requestParameters, initOverrides) {
    if (requestParameters.eventId === null || requestParameters.eventId === void 0) {
      throw new RequiredError("eventId", "Required parameter requestParameters.eventId was null or undefined when calling getEnvironmentAgentLog.");
    }
    if (requestParameters.environmentId === null || requestParameters.environmentId === void 0) {
      throw new RequiredError("environmentId", "Required parameter requestParameters.environmentId was null or undefined when calling getEnvironmentAgentLog.");
    }
    const queryParameters = {};
    if (requestParameters.environmentId !== void 0) {
      queryParameters["environmentId"] = requestParameters.environmentId;
    }
    const headerParameters = {};
    if (this.configuration && this.configuration.accessToken) {
      const token = this.configuration.accessToken;
      const tokenString = await token("BearerAuth", []);
      if (tokenString) {
        headerParameters["Authorization"] = `Bearer ${tokenString}`;
      }
    }
    const response = await this.request({
      path: `/agents/log/{eventId}`.replace(`{${"eventId"}}`, encodeURIComponent(String(requestParameters.eventId))),
      method: "GET",
      headers: headerParameters,
      query: queryParameters
    }, initOverrides);
    return new JSONApiResponse(response, (jsonValue) => AgentLogResponseFromJSON(jsonValue));
  }
  /**
   * Get an agent log
   */
  async getEnvironmentAgentLog(requestParameters, initOverrides) {
    const response = await this.getEnvironmentAgentLogRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Get all agent logs in your environment
   */
  async getEnvironmentAgentLogsRaw(requestParameters, initOverrides) {
    if (requestParameters.environmentId === null || requestParameters.environmentId === void 0) {
      throw new RequiredError("environmentId", "Required parameter requestParameters.environmentId was null or undefined when calling getEnvironmentAgentLogs.");
    }
    const queryParameters = {};
    if (requestParameters.environmentId !== void 0) {
      queryParameters["environmentId"] = requestParameters.environmentId;
    }
    if (requestParameters.spaceId !== void 0) {
      queryParameters["spaceId"] = requestParameters.spaceId;
    }
    if (requestParameters.success !== void 0) {
      queryParameters["success"] = requestParameters.success;
    }
    if (requestParameters.pageSize !== void 0) {
      queryParameters["pageSize"] = requestParameters.pageSize;
    }
    if (requestParameters.pageNumber !== void 0) {
      queryParameters["pageNumber"] = requestParameters.pageNumber;
    }
    const headerParameters = {};
    if (this.configuration && this.configuration.accessToken) {
      const token = this.configuration.accessToken;
      const tokenString = await token("BearerAuth", []);
      if (tokenString) {
        headerParameters["Authorization"] = `Bearer ${tokenString}`;
      }
    }
    const response = await this.request({
      path: `/agents/logs`,
      method: "GET",
      headers: headerParameters,
      query: queryParameters
    }, initOverrides);
    return new JSONApiResponse(response, (jsonValue) => AgentLogsResponse1FromJSON(jsonValue));
  }
  /**
   * Get all agent logs in your environment
   */
  async getEnvironmentAgentLogs(requestParameters, initOverrides) {
    const response = await this.getEnvironmentAgentLogsRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Returns a single environment
   * Get an environment
   */
  async getEnvironmentByIdRaw(requestParameters, initOverrides) {
    if (requestParameters.environmentId === null || requestParameters.environmentId === void 0) {
      throw new RequiredError("environmentId", "Required parameter requestParameters.environmentId was null or undefined when calling getEnvironmentById.");
    }
    const queryParameters = {};
    const headerParameters = {};
    if (this.configuration && this.configuration.accessToken) {
      const token = this.configuration.accessToken;
      const tokenString = await token("BearerAuth", []);
      if (tokenString) {
        headerParameters["Authorization"] = `Bearer ${tokenString}`;
      }
    }
    const response = await this.request({
      path: `/environments/{environmentId}`.replace(`{${"environmentId"}}`, encodeURIComponent(String(requestParameters.environmentId))),
      method: "GET",
      headers: headerParameters,
      query: queryParameters
    }, initOverrides);
    return new JSONApiResponse(response, (jsonValue) => EnvironmentResponseFromJSON(jsonValue));
  }
  /**
   * Returns a single environment
   * Get an environment
   */
  async getEnvironmentById(requestParameters, initOverrides) {
    const response = await this.getEnvironmentByIdRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Get a token which can be used to subscribe to events for this environment
   * Get environment subscription credentials
   */
  async getEnvironmentEventTokenRaw(requestParameters, initOverrides) {
    if (requestParameters.environmentId === null || requestParameters.environmentId === void 0) {
      throw new RequiredError("environmentId", "Required parameter requestParameters.environmentId was null or undefined when calling getEnvironmentEventToken.");
    }
    const queryParameters = {};
    if (requestParameters.environmentId !== void 0) {
      queryParameters["environmentId"] = requestParameters.environmentId;
    }
    const headerParameters = {};
    if (this.configuration && this.configuration.accessToken) {
      const token = this.configuration.accessToken;
      const tokenString = await token("BearerAuth", []);
      if (tokenString) {
        headerParameters["Authorization"] = `Bearer ${tokenString}`;
      }
    }
    const response = await this.request({
      path: `/environments/subscription-token`,
      method: "GET",
      headers: headerParameters,
      query: queryParameters
    }, initOverrides);
    return new JSONApiResponse(response, (jsonValue) => EventTokenResponseFromJSON(jsonValue));
  }
  /**
   * Get a token which can be used to subscribe to events for this environment
   * Get environment subscription credentials
   */
  async getEnvironmentEventToken(requestParameters, initOverrides) {
    const response = await this.getEnvironmentEventTokenRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Returns environment stats
   * Get environment stats
   */
  async getEnvironmentStatsRaw(requestParameters, initOverrides) {
    if (requestParameters.environmentId === null || requestParameters.environmentId === void 0) {
      throw new RequiredError("environmentId", "Required parameter requestParameters.environmentId was null or undefined when calling getEnvironmentStats.");
    }
    const queryParameters = {};
    if (requestParameters.environmentId !== void 0) {
      queryParameters["environmentId"] = requestParameters.environmentId;
    }
    if (requestParameters.start !== void 0) {
      queryParameters["start"] = requestParameters.start.toISOString();
    }
    if (requestParameters.end !== void 0) {
      queryParameters["end"] = requestParameters.end.toISOString();
    }
    const headerParameters = {};
    if (this.configuration && this.configuration.accessToken) {
      const token = this.configuration.accessToken;
      const tokenString = await token("BearerAuth", []);
      if (tokenString) {
        headerParameters["Authorization"] = `Bearer ${tokenString}`;
      }
    }
    const response = await this.request({
      path: `/agents/stats`,
      method: "GET",
      headers: headerParameters,
      query: queryParameters
    }, initOverrides);
    return new JSONApiResponse(response, (jsonValue) => EnvironmentStatsResponseFromJSON(jsonValue));
  }
  /**
   * Returns environment stats
   * Get environment stats
   */
  async getEnvironmentStats(requestParameters, initOverrides) {
    const response = await this.getEnvironmentStatsRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Get all environments
   * List environments
   */
  async getEnvironmentsRaw(requestParameters, initOverrides) {
    const queryParameters = {};
    if (requestParameters.pageSize !== void 0) {
      queryParameters["pageSize"] = requestParameters.pageSize;
    }
    if (requestParameters.pageNumber !== void 0) {
      queryParameters["pageNumber"] = requestParameters.pageNumber;
    }
    const headerParameters = {};
    if (this.configuration && this.configuration.accessToken) {
      const token = this.configuration.accessToken;
      const tokenString = await token("BearerAuth", []);
      if (tokenString) {
        headerParameters["Authorization"] = `Bearer ${tokenString}`;
      }
    }
    const response = await this.request({
      path: `/environments`,
      method: "GET",
      headers: headerParameters,
      query: queryParameters
    }, initOverrides);
    return new JSONApiResponse(response, (jsonValue) => EnvironmentsResponseFromJSON(jsonValue));
  }
  /**
   * Get all environments
   * List environments
   */
  async getEnvironments(requestParameters = {}, initOverrides) {
    const response = await this.getEnvironmentsRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Get an event
   */
  async getEventRaw(requestParameters, initOverrides) {
    if (requestParameters.eventId === null || requestParameters.eventId === void 0) {
      throw new RequiredError("eventId", "Required parameter requestParameters.eventId was null or undefined when calling getEvent.");
    }
    const queryParameters = {};
    const headerParameters = {};
    if (this.configuration && this.configuration.accessToken) {
      const token = this.configuration.accessToken;
      const tokenString = await token("BearerAuth", []);
      if (tokenString) {
        headerParameters["Authorization"] = `Bearer ${tokenString}`;
      }
    }
    const response = await this.request({
      path: `/events/{eventId}`.replace(`{${"eventId"}}`, encodeURIComponent(String(requestParameters.eventId))),
      method: "GET",
      headers: headerParameters,
      query: queryParameters
    }, initOverrides);
    return new JSONApiResponse(response, (jsonValue) => EventResponseFromJSON(jsonValue));
  }
  /**
   * Get an event
   */
  async getEvent(requestParameters, initOverrides) {
    const response = await this.getEventRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Get an event
   */
  async getEventDeprecatedRaw(requestParameters, initOverrides) {
    if (requestParameters.environmentId === null || requestParameters.environmentId === void 0) {
      throw new RequiredError("environmentId", "Required parameter requestParameters.environmentId was null or undefined when calling getEventDeprecated.");
    }
    if (requestParameters.eventId === null || requestParameters.eventId === void 0) {
      throw new RequiredError("eventId", "Required parameter requestParameters.eventId was null or undefined when calling getEventDeprecated.");
    }
    const queryParameters = {};
    const headerParameters = {};
    if (this.configuration && this.configuration.accessToken) {
      const token = this.configuration.accessToken;
      const tokenString = await token("BearerAuth", []);
      if (tokenString) {
        headerParameters["Authorization"] = `Bearer ${tokenString}`;
      }
    }
    const response = await this.request({
      path: `/environments/{environmentId}/events/{eventId}`.replace(`{${"environmentId"}}`, encodeURIComponent(String(requestParameters.environmentId))).replace(`{${"eventId"}}`, encodeURIComponent(String(requestParameters.eventId))),
      method: "GET",
      headers: headerParameters,
      query: queryParameters
    }, initOverrides);
    return new JSONApiResponse(response, (jsonValue) => EventResponseFromJSON(jsonValue));
  }
  /**
   * Get an event
   */
  async getEventDeprecated(requestParameters, initOverrides) {
    const response = await this.getEventDeprecatedRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Get a token which can be used to subscribe to events for this space
   * Get subscription credentials
   */
  async getEventTokenRaw(requestParameters, initOverrides) {
    const queryParameters = {};
    if (requestParameters.spaceId !== void 0) {
      queryParameters["spaceId"] = requestParameters.spaceId;
    }
    if (requestParameters.scope !== void 0) {
      queryParameters["scope"] = requestParameters.scope;
    }
    const headerParameters = {};
    if (this.configuration && this.configuration.accessToken) {
      const token = this.configuration.accessToken;
      const tokenString = await token("BearerAuth", []);
      if (tokenString) {
        headerParameters["Authorization"] = `Bearer ${tokenString}`;
      }
    }
    const response = await this.request({
      path: `/subscription`,
      method: "GET",
      headers: headerParameters,
      query: queryParameters
    }, initOverrides);
    return new JSONApiResponse(response, (jsonValue) => EventTokenResponseFromJSON(jsonValue));
  }
  /**
   * Get a token which can be used to subscribe to events for this space
   * Get subscription credentials
   */
  async getEventToken(requestParameters = {}, initOverrides) {
    const response = await this.getEventTokenRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Get a token which can be used to subscribe to events for this space
   * Get subscription credentials
   */
  async getEventTokenDeprecatedRaw(requestParameters, initOverrides) {
    if (requestParameters.spaceId === null || requestParameters.spaceId === void 0) {
      throw new RequiredError("spaceId", "Required parameter requestParameters.spaceId was null or undefined when calling getEventTokenDeprecated.");
    }
    const queryParameters = {};
    const headerParameters = {};
    if (this.configuration && this.configuration.accessToken) {
      const token = this.configuration.accessToken;
      const tokenString = await token("BearerAuth", []);
      if (tokenString) {
        headerParameters["Authorization"] = `Bearer ${tokenString}`;
      }
    }
    const response = await this.request({
      path: `/spaces/{spaceId}/subscription`.replace(`{${"spaceId"}}`, encodeURIComponent(String(requestParameters.spaceId))),
      method: "GET",
      headers: headerParameters,
      query: queryParameters
    }, initOverrides);
    return new JSONApiResponse(response, (jsonValue) => EventTokenResponseFromJSON(jsonValue));
  }
  /**
   * Get a token which can be used to subscribe to events for this space
   * Get subscription credentials
   */
  async getEventTokenDeprecated(requestParameters, initOverrides) {
    const response = await this.getEventTokenDeprecatedRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Get all events
   */
  async getEventsRaw(requestParameters, initOverrides) {
    const queryParameters = {};
    if (requestParameters.environmentId !== void 0) {
      queryParameters["environmentId"] = requestParameters.environmentId;
    }
    if (requestParameters.spaceId !== void 0) {
      queryParameters["spaceId"] = requestParameters.spaceId;
    }
    if (requestParameters.domain !== void 0) {
      queryParameters["domain"] = requestParameters.domain;
    }
    if (requestParameters.topic !== void 0) {
      queryParameters["topic"] = requestParameters.topic;
    }
    if (requestParameters.since !== void 0) {
      queryParameters["since"] = requestParameters.since.toISOString();
    }
    if (requestParameters.pageSize !== void 0) {
      queryParameters["pageSize"] = requestParameters.pageSize;
    }
    if (requestParameters.pageNumber !== void 0) {
      queryParameters["pageNumber"] = requestParameters.pageNumber;
    }
    if (requestParameters.includeAcknowledged !== void 0) {
      queryParameters["includeAcknowledged"] = requestParameters.includeAcknowledged;
    }
    const headerParameters = {};
    if (this.configuration && this.configuration.accessToken) {
      const token = this.configuration.accessToken;
      const tokenString = await token("BearerAuth", []);
      if (tokenString) {
        headerParameters["Authorization"] = `Bearer ${tokenString}`;
      }
    }
    const response = await this.request({
      path: `/events`,
      method: "GET",
      headers: headerParameters,
      query: queryParameters
    }, initOverrides);
    return new JSONApiResponse(response, (jsonValue) => EventsResponseFromJSON(jsonValue));
  }
  /**
   * Get all events
   */
  async getEvents(requestParameters = {}, initOverrides) {
    const response = await this.getEventsRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * List events
   */
  async getEventsDeprecatedRaw(requestParameters, initOverrides) {
    if (requestParameters.environmentId === null || requestParameters.environmentId === void 0) {
      throw new RequiredError("environmentId", "Required parameter requestParameters.environmentId was null or undefined when calling getEventsDeprecated.");
    }
    const queryParameters = {};
    if (requestParameters.spaceId !== void 0) {
      queryParameters["spaceId"] = requestParameters.spaceId;
    }
    if (requestParameters.domain !== void 0) {
      queryParameters["domain"] = requestParameters.domain;
    }
    if (requestParameters.topic !== void 0) {
      queryParameters["topic"] = requestParameters.topic;
    }
    if (requestParameters.since !== void 0) {
      queryParameters["since"] = requestParameters.since.toISOString();
    }
    if (requestParameters.pageSize !== void 0) {
      queryParameters["pageSize"] = requestParameters.pageSize;
    }
    if (requestParameters.pageNumber !== void 0) {
      queryParameters["pageNumber"] = requestParameters.pageNumber;
    }
    if (requestParameters.includeAcknowledged !== void 0) {
      queryParameters["includeAcknowledged"] = requestParameters.includeAcknowledged;
    }
    const headerParameters = {};
    if (this.configuration && this.configuration.accessToken) {
      const token = this.configuration.accessToken;
      const tokenString = await token("BearerAuth", []);
      if (tokenString) {
        headerParameters["Authorization"] = `Bearer ${tokenString}`;
      }
    }
    const response = await this.request({
      path: `/environments/{environmentId}/events`.replace(`{${"environmentId"}}`, encodeURIComponent(String(requestParameters.environmentId))),
      method: "GET",
      headers: headerParameters,
      query: queryParameters
    }, initOverrides);
    return new JSONApiResponse(response, (jsonValue) => EventsResponseFromJSON(jsonValue));
  }
  /**
   * List events
   */
  async getEventsDeprecated(requestParameters, initOverrides) {
    const response = await this.getEventsDeprecatedRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   */
  async getFieldWeightsRaw(requestParameters, initOverrides) {
    if (requestParameters.getFieldWeightsRequest === null || requestParameters.getFieldWeightsRequest === void 0) {
      throw new RequiredError("getFieldWeightsRequest", "Required parameter requestParameters.getFieldWeightsRequest was null or undefined when calling getFieldWeights.");
    }
    const queryParameters = {};
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    const response = await this.request({
      path: `/mapping/field-weights`,
      method: "POST",
      headers: headerParameters,
      query: queryParameters,
      body: GetFieldWeightsRequestToJSON(requestParameters.getFieldWeightsRequest)
    }, initOverrides);
    return new JSONApiResponse(response, (jsonValue) => SourceDestinationWeightsResponseFromJSON(jsonValue));
  }
  /**
   */
  async getFieldWeights(requestParameters, initOverrides) {
    const response = await this.getFieldWeightsRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Get a file
   */
  async getFileRaw(requestParameters, initOverrides) {
    if (requestParameters.fileId === null || requestParameters.fileId === void 0) {
      throw new RequiredError("fileId", "Required parameter requestParameters.fileId was null or undefined when calling getFile.");
    }
    const queryParameters = {};
    const headerParameters = {};
    if (this.configuration && this.configuration.accessToken) {
      const token = this.configuration.accessToken;
      const tokenString = await token("BearerAuth", []);
      if (tokenString) {
        headerParameters["Authorization"] = `Bearer ${tokenString}`;
      }
    }
    const response = await this.request({
      path: `/files/{fileId}`.replace(`{${"fileId"}}`, encodeURIComponent(String(requestParameters.fileId))),
      method: "GET",
      headers: headerParameters,
      query: queryParameters
    }, initOverrides);
    return new JSONApiResponse(response, (jsonValue) => FileResponseFromJSON(jsonValue));
  }
  /**
   * Get a file
   */
  async getFile(requestParameters, initOverrides) {
    const response = await this.getFileRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * List files
   */
  async getFilesRaw(requestParameters, initOverrides) {
    const queryParameters = {};
    if (requestParameters.spaceId !== void 0) {
      queryParameters["spaceId"] = requestParameters.spaceId;
    }
    if (requestParameters.pageSize !== void 0) {
      queryParameters["pageSize"] = requestParameters.pageSize;
    }
    if (requestParameters.pageNumber !== void 0) {
      queryParameters["pageNumber"] = requestParameters.pageNumber;
    }
    if (requestParameters.mode !== void 0) {
      queryParameters["mode"] = requestParameters.mode;
    }
    const headerParameters = {};
    if (this.configuration && this.configuration.accessToken) {
      const token = this.configuration.accessToken;
      const tokenString = await token("BearerAuth", []);
      if (tokenString) {
        headerParameters["Authorization"] = `Bearer ${tokenString}`;
      }
    }
    const response = await this.request({
      path: `/files`,
      method: "GET",
      headers: headerParameters,
      query: queryParameters
    }, initOverrides);
    return new JSONApiResponse(response, (jsonValue) => FilesResponseFromJSON(jsonValue));
  }
  /**
   * List files
   */
  async getFiles(requestParameters = {}, initOverrides) {
    const response = await this.getFilesRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Returns a single guest token
   * Get a guest token
   */
  async getGuestTokenRaw(requestParameters, initOverrides) {
    if (requestParameters.guestId === null || requestParameters.guestId === void 0) {
      throw new RequiredError("guestId", "Required parameter requestParameters.guestId was null or undefined when calling getGuestToken.");
    }
    const queryParameters = {};
    if (requestParameters.spaceId !== void 0) {
      queryParameters["spaceId"] = requestParameters.spaceId;
    }
    const headerParameters = {};
    if (this.configuration && this.configuration.accessToken) {
      const token = this.configuration.accessToken;
      const tokenString = await token("BearerAuth", []);
      if (tokenString) {
        headerParameters["Authorization"] = `Bearer ${tokenString}`;
      }
    }
    const response = await this.request({
      path: `/guests/{guestId}/token`.replace(`{${"guestId"}}`, encodeURIComponent(String(requestParameters.guestId))),
      method: "GET",
      headers: headerParameters,
      query: queryParameters
    }, initOverrides);
    return new JSONApiResponse(response, (jsonValue) => ExchangeTokenResponseFromJSON(jsonValue));
  }
  /**
   * Returns a single guest token
   * Get a guest token
   */
  async getGuestToken(requestParameters, initOverrides) {
    const response = await this.getGuestTokenRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Returns all guests
   * List guests
   */
  async getGuestsRaw(requestParameters, initOverrides) {
    if (requestParameters.spaceId === null || requestParameters.spaceId === void 0) {
      throw new RequiredError("spaceId", "Required parameter requestParameters.spaceId was null or undefined when calling getGuests.");
    }
    const queryParameters = {};
    if (requestParameters.spaceId !== void 0) {
      queryParameters["spaceId"] = requestParameters.spaceId;
    }
    if (requestParameters.email !== void 0) {
      queryParameters["email"] = requestParameters.email;
    }
    const headerParameters = {};
    if (this.configuration && this.configuration.accessToken) {
      const token = this.configuration.accessToken;
      const tokenString = await token("BearerAuth", []);
      if (tokenString) {
        headerParameters["Authorization"] = `Bearer ${tokenString}`;
      }
    }
    const response = await this.request({
      path: `/guests`,
      method: "GET",
      headers: headerParameters,
      query: queryParameters
    }, initOverrides);
    return new JSONApiResponse(response, (jsonValue) => GuestsResponseFromJSON(jsonValue));
  }
  /**
   * Returns all guests
   * List guests
   */
  async getGuests(requestParameters, initOverrides) {
    const response = await this.getGuestsRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Health check
   */
  async getHealthCheckRaw(initOverrides) {
    const queryParameters = {};
    const headerParameters = {};
    const response = await this.request({
      path: `/health`,
      method: "GET",
      headers: headerParameters,
      query: queryParameters
    }, initOverrides);
    return new JSONApiResponse(response, (jsonValue) => HealthResponseFromJSON(jsonValue));
  }
  /**
   * Health check
   */
  async getHealthCheck(initOverrides) {
    const response = await this.getHealthCheckRaw(initOverrides);
    return await response.value();
  }
  /**
   * Get a job
   */
  async getJobRaw(requestParameters, initOverrides) {
    if (requestParameters.jobId === null || requestParameters.jobId === void 0) {
      throw new RequiredError("jobId", "Required parameter requestParameters.jobId was null or undefined when calling getJob.");
    }
    const queryParameters = {};
    const headerParameters = {};
    if (this.configuration && this.configuration.accessToken) {
      const token = this.configuration.accessToken;
      const tokenString = await token("BearerAuth", []);
      if (tokenString) {
        headerParameters["Authorization"] = `Bearer ${tokenString}`;
      }
    }
    const response = await this.request({
      path: `/jobs/{jobId}`.replace(`{${"jobId"}}`, encodeURIComponent(String(requestParameters.jobId))),
      method: "GET",
      headers: headerParameters,
      query: queryParameters
    }, initOverrides);
    return new JSONApiResponse(response, (jsonValue) => JobResponseFromJSON(jsonValue));
  }
  /**
   * Get a job
   */
  async getJob(requestParameters, initOverrides) {
    const response = await this.getJobRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Returns a single job\'s execution plan
   * Get a job\'s execution plan
   */
  async getJobExecutionPlanRaw(requestParameters, initOverrides) {
    if (requestParameters.jobId === null || requestParameters.jobId === void 0) {
      throw new RequiredError("jobId", "Required parameter requestParameters.jobId was null or undefined when calling getJobExecutionPlan.");
    }
    const queryParameters = {};
    const headerParameters = {};
    if (this.configuration && this.configuration.accessToken) {
      const token = this.configuration.accessToken;
      const tokenString = await token("BearerAuth", []);
      if (tokenString) {
        headerParameters["Authorization"] = `Bearer ${tokenString}`;
      }
    }
    const response = await this.request({
      path: `/jobs/{jobId}/plan`.replace(`{${"jobId"}}`, encodeURIComponent(String(requestParameters.jobId))),
      method: "GET",
      headers: headerParameters,
      query: queryParameters
    }, initOverrides);
    return new JSONApiResponse(response, (jsonValue) => JobPlanResponseFromJSON(jsonValue));
  }
  /**
   * Returns a single job\'s execution plan
   * Get a job\'s execution plan
   */
  async getJobExecutionPlan(requestParameters, initOverrides) {
    const response = await this.getJobExecutionPlanRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Returns job performance report as a CSV
   * Get job performance report
   */
  async getJobPerformanceReportRaw(initOverrides) {
    const queryParameters = {};
    const headerParameters = {};
    if (this.configuration && this.configuration.accessToken) {
      const token = this.configuration.accessToken;
      const tokenString = await token("BearerAuth", []);
      if (tokenString) {
        headerParameters["Authorization"] = `Bearer ${tokenString}`;
      }
    }
    const response = await this.request({
      path: `/jobs/report`,
      method: "GET",
      headers: headerParameters,
      query: queryParameters
    }, initOverrides);
    return new TextApiResponse(response);
  }
  /**
   * Returns job performance report as a CSV
   * Get job performance report
   */
  async getJobPerformanceReport(initOverrides) {
    const response = await this.getJobPerformanceReportRaw(initOverrides);
    return await response.value();
  }
  /**
   * List jobs
   */
  async getJobsRaw(requestParameters, initOverrides) {
    const queryParameters = {};
    if (requestParameters.environmentId !== void 0) {
      queryParameters["environmentId"] = requestParameters.environmentId;
    }
    if (requestParameters.spaceId !== void 0) {
      queryParameters["spaceId"] = requestParameters.spaceId;
    }
    if (requestParameters.workbookId !== void 0) {
      queryParameters["workbookId"] = requestParameters.workbookId;
    }
    if (requestParameters.fileId !== void 0) {
      queryParameters["fileId"] = requestParameters.fileId;
    }
    if (requestParameters.parentId !== void 0) {
      queryParameters["parentId"] = requestParameters.parentId;
    }
    if (requestParameters.pageSize !== void 0) {
      queryParameters["pageSize"] = requestParameters.pageSize;
    }
    if (requestParameters.pageNumber !== void 0) {
      queryParameters["pageNumber"] = requestParameters.pageNumber;
    }
    if (requestParameters.sortDirection !== void 0) {
      queryParameters["sortDirection"] = requestParameters.sortDirection;
    }
    if (requestParameters.excludeChildJobs !== void 0) {
      queryParameters["excludeChildJobs"] = requestParameters.excludeChildJobs;
    }
    const headerParameters = {};
    if (this.configuration && this.configuration.accessToken) {
      const token = this.configuration.accessToken;
      const tokenString = await token("BearerAuth", []);
      if (tokenString) {
        headerParameters["Authorization"] = `Bearer ${tokenString}`;
      }
    }
    const response = await this.request({
      path: `/jobs`,
      method: "GET",
      headers: headerParameters,
      query: queryParameters
    }, initOverrides);
    return new JSONApiResponse(response, (jsonValue) => JobsResponseFromJSON(jsonValue));
  }
  /**
   * List jobs
   */
  async getJobs(requestParameters = {}, initOverrides) {
    const response = await this.getJobsRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Returns job stats
   * Get job stats
   */
  async getJobsStatsRaw(requestParameters, initOverrides) {
    const queryParameters = {};
    if (requestParameters.start !== void 0) {
      queryParameters["start"] = requestParameters.start.toISOString();
    }
    if (requestParameters.end !== void 0) {
      queryParameters["end"] = requestParameters.end.toISOString();
    }
    const headerParameters = {};
    if (this.configuration && this.configuration.accessToken) {
      const token = this.configuration.accessToken;
      const tokenString = await token("BearerAuth", []);
      if (tokenString) {
        headerParameters["Authorization"] = `Bearer ${tokenString}`;
      }
    }
    const response = await this.request({
      path: `/jobs/stats`,
      method: "GET",
      headers: headerParameters,
      query: queryParameters
    }, initOverrides);
    return new JSONApiResponse(response, (jsonValue) => JobStatsResponseFromJSON(jsonValue));
  }
  /**
   * Returns job stats
   * Get job stats
   */
  async getJobsStats(requestParameters = {}, initOverrides) {
    const response = await this.getJobsStatsRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Returns the limits for the current user
   */
  async getLimitsRaw(initOverrides) {
    const queryParameters = {};
    const headerParameters = {};
    if (this.configuration && this.configuration.accessToken) {
      const token = this.configuration.accessToken;
      const tokenString = await token("BearerAuth", []);
      if (tokenString) {
        headerParameters["Authorization"] = `Bearer ${tokenString}`;
      }
    }
    const response = await this.request({
      path: `/billing/limits`,
      method: "GET",
      headers: headerParameters,
      query: queryParameters
    }, initOverrides);
    return new JSONApiResponse(response, (jsonValue) => BillingLimitsResponseFromJSON(jsonValue));
  }
  /**
   * Returns the limits for the current user
   */
  async getLimits(initOverrides) {
    const response = await this.getLimitsRaw(initOverrides);
    return await response.value();
  }
  /**
   * Returns the plan for the current user
   */
  async getPlanRaw(initOverrides) {
    const queryParameters = {};
    const headerParameters = {};
    if (this.configuration && this.configuration.accessToken) {
      const token = this.configuration.accessToken;
      const tokenString = await token("BearerAuth", []);
      if (tokenString) {
        headerParameters["Authorization"] = `Bearer ${tokenString}`;
      }
    }
    const response = await this.request({
      path: `/billing/plan`,
      method: "GET",
      headers: headerParameters,
      query: queryParameters
    }, initOverrides);
    return new JSONApiResponse(response, (jsonValue) => BillingPlanWithUsageResponseFromJSON(jsonValue));
  }
  /**
   * Returns the plan for the current user
   */
  async getPlan(initOverrides) {
    const response = await this.getPlanRaw(initOverrides);
    return await response.value();
  }
  /**
   * List the available products
   */
  async getProductsRaw(requestParameters, initOverrides) {
    const queryParameters = {};
    if (requestParameters.filterMetadata !== void 0) {
      queryParameters["filterMetadata"] = requestParameters.filterMetadata;
    }
    const headerParameters = {};
    const response = await this.request({
      path: `/billing/products`,
      method: "GET",
      headers: headerParameters,
      query: queryParameters
    }, initOverrides);
    return new JSONApiResponse(response, (jsonValue) => ProductsResponseFromJSON(jsonValue));
  }
  /**
   * List the available products
   */
  async getProducts(requestParameters = {}, initOverrides) {
    const response = await this.getProductsRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   */
  async getProgramRaw(requestParameters, initOverrides) {
    if (requestParameters.programId === null || requestParameters.programId === void 0) {
      throw new RequiredError("programId", "Required parameter requestParameters.programId was null or undefined when calling getProgram.");
    }
    const queryParameters = {};
    const headerParameters = {};
    if (this.configuration && this.configuration.accessToken) {
      const token = this.configuration.accessToken;
      const tokenString = await token("BearerAuth", []);
      if (tokenString) {
        headerParameters["Authorization"] = `Bearer ${tokenString}`;
      }
    }
    const response = await this.request({
      path: `/mapping/{programId}`.replace(`{${"programId"}}`, encodeURIComponent(String(requestParameters.programId))),
      method: "GET",
      headers: headerParameters,
      query: queryParameters
    }, initOverrides);
    return new JSONApiResponse(response, (jsonValue) => ProgramResponseFromJSON(jsonValue));
  }
  /**
   */
  async getProgram(requestParameters, initOverrides) {
    const response = await this.getProgramRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Returns a prompt
   * Returns a prompt
   */
  async getPromptRaw(requestParameters, initOverrides) {
    if (requestParameters.promptId === null || requestParameters.promptId === void 0) {
      throw new RequiredError("promptId", "Required parameter requestParameters.promptId was null or undefined when calling getPrompt.");
    }
    const queryParameters = {};
    const headerParameters = {};
    if (this.configuration && this.configuration.accessToken) {
      const token = this.configuration.accessToken;
      const tokenString = await token("BearerAuth", []);
      if (tokenString) {
        headerParameters["Authorization"] = `Bearer ${tokenString}`;
      }
    }
    const response = await this.request({
      path: `/prompts/{promptId}`.replace(`{${"promptId"}}`, encodeURIComponent(String(requestParameters.promptId))),
      method: "GET",
      headers: headerParameters,
      query: queryParameters
    }, initOverrides);
    return new JSONApiResponse(response, (jsonValue) => PromptResponseFromJSON(jsonValue));
  }
  /**
   * Returns a prompt
   * Returns a prompt
   */
  async getPrompt(requestParameters, initOverrides) {
    const response = await this.getPromptRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Returns prompts created by user
   * Returns prompts created by user
   */
  async getPromptsRaw(requestParameters, initOverrides) {
    const queryParameters = {};
    if (requestParameters.pageSize !== void 0) {
      queryParameters["pageSize"] = requestParameters.pageSize;
    }
    if (requestParameters.pageNumber !== void 0) {
      queryParameters["pageNumber"] = requestParameters.pageNumber;
    }
    const headerParameters = {};
    if (this.configuration && this.configuration.accessToken) {
      const token = this.configuration.accessToken;
      const tokenString = await token("BearerAuth", []);
      if (tokenString) {
        headerParameters["Authorization"] = `Bearer ${tokenString}`;
      }
    }
    const response = await this.request({
      path: `/prompts`,
      method: "GET",
      headers: headerParameters,
      query: queryParameters
    }, initOverrides);
    return new JSONApiResponse(response, (jsonValue) => PromptsResponseFromJSON(jsonValue));
  }
  /**
   * Returns prompts created by user
   * Returns prompts created by user
   */
  async getPrompts(requestParameters = {}, initOverrides) {
    const response = await this.getPromptsRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Returns records from a sheet in a workbook
   * Get records
   */
  async getRecordsRaw(requestParameters, initOverrides) {
    if (requestParameters.sheetId === null || requestParameters.sheetId === void 0) {
      throw new RequiredError("sheetId", "Required parameter requestParameters.sheetId was null or undefined when calling getRecords.");
    }
    const queryParameters = {};
    if (requestParameters.versionId !== void 0) {
      queryParameters["versionId"] = requestParameters.versionId;
    }
    if (requestParameters.commitId !== void 0) {
      queryParameters["commitId"] = requestParameters.commitId;
    }
    if (requestParameters.sinceVersionId !== void 0) {
      queryParameters["sinceVersionId"] = requestParameters.sinceVersionId;
    }
    if (requestParameters.sinceCommitId !== void 0) {
      queryParameters["sinceCommitId"] = requestParameters.sinceCommitId;
    }
    if (requestParameters.sortField !== void 0) {
      queryParameters["sortField"] = requestParameters.sortField;
    }
    if (requestParameters.sortDirection !== void 0) {
      queryParameters["sortDirection"] = requestParameters.sortDirection;
    }
    if (requestParameters.filter !== void 0) {
      queryParameters["filter"] = requestParameters.filter;
    }
    if (requestParameters.filterField !== void 0) {
      queryParameters["filterField"] = requestParameters.filterField;
    }
    if (requestParameters.searchValue !== void 0) {
      queryParameters["searchValue"] = requestParameters.searchValue;
    }
    if (requestParameters.searchField !== void 0) {
      queryParameters["searchField"] = requestParameters.searchField;
    }
    if (requestParameters.ids) {
      queryParameters["ids"] = requestParameters.ids;
    }
    if (requestParameters.pageSize !== void 0) {
      queryParameters["pageSize"] = requestParameters.pageSize;
    }
    if (requestParameters.pageNumber !== void 0) {
      queryParameters["pageNumber"] = requestParameters.pageNumber;
    }
    if (requestParameters.includeLinks !== void 0) {
      queryParameters["includeLinks"] = requestParameters.includeLinks;
    }
    if (requestParameters.includeMessages !== void 0) {
      queryParameters["includeMessages"] = requestParameters.includeMessages;
    }
    if (requestParameters._for !== void 0) {
      queryParameters["for"] = requestParameters._for;
    }
    if (requestParameters.q !== void 0) {
      queryParameters["q"] = requestParameters.q;
    }
    if (requestParameters.includeLength !== void 0) {
      queryParameters["includeLength"] = requestParameters.includeLength;
    }
    if (requestParameters.includeCounts !== void 0) {
      queryParameters["includeCounts"] = requestParameters.includeCounts;
    }
    const headerParameters = {};
    if (this.configuration && this.configuration.accessToken) {
      const token = this.configuration.accessToken;
      const tokenString = await token("BearerAuth", []);
      if (tokenString) {
        headerParameters["Authorization"] = `Bearer ${tokenString}`;
      }
    }
    const response = await this.request({
      path: `/sheets/{sheetId}/records`.replace(`{${"sheetId"}}`, encodeURIComponent(String(requestParameters.sheetId))),
      method: "GET",
      headers: headerParameters,
      query: queryParameters
    }, initOverrides);
    return new JSONApiResponse(response, (jsonValue) => GetRecordsResponseFromJSON(jsonValue));
  }
  /**
   * Returns records from a sheet in a workbook
   * Get records
   */
  async getRecords(requestParameters, initOverrides) {
    const response = await this.getRecordsRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Returns records from a sheet in a workbook as a csv file
   * Download records as a CSV file
   */
  async getRecordsCsvRaw(requestParameters, initOverrides) {
    if (requestParameters.sheetId === null || requestParameters.sheetId === void 0) {
      throw new RequiredError("sheetId", "Required parameter requestParameters.sheetId was null or undefined when calling getRecordsCsv.");
    }
    const queryParameters = {};
    if (requestParameters.versionId !== void 0) {
      queryParameters["versionId"] = requestParameters.versionId;
    }
    if (requestParameters.commitId !== void 0) {
      queryParameters["commitId"] = requestParameters.commitId;
    }
    if (requestParameters.sinceVersionId !== void 0) {
      queryParameters["sinceVersionId"] = requestParameters.sinceVersionId;
    }
    if (requestParameters.sinceCommitId !== void 0) {
      queryParameters["sinceCommitId"] = requestParameters.sinceCommitId;
    }
    if (requestParameters.sortField !== void 0) {
      queryParameters["sortField"] = requestParameters.sortField;
    }
    if (requestParameters.sortDirection !== void 0) {
      queryParameters["sortDirection"] = requestParameters.sortDirection;
    }
    if (requestParameters.filter !== void 0) {
      queryParameters["filter"] = requestParameters.filter;
    }
    if (requestParameters.filterField !== void 0) {
      queryParameters["filterField"] = requestParameters.filterField;
    }
    if (requestParameters.searchValue !== void 0) {
      queryParameters["searchValue"] = requestParameters.searchValue;
    }
    if (requestParameters.searchField !== void 0) {
      queryParameters["searchField"] = requestParameters.searchField;
    }
    if (requestParameters.ids) {
      queryParameters["ids"] = requestParameters.ids;
    }
    const headerParameters = {};
    if (this.configuration && this.configuration.accessToken) {
      const token = this.configuration.accessToken;
      const tokenString = await token("BearerAuth", []);
      if (tokenString) {
        headerParameters["Authorization"] = `Bearer ${tokenString}`;
      }
    }
    const response = await this.request({
      path: `/sheets/{sheetId}/download`.replace(`{${"sheetId"}}`, encodeURIComponent(String(requestParameters.sheetId))),
      method: "GET",
      headers: headerParameters,
      query: queryParameters
    }, initOverrides);
    return new BlobApiResponse(response);
  }
  /**
   * Returns records from a sheet in a workbook as a csv file
   * Download records as a CSV file
   */
  async getRecordsCsv(requestParameters, initOverrides) {
    const response = await this.getRecordsCsvRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Returns records from a sheet in a workbook
   * Get records
   */
  async getRecordsDeprecatedRaw(requestParameters, initOverrides) {
    if (requestParameters.workbookId === null || requestParameters.workbookId === void 0) {
      throw new RequiredError("workbookId", "Required parameter requestParameters.workbookId was null or undefined when calling getRecordsDeprecated.");
    }
    if (requestParameters.sheetId === null || requestParameters.sheetId === void 0) {
      throw new RequiredError("sheetId", "Required parameter requestParameters.sheetId was null or undefined when calling getRecordsDeprecated.");
    }
    const queryParameters = {};
    if (requestParameters.versionId !== void 0) {
      queryParameters["versionId"] = requestParameters.versionId;
    }
    if (requestParameters.commitId !== void 0) {
      queryParameters["commitId"] = requestParameters.commitId;
    }
    if (requestParameters.untilVersionId !== void 0) {
      queryParameters["untilVersionId"] = requestParameters.untilVersionId;
    }
    if (requestParameters.untilCommitId !== void 0) {
      queryParameters["untilCommitId"] = requestParameters.untilCommitId;
    }
    if (requestParameters.sinceVersionId !== void 0) {
      queryParameters["sinceVersionId"] = requestParameters.sinceVersionId;
    }
    if (requestParameters.sinceCommitId !== void 0) {
      queryParameters["sinceCommitId"] = requestParameters.sinceCommitId;
    }
    if (requestParameters.sortField !== void 0) {
      queryParameters["sortField"] = requestParameters.sortField;
    }
    if (requestParameters.sortDirection !== void 0) {
      queryParameters["sortDirection"] = requestParameters.sortDirection;
    }
    if (requestParameters.filter !== void 0) {
      queryParameters["filter"] = requestParameters.filter;
    }
    if (requestParameters.filterField !== void 0) {
      queryParameters["filterField"] = requestParameters.filterField;
    }
    if (requestParameters.searchValue !== void 0) {
      queryParameters["searchValue"] = requestParameters.searchValue;
    }
    if (requestParameters.searchField !== void 0) {
      queryParameters["searchField"] = requestParameters.searchField;
    }
    if (requestParameters.pageSize !== void 0) {
      queryParameters["pageSize"] = requestParameters.pageSize;
    }
    if (requestParameters.pageNumber !== void 0) {
      queryParameters["pageNumber"] = requestParameters.pageNumber;
    }
    if (requestParameters.includeCounts !== void 0) {
      queryParameters["includeCounts"] = requestParameters.includeCounts;
    }
    if (requestParameters.includeLinks !== void 0) {
      queryParameters["includeLinks"] = requestParameters.includeLinks;
    }
    if (requestParameters.includeMessages !== void 0) {
      queryParameters["includeMessages"] = requestParameters.includeMessages;
    }
    if (requestParameters.fields) {
      queryParameters["fields"] = requestParameters.fields;
    }
    const headerParameters = {};
    if (this.configuration && this.configuration.accessToken) {
      const token = this.configuration.accessToken;
      const tokenString = await token("BearerAuth", []);
      if (tokenString) {
        headerParameters["Authorization"] = `Bearer ${tokenString}`;
      }
    }
    const response = await this.request({
      path: `/workbooks/{workbookId}/sheets/{sheetId}/records`.replace(`{${"workbookId"}}`, encodeURIComponent(String(requestParameters.workbookId))).replace(`{${"sheetId"}}`, encodeURIComponent(String(requestParameters.sheetId))),
      method: "GET",
      headers: headerParameters,
      query: queryParameters
    }, initOverrides);
    return new JSONApiResponse(response, (jsonValue) => RecordsResponseFromJSON(jsonValue));
  }
  /**
   * Returns records from a sheet in a workbook
   * Get records
   */
  async getRecordsDeprecated(requestParameters, initOverrides) {
    const response = await this.getRecordsDeprecatedRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   */
  async getRulesRaw(requestParameters, initOverrides) {
    if (requestParameters.programId === null || requestParameters.programId === void 0) {
      throw new RequiredError("programId", "Required parameter requestParameters.programId was null or undefined when calling getRules.");
    }
    const queryParameters = {};
    const headerParameters = {};
    if (this.configuration && this.configuration.accessToken) {
      const token = this.configuration.accessToken;
      const tokenString = await token("BearerAuth", []);
      if (tokenString) {
        headerParameters["Authorization"] = `Bearer ${tokenString}`;
      }
    }
    const response = await this.request({
      path: `/mapping/{programId}/rules`.replace(`{${"programId"}}`, encodeURIComponent(String(requestParameters.programId))),
      method: "GET",
      headers: headerParameters,
      query: queryParameters
    }, initOverrides);
    return new JSONApiResponse(response, (jsonValue) => RulesResponseFromJSON(jsonValue));
  }
  /**
   */
  async getRules(requestParameters, initOverrides) {
    const response = await this.getRulesRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Fetch all secrets for a given environmentId and optionally apply space overrides
   * List all Secrets for an Environment or Environment + Space
   */
  async getSecretsRaw(requestParameters, initOverrides) {
    const queryParameters = {};
    if (requestParameters.environmentId !== void 0) {
      queryParameters["environmentId"] = requestParameters.environmentId;
    }
    if (requestParameters.spaceId !== void 0) {
      queryParameters["spaceId"] = requestParameters.spaceId;
    }
    const headerParameters = {};
    if (this.configuration && this.configuration.accessToken) {
      const token = this.configuration.accessToken;
      const tokenString = await token("BearerAuth", []);
      if (tokenString) {
        headerParameters["Authorization"] = `Bearer ${tokenString}`;
      }
    }
    const response = await this.request({
      path: `/secrets`,
      method: "GET",
      headers: headerParameters,
      query: queryParameters
    }, initOverrides);
    return new JSONApiResponse(response, (jsonValue) => SecretsResponseFromJSON(jsonValue));
  }
  /**
   * Fetch all secrets for a given environmentId and optionally apply space overrides
   * List all Secrets for an Environment or Environment + Space
   */
  async getSecrets(requestParameters = {}, initOverrides) {
    const response = await this.getSecretsRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Returns a specific sheet from a workbook
   * Get a sheet
   */
  async getSheetRaw(requestParameters, initOverrides) {
    if (requestParameters.sheetId === null || requestParameters.sheetId === void 0) {
      throw new RequiredError("sheetId", "Required parameter requestParameters.sheetId was null or undefined when calling getSheet.");
    }
    const queryParameters = {};
    const headerParameters = {};
    if (this.configuration && this.configuration.accessToken) {
      const token = this.configuration.accessToken;
      const tokenString = await token("BearerAuth", []);
      if (tokenString) {
        headerParameters["Authorization"] = `Bearer ${tokenString}`;
      }
    }
    const response = await this.request({
      path: `/sheets/{sheetId}`.replace(`{${"sheetId"}}`, encodeURIComponent(String(requestParameters.sheetId))),
      method: "GET",
      headers: headerParameters,
      query: queryParameters
    }, initOverrides);
    return new JSONApiResponse(response, (jsonValue) => SheetResponseFromJSON(jsonValue));
  }
  /**
   * Returns a specific sheet from a workbook
   * Get a sheet
   */
  async getSheet(requestParameters, initOverrides) {
    const response = await this.getSheetRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Returns the commit versions for a sheet
   */
  async getSheetCommitsRaw(requestParameters, initOverrides) {
    if (requestParameters.sheetId === null || requestParameters.sheetId === void 0) {
      throw new RequiredError("sheetId", "Required parameter requestParameters.sheetId was null or undefined when calling getSheetCommits.");
    }
    const queryParameters = {};
    if (requestParameters.completed !== void 0) {
      queryParameters["completed"] = requestParameters.completed;
    }
    const headerParameters = {};
    if (this.configuration && this.configuration.accessToken) {
      const token = this.configuration.accessToken;
      const tokenString = await token("BearerAuth", []);
      if (tokenString) {
        headerParameters["Authorization"] = `Bearer ${tokenString}`;
      }
    }
    const response = await this.request({
      path: `/sheets/{sheetId}/commits`.replace(`{${"sheetId"}}`, encodeURIComponent(String(requestParameters.sheetId))),
      method: "GET",
      headers: headerParameters,
      query: queryParameters
    }, initOverrides);
    return new JSONApiResponse(response, (jsonValue) => GetSheetCommitsResponseFromJSON(jsonValue));
  }
  /**
   * Returns the commit versions for a sheet
   */
  async getSheetCommits(requestParameters, initOverrides) {
    const response = await this.getSheetCommitsRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Returns sheets from a workbook
   * Get all sheets
   */
  async getSheetsRaw(requestParameters, initOverrides) {
    if (requestParameters.workbookId === null || requestParameters.workbookId === void 0) {
      throw new RequiredError("workbookId", "Required parameter requestParameters.workbookId was null or undefined when calling getSheets.");
    }
    const queryParameters = {};
    if (requestParameters.workbookId !== void 0) {
      queryParameters["workbookId"] = requestParameters.workbookId;
    }
    const headerParameters = {};
    if (this.configuration && this.configuration.accessToken) {
      const token = this.configuration.accessToken;
      const tokenString = await token("BearerAuth", []);
      if (tokenString) {
        headerParameters["Authorization"] = `Bearer ${tokenString}`;
      }
    }
    const response = await this.request({
      path: `/sheets`,
      method: "GET",
      headers: headerParameters,
      query: queryParameters
    }, initOverrides);
    return new JSONApiResponse(response, (jsonValue) => SheetsResponseFromJSON(jsonValue));
  }
  /**
   * Returns sheets from a workbook
   * Get all sheets
   */
  async getSheets(requestParameters, initOverrides) {
    const response = await this.getSheetsRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Returns sheets from a workbook
   */
  async getSheetsDeprecatedRaw(requestParameters, initOverrides) {
    if (requestParameters.workbookId === null || requestParameters.workbookId === void 0) {
      throw new RequiredError("workbookId", "Required parameter requestParameters.workbookId was null or undefined when calling getSheetsDeprecated.");
    }
    const queryParameters = {};
    const headerParameters = {};
    if (this.configuration && this.configuration.accessToken) {
      const token = this.configuration.accessToken;
      const tokenString = await token("BearerAuth", []);
      if (tokenString) {
        headerParameters["Authorization"] = `Bearer ${tokenString}`;
      }
    }
    const response = await this.request({
      path: `/workbooks/{workbookId}/sheets`.replace(`{${"workbookId"}}`, encodeURIComponent(String(requestParameters.workbookId))),
      method: "GET",
      headers: headerParameters,
      query: queryParameters
    }, initOverrides);
    return new JSONApiResponse(response, (jsonValue) => SheetsResponseFromJSON(jsonValue));
  }
  /**
   * Returns sheets from a workbook
   */
  async getSheetsDeprecated(requestParameters, initOverrides) {
    const response = await this.getSheetsDeprecatedRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Get a snapshot of a sheet
   * Get snapshot
   */
  async getSnapshotRaw(requestParameters, initOverrides) {
    if (requestParameters.snapshotId === null || requestParameters.snapshotId === void 0) {
      throw new RequiredError("snapshotId", "Required parameter requestParameters.snapshotId was null or undefined when calling getSnapshot.");
    }
    const queryParameters = {};
    if (requestParameters.includeSummary !== void 0) {
      queryParameters["includeSummary"] = requestParameters.includeSummary;
    }
    const headerParameters = {};
    if (this.configuration && this.configuration.accessToken) {
      const token = this.configuration.accessToken;
      const tokenString = await token("BearerAuth", []);
      if (tokenString) {
        headerParameters["Authorization"] = `Bearer ${tokenString}`;
      }
    }
    const response = await this.request({
      path: `/snapshots/{snapshotId}`.replace(`{${"snapshotId"}}`, encodeURIComponent(String(requestParameters.snapshotId))),
      method: "GET",
      headers: headerParameters,
      query: queryParameters
    }, initOverrides);
    return new JSONApiResponse(response, (jsonValue) => SnapshotResponseFromJSON(jsonValue));
  }
  /**
   * Get a snapshot of a sheet
   * Get snapshot
   */
  async getSnapshot(requestParameters, initOverrides) {
    const response = await this.getSnapshotRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Get records from a time period
   * Get records
   */
  async getSnapshotRecordsRaw(requestParameters, initOverrides) {
    if (requestParameters.snapshotId === null || requestParameters.snapshotId === void 0) {
      throw new RequiredError("snapshotId", "Required parameter requestParameters.snapshotId was null or undefined when calling getSnapshotRecords.");
    }
    const queryParameters = {};
    if (requestParameters.pageSize !== void 0) {
      queryParameters["pageSize"] = requestParameters.pageSize;
    }
    if (requestParameters.pageNumber !== void 0) {
      queryParameters["pageNumber"] = requestParameters.pageNumber;
    }
    if (requestParameters.changeType !== void 0) {
      queryParameters["changeType"] = requestParameters.changeType;
    }
    const headerParameters = {};
    if (this.configuration && this.configuration.accessToken) {
      const token = this.configuration.accessToken;
      const tokenString = await token("BearerAuth", []);
      if (tokenString) {
        headerParameters["Authorization"] = `Bearer ${tokenString}`;
      }
    }
    const response = await this.request({
      path: `/snapshots/{snapshotId}/records`.replace(`{${"snapshotId"}}`, encodeURIComponent(String(requestParameters.snapshotId))),
      method: "GET",
      headers: headerParameters,
      query: queryParameters
    }, initOverrides);
    return new JSONApiResponse(response, (jsonValue) => DiffRecordsResponseFromJSON(jsonValue));
  }
  /**
   * Get records from a time period
   * Get records
   */
  async getSnapshotRecords(requestParameters, initOverrides) {
    const response = await this.getSnapshotRecordsRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * List all snapshots of a sheet
   * List snapshots
   */
  async getSnapshotsRaw(requestParameters, initOverrides) {
    if (requestParameters.sheetId === null || requestParameters.sheetId === void 0) {
      throw new RequiredError("sheetId", "Required parameter requestParameters.sheetId was null or undefined when calling getSnapshots.");
    }
    const queryParameters = {};
    if (requestParameters.sheetId !== void 0) {
      queryParameters["sheetId"] = requestParameters.sheetId;
    }
    const headerParameters = {};
    if (this.configuration && this.configuration.accessToken) {
      const token = this.configuration.accessToken;
      const tokenString = await token("BearerAuth", []);
      if (tokenString) {
        headerParameters["Authorization"] = `Bearer ${tokenString}`;
      }
    }
    const response = await this.request({
      path: `/snapshots`,
      method: "GET",
      headers: headerParameters,
      query: queryParameters
    }, initOverrides);
    return new JSONApiResponse(response, (jsonValue) => SnapshotsResponseFromJSON(jsonValue));
  }
  /**
   * List all snapshots of a sheet
   * List snapshots
   */
  async getSnapshots(requestParameters, initOverrides) {
    const response = await this.getSnapshotsRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Returns the contents of manifest.json
   */
  async getSoftwareManifestRaw(initOverrides) {
    const queryParameters = {};
    const headerParameters = {};
    const response = await this.request({
      path: `/manifest`,
      method: "GET",
      headers: headerParameters,
      query: queryParameters
    }, initOverrides);
    return new JSONApiResponse(response, (jsonValue) => ManifestResponseFromJSON(jsonValue));
  }
  /**
   * Returns the contents of manifest.json
   */
  async getSoftwareManifest(initOverrides) {
    const response = await this.getSoftwareManifestRaw(initOverrides);
    return await response.value();
  }
  /**
   * Returns a list of activities that have occurred in a space and who initiated those activities
   * Get the activity for a Space
   */
  async getSpaceActivityRaw(requestParameters, initOverrides) {
    if (requestParameters.spaceId === null || requestParameters.spaceId === void 0) {
      throw new RequiredError("spaceId", "Required parameter requestParameters.spaceId was null or undefined when calling getSpaceActivity.");
    }
    const queryParameters = {};
    if (requestParameters.sortField !== void 0) {
      queryParameters["sortField"] = requestParameters.sortField;
    }
    if (requestParameters.sortDirection !== void 0) {
      queryParameters["sortDirection"] = requestParameters.sortDirection;
    }
    if (requestParameters.pageSize !== void 0) {
      queryParameters["pageSize"] = requestParameters.pageSize;
    }
    if (requestParameters.pageNumber !== void 0) {
      queryParameters["pageNumber"] = requestParameters.pageNumber;
    }
    if (requestParameters.activity !== void 0) {
      queryParameters["activity"] = requestParameters.activity;
    }
    if (requestParameters.since !== void 0) {
      queryParameters["since"] = requestParameters.since;
    }
    const headerParameters = {};
    if (this.configuration && this.configuration.accessToken) {
      const token = this.configuration.accessToken;
      const tokenString = await token("BearerAuth", []);
      if (tokenString) {
        headerParameters["Authorization"] = `Bearer ${tokenString}`;
      }
    }
    const response = await this.request({
      path: `/spaces/{spaceId}/activity`.replace(`{${"spaceId"}}`, encodeURIComponent(String(requestParameters.spaceId))),
      method: "GET",
      headers: headerParameters,
      query: queryParameters
    }, initOverrides);
    return new JSONApiResponse(response, (jsonValue) => ActivitiesResponseFromJSON(jsonValue));
  }
  /**
   * Returns a list of activities that have occurred in a space and who initiated those activities
   * Get the activity for a Space
   */
  async getSpaceActivity(requestParameters, initOverrides) {
    const response = await this.getSpaceActivityRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Returns an access token for this space
   * Generates a new access token scoped to only this space
   */
  async getSpaceAuthRaw(requestParameters, initOverrides) {
    if (requestParameters.spaceId === null || requestParameters.spaceId === void 0) {
      throw new RequiredError("spaceId", "Required parameter requestParameters.spaceId was null or undefined when calling getSpaceAuth.");
    }
    const queryParameters = {};
    const headerParameters = {};
    if (this.configuration && this.configuration.accessToken) {
      const token = this.configuration.accessToken;
      const tokenString = await token("BearerAuth", []);
      if (tokenString) {
        headerParameters["Authorization"] = `Bearer ${tokenString}`;
      }
    }
    const response = await this.request({
      path: `/spaces/{spaceId}/auth`.replace(`{${"spaceId"}}`, encodeURIComponent(String(requestParameters.spaceId))),
      method: "GET",
      headers: headerParameters,
      query: queryParameters
    }, initOverrides);
    return new JSONApiResponse(response, (jsonValue) => AccessTokenResponseFromJSON(jsonValue));
  }
  /**
   * Returns an access token for this space
   * Generates a new access token scoped to only this space
   */
  async getSpaceAuth(requestParameters, initOverrides) {
    const response = await this.getSpaceAuthRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Returns a single space
   * Get a space
   */
  async getSpaceByIdRaw(requestParameters, initOverrides) {
    if (requestParameters.spaceId === null || requestParameters.spaceId === void 0) {
      throw new RequiredError("spaceId", "Required parameter requestParameters.spaceId was null or undefined when calling getSpaceById.");
    }
    const queryParameters = {};
    const headerParameters = {};
    if (this.configuration && this.configuration.accessToken) {
      const token = this.configuration.accessToken;
      const tokenString = await token("BearerAuth", []);
      if (tokenString) {
        headerParameters["Authorization"] = `Bearer ${tokenString}`;
      }
    }
    const response = await this.request({
      path: `/spaces/{spaceId}`.replace(`{${"spaceId"}}`, encodeURIComponent(String(requestParameters.spaceId))),
      method: "GET",
      headers: headerParameters,
      query: queryParameters
    }, initOverrides);
    return new JSONApiResponse(response, (jsonValue) => SpaceResponseFromJSON(jsonValue));
  }
  /**
   * Returns a single space
   * Get a space
   */
  async getSpaceById(requestParameters, initOverrides) {
    const response = await this.getSpaceByIdRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Returns a single document
   * Get a document
   */
  async getSpaceDocumentByIdRaw(requestParameters, initOverrides) {
    if (requestParameters.spaceId === null || requestParameters.spaceId === void 0) {
      throw new RequiredError("spaceId", "Required parameter requestParameters.spaceId was null or undefined when calling getSpaceDocumentById.");
    }
    if (requestParameters.documentId === null || requestParameters.documentId === void 0) {
      throw new RequiredError("documentId", "Required parameter requestParameters.documentId was null or undefined when calling getSpaceDocumentById.");
    }
    const queryParameters = {};
    const headerParameters = {};
    if (this.configuration && this.configuration.accessToken) {
      const token = this.configuration.accessToken;
      const tokenString = await token("BearerAuth", []);
      if (tokenString) {
        headerParameters["Authorization"] = `Bearer ${tokenString}`;
      }
    }
    const response = await this.request({
      path: `/spaces/{spaceId}/documents/{documentId}`.replace(`{${"spaceId"}}`, encodeURIComponent(String(requestParameters.spaceId))).replace(`{${"documentId"}}`, encodeURIComponent(String(requestParameters.documentId))),
      method: "GET",
      headers: headerParameters,
      query: queryParameters
    }, initOverrides);
    return new JSONApiResponse(response, (jsonValue) => DocumentResponseFromJSON(jsonValue));
  }
  /**
   * Returns a single document
   * Get a document
   */
  async getSpaceDocumentById(requestParameters, initOverrides) {
    const response = await this.getSpaceDocumentByIdRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Returns all documents for a space
   * List documents
   */
  async getSpaceDocumentsRaw(requestParameters, initOverrides) {
    if (requestParameters.spaceId === null || requestParameters.spaceId === void 0) {
      throw new RequiredError("spaceId", "Required parameter requestParameters.spaceId was null or undefined when calling getSpaceDocuments.");
    }
    const queryParameters = {};
    const headerParameters = {};
    if (this.configuration && this.configuration.accessToken) {
      const token = this.configuration.accessToken;
      const tokenString = await token("BearerAuth", []);
      if (tokenString) {
        headerParameters["Authorization"] = `Bearer ${tokenString}`;
      }
    }
    const response = await this.request({
      path: `/spaces/{spaceId}/documents`.replace(`{${"spaceId"}}`, encodeURIComponent(String(requestParameters.spaceId))),
      method: "GET",
      headers: headerParameters,
      query: queryParameters
    }, initOverrides);
    return new JSONApiResponse(response, (jsonValue) => DocumentsResponseFromJSON(jsonValue));
  }
  /**
   * Returns all documents for a space
   * List documents
   */
  async getSpaceDocuments(requestParameters, initOverrides) {
    const response = await this.getSpaceDocumentsRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Returns a single guest
   * Get a guest
   */
  async getSpaceGuestByIdRaw(requestParameters, initOverrides) {
    if (requestParameters.guestId === null || requestParameters.guestId === void 0) {
      throw new RequiredError("guestId", "Required parameter requestParameters.guestId was null or undefined when calling getSpaceGuestById.");
    }
    const queryParameters = {};
    const headerParameters = {};
    if (this.configuration && this.configuration.accessToken) {
      const token = this.configuration.accessToken;
      const tokenString = await token("BearerAuth", []);
      if (tokenString) {
        headerParameters["Authorization"] = `Bearer ${tokenString}`;
      }
    }
    const response = await this.request({
      path: `/guests/{guestId}`.replace(`{${"guestId"}}`, encodeURIComponent(String(requestParameters.guestId))),
      method: "GET",
      headers: headerParameters,
      query: queryParameters
    }, initOverrides);
    return new JSONApiResponse(response, (jsonValue) => GuestResponseFromJSON(jsonValue));
  }
  /**
   * Returns a single guest
   * Get a guest
   */
  async getSpaceGuestById(requestParameters, initOverrides) {
    const response = await this.getSpaceGuestByIdRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Returns the subscriptions for the current user
   */
  async getSubscriptionsRaw(initOverrides) {
    const queryParameters = {};
    const headerParameters = {};
    if (this.configuration && this.configuration.accessToken) {
      const token = this.configuration.accessToken;
      const tokenString = await token("BearerAuth", []);
      if (tokenString) {
        headerParameters["Authorization"] = `Bearer ${tokenString}`;
      }
    }
    const response = await this.request({
      path: `/billing/subscriptions`,
      method: "GET",
      headers: headerParameters,
      query: queryParameters
    }, initOverrides);
    return new JSONApiResponse(response, (jsonValue) => BillingSubscriptionsResponseFromJSON(jsonValue));
  }
  /**
   * Returns the subscriptions for the current user
   */
  async getSubscriptions(initOverrides) {
    const response = await this.getSubscriptionsRaw(initOverrides);
    return await response.value();
  }
  /**
   * Gets a user
   * Get a user
   */
  async getUserRaw(requestParameters, initOverrides) {
    if (requestParameters.userId === null || requestParameters.userId === void 0) {
      throw new RequiredError("userId", "Required parameter requestParameters.userId was null or undefined when calling getUser.");
    }
    const queryParameters = {};
    const headerParameters = {};
    if (this.configuration && this.configuration.accessToken) {
      const token = this.configuration.accessToken;
      const tokenString = await token("BearerAuth", []);
      if (tokenString) {
        headerParameters["Authorization"] = `Bearer ${tokenString}`;
      }
    }
    const response = await this.request({
      path: `/users/{userId}`.replace(`{${"userId"}}`, encodeURIComponent(String(requestParameters.userId))),
      method: "GET",
      headers: headerParameters,
      query: queryParameters
    }, initOverrides);
    return new JSONApiResponse(response, (jsonValue) => UserResponseFromJSON(jsonValue));
  }
  /**
   * Gets a user
   * Get a user
   */
  async getUser(requestParameters, initOverrides) {
    const response = await this.getUserRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Lists roles assigned to a user
   * List User Roles
   */
  async getUserRolesRaw(requestParameters, initOverrides) {
    if (requestParameters.userId === null || requestParameters.userId === void 0) {
      throw new RequiredError("userId", "Required parameter requestParameters.userId was null or undefined when calling getUserRoles.");
    }
    const queryParameters = {};
    const headerParameters = {};
    if (this.configuration && this.configuration.accessToken) {
      const token = this.configuration.accessToken;
      const tokenString = await token("BearerAuth", []);
      if (tokenString) {
        headerParameters["Authorization"] = `Bearer ${tokenString}`;
      }
    }
    const response = await this.request({
      path: `/users/{userId}/roles`.replace(`{${"userId"}}`, encodeURIComponent(String(requestParameters.userId))),
      method: "GET",
      headers: headerParameters,
      query: queryParameters
    }, initOverrides);
    return new JSONApiResponse(response, (jsonValue) => ListActorRolesResponseFromJSON(jsonValue));
  }
  /**
   * Lists roles assigned to a user
   * List User Roles
   */
  async getUserRoles(requestParameters, initOverrides) {
    const response = await this.getUserRolesRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Gets a list of users
   * List users
   */
  async getUsersRaw(requestParameters, initOverrides) {
    const queryParameters = {};
    if (requestParameters.email !== void 0) {
      queryParameters["email"] = requestParameters.email;
    }
    if (requestParameters.pageSize !== void 0) {
      queryParameters["pageSize"] = requestParameters.pageSize;
    }
    if (requestParameters.pageNumber !== void 0) {
      queryParameters["pageNumber"] = requestParameters.pageNumber;
    }
    if (requestParameters.search !== void 0) {
      queryParameters["search"] = requestParameters.search;
    }
    if (requestParameters.sortField !== void 0) {
      queryParameters["sortField"] = requestParameters.sortField;
    }
    if (requestParameters.sortDirection !== void 0) {
      queryParameters["sortDirection"] = requestParameters.sortDirection;
    }
    const headerParameters = {};
    if (this.configuration && this.configuration.accessToken) {
      const token = this.configuration.accessToken;
      const tokenString = await token("BearerAuth", []);
      if (tokenString) {
        headerParameters["Authorization"] = `Bearer ${tokenString}`;
      }
    }
    const response = await this.request({
      path: `/users`,
      method: "GET",
      headers: headerParameters,
      query: queryParameters
    }, initOverrides);
    return new JSONApiResponse(response, (jsonValue) => UsersResponseFromJSON(jsonValue));
  }
  /**
   * Gets a list of users
   * List users
   */
  async getUsers(requestParameters = {}, initOverrides) {
    const response = await this.getUsersRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Returns a single view
   * Returns a view
   */
  async getViewRaw(requestParameters, initOverrides) {
    if (requestParameters.viewId === null || requestParameters.viewId === void 0) {
      throw new RequiredError("viewId", "Required parameter requestParameters.viewId was null or undefined when calling getView.");
    }
    const queryParameters = {};
    const headerParameters = {};
    if (this.configuration && this.configuration.accessToken) {
      const token = this.configuration.accessToken;
      const tokenString = await token("BearerAuth", []);
      if (tokenString) {
        headerParameters["Authorization"] = `Bearer ${tokenString}`;
      }
    }
    const response = await this.request({
      path: `/views/{viewId}`.replace(`{${"viewId"}}`, encodeURIComponent(String(requestParameters.viewId))),
      method: "GET",
      headers: headerParameters,
      query: queryParameters
    }, initOverrides);
    return new JSONApiResponse(response, (jsonValue) => ViewResponseFromJSON(jsonValue));
  }
  /**
   * Returns a single view
   * Returns a view
   */
  async getView(requestParameters, initOverrides) {
    const response = await this.getViewRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * List views by Sheet
   * Returns all views for the sheet
   */
  async getViewsRaw(requestParameters, initOverrides) {
    if (requestParameters.sheetId === null || requestParameters.sheetId === void 0) {
      throw new RequiredError("sheetId", "Required parameter requestParameters.sheetId was null or undefined when calling getViews.");
    }
    const queryParameters = {};
    if (requestParameters.sheetId !== void 0) {
      queryParameters["sheetId"] = requestParameters.sheetId;
    }
    if (requestParameters.pageSize !== void 0) {
      queryParameters["pageSize"] = requestParameters.pageSize;
    }
    if (requestParameters.pageNumber !== void 0) {
      queryParameters["pageNumber"] = requestParameters.pageNumber;
    }
    const headerParameters = {};
    if (this.configuration && this.configuration.accessToken) {
      const token = this.configuration.accessToken;
      const tokenString = await token("BearerAuth", []);
      if (tokenString) {
        headerParameters["Authorization"] = `Bearer ${tokenString}`;
      }
    }
    const response = await this.request({
      path: `/views`,
      method: "GET",
      headers: headerParameters,
      query: queryParameters
    }, initOverrides);
    return new JSONApiResponse(response, (jsonValue) => ViewsResponseFromJSON(jsonValue));
  }
  /**
   * List views by Sheet
   * Returns all views for the sheet
   */
  async getViews(requestParameters, initOverrides) {
    const response = await this.getViewsRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Returns a single workbook
   * Get a workbook
   */
  async getWorkbookByIdRaw(requestParameters, initOverrides) {
    if (requestParameters.workbookId === null || requestParameters.workbookId === void 0) {
      throw new RequiredError("workbookId", "Required parameter requestParameters.workbookId was null or undefined when calling getWorkbookById.");
    }
    const queryParameters = {};
    const headerParameters = {};
    if (this.configuration && this.configuration.accessToken) {
      const token = this.configuration.accessToken;
      const tokenString = await token("BearerAuth", []);
      if (tokenString) {
        headerParameters["Authorization"] = `Bearer ${tokenString}`;
      }
    }
    const response = await this.request({
      path: `/workbooks/{workbookId}`.replace(`{${"workbookId"}}`, encodeURIComponent(String(requestParameters.workbookId))),
      method: "GET",
      headers: headerParameters,
      query: queryParameters
    }, initOverrides);
    return new JSONApiResponse(response, (jsonValue) => WorkbookResponseFromJSON(jsonValue));
  }
  /**
   * Returns a single workbook
   * Get a workbook
   */
  async getWorkbookById(requestParameters, initOverrides) {
    const response = await this.getWorkbookByIdRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Returns the sheet commits for an entire workbook
   */
  async getWorkbookCommitsRaw(requestParameters, initOverrides) {
    if (requestParameters.workbookId === null || requestParameters.workbookId === void 0) {
      throw new RequiredError("workbookId", "Required parameter requestParameters.workbookId was null or undefined when calling getWorkbookCommits.");
    }
    const queryParameters = {};
    if (requestParameters.completed !== void 0) {
      queryParameters["completed"] = requestParameters.completed;
    }
    const headerParameters = {};
    if (this.configuration && this.configuration.accessToken) {
      const token = this.configuration.accessToken;
      const tokenString = await token("BearerAuth", []);
      if (tokenString) {
        headerParameters["Authorization"] = `Bearer ${tokenString}`;
      }
    }
    const response = await this.request({
      path: `/workbooks/{workbookId}/commits`.replace(`{${"workbookId"}}`, encodeURIComponent(String(requestParameters.workbookId))),
      method: "GET",
      headers: headerParameters,
      query: queryParameters
    }, initOverrides);
    return new JSONApiResponse(response, (jsonValue) => GetWorkbookCommitsResponseFromJSON(jsonValue));
  }
  /**
   * Returns the sheet commits for an entire workbook
   */
  async getWorkbookCommits(requestParameters, initOverrides) {
    const response = await this.getWorkbookCommitsRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Receives data when a user accepted the invite with Frontegg
   * Handle accept invite webhooks
   */
  async handleAcceptInviteRaw(requestParameters, initOverrides) {
    const queryParameters = {};
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    const response = await this.request({
      path: `/frontegg/accept-invite`,
      method: "POST",
      headers: headerParameters,
      query: queryParameters,
      body: FronteggSignupToJSON(requestParameters.fronteggSignup)
    }, initOverrides);
    return new JSONApiResponse(response, (jsonValue) => UserResponseFromJSON(jsonValue));
  }
  /**
   * Receives data when a user accepted the invite with Frontegg
   * Handle accept invite webhooks
   */
  async handleAcceptInvite(requestParameters = {}, initOverrides) {
    const response = await this.handleAcceptInviteRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Handles events coming from Stripe
   * Handle an incoming GET request from Stripe
   */
  async handleStripWebgetRaw(requestParameters, initOverrides) {
    if (requestParameters.sessionId === null || requestParameters.sessionId === void 0) {
      throw new RequiredError("sessionId", "Required parameter requestParameters.sessionId was null or undefined when calling handleStripWebget.");
    }
    const queryParameters = {};
    if (requestParameters.sessionId !== void 0) {
      queryParameters["session_id"] = requestParameters.sessionId;
    }
    const headerParameters = {};
    const response = await this.request({
      path: `/webhook/stripe`,
      method: "GET",
      headers: headerParameters,
      query: queryParameters
    }, initOverrides);
    return new JSONApiResponse(response, (jsonValue) => SuccessResponseFromJSON(jsonValue));
  }
  /**
   * Handles events coming from Stripe
   * Handle an incoming GET request from Stripe
   */
  async handleStripWebget(requestParameters, initOverrides) {
    const response = await this.handleStripWebgetRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Handles events coming from Stripe
   * Handle an incoming Stripe webhook
   */
  async handleStripeWebhookRaw(requestParameters, initOverrides) {
    const queryParameters = {};
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    const response = await this.request({
      path: `/webhook/stripe`,
      method: "POST",
      headers: headerParameters,
      query: queryParameters,
      body: StripeWebhookRequestBodyToJSON(requestParameters.stripeWebhookRequestBody)
    }, initOverrides);
    return new JSONApiResponse(response, (jsonValue) => SuccessResponseFromJSON(jsonValue));
  }
  /**
   * Handles events coming from Stripe
   * Handle an incoming Stripe webhook
   */
  async handleStripeWebhook(requestParameters = {}, initOverrides) {
    const response = await this.handleStripeWebhookRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Receives data when a user has activated their Frontegg account
   * Handle user activate webhooks
   */
  async handleUserActivateRaw(requestParameters, initOverrides) {
    const queryParameters = {};
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    const response = await this.request({
      path: `/frontegg/activate`,
      method: "POST",
      headers: headerParameters,
      query: queryParameters,
      body: FronteggSignupToJSON(requestParameters.fronteggSignup)
    }, initOverrides);
    return new JSONApiResponse(response, (jsonValue) => UserResponseFromJSON(jsonValue));
  }
  /**
   * Receives data when a user has activated their Frontegg account
   * Handle user activate webhooks
   */
  async handleUserActivate(requestParameters = {}, initOverrides) {
    const response = await this.handleUserActivateRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Receives data when a user is invited with Frontegg
   * Handle user invite webhooks
   */
  async handleUserInviteRaw(requestParameters, initOverrides) {
    const queryParameters = {};
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    const response = await this.request({
      path: `/frontegg/invite`,
      method: "POST",
      headers: headerParameters,
      query: queryParameters,
      body: FronteggSignupToJSON(requestParameters.fronteggSignup)
    }, initOverrides);
    return new JSONApiResponse(response, (jsonValue) => UserResponseFromJSON(jsonValue));
  }
  /**
   * Receives data when a user is invited with Frontegg
   * Handle user invite webhooks
   */
  async handleUserInvite(requestParameters = {}, initOverrides) {
    const response = await this.handleUserInviteRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Receives data when a user is signed up with Frontegg
   * Handle user sign up webhooks
   */
  async handleUserSignUpRaw(requestParameters, initOverrides) {
    const queryParameters = {};
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    const response = await this.request({
      path: `/frontegg/sign-up`,
      method: "POST",
      headers: headerParameters,
      query: queryParameters,
      body: FronteggSignupToJSON(requestParameters.fronteggSignup)
    }, initOverrides);
    return new JSONApiResponse(response, (jsonValue) => UserResponseFromJSON(jsonValue));
  }
  /**
   * Receives data when a user is signed up with Frontegg
   * Handle user sign up webhooks
   */
  async handleUserSignUp(requestParameters = {}, initOverrides) {
    const response = await this.handleUserSignUpRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Sends an invite each guest
   * Invite guests
   */
  async inviteGuestsRaw(requestParameters, initOverrides) {
    if (requestParameters.inviteGuestsRequestInner === null || requestParameters.inviteGuestsRequestInner === void 0) {
      throw new RequiredError("inviteGuestsRequestInner", "Required parameter requestParameters.inviteGuestsRequestInner was null or undefined when calling inviteGuests.");
    }
    const queryParameters = {};
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    if (this.configuration && this.configuration.accessToken) {
      const token = this.configuration.accessToken;
      const tokenString = await token("BearerAuth", []);
      if (tokenString) {
        headerParameters["Authorization"] = `Bearer ${tokenString}`;
      }
    }
    const response = await this.request({
      path: `/invitations`,
      method: "POST",
      headers: headerParameters,
      query: queryParameters,
      body: requestParameters.inviteGuestsRequestInner.map(InviteGuestsRequestInnerToJSON)
    }, initOverrides);
    return new JSONApiResponse(response, (jsonValue) => SuccessResponseFromJSON(jsonValue));
  }
  /**
   * Sends an invite each guest
   * Invite guests
   */
  async inviteGuests(requestParameters, initOverrides) {
    const response = await this.inviteGuestsRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Fetch a list of mapping programs
   */
  async listMappingProgramsRaw(requestParameters, initOverrides) {
    const queryParameters = {};
    if (requestParameters.pageSize !== void 0) {
      queryParameters["pageSize"] = requestParameters.pageSize;
    }
    if (requestParameters.pageNumber !== void 0) {
      queryParameters["pageNumber"] = requestParameters.pageNumber;
    }
    if (requestParameters.createdBy !== void 0) {
      queryParameters["createdBy"] = requestParameters.createdBy;
    }
    if (requestParameters.createdAfter !== void 0) {
      queryParameters["createdAfter"] = requestParameters.createdAfter.toISOString();
    }
    if (requestParameters.createdBefore !== void 0) {
      queryParameters["createdBefore"] = requestParameters.createdBefore.toISOString();
    }
    if (requestParameters.environmentId !== void 0) {
      queryParameters["environmentId"] = requestParameters.environmentId;
    }
    if (requestParameters.sourceKeys !== void 0) {
      queryParameters["sourceKeys"] = requestParameters.sourceKeys;
    }
    if (requestParameters.destinationKeys !== void 0) {
      queryParameters["destinationKeys"] = requestParameters.destinationKeys;
    }
    const headerParameters = {};
    if (this.configuration && this.configuration.accessToken) {
      const token = this.configuration.accessToken;
      const tokenString = await token("BearerAuth", []);
      if (tokenString) {
        headerParameters["Authorization"] = `Bearer ${tokenString}`;
      }
    }
    const response = await this.request({
      path: `/mapping`,
      method: "GET",
      headers: headerParameters,
      query: queryParameters
    }, initOverrides);
    return new JSONApiResponse(response, (jsonValue) => ProgramsResponseFromJSON(jsonValue));
  }
  /**
   * Fetch a list of mapping programs
   */
  async listMappingPrograms(requestParameters = {}, initOverrides) {
    const response = await this.listMappingProgramsRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * List all roles for an account
   * List Roles
   */
  async listRolesRaw(initOverrides) {
    const queryParameters = {};
    const headerParameters = {};
    if (this.configuration && this.configuration.accessToken) {
      const token = this.configuration.accessToken;
      const tokenString = await token("BearerAuth", []);
      if (tokenString) {
        headerParameters["Authorization"] = `Bearer ${tokenString}`;
      }
    }
    const response = await this.request({
      path: `/roles`,
      method: "GET",
      headers: headerParameters,
      query: queryParameters
    }, initOverrides);
    return new JSONApiResponse(response, (jsonValue) => ListRolesResponseFromJSON(jsonValue));
  }
  /**
   * List all roles for an account
   * List Roles
   */
  async listRoles(initOverrides) {
    const response = await this.listRolesRaw(initOverrides);
    return await response.value();
  }
  /**
   * Locks a sheet to prevent further changes
   * Lock a sheet
   */
  async lockSheetRaw(requestParameters, initOverrides) {
    if (requestParameters.sheetId === null || requestParameters.sheetId === void 0) {
      throw new RequiredError("sheetId", "Required parameter requestParameters.sheetId was null or undefined when calling lockSheet.");
    }
    const queryParameters = {};
    const headerParameters = {};
    if (this.configuration && this.configuration.accessToken) {
      const token = this.configuration.accessToken;
      const tokenString = await token("BearerAuth", []);
      if (tokenString) {
        headerParameters["Authorization"] = `Bearer ${tokenString}`;
      }
    }
    const response = await this.request({
      path: `/sheets/{sheetId}/lock`.replace(`{${"sheetId"}}`, encodeURIComponent(String(requestParameters.sheetId))),
      method: "POST",
      headers: headerParameters,
      query: queryParameters
    }, initOverrides);
    return new JSONApiResponse(response, (jsonValue) => SuccessResponseFromJSON(jsonValue));
  }
  /**
   * Locks a sheet to prevent further changes
   * Lock a sheet
   */
  async lockSheet(requestParameters, initOverrides) {
    const response = await this.lockSheetRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Preview a mutation
   * Preview a mutation
   */
  async previewMutationRaw(requestParameters, initOverrides) {
    const queryParameters = {};
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    if (this.configuration && this.configuration.accessToken) {
      const token = this.configuration.accessToken;
      const tokenString = await token("BearerAuth", []);
      if (tokenString) {
        headerParameters["Authorization"] = `Bearer ${tokenString}`;
      }
    }
    const response = await this.request({
      path: `/jobs/preview-mutation`,
      method: "POST",
      headers: headerParameters,
      query: queryParameters,
      body: MutateJobConfigToJSON(requestParameters.mutateJobConfig)
    }, initOverrides);
    return new JSONApiResponse(response, (jsonValue) => DiffRecordsResponseFromJSON(jsonValue));
  }
  /**
   * Preview a mutation
   * Preview a mutation
   */
  async previewMutation(requestParameters = {}, initOverrides) {
    const response = await this.previewMutationRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Restore a sheet to a previous snapshot
   * Restore snapshot
   */
  async putSnapshotRaw(requestParameters, initOverrides) {
    if (requestParameters.snapshotId === null || requestParameters.snapshotId === void 0) {
      throw new RequiredError("snapshotId", "Required parameter requestParameters.snapshotId was null or undefined when calling putSnapshot.");
    }
    const queryParameters = {};
    const headerParameters = {};
    if (this.configuration && this.configuration.accessToken) {
      const token = this.configuration.accessToken;
      const tokenString = await token("BearerAuth", []);
      if (tokenString) {
        headerParameters["Authorization"] = `Bearer ${tokenString}`;
      }
    }
    const response = await this.request({
      path: `/snapshots/{snapshotId}`.replace(`{${"snapshotId"}}`, encodeURIComponent(String(requestParameters.snapshotId))),
      method: "PUT",
      headers: headerParameters,
      query: queryParameters
    }, initOverrides);
    return new JSONApiResponse(response, (jsonValue) => SnapshotResponseFromJSON(jsonValue));
  }
  /**
   * Restore a sheet to a previous snapshot
   * Restore snapshot
   */
  async putSnapshot(requestParameters, initOverrides) {
    const response = await this.putSnapshotRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Rebuild a workbook
   * Rebuild a workbook
   */
  async rebuildWorkbookRaw(requestParameters, initOverrides) {
    if (requestParameters.workbookId === null || requestParameters.workbookId === void 0) {
      throw new RequiredError("workbookId", "Required parameter requestParameters.workbookId was null or undefined when calling rebuildWorkbook.");
    }
    const queryParameters = {};
    const headerParameters = {};
    if (this.configuration && this.configuration.accessToken) {
      const token = this.configuration.accessToken;
      const tokenString = await token("BearerAuth", []);
      if (tokenString) {
        headerParameters["Authorization"] = `Bearer ${tokenString}`;
      }
    }
    const response = await this.request({
      path: `/workbooks/{workbookId}/rebuild`.replace(`{${"workbookId"}}`, encodeURIComponent(String(requestParameters.workbookId))),
      method: "POST",
      headers: headerParameters,
      query: queryParameters
    }, initOverrides);
    return new JSONApiResponse(response, (jsonValue) => SuccessResponseFromJSON(jsonValue));
  }
  /**
   * Rebuild a workbook
   * Rebuild a workbook
   */
  async rebuildWorkbook(requestParameters, initOverrides) {
    const response = await this.rebuildWorkbookRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * This endpoint handles an incoming GET request with query parameters from Google Drive and redirects the request to Paragon, preserving the original query parameters.
   * Handle an incoming GET request from Google Drive and redirects to Paragon
   */
  async redirectToParagonRaw(requestParameters, initOverrides) {
    const queryParameters = {};
    if (requestParameters.query !== void 0) {
      queryParameters["query"] = requestParameters.query;
    }
    const headerParameters = {};
    const response = await this.request({
      path: `/webhook/paragon-redirect`,
      method: "GET",
      headers: headerParameters,
      query: queryParameters
    }, initOverrides);
    return new VoidApiResponse(response);
  }
  /**
   * This endpoint handles an incoming GET request with query parameters from Google Drive and redirects the request to Paragon, preserving the original query parameters.
   * Handle an incoming GET request from Google Drive and redirects to Paragon
   */
  async redirectToParagon(requestParameters = {}, initOverrides) {
    await this.redirectToParagonRaw(requestParameters, initOverrides);
  }
  /**
   * Searches for all field values that match the \'searchValue\' and replaces them with the \'replaceValue\'. Wrap \'searchValue\' in double quotes for exact match (\"\"). Returns updated records.
   * Finds and replaces all field values matching search criteria with a given value
   */
  async replaceRaw(requestParameters, initOverrides) {
    if (requestParameters.sheetId === null || requestParameters.sheetId === void 0) {
      throw new RequiredError("sheetId", "Required parameter requestParameters.sheetId was null or undefined when calling replace.");
    }
    if (requestParameters.fieldKey === null || requestParameters.fieldKey === void 0) {
      throw new RequiredError("fieldKey", "Required parameter requestParameters.fieldKey was null or undefined when calling replace.");
    }
    if (requestParameters.searchValue === null || requestParameters.searchValue === void 0) {
      throw new RequiredError("searchValue", "Required parameter requestParameters.searchValue was null or undefined when calling replace.");
    }
    if (requestParameters.replaceRequest === null || requestParameters.replaceRequest === void 0) {
      throw new RequiredError("replaceRequest", "Required parameter requestParameters.replaceRequest was null or undefined when calling replace.");
    }
    const queryParameters = {};
    if (requestParameters.fieldKey !== void 0) {
      queryParameters["fieldKey"] = requestParameters.fieldKey;
    }
    if (requestParameters.searchValue !== void 0) {
      queryParameters["searchValue"] = requestParameters.searchValue;
    }
    if (requestParameters.filter !== void 0) {
      queryParameters["filter"] = requestParameters.filter;
    }
    if (requestParameters.pageSize !== void 0) {
      queryParameters["pageSize"] = requestParameters.pageSize;
    }
    if (requestParameters.pageNumber !== void 0) {
      queryParameters["pageNumber"] = requestParameters.pageNumber;
    }
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    if (this.configuration && this.configuration.accessToken) {
      const token = this.configuration.accessToken;
      const tokenString = await token("BearerAuth", []);
      if (tokenString) {
        headerParameters["Authorization"] = `Bearer ${tokenString}`;
      }
    }
    const response = await this.request({
      path: `/sheets/{sheetId}/replace`.replace(`{${"sheetId"}}`, encodeURIComponent(String(requestParameters.sheetId))),
      method: "PUT",
      headers: headerParameters,
      query: queryParameters,
      body: ReplaceRequestToJSON(requestParameters.replaceRequest)
    }, initOverrides);
    return new JSONApiResponse(response, (jsonValue) => RecordsResponseFromJSON(jsonValue));
  }
  /**
   * Searches for all field values that match the \'searchValue\' and replaces them with the \'replaceValue\'. Wrap \'searchValue\' in double quotes for exact match (\"\"). Returns updated records.
   * Finds and replaces all field values matching search criteria with a given value
   */
  async replace(requestParameters, initOverrides) {
    const response = await this.replaceRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Replaces an existing Space Config and optionally migrates all spaces using the config
   * Replace a space config
   */
  async replaceSpaceConfigDeprecatedRaw(requestParameters, initOverrides) {
    if (requestParameters.spacePatternConfig === null || requestParameters.spacePatternConfig === void 0) {
      throw new RequiredError("spacePatternConfig", "Required parameter requestParameters.spacePatternConfig was null or undefined when calling replaceSpaceConfigDeprecated.");
    }
    const queryParameters = {};
    if (requestParameters.migrate !== void 0) {
      queryParameters["migrate"] = requestParameters.migrate;
    }
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    if (this.configuration && this.configuration.accessToken) {
      const token = this.configuration.accessToken;
      const tokenString = await token("BearerAuth", []);
      if (tokenString) {
        headerParameters["Authorization"] = `Bearer ${tokenString}`;
      }
    }
    const response = await this.request({
      path: `/spaces/configs`,
      method: "PUT",
      headers: headerParameters,
      query: queryParameters,
      body: SpacePatternConfigToJSON(requestParameters.spacePatternConfig)
    }, initOverrides);
    return new JSONApiResponse(response, (jsonValue) => SpaceConfigResponseFromJSON(jsonValue));
  }
  /**
   * Replaces an existing Space Config and optionally migrates all spaces using the config
   * Replace a space config
   */
  async replaceSpaceConfigDeprecated(requestParameters, initOverrides) {
    const response = await this.replaceSpaceConfigDeprecatedRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Replays a commit:created event.
   * Replay a commit:created event
   */
  async replayCommitRaw(requestParameters, initOverrides) {
    if (requestParameters.commitId === null || requestParameters.commitId === void 0) {
      throw new RequiredError("commitId", "Required parameter requestParameters.commitId was null or undefined when calling replayCommit.");
    }
    const queryParameters = {};
    const headerParameters = {};
    if (this.configuration && this.configuration.accessToken) {
      const token = this.configuration.accessToken;
      const tokenString = await token("BearerAuth", []);
      if (tokenString) {
        headerParameters["Authorization"] = `Bearer ${tokenString}`;
      }
    }
    const response = await this.request({
      path: `/commits/{commitId}/replay`.replace(`{${"commitId"}}`, encodeURIComponent(String(requestParameters.commitId))),
      method: "POST",
      headers: headerParameters,
      query: queryParameters
    }, initOverrides);
    return new JSONApiResponse(response, (jsonValue) => SuccessResponseFromJSON(jsonValue));
  }
  /**
   * Replays a commit:created event.
   * Replay a commit:created event
   */
  async replayCommit(requestParameters, initOverrides) {
    const response = await this.replayCommitRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Resends a user invite
   * Resends a user invite
   */
  async resendUserInviteRaw(requestParameters, initOverrides) {
    if (requestParameters.userId === null || requestParameters.userId === void 0) {
      throw new RequiredError("userId", "Required parameter requestParameters.userId was null or undefined when calling resendUserInvite.");
    }
    const queryParameters = {};
    const headerParameters = {};
    if (this.configuration && this.configuration.accessToken) {
      const token = this.configuration.accessToken;
      const tokenString = await token("BearerAuth", []);
      if (tokenString) {
        headerParameters["Authorization"] = `Bearer ${tokenString}`;
      }
    }
    const response = await this.request({
      path: `/users/{userId}/resend-invite`.replace(`{${"userId"}}`, encodeURIComponent(String(requestParameters.userId))),
      method: "POST",
      headers: headerParameters,
      query: queryParameters
    }, initOverrides);
    return new JSONApiResponse(response, (jsonValue) => SuccessResponseFromJSON(jsonValue));
  }
  /**
   * Resends a user invite
   * Resends a user invite
   */
  async resendUserInvite(requestParameters, initOverrides) {
    const response = await this.resendUserInviteRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Restore a database from a backup
   * Restore a database
   */
  async restoreDatabaseRaw(requestParameters, initOverrides) {
    if (requestParameters.workbookId === null || requestParameters.workbookId === void 0) {
      throw new RequiredError("workbookId", "Required parameter requestParameters.workbookId was null or undefined when calling restoreDatabase.");
    }
    if (requestParameters.restoreConfig === null || requestParameters.restoreConfig === void 0) {
      throw new RequiredError("restoreConfig", "Required parameter requestParameters.restoreConfig was null or undefined when calling restoreDatabase.");
    }
    const queryParameters = {};
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    const response = await this.request({
      path: `/foreigndb/{workbookId}`.replace(`{${"workbookId"}}`, encodeURIComponent(String(requestParameters.workbookId))),
      method: "POST",
      headers: headerParameters,
      query: queryParameters,
      body: RestoreConfigToJSON(requestParameters.restoreConfig)
    }, initOverrides);
    return new JSONApiResponse(response, (jsonValue) => RestoreResponseFromJSON(jsonValue));
  }
  /**
   * Restore a database from a backup
   * Restore a database
   */
  async restoreDatabase(requestParameters, initOverrides) {
    const response = await this.restoreDatabaseRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Restore a sheet to a previous snapshot
   * Restore snapshot
   */
  async restoreSnapshotRaw(requestParameters, initOverrides) {
    if (requestParameters.snapshotId === null || requestParameters.snapshotId === void 0) {
      throw new RequiredError("snapshotId", "Required parameter requestParameters.snapshotId was null or undefined when calling restoreSnapshot.");
    }
    const queryParameters = {};
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    if (this.configuration && this.configuration.accessToken) {
      const token = this.configuration.accessToken;
      const tokenString = await token("BearerAuth", []);
      if (tokenString) {
        headerParameters["Authorization"] = `Bearer ${tokenString}`;
      }
    }
    const response = await this.request({
      path: `/snapshots/{snapshotId}/restore`.replace(`{${"snapshotId"}}`, encodeURIComponent(String(requestParameters.snapshotId))),
      method: "POST",
      headers: headerParameters,
      query: queryParameters,
      body: RestoreSnapshotRequestToJSON(requestParameters.restoreSnapshotRequest)
    }, initOverrides);
    return new JSONApiResponse(response, (jsonValue) => SnapshotResponseFromJSON(jsonValue));
  }
  /**
   * Restore a sheet to a previous snapshot
   * Restore snapshot
   */
  async restoreSnapshot(requestParameters, initOverrides) {
    const response = await this.restoreSnapshotRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Retry a failed job and return the job
   * Retry a job
   */
  async retryJobRaw(requestParameters, initOverrides) {
    if (requestParameters.jobId === null || requestParameters.jobId === void 0) {
      throw new RequiredError("jobId", "Required parameter requestParameters.jobId was null or undefined when calling retryJob.");
    }
    const queryParameters = {};
    const headerParameters = {};
    if (this.configuration && this.configuration.accessToken) {
      const token = this.configuration.accessToken;
      const tokenString = await token("BearerAuth", []);
      if (tokenString) {
        headerParameters["Authorization"] = `Bearer ${tokenString}`;
      }
    }
    const response = await this.request({
      path: `/jobs/{jobId}/retry`.replace(`{${"jobId"}}`, encodeURIComponent(String(requestParameters.jobId))),
      method: "POST",
      headers: headerParameters,
      query: queryParameters
    }, initOverrides);
    return new JSONApiResponse(response, (jsonValue) => JobResponseFromJSON(jsonValue));
  }
  /**
   * Retry a failed job and return the job
   * Retry a job
   */
  async retryJob(requestParameters, initOverrides) {
    const response = await this.retryJobRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Create a snapshot of a sheet
   * Create snapshot
   */
  async snapshotSheetRaw(requestParameters, initOverrides) {
    const queryParameters = {};
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    if (this.configuration && this.configuration.accessToken) {
      const token = this.configuration.accessToken;
      const tokenString = await token("BearerAuth", []);
      if (tokenString) {
        headerParameters["Authorization"] = `Bearer ${tokenString}`;
      }
    }
    const response = await this.request({
      path: `/snapshots`,
      method: "POST",
      headers: headerParameters,
      query: queryParameters,
      body: SnapshotSheetRequestToJSON(requestParameters.snapshotSheetRequest)
    }, initOverrides);
    return new JSONApiResponse(response, (jsonValue) => SnapshotResponseFromJSON(jsonValue));
  }
  /**
   * Create a snapshot of a sheet
   * Create snapshot
   */
  async snapshotSheet(requestParameters = {}, initOverrides) {
    const response = await this.snapshotSheetRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Split a job and return the job
   * Split a job
   */
  async splitJobRaw(requestParameters, initOverrides) {
    if (requestParameters.jobId === null || requestParameters.jobId === void 0) {
      throw new RequiredError("jobId", "Required parameter requestParameters.jobId was null or undefined when calling splitJob.");
    }
    if (requestParameters.jobSplitDetails === null || requestParameters.jobSplitDetails === void 0) {
      throw new RequiredError("jobSplitDetails", "Required parameter requestParameters.jobSplitDetails was null or undefined when calling splitJob.");
    }
    const queryParameters = {};
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    if (this.configuration && this.configuration.accessToken) {
      const token = this.configuration.accessToken;
      const tokenString = await token("BearerAuth", []);
      if (tokenString) {
        headerParameters["Authorization"] = `Bearer ${tokenString}`;
      }
    }
    const response = await this.request({
      path: `/jobs/{jobId}/split`.replace(`{${"jobId"}}`, encodeURIComponent(String(requestParameters.jobId))),
      method: "POST",
      headers: headerParameters,
      query: queryParameters,
      body: JobSplitDetailsToJSON(requestParameters.jobSplitDetails)
    }, initOverrides);
    return new JSONApiResponse(response, (jsonValue) => JobResponseFromJSON(jsonValue));
  }
  /**
   * Split a job and return the job
   * Split a job
   */
  async splitJob(requestParameters, initOverrides) {
    const response = await this.splitJobRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Removes a lock from a sheet
   * Unlock a sheet
   */
  async unlockSheetRaw(requestParameters, initOverrides) {
    if (requestParameters.sheetId === null || requestParameters.sheetId === void 0) {
      throw new RequiredError("sheetId", "Required parameter requestParameters.sheetId was null or undefined when calling unlockSheet.");
    }
    const queryParameters = {};
    const headerParameters = {};
    if (this.configuration && this.configuration.accessToken) {
      const token = this.configuration.accessToken;
      const tokenString = await token("BearerAuth", []);
      if (tokenString) {
        headerParameters["Authorization"] = `Bearer ${tokenString}`;
      }
    }
    const response = await this.request({
      path: `/sheets/{sheetId}/unlock`.replace(`{${"sheetId"}}`, encodeURIComponent(String(requestParameters.sheetId))),
      method: "POST",
      headers: headerParameters,
      query: queryParameters
    }, initOverrides);
    return new JSONApiResponse(response, (jsonValue) => SuccessResponseFromJSON(jsonValue));
  }
  /**
   * Removes a lock from a sheet
   * Unlock a sheet
   */
  async unlockSheet(requestParameters, initOverrides) {
    const response = await this.unlockSheetRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Update the default app for the account of the currently authenticated user.
   * Update the default app for the account
   */
  async updateAccountDefaultAppRaw(requestParameters, initOverrides) {
    if (requestParameters.updateAccount === null || requestParameters.updateAccount === void 0) {
      throw new RequiredError("updateAccount", "Required parameter requestParameters.updateAccount was null or undefined when calling updateAccountDefaultApp.");
    }
    const queryParameters = {};
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    if (this.configuration && this.configuration.accessToken) {
      const token = this.configuration.accessToken;
      const tokenString = await token("BearerAuth", []);
      if (tokenString) {
        headerParameters["Authorization"] = `Bearer ${tokenString}`;
      }
    }
    const response = await this.request({
      path: `/accounts/current`,
      method: "PATCH",
      headers: headerParameters,
      query: queryParameters,
      body: UpdateAccountToJSON(requestParameters.updateAccount)
    }, initOverrides);
    return new JSONApiResponse(response, (jsonValue) => AccountResponseFromJSON(jsonValue));
  }
  /**
   * Update the default app for the account of the currently authenticated user.
   * Update the default app for the account
   */
  async updateAccountDefaultApp(requestParameters, initOverrides) {
    const response = await this.updateAccountDefaultAppRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Update a Data Retention Policy, to change the type for example
   * Update a Data Retention Policy
   */
  async updateDataRetentionPolicyRaw(requestParameters, initOverrides) {
    if (requestParameters.id === null || requestParameters.id === void 0) {
      throw new RequiredError("id", "Required parameter requestParameters.id was null or undefined when calling updateDataRetentionPolicy.");
    }
    const queryParameters = {};
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    if (this.configuration && this.configuration.accessToken) {
      const token = this.configuration.accessToken;
      const tokenString = await token("BearerAuth", []);
      if (tokenString) {
        headerParameters["Authorization"] = `Bearer ${tokenString}`;
      }
    }
    const response = await this.request({
      path: `/data-retention-policies/{id}`.replace(`{${"id"}}`, encodeURIComponent(String(requestParameters.id))),
      method: "PATCH",
      headers: headerParameters,
      query: queryParameters,
      body: DataRetentionPolicyConfigUpdateToJSON(requestParameters.dataRetentionPolicyConfigUpdate)
    }, initOverrides);
    return new JSONApiResponse(response, (jsonValue) => DataRetentionPolicyResponseFromJSON(jsonValue));
  }
  /**
   * Update a Data Retention Policy, to change the type for example
   * Update a Data Retention Policy
   */
  async updateDataRetentionPolicy(requestParameters, initOverrides) {
    const response = await this.updateDataRetentionPolicyRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * updates a single document, for only the body and title
   * Update a document
   */
  async updateDocumentRaw(requestParameters, initOverrides) {
    if (requestParameters.documentId === null || requestParameters.documentId === void 0) {
      throw new RequiredError("documentId", "Required parameter requestParameters.documentId was null or undefined when calling updateDocument.");
    }
    if (requestParameters.documentConfig === null || requestParameters.documentConfig === void 0) {
      throw new RequiredError("documentConfig", "Required parameter requestParameters.documentConfig was null or undefined when calling updateDocument.");
    }
    const queryParameters = {};
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    if (this.configuration && this.configuration.accessToken) {
      const token = this.configuration.accessToken;
      const tokenString = await token("BearerAuth", []);
      if (tokenString) {
        headerParameters["Authorization"] = `Bearer ${tokenString}`;
      }
    }
    const response = await this.request({
      path: `/documents/{documentId}`.replace(`{${"documentId"}}`, encodeURIComponent(String(requestParameters.documentId))),
      method: "PATCH",
      headers: headerParameters,
      query: queryParameters,
      body: DocumentConfigToJSON(requestParameters.documentConfig)
    }, initOverrides);
    return new JSONApiResponse(response, (jsonValue) => DocumentResponseFromJSON(jsonValue));
  }
  /**
   * updates a single document, for only the body and title
   * Update a document
   */
  async updateDocument(requestParameters, initOverrides) {
    const response = await this.updateDocumentRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Updates a single environment, to change the name for example
   * Update an environment
   */
  async updateEnvironmentByIdRaw(requestParameters, initOverrides) {
    if (requestParameters.environmentId === null || requestParameters.environmentId === void 0) {
      throw new RequiredError("environmentId", "Required parameter requestParameters.environmentId was null or undefined when calling updateEnvironmentById.");
    }
    if (requestParameters.updateEnvironmentByIdRequest === null || requestParameters.updateEnvironmentByIdRequest === void 0) {
      throw new RequiredError("updateEnvironmentByIdRequest", "Required parameter requestParameters.updateEnvironmentByIdRequest was null or undefined when calling updateEnvironmentById.");
    }
    const queryParameters = {};
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    if (this.configuration && this.configuration.accessToken) {
      const token = this.configuration.accessToken;
      const tokenString = await token("BearerAuth", []);
      if (tokenString) {
        headerParameters["Authorization"] = `Bearer ${tokenString}`;
      }
    }
    const response = await this.request({
      path: `/environments/{environmentId}`.replace(`{${"environmentId"}}`, encodeURIComponent(String(requestParameters.environmentId))),
      method: "PATCH",
      headers: headerParameters,
      query: queryParameters,
      body: UpdateEnvironmentByIdRequestToJSON(requestParameters.updateEnvironmentByIdRequest)
    }, initOverrides);
    return new JSONApiResponse(response, (jsonValue) => EnvironmentResponseFromJSON(jsonValue));
  }
  /**
   * Updates a single environment, to change the name for example
   * Update an environment
   */
  async updateEnvironmentById(requestParameters, initOverrides) {
    const response = await this.updateEnvironmentByIdRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Update a file, to change the workbook id for example
   * Update a File
   */
  async updateFileByIdRaw(requestParameters, initOverrides) {
    if (requestParameters.fileId === null || requestParameters.fileId === void 0) {
      throw new RequiredError("fileId", "Required parameter requestParameters.fileId was null or undefined when calling updateFileById.");
    }
    if (requestParameters.fileConfig === null || requestParameters.fileConfig === void 0) {
      throw new RequiredError("fileConfig", "Required parameter requestParameters.fileConfig was null or undefined when calling updateFileById.");
    }
    const queryParameters = {};
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    if (this.configuration && this.configuration.accessToken) {
      const token = this.configuration.accessToken;
      const tokenString = await token("BearerAuth", []);
      if (tokenString) {
        headerParameters["Authorization"] = `Bearer ${tokenString}`;
      }
    }
    const response = await this.request({
      path: `/files/{fileId}`.replace(`{${"fileId"}}`, encodeURIComponent(String(requestParameters.fileId))),
      method: "PATCH",
      headers: headerParameters,
      query: queryParameters,
      body: FileConfigToJSON(requestParameters.fileConfig)
    }, initOverrides);
    return new JSONApiResponse(response, (jsonValue) => FileResponseFromJSON(jsonValue));
  }
  /**
   * Update a file, to change the workbook id for example
   * Update a File
   */
  async updateFileById(requestParameters, initOverrides) {
    const response = await this.updateFileByIdRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Updates a single guest, for example to change name or email
   * Update a guest
   */
  async updateGuestRaw(requestParameters, initOverrides) {
    if (requestParameters.guestId === null || requestParameters.guestId === void 0) {
      throw new RequiredError("guestId", "Required parameter requestParameters.guestId was null or undefined when calling updateGuest.");
    }
    if (requestParameters.guestConfigUpdate === null || requestParameters.guestConfigUpdate === void 0) {
      throw new RequiredError("guestConfigUpdate", "Required parameter requestParameters.guestConfigUpdate was null or undefined when calling updateGuest.");
    }
    const queryParameters = {};
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    if (this.configuration && this.configuration.accessToken) {
      const token = this.configuration.accessToken;
      const tokenString = await token("BearerAuth", []);
      if (tokenString) {
        headerParameters["Authorization"] = `Bearer ${tokenString}`;
      }
    }
    const response = await this.request({
      path: `/guests/{guestId}`.replace(`{${"guestId"}}`, encodeURIComponent(String(requestParameters.guestId))),
      method: "PATCH",
      headers: headerParameters,
      query: queryParameters,
      body: GuestConfigUpdateToJSON(requestParameters.guestConfigUpdate)
    }, initOverrides);
    return new JSONApiResponse(response, (jsonValue) => GuestResponseFromJSON(jsonValue));
  }
  /**
   * Updates a single guest, for example to change name or email
   * Update a guest
   */
  async updateGuest(requestParameters, initOverrides) {
    const response = await this.updateGuestRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Update a job
   */
  async updateJobRaw(requestParameters, initOverrides) {
    if (requestParameters.jobId === null || requestParameters.jobId === void 0) {
      throw new RequiredError("jobId", "Required parameter requestParameters.jobId was null or undefined when calling updateJob.");
    }
    if (requestParameters.jobUpdate === null || requestParameters.jobUpdate === void 0) {
      throw new RequiredError("jobUpdate", "Required parameter requestParameters.jobUpdate was null or undefined when calling updateJob.");
    }
    const queryParameters = {};
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    if (this.configuration && this.configuration.accessToken) {
      const token = this.configuration.accessToken;
      const tokenString = await token("BearerAuth", []);
      if (tokenString) {
        headerParameters["Authorization"] = `Bearer ${tokenString}`;
      }
    }
    const response = await this.request({
      path: `/jobs/{jobId}`.replace(`{${"jobId"}}`, encodeURIComponent(String(requestParameters.jobId))),
      method: "PATCH",
      headers: headerParameters,
      query: queryParameters,
      body: JobUpdateToJSON(requestParameters.jobUpdate)
    }, initOverrides);
    return new JSONApiResponse(response, (jsonValue) => JobResponseFromJSON(jsonValue));
  }
  /**
   * Update a job
   */
  async updateJob(requestParameters, initOverrides) {
    const response = await this.updateJobRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Update a job\'s entire execution plan
   * Replace a job\'s plan
   */
  async updateJobExecutionPlanByIdRaw(requestParameters, initOverrides) {
    if (requestParameters.jobId === null || requestParameters.jobId === void 0) {
      throw new RequiredError("jobId", "Required parameter requestParameters.jobId was null or undefined when calling updateJobExecutionPlanById.");
    }
    if (requestParameters.updateJobExecutionPlanByIdRequest === null || requestParameters.updateJobExecutionPlanByIdRequest === void 0) {
      throw new RequiredError("updateJobExecutionPlanByIdRequest", "Required parameter requestParameters.updateJobExecutionPlanByIdRequest was null or undefined when calling updateJobExecutionPlanById.");
    }
    const queryParameters = {};
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    if (this.configuration && this.configuration.accessToken) {
      const token = this.configuration.accessToken;
      const tokenString = await token("BearerAuth", []);
      if (tokenString) {
        headerParameters["Authorization"] = `Bearer ${tokenString}`;
      }
    }
    const response = await this.request({
      path: `/jobs/{jobId}/plan`.replace(`{${"jobId"}}`, encodeURIComponent(String(requestParameters.jobId))),
      method: "PUT",
      headers: headerParameters,
      query: queryParameters,
      body: UpdateJobExecutionPlanByIdRequestToJSON(requestParameters.updateJobExecutionPlanByIdRequest)
    }, initOverrides);
    return new JSONApiResponse(response, (jsonValue) => JobPlanResponseFromJSON(jsonValue));
  }
  /**
   * Update a job\'s entire execution plan
   * Replace a job\'s plan
   */
  async updateJobExecutionPlanById(requestParameters, initOverrides) {
    const response = await this.updateJobExecutionPlanByIdRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Update a one or more individual fields on a job\'s execution plan
   * Update a job\'s plan
   */
  async updateJobExecutionPlanFieldsRaw(requestParameters, initOverrides) {
    if (requestParameters.jobId === null || requestParameters.jobId === void 0) {
      throw new RequiredError("jobId", "Required parameter requestParameters.jobId was null or undefined when calling updateJobExecutionPlanFields.");
    }
    if (requestParameters.updateJobExecutionPlanFieldsRequest === null || requestParameters.updateJobExecutionPlanFieldsRequest === void 0) {
      throw new RequiredError("updateJobExecutionPlanFieldsRequest", "Required parameter requestParameters.updateJobExecutionPlanFieldsRequest was null or undefined when calling updateJobExecutionPlanFields.");
    }
    const queryParameters = {};
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    if (this.configuration && this.configuration.accessToken) {
      const token = this.configuration.accessToken;
      const tokenString = await token("BearerAuth", []);
      if (tokenString) {
        headerParameters["Authorization"] = `Bearer ${tokenString}`;
      }
    }
    const response = await this.request({
      path: `/jobs/{jobId}/plan`.replace(`{${"jobId"}}`, encodeURIComponent(String(requestParameters.jobId))),
      method: "PATCH",
      headers: headerParameters,
      query: queryParameters,
      body: UpdateJobExecutionPlanFieldsRequestToJSON(requestParameters.updateJobExecutionPlanFieldsRequest)
    }, initOverrides);
    return new JSONApiResponse(response, (jsonValue) => JobPlanResponseFromJSON(jsonValue));
  }
  /**
   * Update a one or more individual fields on a job\'s execution plan
   * Update a job\'s plan
   */
  async updateJobExecutionPlanFields(requestParameters, initOverrides) {
    const response = await this.updateJobExecutionPlanFieldsRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Updates a prompt
   * Updates a prompt
   */
  async updatePromptRaw(requestParameters, initOverrides) {
    if (requestParameters.promptId === null || requestParameters.promptId === void 0) {
      throw new RequiredError("promptId", "Required parameter requestParameters.promptId was null or undefined when calling updatePrompt.");
    }
    const queryParameters = {};
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    if (this.configuration && this.configuration.accessToken) {
      const token = this.configuration.accessToken;
      const tokenString = await token("BearerAuth", []);
      if (tokenString) {
        headerParameters["Authorization"] = `Bearer ${tokenString}`;
      }
    }
    const response = await this.request({
      path: `/prompts/{promptId}`.replace(`{${"promptId"}}`, encodeURIComponent(String(requestParameters.promptId))),
      method: "PATCH",
      headers: headerParameters,
      query: queryParameters,
      body: PromptConfigToJSON(requestParameters.promptConfig)
    }, initOverrides);
    return new JSONApiResponse(response, (jsonValue) => PromptResponseFromJSON(jsonValue));
  }
  /**
   * Updates a prompt
   * Updates a prompt
   */
  async updatePrompt(requestParameters, initOverrides) {
    const response = await this.updatePromptRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Updates existing records in a workbook sheet
   * Update records
   */
  async updateRecordsRaw(requestParameters, initOverrides) {
    if (requestParameters.sheetId === null || requestParameters.sheetId === void 0) {
      throw new RequiredError("sheetId", "Required parameter requestParameters.sheetId was null or undefined when calling updateRecords.");
    }
    if (requestParameters.recordsUpdates === null || requestParameters.recordsUpdates === void 0) {
      throw new RequiredError("recordsUpdates", "Required parameter requestParameters.recordsUpdates was null or undefined when calling updateRecords.");
    }
    const queryParameters = {};
    if (requestParameters._for !== void 0) {
      queryParameters["for"] = requestParameters._for;
    }
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    if (this.configuration && this.configuration.accessToken) {
      const token = this.configuration.accessToken;
      const tokenString = await token("BearerAuth", []);
      if (tokenString) {
        headerParameters["Authorization"] = `Bearer ${tokenString}`;
      }
    }
    const response = await this.request({
      path: `/sheets/{sheetId}/records`.replace(`{${"sheetId"}}`, encodeURIComponent(String(requestParameters.sheetId))),
      method: "PUT",
      headers: headerParameters,
      query: queryParameters,
      body: RecordsUpdatesToJSON(requestParameters.recordsUpdates)
    }, initOverrides);
    return new JSONApiResponse(response, (jsonValue) => VersionResponseFromJSON(jsonValue));
  }
  /**
   * Updates existing records in a workbook sheet
   * Update records
   */
  async updateRecords(requestParameters, initOverrides) {
    const response = await this.updateRecordsRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Updates existing records in a workbook sheet
   * Update records
   */
  async updateRecordsDeprecatedRaw(requestParameters, initOverrides) {
    if (requestParameters.workbookId === null || requestParameters.workbookId === void 0) {
      throw new RequiredError("workbookId", "Required parameter requestParameters.workbookId was null or undefined when calling updateRecordsDeprecated.");
    }
    if (requestParameters.sheetId === null || requestParameters.sheetId === void 0) {
      throw new RequiredError("sheetId", "Required parameter requestParameters.sheetId was null or undefined when calling updateRecordsDeprecated.");
    }
    if (requestParameters.recordsUpdates === null || requestParameters.recordsUpdates === void 0) {
      throw new RequiredError("recordsUpdates", "Required parameter requestParameters.recordsUpdates was null or undefined when calling updateRecordsDeprecated.");
    }
    const queryParameters = {};
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    if (this.configuration && this.configuration.accessToken) {
      const token = this.configuration.accessToken;
      const tokenString = await token("BearerAuth", []);
      if (tokenString) {
        headerParameters["Authorization"] = `Bearer ${tokenString}`;
      }
    }
    const response = await this.request({
      path: `/workbooks/{workbookId}/sheets/{sheetId}/records`.replace(`{${"workbookId"}}`, encodeURIComponent(String(requestParameters.workbookId))).replace(`{${"sheetId"}}`, encodeURIComponent(String(requestParameters.sheetId))),
      method: "PUT",
      headers: headerParameters,
      query: queryParameters,
      body: RecordsUpdatesToJSON(requestParameters.recordsUpdates)
    }, initOverrides);
    return new JSONApiResponse(response, (jsonValue) => VersionResponseFromJSON(jsonValue));
  }
  /**
   * Updates existing records in a workbook sheet
   * Update records
   */
  async updateRecordsDeprecated(requestParameters, initOverrides) {
    const response = await this.updateRecordsDeprecatedRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   */
  async updateRuleRaw(requestParameters, initOverrides) {
    if (requestParameters.programId === null || requestParameters.programId === void 0) {
      throw new RequiredError("programId", "Required parameter requestParameters.programId was null or undefined when calling updateRule.");
    }
    if (requestParameters.id === null || requestParameters.id === void 0) {
      throw new RequiredError("id", "Required parameter requestParameters.id was null or undefined when calling updateRule.");
    }
    const queryParameters = {};
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    if (this.configuration && this.configuration.accessToken) {
      const token = this.configuration.accessToken;
      const tokenString = await token("BearerAuth", []);
      if (tokenString) {
        headerParameters["Authorization"] = `Bearer ${tokenString}`;
      }
    }
    const response = await this.request({
      path: `/mapping/{programId}/rules/{id}`.replace(`{${"programId"}}`, encodeURIComponent(String(requestParameters.programId))).replace(`{${"id"}}`, encodeURIComponent(String(requestParameters.id))),
      method: "PATCH",
      headers: headerParameters,
      query: queryParameters,
      body: MappingRuleConfigToJSON(requestParameters.mappingRuleConfig)
    }, initOverrides);
    return new JSONApiResponse(response, (jsonValue) => RuleResponseFromJSON(jsonValue));
  }
  /**
   */
  async updateRule(requestParameters, initOverrides) {
    const response = await this.updateRuleRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Update a sheet
   * Update a sheet
   */
  async updateSheetRaw(requestParameters, initOverrides) {
    if (requestParameters.sheetId === null || requestParameters.sheetId === void 0) {
      throw new RequiredError("sheetId", "Required parameter requestParameters.sheetId was null or undefined when calling updateSheet.");
    }
    if (requestParameters.sheetUpdateRequest === null || requestParameters.sheetUpdateRequest === void 0) {
      throw new RequiredError("sheetUpdateRequest", "Required parameter requestParameters.sheetUpdateRequest was null or undefined when calling updateSheet.");
    }
    const queryParameters = {};
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    if (this.configuration && this.configuration.accessToken) {
      const token = this.configuration.accessToken;
      const tokenString = await token("BearerAuth", []);
      if (tokenString) {
        headerParameters["Authorization"] = `Bearer ${tokenString}`;
      }
    }
    const response = await this.request({
      path: `/sheets/{sheetId}`.replace(`{${"sheetId"}}`, encodeURIComponent(String(requestParameters.sheetId))),
      method: "PATCH",
      headers: headerParameters,
      query: queryParameters,
      body: SheetUpdateRequestToJSON(requestParameters.sheetUpdateRequest)
    }, initOverrides);
    return new JSONApiResponse(response, (jsonValue) => SheetResponseFromJSON(jsonValue));
  }
  /**
   * Update a sheet
   * Update a sheet
   */
  async updateSheet(requestParameters, initOverrides) {
    const response = await this.updateSheetRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Update a space, to change the name for example
   * Update a space
   */
  async updateSpaceByIdRaw(requestParameters, initOverrides) {
    if (requestParameters.spaceId === null || requestParameters.spaceId === void 0) {
      throw new RequiredError("spaceId", "Required parameter requestParameters.spaceId was null or undefined when calling updateSpaceById.");
    }
    if (requestParameters.spaceConfig === null || requestParameters.spaceConfig === void 0) {
      throw new RequiredError("spaceConfig", "Required parameter requestParameters.spaceConfig was null or undefined when calling updateSpaceById.");
    }
    const queryParameters = {};
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    if (this.configuration && this.configuration.accessToken) {
      const token = this.configuration.accessToken;
      const tokenString = await token("BearerAuth", []);
      if (tokenString) {
        headerParameters["Authorization"] = `Bearer ${tokenString}`;
      }
    }
    const response = await this.request({
      path: `/spaces/{spaceId}`.replace(`{${"spaceId"}}`, encodeURIComponent(String(requestParameters.spaceId))),
      method: "PATCH",
      headers: headerParameters,
      query: queryParameters,
      body: SpaceConfigToJSON(requestParameters.spaceConfig)
    }, initOverrides);
    return new JSONApiResponse(response, (jsonValue) => SpaceResponseFromJSON(jsonValue));
  }
  /**
   * Update a space, to change the name for example
   * Update a space
   */
  async updateSpaceById(requestParameters, initOverrides) {
    const response = await this.updateSpaceByIdRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Updates an existing Space Config and optionally migrates all spaces using the config
   * Update a space config
   */
  async updateSpaceConfigRaw(requestParameters, initOverrides) {
    if (requestParameters.spacePatternConfig === null || requestParameters.spacePatternConfig === void 0) {
      throw new RequiredError("spacePatternConfig", "Required parameter requestParameters.spacePatternConfig was null or undefined when calling updateSpaceConfig.");
    }
    const queryParameters = {};
    if (requestParameters.migrate !== void 0) {
      queryParameters["migrate"] = requestParameters.migrate;
    }
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    if (this.configuration && this.configuration.accessToken) {
      const token = this.configuration.accessToken;
      const tokenString = await token("BearerAuth", []);
      if (tokenString) {
        headerParameters["Authorization"] = `Bearer ${tokenString}`;
      }
    }
    const response = await this.request({
      path: `/space-configs`,
      method: "PUT",
      headers: headerParameters,
      query: queryParameters,
      body: SpacePatternConfigToJSON(requestParameters.spacePatternConfig)
    }, initOverrides);
    return new JSONApiResponse(response, (jsonValue) => SpaceConfigResponseFromJSON(jsonValue));
  }
  /**
   * Updates an existing Space Config and optionally migrates all spaces using the config
   * Update a space config
   */
  async updateSpaceConfig(requestParameters, initOverrides) {
    const response = await this.updateSpaceConfigRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * updates a single document, for only the body and title
   * Update a document
   */
  async updateSpaceDocumentRaw(requestParameters, initOverrides) {
    if (requestParameters.spaceId === null || requestParameters.spaceId === void 0) {
      throw new RequiredError("spaceId", "Required parameter requestParameters.spaceId was null or undefined when calling updateSpaceDocument.");
    }
    if (requestParameters.documentId === null || requestParameters.documentId === void 0) {
      throw new RequiredError("documentId", "Required parameter requestParameters.documentId was null or undefined when calling updateSpaceDocument.");
    }
    if (requestParameters.documentConfig === null || requestParameters.documentConfig === void 0) {
      throw new RequiredError("documentConfig", "Required parameter requestParameters.documentConfig was null or undefined when calling updateSpaceDocument.");
    }
    const queryParameters = {};
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    if (this.configuration && this.configuration.accessToken) {
      const token = this.configuration.accessToken;
      const tokenString = await token("BearerAuth", []);
      if (tokenString) {
        headerParameters["Authorization"] = `Bearer ${tokenString}`;
      }
    }
    const response = await this.request({
      path: `/spaces/{spaceId}/documents/{documentId}`.replace(`{${"spaceId"}}`, encodeURIComponent(String(requestParameters.spaceId))).replace(`{${"documentId"}}`, encodeURIComponent(String(requestParameters.documentId))),
      method: "PATCH",
      headers: headerParameters,
      query: queryParameters,
      body: DocumentConfigToJSON(requestParameters.documentConfig)
    }, initOverrides);
    return new JSONApiResponse(response, (jsonValue) => DocumentResponseFromJSON(jsonValue));
  }
  /**
   * updates a single document, for only the body and title
   * Update a document
   */
  async updateSpaceDocument(requestParameters, initOverrides) {
    const response = await this.updateSpaceDocumentRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Updates the given subscriptions line items
   */
  async updateSubscriptionRaw(requestParameters, initOverrides) {
    if (requestParameters.subscriptionId === null || requestParameters.subscriptionId === void 0) {
      throw new RequiredError("subscriptionId", "Required parameter requestParameters.subscriptionId was null or undefined when calling updateSubscription.");
    }
    const queryParameters = {};
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    const response = await this.request({
      path: `/billing/subscription/{subscriptionId}`.replace(`{${"subscriptionId"}}`, encodeURIComponent(String(requestParameters.subscriptionId))),
      method: "PUT",
      headers: headerParameters,
      query: queryParameters,
      body: SubscriptionUpdatesBodyToJSON(requestParameters.subscriptionUpdatesBody)
    }, initOverrides);
    return new JSONApiResponse(response, (jsonValue) => BillingSubscriptionResponseFromJSON(jsonValue));
  }
  /**
   * Updates the given subscriptions line items
   */
  async updateSubscription(requestParameters, initOverrides) {
    const response = await this.updateSubscriptionRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Updates a user
   * Update a user
   */
  async updateUserRaw(requestParameters, initOverrides) {
    if (requestParameters.userId === null || requestParameters.userId === void 0) {
      throw new RequiredError("userId", "Required parameter requestParameters.userId was null or undefined when calling updateUser.");
    }
    const queryParameters = {};
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    if (this.configuration && this.configuration.accessToken) {
      const token = this.configuration.accessToken;
      const tokenString = await token("BearerAuth", []);
      if (tokenString) {
        headerParameters["Authorization"] = `Bearer ${tokenString}`;
      }
    }
    const response = await this.request({
      path: `/users/{userId}`.replace(`{${"userId"}}`, encodeURIComponent(String(requestParameters.userId))),
      method: "PATCH",
      headers: headerParameters,
      query: queryParameters,
      body: UpdateUserRequestToJSON(requestParameters.updateUserRequest)
    }, initOverrides);
    return new JSONApiResponse(response, (jsonValue) => UserResponseFromJSON(jsonValue));
  }
  /**
   * Updates a user
   * Update a user
   */
  async updateUser(requestParameters, initOverrides) {
    const response = await this.updateUserRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Updates a view
   * Updates a view
   */
  async updateViewRaw(requestParameters, initOverrides) {
    if (requestParameters.viewId === null || requestParameters.viewId === void 0) {
      throw new RequiredError("viewId", "Required parameter requestParameters.viewId was null or undefined when calling updateView.");
    }
    const queryParameters = {};
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    if (this.configuration && this.configuration.accessToken) {
      const token = this.configuration.accessToken;
      const tokenString = await token("BearerAuth", []);
      if (tokenString) {
        headerParameters["Authorization"] = `Bearer ${tokenString}`;
      }
    }
    const response = await this.request({
      path: `/views/{viewId}`.replace(`{${"viewId"}}`, encodeURIComponent(String(requestParameters.viewId))),
      method: "PATCH",
      headers: headerParameters,
      query: queryParameters,
      body: UpdateViewRequestToJSON(requestParameters.updateViewRequest)
    }, initOverrides);
    return new JSONApiResponse(response, (jsonValue) => ViewResponseFromJSON(jsonValue));
  }
  /**
   * Updates a view
   * Updates a view
   */
  async updateView(requestParameters, initOverrides) {
    const response = await this.updateViewRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Updates a workbook
   * Update a workbook
   */
  async updateWorkbookByIdRaw(requestParameters, initOverrides) {
    if (requestParameters.workbookId === null || requestParameters.workbookId === void 0) {
      throw new RequiredError("workbookId", "Required parameter requestParameters.workbookId was null or undefined when calling updateWorkbookById.");
    }
    const queryParameters = {};
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    if (this.configuration && this.configuration.accessToken) {
      const token = this.configuration.accessToken;
      const tokenString = await token("BearerAuth", []);
      if (tokenString) {
        headerParameters["Authorization"] = `Bearer ${tokenString}`;
      }
    }
    const response = await this.request({
      path: `/workbooks/{workbookId}`.replace(`{${"workbookId"}}`, encodeURIComponent(String(requestParameters.workbookId))),
      method: "PATCH",
      headers: headerParameters,
      query: queryParameters,
      body: WorkbookUpdateToJSON(requestParameters.workbookUpdate)
    }, initOverrides);
    return new JSONApiResponse(response, (jsonValue) => WorkbookResponseFromJSON(jsonValue));
  }
  /**
   * Updates a workbook
   * Update a workbook
   */
  async updateWorkbookById(requestParameters, initOverrides) {
    const response = await this.updateWorkbookByIdRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Upgrade a space
   * Upgrade a space
   */
  async upgradeSpaceByIdRaw(requestParameters, initOverrides) {
    if (requestParameters.spaceId === null || requestParameters.spaceId === void 0) {
      throw new RequiredError("spaceId", "Required parameter requestParameters.spaceId was null or undefined when calling upgradeSpaceById.");
    }
    const queryParameters = {};
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    if (this.configuration && this.configuration.accessToken) {
      const token = this.configuration.accessToken;
      const tokenString = await token("BearerAuth", []);
      if (tokenString) {
        headerParameters["Authorization"] = `Bearer ${tokenString}`;
      }
    }
    const response = await this.request({
      path: `/spaces/{spaceId}/upgrade`.replace(`{${"spaceId"}}`, encodeURIComponent(String(requestParameters.spaceId))),
      method: "POST",
      headers: headerParameters,
      query: queryParameters,
      body: UpgradeSpaceConfigToJSON(requestParameters.upgradeSpaceConfig)
    }, initOverrides);
    return new JSONApiResponse(response, (jsonValue) => SpaceResponseFromJSON(jsonValue));
  }
  /**
   * Upgrade a space
   * Upgrade a space
   */
  async upgradeSpaceById(requestParameters, initOverrides) {
    const response = await this.upgradeSpaceByIdRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Upload a file to S3 storage
   * Upload a file
   */
  async uploadFileRaw(requestParameters, initOverrides) {
    if (requestParameters.workbookId === null || requestParameters.workbookId === void 0) {
      throw new RequiredError("workbookId", "Required parameter requestParameters.workbookId was null or undefined when calling uploadFile.");
    }
    if (requestParameters.uploadConfig === null || requestParameters.uploadConfig === void 0) {
      throw new RequiredError("uploadConfig", "Required parameter requestParameters.uploadConfig was null or undefined when calling uploadFile.");
    }
    const queryParameters = {};
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    const response = await this.request({
      path: `/foreigndb/{workbookId}/storage`.replace(`{${"workbookId"}}`, encodeURIComponent(String(requestParameters.workbookId))),
      method: "POST",
      headers: headerParameters,
      query: queryParameters,
      body: UploadConfigToJSON(requestParameters.uploadConfig)
    }, initOverrides);
    return new JSONApiResponse(response, (jsonValue) => SuccessResponseFromJSON(jsonValue));
  }
  /**
   * Upload a file to S3 storage
   * Upload a file
   */
  async uploadFile(requestParameters, initOverrides) {
    const response = await this.uploadFileRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Upserts a new field to a sheet
   */
  async upsertFieldRaw(requestParameters, initOverrides) {
    if (requestParameters.sheetId === null || requestParameters.sheetId === void 0) {
      throw new RequiredError("sheetId", "Required parameter requestParameters.sheetId was null or undefined when calling upsertField.");
    }
    if (requestParameters.fieldKey === null || requestParameters.fieldKey === void 0) {
      throw new RequiredError("fieldKey", "Required parameter requestParameters.fieldKey was null or undefined when calling upsertField.");
    }
    if (requestParameters.fieldConfig === null || requestParameters.fieldConfig === void 0) {
      throw new RequiredError("fieldConfig", "Required parameter requestParameters.fieldConfig was null or undefined when calling upsertField.");
    }
    const queryParameters = {};
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    if (this.configuration && this.configuration.accessToken) {
      const token = this.configuration.accessToken;
      const tokenString = await token("BearerAuth", []);
      if (tokenString) {
        headerParameters["Authorization"] = `Bearer ${tokenString}`;
      }
    }
    const response = await this.request({
      path: `/sheets/{sheetId}/fields/{fieldKey}`.replace(`{${"sheetId"}}`, encodeURIComponent(String(requestParameters.sheetId))).replace(`{${"fieldKey"}}`, encodeURIComponent(String(requestParameters.fieldKey))),
      method: "PUT",
      headers: headerParameters,
      query: queryParameters,
      body: FieldConfigToJSON(requestParameters.fieldConfig)
    }, initOverrides);
    return new JSONApiResponse(response, (jsonValue) => FieldConfigResponseFromJSON(jsonValue));
  }
  /**
   * Upserts a new field to a sheet
   */
  async upsertField(requestParameters, initOverrides) {
    const response = await this.upsertFieldRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Insert or Update a Secret by name for environment or space
   * Upsert a Secret
   */
  async upsertSecretRaw(requestParameters, initOverrides) {
    const queryParameters = {};
    const headerParameters = {};
    headerParameters["Content-Type"] = "application/json";
    if (this.configuration && this.configuration.accessToken) {
      const token = this.configuration.accessToken;
      const tokenString = await token("BearerAuth", []);
      if (tokenString) {
        headerParameters["Authorization"] = `Bearer ${tokenString}`;
      }
    }
    const response = await this.request({
      path: `/secrets`,
      method: "POST",
      headers: headerParameters,
      query: queryParameters,
      body: WriteSecretToJSON(requestParameters.writeSecret)
    }, initOverrides);
    return new JSONApiResponse(response, (jsonValue) => SecretsResponseFromJSON(jsonValue));
  }
  /**
   * Insert or Update a Secret by name for environment or space
   * Upsert a Secret
   */
  async upsertSecret(requestParameters = {}, initOverrides) {
    const response = await this.upsertSecretRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Trigger data hooks and validation to run on a sheet
   * Validate sheet
   */
  async validateSheetRaw(requestParameters, initOverrides) {
    if (requestParameters.sheetId === null || requestParameters.sheetId === void 0) {
      throw new RequiredError("sheetId", "Required parameter requestParameters.sheetId was null or undefined when calling validateSheet.");
    }
    const queryParameters = {};
    const headerParameters = {};
    if (this.configuration && this.configuration.accessToken) {
      const token = this.configuration.accessToken;
      const tokenString = await token("BearerAuth", []);
      if (tokenString) {
        headerParameters["Authorization"] = `Bearer ${tokenString}`;
      }
    }
    const response = await this.request({
      path: `/sheets/{sheetId}/validate`.replace(`{${"sheetId"}}`, encodeURIComponent(String(requestParameters.sheetId))),
      method: "POST",
      headers: headerParameters,
      query: queryParameters
    }, initOverrides);
    return new JSONApiResponse(response, (jsonValue) => SuccessResponseFromJSON(jsonValue));
  }
  /**
   * Trigger data hooks and validation to run on a sheet
   * Validate sheet
   */
  async validateSheet(requestParameters, initOverrides) {
    const response = await this.validateSheetRaw(requestParameters, initOverrides);
    return await response.value();
  }
  /**
   * Trigger data hooks and validation to run on a sheet
   * Validate sheet
   */
  async validateSheetDeprecatedRaw(requestParameters, initOverrides) {
    if (requestParameters.workbookId === null || requestParameters.workbookId === void 0) {
      throw new RequiredError("workbookId", "Required parameter requestParameters.workbookId was null or undefined when calling validateSheetDeprecated.");
    }
    if (requestParameters.sheetId === null || requestParameters.sheetId === void 0) {
      throw new RequiredError("sheetId", "Required parameter requestParameters.sheetId was null or undefined when calling validateSheetDeprecated.");
    }
    const queryParameters = {};
    const headerParameters = {};
    if (this.configuration && this.configuration.accessToken) {
      const token = this.configuration.accessToken;
      const tokenString = await token("BearerAuth", []);
      if (tokenString) {
        headerParameters["Authorization"] = `Bearer ${tokenString}`;
      }
    }
    const response = await this.request({
      path: `/workbooks/{workbookId}/sheets/{sheetId}/validate`.replace(`{${"workbookId"}}`, encodeURIComponent(String(requestParameters.workbookId))).replace(`{${"sheetId"}}`, encodeURIComponent(String(requestParameters.sheetId))),
      method: "POST",
      headers: headerParameters,
      query: queryParameters
    }, initOverrides);
    return new JSONApiResponse(response, (jsonValue) => SuccessResponseFromJSON(jsonValue));
  }
  /**
   * Trigger data hooks and validation to run on a sheet
   * Validate sheet
   */
  async validateSheetDeprecated(requestParameters, initOverrides) {
    const response = await this.validateSheetDeprecatedRaw(requestParameters, initOverrides);
    return await response.value();
  }
};
var CreateNewApiKeyTypeEnum = {
  Publishable: "PUBLISHABLE",
  Secret: "SECRET"
};
var GetSnapshotRecordsChangeTypeEnum = {
  CreatedSince: "createdSince",
  UpdatedSince: "updatedSince",
  DeletedSince: "deletedSince"
};
export {
  AccessTokenFromJSON,
  AccessTokenFromJSONTyped,
  AccessTokenResponseFromJSON,
  AccessTokenResponseFromJSONTyped,
  AccessTokenResponseToJSON,
  AccessTokenToJSON,
  AccountFromJSON,
  AccountFromJSONTyped,
  AccountResponseFromJSON,
  AccountResponseFromJSONTyped,
  AccountResponseToJSON,
  AccountToJSON,
  ActionConstraintFromJSON,
  ActionConstraintFromJSONTyped,
  ActionConstraintToJSON,
  ActionFromJSON,
  ActionFromJSONTyped,
  ActionInputFormFromJSON,
  ActionInputFormFromJSONTyped,
  ActionInputFormToJSON,
  ActionInputFormTypeEnum,
  ActionMessageFromJSON,
  ActionMessageFromJSONTyped,
  ActionMessageToJSON,
  ActionMessageTypeEnum,
  ActionModeEnum,
  ActionScheduleEnum,
  ActionToJSON,
  ActivitiesResponseFromJSON,
  ActivitiesResponseFromJSONTyped,
  ActivitiesResponseToJSON,
  ActivityContextFromJSON,
  ActivityContextFromJSONTyped,
  ActivityContextSlugsFromJSON,
  ActivityContextSlugsFromJSONTyped,
  ActivityContextSlugsToJSON,
  ActivityContextToJSON,
  ActivityFromJSON,
  ActivityFromJSONTyped,
  ActivityToJSON,
  ActorIdFromJSON,
  ActorIdFromJSONTyped,
  ActorIdToJSON,
  ActorResourceFromJSON,
  ActorResourceFromJSONTyped,
  ActorResourceToJSON,
  ActorRoleResponseFromJSON,
  ActorRoleResponseFromJSONTyped,
  ActorRoleResponseToJSON,
  AgentCompilerEnum,
  AgentConfigCompilerEnum,
  AgentConfigFromJSON,
  AgentConfigFromJSONTyped,
  AgentConfigToJSON,
  AgentFromJSON,
  AgentFromJSONTyped,
  AgentLogFromJSON,
  AgentLogFromJSONTyped,
  AgentLogResponseFromJSON,
  AgentLogResponseFromJSONTyped,
  AgentLogResponseToJSON,
  AgentLogToJSON,
  AgentLogsResponse1FromJSON,
  AgentLogsResponse1FromJSONTyped,
  AgentLogsResponse1ToJSON,
  AgentLogsResponseFromJSON,
  AgentLogsResponseFromJSONTyped,
  AgentLogsResponseToJSON,
  AgentResponseFromJSON,
  AgentResponseFromJSONTyped,
  AgentResponseToJSON,
  AgentStatsFromJSON,
  AgentStatsFromJSONTyped,
  AgentStatsToJSON,
  AgentToJSON,
  AgentsResponseFromJSON,
  AgentsResponseFromJSONTyped,
  AgentsResponseToJSON,
  ApiCredentialsFromJSON,
  ApiCredentialsFromJSONTyped,
  ApiCredentialsToJSON,
  ApiKeyFromJSON,
  ApiKeyFromJSONTyped,
  ApiKeyOperationsInnerFromJSON,
  ApiKeyOperationsInnerFromJSONTyped,
  ApiKeyOperationsInnerToJSON,
  ApiKeyToJSON,
  ApiKeyTypeEnum,
  ApiKeysResponseFromJSON,
  ApiKeysResponseFromJSONTyped,
  ApiKeysResponseToJSON,
  ApiTokenFromJSON,
  ApiTokenFromJSONTyped,
  ApiTokenResponseFromJSON,
  ApiTokenResponseFromJSONTyped,
  ApiTokenResponseToJSON,
  ApiTokenToJSON,
  ApiTokensResponseFromJSON,
  ApiTokensResponseFromJSONTyped,
  ApiTokensResponseToJSON,
  AppVersionResponseFromJSON,
  AppVersionResponseFromJSONTyped,
  AppVersionResponseToJSON,
  ArrayablePropertyFromJSON,
  ArrayablePropertyFromJSONTyped,
  ArrayablePropertyToJSON,
  AssignActorRoleRequestFromJSON,
  AssignActorRoleRequestFromJSONTyped,
  AssignActorRoleRequestToJSON,
  AssignRoleResponseFromJSON,
  AssignRoleResponseFromJSONTyped,
  AssignRoleResponseToJSON,
  AuditActivity,
  AuditActivityFromJSON,
  AuditActivityFromJSONTyped,
  AuditActivityToJSON,
  BASE_PATH,
  BaseAPI,
  BaseActionConstraintFromJSON,
  BaseActionConstraintFromJSONTyped,
  BaseActionConstraintToJSON,
  BaseActionConstraintTypeEnum,
  BaseConstraintFromJSON,
  BaseConstraintFromJSONTyped,
  BaseConstraintToJSON,
  BaseConstraintTypeEnum,
  BasePropertyAppearanceFromJSON,
  BasePropertyAppearanceFromJSONTyped,
  BasePropertyAppearanceSizeEnum,
  BasePropertyAppearanceToJSON,
  BasePropertyFromJSON,
  BasePropertyFromJSONTyped,
  BasePropertyToJSON,
  BasePropertyTypeEnum,
  BaseSideBarConfigFromJSON,
  BaseSideBarConfigFromJSONTyped,
  BaseSideBarConfigToJSON,
  BaseSideBarConfigTypeEnum,
  BillingLimitsFromJSON,
  BillingLimitsFromJSONTyped,
  BillingLimitsResponseFromJSON,
  BillingLimitsResponseFromJSONTyped,
  BillingLimitsResponseToJSON,
  BillingLimitsToJSON,
  BillingPlanWithUsageFromJSON,
  BillingPlanWithUsageFromJSONTyped,
  BillingPlanWithUsageResponseFromJSON,
  BillingPlanWithUsageResponseFromJSONTyped,
  BillingPlanWithUsageResponseToJSON,
  BillingPlanWithUsageToJSON,
  BillingSubscriptionFromJSON,
  BillingSubscriptionFromJSONTyped,
  BillingSubscriptionItemFromJSON,
  BillingSubscriptionItemFromJSONTyped,
  BillingSubscriptionItemToJSON,
  BillingSubscriptionResponseFromJSON,
  BillingSubscriptionResponseFromJSONTyped,
  BillingSubscriptionResponseToJSON,
  BillingSubscriptionToJSON,
  BillingSubscriptionsResponseFromJSON,
  BillingSubscriptionsResponseFromJSONTyped,
  BillingSubscriptionsResponseToJSON,
  BlobApiResponse,
  BlueprintFromJSON,
  BlueprintFromJSONTyped,
  BlueprintToJSON,
  BooleanPropertyAllOfConfigFromJSON,
  BooleanPropertyAllOfConfigFromJSONTyped,
  BooleanPropertyAllOfConfigToJSON,
  BooleanPropertyAllOfFromJSON,
  BooleanPropertyAllOfFromJSONTyped,
  BooleanPropertyAllOfToJSON,
  BooleanPropertyAllOfTypeEnum,
  BooleanPropertyFromJSON,
  BooleanPropertyFromJSONTyped,
  BooleanPropertyToJSON,
  BooleanPropertyTypeEnum,
  COLLECTION_FORMATS,
  CellValueFromJSON,
  CellValueFromJSONTyped,
  CellValueToJSON,
  CellValueWithCountsAllOfFromJSON,
  CellValueWithCountsAllOfFromJSONTyped,
  CellValueWithCountsAllOfToJSON,
  CellValueWithCountsFromJSON,
  CellValueWithCountsFromJSONTyped,
  CellValueWithCountsToJSON,
  CellValueWithLinksFromJSON,
  CellValueWithLinksFromJSONTyped,
  CellValueWithLinksToJSON,
  CellsResponseDataFromJSON,
  CellsResponseDataFromJSONTyped,
  CellsResponseDataToJSON,
  CellsResponseFromJSON,
  CellsResponseFromJSONTyped,
  CellsResponseToJSON,
  CheckoutSessionBodyFromJSON,
  CheckoutSessionBodyFromJSONTyped,
  CheckoutSessionBodyToJSON,
  CheckoutSessionFromJSON,
  CheckoutSessionFromJSONTyped,
  CheckoutSessionToJSON,
  CollectionJobSubjectFromJSON,
  CollectionJobSubjectFromJSONTyped,
  CollectionJobSubjectToJSON,
  CommitFromJSON,
  CommitFromJSONTyped,
  CommitToJSON,
  CompositeUniqueConstraintAllOfFromJSON,
  CompositeUniqueConstraintAllOfFromJSONTyped,
  CompositeUniqueConstraintAllOfStrategyEnum,
  CompositeUniqueConstraintAllOfToJSON,
  CompositeUniqueConstraintAllOfTypeEnum,
  CompositeUniqueConstraintFromJSON,
  CompositeUniqueConstraintFromJSONTyped,
  CompositeUniqueConstraintStrategyEnum,
  CompositeUniqueConstraintToJSON,
  CompositeUniqueConstraintTypeEnum,
  ComputedConstraintAllOfFromJSON,
  ComputedConstraintAllOfFromJSONTyped,
  ComputedConstraintAllOfToJSON,
  ComputedConstraintAllOfTypeEnum,
  ComputedConstraintFromJSON,
  ComputedConstraintFromJSONTyped,
  ComputedConstraintToJSON,
  ComputedConstraintTypeEnum,
  Configuration,
  ConnectionLinkBodyFromJSON,
  ConnectionLinkBodyFromJSONTyped,
  ConnectionLinkBodyToJSON,
  ConnectionLinkFromJSON,
  ConnectionLinkFromJSONTyped,
  ConnectionLinkToJSON,
  ConstraintFromJSON,
  ConstraintFromJSONTyped,
  ConstraintToJSON,
  CreateAccessTokenRequestFromJSON,
  CreateAccessTokenRequestFromJSONTyped,
  CreateAccessTokenRequestToJSON,
  CreateCheckoutSessionResponseFromJSON,
  CreateCheckoutSessionResponseFromJSONTyped,
  CreateCheckoutSessionResponseToJSON,
  CreateCommitIdRequestFromJSON,
  CreateCommitIdRequestFromJSONTyped,
  CreateCommitIdRequestToJSON,
  CreateConnectionLinkResponseFromJSON,
  CreateConnectionLinkResponseFromJSONTyped,
  CreateConnectionLinkResponseToJSON,
  CreateEnvironmentRequestDataRetentionPolicyEnum,
  CreateEnvironmentRequestFromJSON,
  CreateEnvironmentRequestFromJSONTyped,
  CreateEnvironmentRequestToJSON,
  CreateMappingRequestFromJSON,
  CreateMappingRequestFromJSONTyped,
  CreateMappingRequestToJSON,
  CreateNewApiKeyTypeEnum,
  CreateVersionIdRequestFromJSON,
  CreateVersionIdRequestFromJSONTyped,
  CreateVersionIdRequestToJSON,
  CreateViewRequestFromJSON,
  CreateViewRequestFromJSONTyped,
  CreateViewRequestToJSON,
  DBUserFromJSON,
  DBUserFromJSONTyped,
  DBUserToJSON,
  DataRetentionPolicyConfigFromJSON,
  DataRetentionPolicyConfigFromJSONTyped,
  DataRetentionPolicyConfigToJSON,
  DataRetentionPolicyConfigUpdateFromJSON,
  DataRetentionPolicyConfigUpdateFromJSONTyped,
  DataRetentionPolicyConfigUpdateToJSON,
  DataRetentionPolicyEnum,
  DataRetentionPolicyEnumFromJSON,
  DataRetentionPolicyEnumFromJSONTyped,
  DataRetentionPolicyEnumToJSON,
  DataRetentionPolicyFromJSON,
  DataRetentionPolicyFromJSONTyped,
  DataRetentionPolicyResponseFromJSON,
  DataRetentionPolicyResponseFromJSONTyped,
  DataRetentionPolicyResponseToJSON,
  DataRetentionPolicyToJSON,
  DatePropertyAllOfFromJSON,
  DatePropertyAllOfFromJSONTyped,
  DatePropertyAllOfToJSON,
  DatePropertyAllOfTypeEnum,
  DatePropertyFromJSON,
  DatePropertyFromJSONTyped,
  DatePropertyToJSON,
  DatePropertyTypeEnum,
  DefaultApi,
  DefaultConfig,
  DeleteRecordsJobConfigFromJSON,
  DeleteRecordsJobConfigFromJSONTyped,
  DeleteRecordsJobConfigToJSON,
  DeleteRulesRequestFromJSON,
  DeleteRulesRequestFromJSONTyped,
  DeleteRulesRequestToJSON,
  DestinationFieldFromJSON,
  DestinationFieldFromJSONTyped,
  DestinationFieldToJSON,
  DetailedAgentLogFromJSON,
  DetailedAgentLogFromJSONTyped,
  DetailedAgentLogToJSON,
  DiffDataFromJSON,
  DiffDataFromJSONTyped,
  DiffDataToJSON,
  DiffRecordFromJSON,
  DiffRecordFromJSONTyped,
  DiffRecordToJSON,
  DiffRecordsResponseDataFromJSON,
  DiffRecordsResponseDataFromJSONTyped,
  DiffRecordsResponseDataToJSON,
  DiffRecordsResponseFromJSON,
  DiffRecordsResponseFromJSONTyped,
  DiffRecordsResponseToJSON,
  DiffValueFromJSON,
  DiffValueFromJSONTyped,
  DiffValueToJSON,
  DocumentConfigFromJSON,
  DocumentConfigFromJSONTyped,
  DocumentConfigToJSON,
  DocumentFromJSON,
  DocumentFromJSONTyped,
  DocumentResponseFromJSON,
  DocumentResponseFromJSONTyped,
  DocumentResponseToJSON,
  DocumentSidebarConfigAllOfFromJSON,
  DocumentSidebarConfigAllOfFromJSONTyped,
  DocumentSidebarConfigAllOfToJSON,
  DocumentSidebarConfigFromJSON,
  DocumentSidebarConfigFromJSONTyped,
  DocumentSidebarConfigToJSON,
  DocumentSidebarConfigTypeEnum,
  DocumentToJSON,
  DocumentsResponseFromJSON,
  DocumentsResponseFromJSONTyped,
  DocumentsResponseToJSON,
  EdgeFromJSON,
  EdgeFromJSONTyped,
  EdgeMetadataFromJSON,
  EdgeMetadataFromJSONTyped,
  EdgeMetadataToJSON,
  EdgeToJSON,
  EntitlementFromJSON,
  EntitlementFromJSONTyped,
  EntitlementToJSON,
  EntitlementsResponseFromJSON,
  EntitlementsResponseFromJSONTyped,
  EntitlementsResponseToJSON,
  EnumDetailsFromJSON,
  EnumDetailsFromJSONTyped,
  EnumDetailsToJSON,
  EnumListPropertyAllOfFromJSON,
  EnumListPropertyAllOfFromJSONTyped,
  EnumListPropertyAllOfToJSON,
  EnumListPropertyAllOfTypeEnum,
  EnumListPropertyFromJSON,
  EnumListPropertyFromJSONTyped,
  EnumListPropertyToJSON,
  EnumListPropertyTypeEnum,
  EnumMappingFieldFromJSON,
  EnumMappingFieldFromJSONTyped,
  EnumMappingFieldToJSON,
  EnumPropertyAllOfConfigFromJSON,
  EnumPropertyAllOfConfigFromJSONTyped,
  EnumPropertyAllOfConfigToJSON,
  EnumPropertyAllOfFromJSON,
  EnumPropertyAllOfFromJSONTyped,
  EnumPropertyAllOfToJSON,
  EnumPropertyAllOfTypeEnum,
  EnumPropertyFromJSON,
  EnumPropertyFromJSONTyped,
  EnumPropertyOptionFromJSON,
  EnumPropertyOptionFromJSONTyped,
  EnumPropertyOptionToJSON,
  EnumPropertyOptionValueFromJSON,
  EnumPropertyOptionValueFromJSONTyped,
  EnumPropertyOptionValueToJSON,
  EnumPropertyToJSON,
  EnumPropertyTypeEnum,
  EnvironmentConfigDataRetentionPolicyEnum,
  EnvironmentConfigFromJSON,
  EnvironmentConfigFromJSONTyped,
  EnvironmentConfigToJSON,
  EnvironmentDataRetentionPolicyEnum,
  EnvironmentFromJSON,
  EnvironmentFromJSONTyped,
  EnvironmentResponseFromJSON,
  EnvironmentResponseFromJSONTyped,
  EnvironmentResponseToJSON,
  EnvironmentStatsResponseDataFromJSON,
  EnvironmentStatsResponseDataFromJSONTyped,
  EnvironmentStatsResponseDataToJSON,
  EnvironmentStatsResponseFromJSON,
  EnvironmentStatsResponseFromJSONTyped,
  EnvironmentStatsResponseToJSON,
  EnvironmentToJSON,
  EnvironmentsResponseFromJSON,
  EnvironmentsResponseFromJSONTyped,
  EnvironmentsResponseToJSON,
  ErrorResponseErrorsInnerFromJSON,
  ErrorResponseErrorsInnerFromJSONTyped,
  ErrorResponseErrorsInnerToJSON,
  ErrorResponseFromJSON,
  ErrorResponseFromJSONTyped,
  ErrorResponseToJSON,
  EventConfigAttributesFromJSON,
  EventConfigAttributesFromJSONTyped,
  EventConfigAttributesProgressFromJSON,
  EventConfigAttributesProgressFromJSONTyped,
  EventConfigAttributesProgressToJSON,
  EventConfigAttributesToJSON,
  EventConfigDomainEnum,
  EventConfigFromJSON,
  EventConfigFromJSONTyped,
  EventConfigOriginFromJSON,
  EventConfigOriginFromJSONTyped,
  EventConfigOriginToJSON,
  EventConfigToJSON,
  EventDomainEnum,
  EventFromJSON,
  EventFromJSONTyped,
  EventResponseFromJSON,
  EventResponseFromJSONTyped,
  EventResponseToJSON,
  EventToJSON,
  EventTokenFromJSON,
  EventTokenFromJSONTyped,
  EventTokenResponseFromJSON,
  EventTokenResponseFromJSONTyped,
  EventTokenResponseToJSON,
  EventTokenToJSON,
  EventTopic,
  EventTopicFromJSON,
  EventTopicFromJSONTyped,
  EventTopicToJSON,
  EventsResponseFromJSON,
  EventsResponseFromJSONTyped,
  EventsResponseToJSON,
  ExchangeInvitationRequestFromJSON,
  ExchangeInvitationRequestFromJSONTyped,
  ExchangeInvitationRequestToJSON,
  ExchangeTokenResponseDataFromJSON,
  ExchangeTokenResponseDataFromJSONTyped,
  ExchangeTokenResponseDataToJSON,
  ExchangeTokenResponseFromJSON,
  ExchangeTokenResponseFromJSONTyped,
  ExchangeTokenResponseToJSON,
  ExecutionFromJSON,
  ExecutionFromJSONTyped,
  ExecutionToJSON,
  ExecutionsResponseFromJSON,
  ExecutionsResponseFromJSONTyped,
  ExecutionsResponseToJSON,
  ExportJobConfigFromJSON,
  ExportJobConfigFromJSONTyped,
  ExportJobConfigToJSON,
  ExportOptionsFromJSON,
  ExportOptionsFromJSONTyped,
  ExportOptionsToJSON,
  ExternalConstraintAllOfFromJSON,
  ExternalConstraintAllOfFromJSONTyped,
  ExternalConstraintAllOfToJSON,
  ExternalConstraintAllOfTypeEnum,
  ExternalConstraintFromJSON,
  ExternalConstraintFromJSONTyped,
  ExternalConstraintToJSON,
  ExternalConstraintTypeEnum,
  ExternalSheetConstraintAllOfFromJSON,
  ExternalSheetConstraintAllOfFromJSONTyped,
  ExternalSheetConstraintAllOfToJSON,
  ExternalSheetConstraintAllOfTypeEnum,
  ExternalSheetConstraintFromJSON,
  ExternalSheetConstraintFromJSONTyped,
  ExternalSheetConstraintToJSON,
  ExternalSheetConstraintTypeEnum,
  FetchError,
  FieldConfigFromJSON,
  FieldConfigFromJSONTyped,
  FieldConfigResponseFromJSON,
  FieldConfigResponseFromJSONTyped,
  FieldConfigResponseToJSON,
  FieldConfigToJSON,
  FieldRecordCountsFromJSON,
  FieldRecordCountsFromJSONTyped,
  FieldRecordCountsToJSON,
  FileConfigFromJSON,
  FileConfigFromJSONTyped,
  FileConfigToJSON,
  FileJobConfigDriverEnum,
  FileJobConfigFromJSON,
  FileJobConfigFromJSONTyped,
  FileJobConfigToJSON,
  FileMode,
  FileModeFromJSON,
  FileModeFromJSONTyped,
  FileModeToJSON,
  FileOrigin,
  FileOriginFromJSON,
  FileOriginFromJSONTyped,
  FileOriginToJSON,
  FileResponseFromJSON,
  FileResponseFromJSONTyped,
  FileResponseToJSON,
  FilesResponseFromJSON,
  FilesResponseFromJSONTyped,
  FilesResponseToJSON,
  Filter,
  FilterFromJSON,
  FilterFromJSONTyped,
  FilterToJSON,
  FindAndReplaceJobConfigFromJSON,
  FindAndReplaceJobConfigFromJSONTyped,
  FindAndReplaceJobConfigToJSON,
  FindAndReplaceRequestFromJSON,
  FindAndReplaceRequestFromJSONTyped,
  FindAndReplaceRequestToJSON,
  FronteggSignupFromJSON,
  FronteggSignupFromJSONTyped,
  FronteggSignupToJSON,
  FronteggUserFromJSON,
  FronteggUserFromJSONTyped,
  FronteggUserToJSON,
  GenerateMutationConfigFromJSON,
  GenerateMutationConfigFromJSONTyped,
  GenerateMutationConfigToJSON,
  GenerateQueryConfigFromJSON,
  GenerateQueryConfigFromJSONTyped,
  GenerateQueryConfigToJSON,
  GeneratedMutationFromJSON,
  GeneratedMutationFromJSONTyped,
  GeneratedMutationMetadataFromJSON,
  GeneratedMutationMetadataFromJSONTyped,
  GeneratedMutationMetadataToJSON,
  GeneratedMutationResponseFromJSON,
  GeneratedMutationResponseFromJSONTyped,
  GeneratedMutationResponseToJSON,
  GeneratedMutationToJSON,
  GeneratedQueryFromJSON,
  GeneratedQueryFromJSONTyped,
  GeneratedQueryMetadataFromJSON,
  GeneratedQueryMetadataFromJSONTyped,
  GeneratedQueryMetadataToJSON,
  GeneratedQueryResponseFromJSON,
  GeneratedQueryResponseFromJSONTyped,
  GeneratedQueryResponseToJSON,
  GeneratedQueryToJSON,
  GetCommitResponseFromJSON,
  GetCommitResponseFromJSONTyped,
  GetCommitResponseToJSON,
  GetDatabaseDataFromJSON,
  GetDatabaseDataFromJSONTyped,
  GetDatabaseDataTaskFromJSON,
  GetDatabaseDataTaskFromJSONTyped,
  GetDatabaseDataTaskToJSON,
  GetDatabaseDataToJSON,
  GetDatabaseResponseResponseFromJSON,
  GetDatabaseResponseResponseFromJSONTyped,
  GetDatabaseResponseResponseToJSON,
  GetEnumWeightsRequestFromJSON,
  GetEnumWeightsRequestFromJSONTyped,
  GetEnumWeightsRequestToJSON,
  GetFieldWeightsRequestFromJSON,
  GetFieldWeightsRequestFromJSONTyped,
  GetFieldWeightsRequestToJSON,
  GetRecordsResponseDataAllOfFromJSON,
  GetRecordsResponseDataAllOfFromJSONTyped,
  GetRecordsResponseDataAllOfToJSON,
  GetRecordsResponseDataFromJSON,
  GetRecordsResponseDataFromJSONTyped,
  GetRecordsResponseDataToJSON,
  GetRecordsResponseFromJSON,
  GetRecordsResponseFromJSONTyped,
  GetRecordsResponseToJSON,
  GetSheetCommitsResponseFromJSON,
  GetSheetCommitsResponseFromJSONTyped,
  GetSheetCommitsResponseToJSON,
  GetSnapshotRecordsChangeTypeEnum,
  GetSpaceActivitySortField,
  GetSpaceActivitySortFieldFromJSON,
  GetSpaceActivitySortFieldFromJSONTyped,
  GetSpaceActivitySortFieldToJSON,
  GetSpacesSortField,
  GetSpacesSortFieldFromJSON,
  GetSpacesSortFieldFromJSONTyped,
  GetSpacesSortFieldToJSON,
  GetUserResponseFromJSON,
  GetUserResponseFromJSONTyped,
  GetUserResponseToJSON,
  GetWorkbookCommitsResponseFromJSON,
  GetWorkbookCommitsResponseFromJSONTyped,
  GetWorkbookCommitsResponseToJSON,
  GuestAuthenticationEnum,
  GuestAuthenticationEnumFromJSON,
  GuestAuthenticationEnumFromJSONTyped,
  GuestAuthenticationEnumToJSON,
  GuestConfigFromJSON,
  GuestConfigFromJSONTyped,
  GuestConfigSpacesInnerFromJSON,
  GuestConfigSpacesInnerFromJSONTyped,
  GuestConfigSpacesInnerToJSON,
  GuestConfigSpacesInnerWorkbooksInnerFromJSON,
  GuestConfigSpacesInnerWorkbooksInnerFromJSONTyped,
  GuestConfigSpacesInnerWorkbooksInnerToJSON,
  GuestConfigToJSON,
  GuestConfigUpdateFromJSON,
  GuestConfigUpdateFromJSONTyped,
  GuestConfigUpdateToJSON,
  GuestFromJSON,
  GuestFromJSONTyped,
  GuestResponseFromJSON,
  GuestResponseFromJSONTyped,
  GuestResponseToJSON,
  GuestToJSON,
  GuestsResponseFromJSON,
  GuestsResponseFromJSONTyped,
  GuestsResponseToJSON,
  HasAllValidConstraintAllOfFromJSON,
  HasAllValidConstraintAllOfFromJSONTyped,
  HasAllValidConstraintAllOfToJSON,
  HasAllValidConstraintAllOfTypeEnum,
  HasAllValidConstraintFromJSON,
  HasAllValidConstraintFromJSONTyped,
  HasAllValidConstraintToJSON,
  HasAllValidConstraintTypeEnum,
  HasDataConstraintAllOfFromJSON,
  HasDataConstraintAllOfFromJSONTyped,
  HasDataConstraintAllOfToJSON,
  HasDataConstraintAllOfTypeEnum,
  HasDataConstraintFromJSON,
  HasDataConstraintFromJSONTyped,
  HasDataConstraintToJSON,
  HasDataConstraintTypeEnum,
  HasSelectionConstraintAllOfFromJSON,
  HasSelectionConstraintAllOfFromJSONTyped,
  HasSelectionConstraintAllOfToJSON,
  HasSelectionConstraintAllOfTypeEnum,
  HasSelectionConstraintFromJSON,
  HasSelectionConstraintFromJSONTyped,
  HasSelectionConstraintToJSON,
  HasSelectionConstraintTypeEnum,
  HealthResponseFromJSON,
  HealthResponseFromJSONTyped,
  HealthResponseToJSON,
  InputConstraintFromJSON,
  InputConstraintFromJSONTyped,
  InputConstraintToJSON,
  InputFieldConfigFromJSON,
  InputFieldConfigFromJSONTyped,
  InputFieldConfigToJSON,
  InputFieldDefaultValueFromJSON,
  InputFieldDefaultValueFromJSONTyped,
  InputFieldDefaultValueToJSON,
  InputFieldFromJSON,
  InputFieldFromJSONTyped,
  InputFieldToJSON,
  InputFieldTypeEnum,
  InviteGuestsRequestInnerFromJSON,
  InviteGuestsRequestInnerFromJSONTyped,
  InviteGuestsRequestInnerToJSON,
  JSONApiResponse,
  JobAckDetailsFromJSON,
  JobAckDetailsFromJSONTyped,
  JobAckDetailsToJSON,
  JobCancelDetailsFromJSON,
  JobCancelDetailsFromJSONTyped,
  JobCancelDetailsToJSON,
  JobCompleteDetailsFromJSON,
  JobCompleteDetailsFromJSONTyped,
  JobCompleteDetailsToJSON,
  JobConfigConfigFromJSON,
  JobConfigConfigFromJSONTyped,
  JobConfigConfigToJSON,
  JobConfigFromJSON,
  JobConfigFromJSONTyped,
  JobConfigModeEnum,
  JobConfigPartExecutionEnum,
  JobConfigStatusEnum,
  JobConfigToJSON,
  JobConfigTriggerEnum,
  JobConfigTypeEnum,
  JobFromJSON,
  JobFromJSONTyped,
  JobModeEnum,
  JobOutcomeFromJSON,
  JobOutcomeFromJSONTyped,
  JobOutcomeNextDownloadFromJSON,
  JobOutcomeNextDownloadFromJSONTyped,
  JobOutcomeNextDownloadToJSON,
  JobOutcomeNextDownloadTypeEnum,
  JobOutcomeNextFilesFilesInnerFromJSON,
  JobOutcomeNextFilesFilesInnerFromJSONTyped,
  JobOutcomeNextFilesFilesInnerToJSON,
  JobOutcomeNextFilesFromJSON,
  JobOutcomeNextFilesFromJSONTyped,
  JobOutcomeNextFilesToJSON,
  JobOutcomeNextFilesTypeEnum,
  JobOutcomeNextFromJSON,
  JobOutcomeNextFromJSONTyped,
  JobOutcomeNextIdFromJSON,
  JobOutcomeNextIdFromJSONTyped,
  JobOutcomeNextIdToJSON,
  JobOutcomeNextIdTypeEnum,
  JobOutcomeNextRetryFromJSON,
  JobOutcomeNextRetryFromJSONTyped,
  JobOutcomeNextRetryToJSON,
  JobOutcomeNextRetryTypeEnum,
  JobOutcomeNextSnapshotFromJSON,
  JobOutcomeNextSnapshotFromJSONTyped,
  JobOutcomeNextSnapshotToJSON,
  JobOutcomeNextSnapshotTypeEnum,
  JobOutcomeNextToJSON,
  JobOutcomeNextType,
  JobOutcomeNextTypeFromJSON,
  JobOutcomeNextTypeFromJSONTyped,
  JobOutcomeNextTypeToJSON,
  JobOutcomeNextUrlFromJSON,
  JobOutcomeNextUrlFromJSONTyped,
  JobOutcomeNextUrlToJSON,
  JobOutcomeNextUrlTypeEnum,
  JobOutcomeNextViewFromJSON,
  JobOutcomeNextViewFromJSONTyped,
  JobOutcomeNextViewToJSON,
  JobOutcomeNextViewTypeEnum,
  JobOutcomeNextWaitFromJSON,
  JobOutcomeNextWaitFromJSONTyped,
  JobOutcomeNextWaitToJSON,
  JobOutcomeNextWaitTypeEnum,
  JobOutcomeToJSON,
  JobOutcomeTrigger,
  JobOutcomeTriggerFromJSON,
  JobOutcomeTriggerFromJSONTyped,
  JobOutcomeTriggerToJSON,
  JobPartExecutionEnum,
  JobPlanFromJSON,
  JobPlanFromJSONTyped,
  JobPlanResponseFromJSON,
  JobPlanResponseFromJSONTyped,
  JobPlanResponseToJSON,
  JobPlanToJSON,
  JobResponseFromJSON,
  JobResponseFromJSONTyped,
  JobResponseToJSON,
  JobSplitDetailsFromJSON,
  JobSplitDetailsFromJSONTyped,
  JobSplitDetailsPartsFromJSON,
  JobSplitDetailsPartsFromJSONTyped,
  JobSplitDetailsPartsToJSON,
  JobSplitDetailsToJSON,
  JobStatsAccountsFromJSON,
  JobStatsAccountsFromJSONTyped,
  JobStatsAccountsToJSON,
  JobStatsFromJSON,
  JobStatsFromJSONTyped,
  JobStatsJobsFromJSON,
  JobStatsJobsFromJSONTyped,
  JobStatsJobsMapFromJSON,
  JobStatsJobsMapFromJSONTyped,
  JobStatsJobsMapToJSON,
  JobStatsJobsToJSON,
  JobStatsResponseFromJSON,
  JobStatsResponseFromJSONTyped,
  JobStatsResponseToJSON,
  JobStatsToJSON,
  JobStatusEnum,
  JobSubjectFromJSON,
  JobSubjectFromJSONTyped,
  JobSubjectToJSON,
  JobSubjectType,
  JobSubjectTypeFromJSON,
  JobSubjectTypeFromJSONTyped,
  JobSubjectTypeToJSON,
  JobToJSON,
  JobTriggerEnum,
  JobTypeEnum,
  JobUpdateConfigFromJSON,
  JobUpdateConfigFromJSONTyped,
  JobUpdateConfigToJSON,
  JobUpdateFromJSON,
  JobUpdateFromJSONTyped,
  JobUpdateStatusEnum,
  JobUpdateToJSON,
  JobsResponseFromJSON,
  JobsResponseFromJSONTyped,
  JobsResponseToJSON,
  LineItemFromJSON,
  LineItemFromJSONTyped,
  LineItemToJSON,
  LinkSidebarConfigAllOfFromJSON,
  LinkSidebarConfigAllOfFromJSONTyped,
  LinkSidebarConfigAllOfToJSON,
  LinkSidebarConfigFromJSON,
  LinkSidebarConfigFromJSONTyped,
  LinkSidebarConfigToJSON,
  LinkSidebarConfigTypeEnum,
  ListActorRolesResponseFromJSON,
  ListActorRolesResponseFromJSONTyped,
  ListActorRolesResponseToJSON,
  ListRolesResponseFromJSON,
  ListRolesResponseFromJSONTyped,
  ListRolesResponseToJSON,
  ListUsersSortField,
  ListUsersSortFieldFromJSON,
  ListUsersSortFieldFromJSONTyped,
  ListUsersSortFieldToJSON,
  ManifestResponseFromJSON,
  ManifestResponseFromJSONTyped,
  ManifestResponseToJSON,
  MappingCertainty,
  MappingCertaintyFromJSON,
  MappingCertaintyFromJSONTyped,
  MappingCertaintyToJSON,
  MappingProgramFromJSON,
  MappingProgramFromJSONTyped,
  MappingProgramJobConfigFromJSON,
  MappingProgramJobConfigFromJSONTyped,
  MappingProgramJobConfigToJSON,
  MappingProgramSummaryFromJSON,
  MappingProgramSummaryFromJSONTyped,
  MappingProgramSummaryToJSON,
  MappingProgramToJSON,
  MappingRuleBaseFromJSON,
  MappingRuleBaseFromJSONTyped,
  MappingRuleBaseToJSON,
  MappingRuleConfigFromJSON,
  MappingRuleConfigFromJSONTyped,
  MappingRuleConfigToJSON,
  MappingRuleFromJSON,
  MappingRuleFromJSONTyped,
  MappingRuleToJSON,
  ModelFileFromJSON,
  ModelFileFromJSONTyped,
  ModelFileStatusEnum,
  ModelFileStatusEnumFromJSON,
  ModelFileStatusEnumFromJSONTyped,
  ModelFileStatusEnumToJSON,
  ModelFileToJSON,
  MutateJobConfigFromJSON,
  MutateJobConfigFromJSONTyped,
  MutateJobConfigToJSON,
  NumberPropertyAllOfConfigFromJSON,
  NumberPropertyAllOfConfigFromJSONTyped,
  NumberPropertyAllOfConfigToJSON,
  NumberPropertyAllOfFromJSON,
  NumberPropertyAllOfFromJSONTyped,
  NumberPropertyAllOfToJSON,
  NumberPropertyAllOfTypeEnum,
  NumberPropertyFromJSON,
  NumberPropertyFromJSONTyped,
  NumberPropertyToJSON,
  NumberPropertyTypeEnum,
  PaginationFromJSON,
  PaginationFromJSONTyped,
  PaginationToJSON,
  PipelineJobConfigFromJSON,
  PipelineJobConfigFromJSONTyped,
  PipelineJobConfigToJSON,
  PlanConfigFromJSON,
  PlanConfigFromJSONTyped,
  PlanConfigToJSON,
  PlanFromJSON,
  PlanFromJSONTyped,
  PlanToJSON,
  ProductFromJSON,
  ProductFromJSONTyped,
  ProductToJSON,
  ProductsResponseFromJSON,
  ProductsResponseFromJSONTyped,
  ProductsResponseToJSON,
  ProgramResponseFromJSON,
  ProgramResponseFromJSONTyped,
  ProgramResponseToJSON,
  ProgramsResponseFromJSON,
  ProgramsResponseFromJSONTyped,
  ProgramsResponseToJSON,
  PromptConfigFromJSON,
  PromptConfigFromJSONTyped,
  PromptConfigToJSON,
  PromptFromJSON,
  PromptFromJSONTyped,
  PromptResponseFromJSON,
  PromptResponseFromJSONTyped,
  PromptResponseToJSON,
  PromptToJSON,
  PromptsResponseFromJSON,
  PromptsResponseFromJSONTyped,
  PromptsResponseToJSON,
  PropertyFromJSON,
  PropertyFromJSONTyped,
  PropertyToJSON,
  RecordCountsFromJSON,
  RecordCountsFromJSONTyped,
  RecordCountsResponseDataAllOfFromJSON,
  RecordCountsResponseDataAllOfFromJSONTyped,
  RecordCountsResponseDataAllOfToJSON,
  RecordCountsResponseDataFromJSON,
  RecordCountsResponseDataFromJSONTyped,
  RecordCountsResponseDataToJSON,
  RecordCountsResponseFromJSON,
  RecordCountsResponseFromJSONTyped,
  RecordCountsResponseToJSON,
  RecordCountsToJSON,
  RecordDataFromJSON,
  RecordDataFromJSONTyped,
  RecordDataToJSON,
  RecordDataWithLinksFromJSON,
  RecordDataWithLinksFromJSONTyped,
  RecordDataWithLinksToJSON,
  RecordFromJSON,
  RecordFromJSONTyped,
  RecordToJSON,
  RecordUpdatesAllOfFromJSON,
  RecordUpdatesAllOfFromJSONTyped,
  RecordUpdatesAllOfToJSON,
  RecordUpdatesFromJSON,
  RecordUpdatesFromJSONTyped,
  RecordUpdatesToJSON,
  RecordWithLinksFromJSON,
  RecordWithLinksFromJSONTyped,
  RecordWithLinksToJSON,
  RecordsDataFromJSON,
  RecordsDataFromJSONTyped,
  RecordsDataToJSON,
  RecordsFromJSON,
  RecordsFromJSONTyped,
  RecordsResponseDataAllOfFromJSON,
  RecordsResponseDataAllOfFromJSONTyped,
  RecordsResponseDataAllOfToJSON,
  RecordsResponseDataFromJSON,
  RecordsResponseDataFromJSONTyped,
  RecordsResponseDataToJSON,
  RecordsResponseFromJSON,
  RecordsResponseFromJSONTyped,
  RecordsResponseToJSON,
  RecordsToJSON,
  RecordsUpdatesFromJSON,
  RecordsUpdatesFromJSONTyped,
  RecordsUpdatesToJSON,
  RecordsWithLinksFromJSON,
  RecordsWithLinksFromJSONTyped,
  RecordsWithLinksToJSON,
  ReferenceListPropertyAllOfConfigFromJSON,
  ReferenceListPropertyAllOfConfigFromJSONTyped,
  ReferenceListPropertyAllOfConfigToJSON,
  ReferenceListPropertyAllOfFromJSON,
  ReferenceListPropertyAllOfFromJSONTyped,
  ReferenceListPropertyAllOfToJSON,
  ReferenceListPropertyAllOfTypeEnum,
  ReferenceListPropertyFromJSON,
  ReferenceListPropertyFromJSONTyped,
  ReferenceListPropertyToJSON,
  ReferenceListPropertyTypeEnum,
  ReferencePropertyAllOfConfigFromJSON,
  ReferencePropertyAllOfConfigFromJSONTyped,
  ReferencePropertyAllOfConfigRelationshipEnum,
  ReferencePropertyAllOfConfigToJSON,
  ReferencePropertyAllOfFromJSON,
  ReferencePropertyAllOfFromJSONTyped,
  ReferencePropertyAllOfToJSON,
  ReferencePropertyAllOfTypeEnum,
  ReferencePropertyFromJSON,
  ReferencePropertyFromJSONTyped,
  ReferencePropertyToJSON,
  ReferencePropertyTypeEnum,
  ReplaceRequestFromJSON,
  ReplaceRequestFromJSONTyped,
  ReplaceRequestToJSON,
  RequiredConstraintAllOfFromJSON,
  RequiredConstraintAllOfFromJSONTyped,
  RequiredConstraintAllOfToJSON,
  RequiredConstraintAllOfTypeEnum,
  RequiredConstraintFromJSON,
  RequiredConstraintFromJSONTyped,
  RequiredConstraintToJSON,
  RequiredConstraintTypeEnum,
  RequiredError,
  ResourceIdFromJSON,
  ResourceIdFromJSONTyped,
  ResourceIdToJSON,
  ResourceJobSubjectFromJSON,
  ResourceJobSubjectFromJSONTyped,
  ResourceJobSubjectToJSON,
  ResponseError,
  RestoreConfigFromJSON,
  RestoreConfigFromJSONTyped,
  RestoreConfigToJSON,
  RestoreFromJSON,
  RestoreFromJSONTyped,
  RestoreResponseFromJSON,
  RestoreResponseFromJSONTyped,
  RestoreResponseToJSON,
  RestoreSnapshotRequestFromJSON,
  RestoreSnapshotRequestFromJSONTyped,
  RestoreSnapshotRequestToJSON,
  RestoreToJSON,
  RoleActorResourceAllOfFromJSON,
  RoleActorResourceAllOfFromJSONTyped,
  RoleActorResourceAllOfToJSON,
  RoleActorResourceFromJSON,
  RoleActorResourceFromJSONTyped,
  RoleActorResourceToJSON,
  RoleResponseFromJSON,
  RoleResponseFromJSONTyped,
  RoleResponseToJSON,
  RuleResponseFromJSON,
  RuleResponseFromJSONTyped,
  RuleResponseToJSON,
  RulesResponseFromJSON,
  RulesResponseFromJSONTyped,
  RulesResponseToJSON,
  SecretFromJSON,
  SecretFromJSONTyped,
  SecretToJSON,
  SecretsFromJSON,
  SecretsFromJSONTyped,
  SecretsResponseFromJSON,
  SecretsResponseFromJSONTyped,
  SecretsResponseToJSON,
  SecretsToJSON,
  SheetAccess,
  SheetAccessFromJSON,
  SheetAccessFromJSONTyped,
  SheetAccessToJSON,
  SheetConfigFromJSON,
  SheetConfigFromJSONTyped,
  SheetConfigOptionsFromJSON,
  SheetConfigOptionsFromJSONTyped,
  SheetConfigOptionsToJSON,
  SheetConfigOrUpdateFromJSON,
  SheetConfigOrUpdateFromJSONTyped,
  SheetConfigOrUpdateToJSON,
  SheetConfigToJSON,
  SheetConfigUpdateFromJSON,
  SheetConfigUpdateFromJSONTyped,
  SheetConfigUpdateToJSON,
  SheetConstraintFromJSON,
  SheetConstraintFromJSONTyped,
  SheetConstraintToJSON,
  SheetFromJSON,
  SheetFromJSONTyped,
  SheetResponseFromJSON,
  SheetResponseFromJSONTyped,
  SheetResponseToJSON,
  SheetToJSON,
  SheetUpdateFromJSON,
  SheetUpdateFromJSONTyped,
  SheetUpdateRequestFromJSON,
  SheetUpdateRequestFromJSONTyped,
  SheetUpdateRequestToJSON,
  SheetUpdateToJSON,
  SheetsResponseFromJSON,
  SheetsResponseFromJSONTyped,
  SheetsResponseToJSON,
  SidebarConfigFromJSON,
  SidebarConfigFromJSONTyped,
  SidebarConfigToJSON,
  SnapshotFromJSON,
  SnapshotFromJSONTyped,
  SnapshotResponseFromJSON,
  SnapshotResponseFromJSONTyped,
  SnapshotResponseToJSON,
  SnapshotSheetRequestFromJSON,
  SnapshotSheetRequestFromJSONTyped,
  SnapshotSheetRequestToJSON,
  SnapshotSummaryCreatedSinceFromJSON,
  SnapshotSummaryCreatedSinceFromJSONTyped,
  SnapshotSummaryCreatedSinceToJSON,
  SnapshotSummaryFromJSON,
  SnapshotSummaryFromJSONTyped,
  SnapshotSummaryToJSON,
  SnapshotSummaryUpdatedSinceFromJSON,
  SnapshotSummaryUpdatedSinceFromJSONTyped,
  SnapshotSummaryUpdatedSinceToJSON,
  SnapshotToJSON,
  SnapshotsResponseFromJSON,
  SnapshotsResponseFromJSONTyped,
  SnapshotsResponseToJSON,
  SortDirection,
  SortDirectionFromJSON,
  SortDirectionFromJSONTyped,
  SortDirectionToJSON,
  SourceDestinationWeightFromJSON,
  SourceDestinationWeightFromJSONTyped,
  SourceDestinationWeightToJSON,
  SourceDestinationWeightsResponseDataFromJSON,
  SourceDestinationWeightsResponseDataFromJSONTyped,
  SourceDestinationWeightsResponseDataToJSON,
  SourceDestinationWeightsResponseFromJSON,
  SourceDestinationWeightsResponseFromJSONTyped,
  SourceDestinationWeightsResponseToJSON,
  SourceFieldFromJSON,
  SourceFieldFromJSONTyped,
  SourceFieldToJSON,
  SpaceAccess,
  SpaceAccessFromJSON,
  SpaceAccessFromJSONTyped,
  SpaceAccessToJSON,
  SpaceConfigFromJSON,
  SpaceConfigFromJSONTyped,
  SpaceConfigResponseFromJSON,
  SpaceConfigResponseFromJSONTyped,
  SpaceConfigResponseToJSON,
  SpaceConfigToJSON,
  SpaceConfigsResponseFromJSON,
  SpaceConfigsResponseFromJSONTyped,
  SpaceConfigsResponseToJSON,
  SpaceFromJSON,
  SpaceFromJSONTyped,
  SpacePatternConfigFromJSON,
  SpacePatternConfigFromJSONTyped,
  SpacePatternConfigToJSON,
  SpacePatternFromJSON,
  SpacePatternFromJSONTyped,
  SpacePatternToJSON,
  SpaceResponseFromJSON,
  SpaceResponseFromJSONTyped,
  SpaceResponseToJSON,
  SpaceSettingsFromJSON,
  SpaceSettingsFromJSONTyped,
  SpaceSettingsToJSON,
  SpaceSidebarConfigFromJSON,
  SpaceSidebarConfigFromJSONTyped,
  SpaceSidebarConfigToJSON,
  SpaceSizeFromJSON,
  SpaceSizeFromJSONTyped,
  SpaceSizeToJSON,
  SpaceToJSON,
  SpacesResponseFromJSON,
  SpacesResponseFromJSONTyped,
  SpacesResponseToJSON,
  StringListPropertyAllOfFromJSON,
  StringListPropertyAllOfFromJSONTyped,
  StringListPropertyAllOfToJSON,
  StringListPropertyAllOfTypeEnum,
  StringListPropertyFromJSON,
  StringListPropertyFromJSONTyped,
  StringListPropertyToJSON,
  StringListPropertyTypeEnum,
  StringPropertyAllOfFromJSON,
  StringPropertyAllOfFromJSONTyped,
  StringPropertyAllOfToJSON,
  StringPropertyAllOfTypeEnum,
  StringPropertyFromJSON,
  StringPropertyFromJSONTyped,
  StringPropertyToJSON,
  StringPropertyTypeEnum,
  StripeMetadataFromJSON,
  StripeMetadataFromJSONTyped,
  StripeMetadataToJSON,
  StripePriceFromJSON,
  StripePriceFromJSONTyped,
  StripePriceTierFromJSON,
  StripePriceTierFromJSONTyped,
  StripePriceTierToJSON,
  StripePriceToJSON,
  StripeWebhookRequestBodyFromJSON,
  StripeWebhookRequestBodyFromJSONTyped,
  StripeWebhookRequestBodyRequestFromJSON,
  StripeWebhookRequestBodyRequestFromJSONTyped,
  StripeWebhookRequestBodyRequestToJSON,
  StripeWebhookRequestBodyToJSON,
  SubscriptionUpdatesBodyFromJSON,
  SubscriptionUpdatesBodyFromJSONTyped,
  SubscriptionUpdatesBodyToJSON,
  SuccessFromJSON,
  SuccessFromJSONTyped,
  SuccessResponseFromJSON,
  SuccessResponseFromJSONTyped,
  SuccessResponseToJSON,
  SuccessToJSON,
  TextApiResponse,
  UniqueConstraintAllOfConfigFromJSON,
  UniqueConstraintAllOfConfigFromJSONTyped,
  UniqueConstraintAllOfConfigToJSON,
  UniqueConstraintAllOfFromJSON,
  UniqueConstraintAllOfFromJSONTyped,
  UniqueConstraintAllOfToJSON,
  UniqueConstraintAllOfTypeEnum,
  UniqueConstraintFromJSON,
  UniqueConstraintFromJSONTyped,
  UniqueConstraintToJSON,
  UniqueConstraintTypeEnum,
  UpdateAccountFromJSON,
  UpdateAccountFromJSONTyped,
  UpdateAccountToJSON,
  UpdateEnvironmentByIdRequestDataRetentionPolicyEnum,
  UpdateEnvironmentByIdRequestFromJSON,
  UpdateEnvironmentByIdRequestFromJSONTyped,
  UpdateEnvironmentByIdRequestToJSON,
  UpdateJobExecutionPlanByIdRequestFromJSON,
  UpdateJobExecutionPlanByIdRequestFromJSONTyped,
  UpdateJobExecutionPlanByIdRequestToJSON,
  UpdateJobExecutionPlanFieldsRequestFromJSON,
  UpdateJobExecutionPlanFieldsRequestFromJSONTyped,
  UpdateJobExecutionPlanFieldsRequestToJSON,
  UpdateUserRequestFromJSON,
  UpdateUserRequestFromJSONTyped,
  UpdateUserRequestToJSON,
  UpdateViewRequestFromJSON,
  UpdateViewRequestFromJSONTyped,
  UpdateViewRequestToJSON,
  UpgradeSpaceConfigFromJSON,
  UpgradeSpaceConfigFromJSONTyped,
  UpgradeSpaceConfigToJSON,
  UploadConfigFromJSON,
  UploadConfigFromJSONTyped,
  UploadConfigToJSON,
  UserConfigFromJSON,
  UserConfigFromJSONTyped,
  UserConfigToJSON,
  UserCreateAndInviteBodyFromJSON,
  UserCreateAndInviteBodyFromJSONTyped,
  UserCreateAndInviteBodyToJSON,
  UserCredentialsFromJSON,
  UserCredentialsFromJSONTyped,
  UserCredentialsToJSON,
  UserFromJSON,
  UserFromJSONTyped,
  UserResponseFromJSON,
  UserResponseFromJSONTyped,
  UserResponseToJSON,
  UserToJSON,
  UserWithRolesFromJSON,
  UserWithRolesFromJSONTyped,
  UserWithRolesToJSON,
  UsersResponseFromJSON,
  UsersResponseFromJSONTyped,
  UsersResponseToJSON,
  ValidationFromJSON,
  ValidationFromJSONTyped,
  ValidationMessageFromJSON,
  ValidationMessageFromJSONTyped,
  ValidationMessageSourceEnum,
  ValidationMessageToJSON,
  ValidationMessageTypeEnum,
  ValidationToJSON,
  ValueFromJSON,
  ValueFromJSONTyped,
  ValueToJSON,
  VersionResponseDataFromJSON,
  VersionResponseDataFromJSONTyped,
  VersionResponseDataToJSON,
  VersionResponseFromJSON,
  VersionResponseFromJSONTyped,
  VersionResponseToJSON,
  ViewConfigFromJSON,
  ViewConfigFromJSONTyped,
  ViewConfigToJSON,
  ViewFromJSON,
  ViewFromJSONTyped,
  ViewResponseFromJSON,
  ViewResponseFromJSONTyped,
  ViewResponseToJSON,
  ViewToJSON,
  ViewsResponseFromJSON,
  ViewsResponseFromJSONTyped,
  ViewsResponseToJSON,
  VoidApiResponse,
  WorkbookConfigFromJSON,
  WorkbookConfigFromJSONTyped,
  WorkbookConfigSettingsFromJSON,
  WorkbookConfigSettingsFromJSONTyped,
  WorkbookConfigSettingsToJSON,
  WorkbookConfigToJSON,
  WorkbookFromJSON,
  WorkbookFromJSONTyped,
  WorkbookResponseFromJSON,
  WorkbookResponseFromJSONTyped,
  WorkbookResponseToJSON,
  WorkbookSidebarConfigAllOfFromJSON,
  WorkbookSidebarConfigAllOfFromJSONTyped,
  WorkbookSidebarConfigAllOfToJSON,
  WorkbookSidebarConfigFromJSON,
  WorkbookSidebarConfigFromJSONTyped,
  WorkbookSidebarConfigToJSON,
  WorkbookSidebarConfigTypeEnum,
  WorkbookToJSON,
  WorkbookTreatmentsEnum,
  WorkbookTreatmentsEnumFromJSON,
  WorkbookTreatmentsEnumFromJSONTyped,
  WorkbookTreatmentsEnumToJSON,
  WorkbookUpdateFromJSON,
  WorkbookUpdateFromJSONTyped,
  WorkbookUpdateToJSON,
  WorkbooksResponseFromJSON,
  WorkbooksResponseFromJSONTyped,
  WorkbooksResponseToJSON,
  WriteSecretFromJSON,
  WriteSecretFromJSONTyped,
  WriteSecretToJSON,
  canConsumeForm,
  exists,
  instanceOfAccessToken,
  instanceOfAccessTokenResponse,
  instanceOfAccount,
  instanceOfAccountResponse,
  instanceOfAction,
  instanceOfActionInputForm,
  instanceOfActionMessage,
  instanceOfActivitiesResponse,
  instanceOfActivity,
  instanceOfActivityContext,
  instanceOfActivityContextSlugs,
  instanceOfActorId,
  instanceOfActorResource,
  instanceOfActorRoleResponse,
  instanceOfAgent,
  instanceOfAgentConfig,
  instanceOfAgentLog,
  instanceOfAgentLogResponse,
  instanceOfAgentLogsResponse,
  instanceOfAgentLogsResponse1,
  instanceOfAgentResponse,
  instanceOfAgentStats,
  instanceOfAgentsResponse,
  instanceOfApiCredentials,
  instanceOfApiKey,
  instanceOfApiKeyOperationsInner,
  instanceOfApiKeysResponse,
  instanceOfApiToken,
  instanceOfApiTokenResponse,
  instanceOfApiTokensResponse,
  instanceOfAppVersionResponse,
  instanceOfArrayableProperty,
  instanceOfAssignActorRoleRequest,
  instanceOfAssignRoleResponse,
  instanceOfBaseActionConstraint,
  instanceOfBaseConstraint,
  instanceOfBaseProperty,
  instanceOfBasePropertyAppearance,
  instanceOfBaseSideBarConfig,
  instanceOfBillingLimits,
  instanceOfBillingLimitsResponse,
  instanceOfBillingPlanWithUsage,
  instanceOfBillingPlanWithUsageResponse,
  instanceOfBillingSubscription,
  instanceOfBillingSubscriptionItem,
  instanceOfBillingSubscriptionResponse,
  instanceOfBillingSubscriptionsResponse,
  instanceOfBlueprint,
  instanceOfBooleanProperty,
  instanceOfBooleanPropertyAllOf,
  instanceOfBooleanPropertyAllOfConfig,
  instanceOfCellValue,
  instanceOfCellValueWithCounts,
  instanceOfCellValueWithCountsAllOf,
  instanceOfCellValueWithLinks,
  instanceOfCellsResponse,
  instanceOfCellsResponseData,
  instanceOfCheckoutSession,
  instanceOfCheckoutSessionBody,
  instanceOfCollectionJobSubject,
  instanceOfCommit,
  instanceOfCompositeUniqueConstraint,
  instanceOfCompositeUniqueConstraintAllOf,
  instanceOfComputedConstraint,
  instanceOfComputedConstraintAllOf,
  instanceOfConnectionLink,
  instanceOfConnectionLinkBody,
  instanceOfCreateCheckoutSessionResponse,
  instanceOfCreateCommitIdRequest,
  instanceOfCreateConnectionLinkResponse,
  instanceOfCreateEnvironmentRequest,
  instanceOfCreateMappingRequest,
  instanceOfCreateVersionIdRequest,
  instanceOfCreateViewRequest,
  instanceOfDBUser,
  instanceOfDataRetentionPolicy,
  instanceOfDataRetentionPolicyConfig,
  instanceOfDataRetentionPolicyConfigUpdate,
  instanceOfDataRetentionPolicyResponse,
  instanceOfDateProperty,
  instanceOfDatePropertyAllOf,
  instanceOfDeleteRecordsJobConfig,
  instanceOfDeleteRulesRequest,
  instanceOfDestinationField,
  instanceOfDetailedAgentLog,
  instanceOfDiffData,
  instanceOfDiffRecord,
  instanceOfDiffRecordsResponse,
  instanceOfDiffRecordsResponseData,
  instanceOfDiffValue,
  instanceOfDocument,
  instanceOfDocumentConfig,
  instanceOfDocumentResponse,
  instanceOfDocumentSidebarConfig,
  instanceOfDocumentSidebarConfigAllOf,
  instanceOfDocumentsResponse,
  instanceOfEdge,
  instanceOfEdgeMetadata,
  instanceOfEntitlement,
  instanceOfEntitlementsResponse,
  instanceOfEnumDetails,
  instanceOfEnumListProperty,
  instanceOfEnumListPropertyAllOf,
  instanceOfEnumMappingField,
  instanceOfEnumProperty,
  instanceOfEnumPropertyAllOf,
  instanceOfEnumPropertyAllOfConfig,
  instanceOfEnumPropertyOption,
  instanceOfEnumPropertyOptionValue,
  instanceOfEnvironment,
  instanceOfEnvironmentConfig,
  instanceOfEnvironmentResponse,
  instanceOfEnvironmentStatsResponse,
  instanceOfEnvironmentStatsResponseData,
  instanceOfEnvironmentsResponse,
  instanceOfErrorResponse,
  instanceOfErrorResponseErrorsInner,
  instanceOfEvent,
  instanceOfEventConfig,
  instanceOfEventConfigAttributes,
  instanceOfEventConfigAttributesProgress,
  instanceOfEventConfigOrigin,
  instanceOfEventResponse,
  instanceOfEventToken,
  instanceOfEventTokenResponse,
  instanceOfEventsResponse,
  instanceOfExchangeInvitationRequest,
  instanceOfExchangeTokenResponse,
  instanceOfExchangeTokenResponseData,
  instanceOfExecution,
  instanceOfExecutionsResponse,
  instanceOfExportJobConfig,
  instanceOfExportOptions,
  instanceOfExternalConstraint,
  instanceOfExternalConstraintAllOf,
  instanceOfExternalSheetConstraint,
  instanceOfExternalSheetConstraintAllOf,
  instanceOfFieldConfigResponse,
  instanceOfFieldRecordCounts,
  instanceOfFileConfig,
  instanceOfFileJobConfig,
  instanceOfFileResponse,
  instanceOfFilesResponse,
  instanceOfFindAndReplaceJobConfig,
  instanceOfFindAndReplaceRequest,
  instanceOfFronteggSignup,
  instanceOfFronteggUser,
  instanceOfGenerateMutationConfig,
  instanceOfGenerateQueryConfig,
  instanceOfGeneratedMutation,
  instanceOfGeneratedMutationMetadata,
  instanceOfGeneratedMutationResponse,
  instanceOfGeneratedQuery,
  instanceOfGeneratedQueryMetadata,
  instanceOfGeneratedQueryResponse,
  instanceOfGetCommitResponse,
  instanceOfGetDatabaseData,
  instanceOfGetDatabaseDataTask,
  instanceOfGetDatabaseResponseResponse,
  instanceOfGetEnumWeightsRequest,
  instanceOfGetFieldWeightsRequest,
  instanceOfGetRecordsResponse,
  instanceOfGetRecordsResponseData,
  instanceOfGetRecordsResponseDataAllOf,
  instanceOfGetSheetCommitsResponse,
  instanceOfGetUserResponse,
  instanceOfGetWorkbookCommitsResponse,
  instanceOfGuest,
  instanceOfGuestConfig,
  instanceOfGuestConfigSpacesInner,
  instanceOfGuestConfigSpacesInnerWorkbooksInner,
  instanceOfGuestConfigUpdate,
  instanceOfGuestResponse,
  instanceOfGuestsResponse,
  instanceOfHasAllValidConstraint,
  instanceOfHasAllValidConstraintAllOf,
  instanceOfHasDataConstraint,
  instanceOfHasDataConstraintAllOf,
  instanceOfHasSelectionConstraint,
  instanceOfHasSelectionConstraintAllOf,
  instanceOfHealthResponse,
  instanceOfInputField,
  instanceOfInputFieldConfig,
  instanceOfInputFieldDefaultValue,
  instanceOfInviteGuestsRequestInner,
  instanceOfJob,
  instanceOfJobAckDetails,
  instanceOfJobCancelDetails,
  instanceOfJobCompleteDetails,
  instanceOfJobConfig,
  instanceOfJobOutcome,
  instanceOfJobOutcomeNextDownload,
  instanceOfJobOutcomeNextFiles,
  instanceOfJobOutcomeNextFilesFilesInner,
  instanceOfJobOutcomeNextId,
  instanceOfJobOutcomeNextRetry,
  instanceOfJobOutcomeNextSnapshot,
  instanceOfJobOutcomeNextUrl,
  instanceOfJobOutcomeNextView,
  instanceOfJobOutcomeNextWait,
  instanceOfJobPlan,
  instanceOfJobPlanResponse,
  instanceOfJobResponse,
  instanceOfJobSplitDetails,
  instanceOfJobSplitDetailsParts,
  instanceOfJobStats,
  instanceOfJobStatsAccounts,
  instanceOfJobStatsJobs,
  instanceOfJobStatsJobsMap,
  instanceOfJobStatsResponse,
  instanceOfJobUpdate,
  instanceOfJobsResponse,
  instanceOfLineItem,
  instanceOfLinkSidebarConfig,
  instanceOfLinkSidebarConfigAllOf,
  instanceOfListActorRolesResponse,
  instanceOfListRolesResponse,
  instanceOfManifestResponse,
  instanceOfMappingProgram,
  instanceOfMappingProgramJobConfig,
  instanceOfMappingProgramSummary,
  instanceOfMappingRule,
  instanceOfMappingRuleBase,
  instanceOfMappingRuleConfig,
  instanceOfModelFile,
  instanceOfMutateJobConfig,
  instanceOfNumberProperty,
  instanceOfNumberPropertyAllOf,
  instanceOfNumberPropertyAllOfConfig,
  instanceOfPagination,
  instanceOfPipelineJobConfig,
  instanceOfPlan,
  instanceOfPlanConfig,
  instanceOfProduct,
  instanceOfProductsResponse,
  instanceOfProgramResponse,
  instanceOfProgramsResponse,
  instanceOfPrompt,
  instanceOfPromptConfig,
  instanceOfPromptResponse,
  instanceOfPromptsResponse,
  instanceOfRecord,
  instanceOfRecordCounts,
  instanceOfRecordCountsResponse,
  instanceOfRecordCountsResponseData,
  instanceOfRecordCountsResponseDataAllOf,
  instanceOfRecordData,
  instanceOfRecordDataWithLinks,
  instanceOfRecordUpdates,
  instanceOfRecordUpdatesAllOf,
  instanceOfRecordWithLinks,
  instanceOfRecords,
  instanceOfRecordsData,
  instanceOfRecordsResponse,
  instanceOfRecordsResponseData,
  instanceOfRecordsResponseDataAllOf,
  instanceOfRecordsUpdates,
  instanceOfRecordsWithLinks,
  instanceOfReferenceListProperty,
  instanceOfReferenceListPropertyAllOf,
  instanceOfReferenceListPropertyAllOfConfig,
  instanceOfReferenceProperty,
  instanceOfReferencePropertyAllOf,
  instanceOfReferencePropertyAllOfConfig,
  instanceOfReplaceRequest,
  instanceOfRequiredConstraint,
  instanceOfRequiredConstraintAllOf,
  instanceOfResourceId,
  instanceOfResourceJobSubject,
  instanceOfRestore,
  instanceOfRestoreConfig,
  instanceOfRestoreResponse,
  instanceOfRestoreSnapshotRequest,
  instanceOfRoleActorResource,
  instanceOfRoleActorResourceAllOf,
  instanceOfRoleResponse,
  instanceOfRuleResponse,
  instanceOfRulesResponse,
  instanceOfSecret,
  instanceOfSecrets,
  instanceOfSecretsResponse,
  instanceOfSheet,
  instanceOfSheetConfig,
  instanceOfSheetConfigOptions,
  instanceOfSheetConfigOrUpdate,
  instanceOfSheetConfigUpdate,
  instanceOfSheetResponse,
  instanceOfSheetUpdate,
  instanceOfSheetUpdateRequest,
  instanceOfSheetsResponse,
  instanceOfSnapshot,
  instanceOfSnapshotResponse,
  instanceOfSnapshotSheetRequest,
  instanceOfSnapshotSummary,
  instanceOfSnapshotSummaryCreatedSince,
  instanceOfSnapshotSummaryUpdatedSince,
  instanceOfSnapshotsResponse,
  instanceOfSourceDestinationWeight,
  instanceOfSourceDestinationWeightsResponse,
  instanceOfSourceDestinationWeightsResponseData,
  instanceOfSourceField,
  instanceOfSpace,
  instanceOfSpaceConfig,
  instanceOfSpaceConfigResponse,
  instanceOfSpaceConfigsResponse,
  instanceOfSpacePattern,
  instanceOfSpacePatternConfig,
  instanceOfSpaceResponse,
  instanceOfSpaceSettings,
  instanceOfSpaceSidebarConfig,
  instanceOfSpaceSize,
  instanceOfSpacesResponse,
  instanceOfStringListProperty,
  instanceOfStringListPropertyAllOf,
  instanceOfStringProperty,
  instanceOfStringPropertyAllOf,
  instanceOfStripeMetadata,
  instanceOfStripePrice,
  instanceOfStripePriceTier,
  instanceOfStripeWebhookRequestBody,
  instanceOfStripeWebhookRequestBodyRequest,
  instanceOfSubscriptionUpdatesBody,
  instanceOfSuccess,
  instanceOfSuccessResponse,
  instanceOfUniqueConstraint,
  instanceOfUniqueConstraintAllOf,
  instanceOfUniqueConstraintAllOfConfig,
  instanceOfUpdateAccount,
  instanceOfUpdateEnvironmentByIdRequest,
  instanceOfUpdateJobExecutionPlanByIdRequest,
  instanceOfUpdateJobExecutionPlanFieldsRequest,
  instanceOfUpdateUserRequest,
  instanceOfUpdateViewRequest,
  instanceOfUpgradeSpaceConfig,
  instanceOfUploadConfig,
  instanceOfUser,
  instanceOfUserConfig,
  instanceOfUserCreateAndInviteBody,
  instanceOfUserCredentials,
  instanceOfUserResponse,
  instanceOfUserWithRoles,
  instanceOfUsersResponse,
  instanceOfValidation,
  instanceOfValidationMessage,
  instanceOfValue,
  instanceOfVersionResponse,
  instanceOfVersionResponseData,
  instanceOfView,
  instanceOfViewConfig,
  instanceOfViewResponse,
  instanceOfViewsResponse,
  instanceOfWorkbook,
  instanceOfWorkbookConfig,
  instanceOfWorkbookConfigSettings,
  instanceOfWorkbookResponse,
  instanceOfWorkbookSidebarConfig,
  instanceOfWorkbookSidebarConfigAllOf,
  instanceOfWorkbookUpdate,
  instanceOfWorkbooksResponse,
  instanceOfWriteSecret,
  mapValues,
  querystring
};
//# sourceMappingURL=index.mjs.map